import { apiService } from "../../services/api";
import { authService } from "../../services/auth";
import { uploadImage } from "../../services/fileService";
import {
  buildFormDataFromRecord,
  deriveRecordIdFromRow,
  deriveScreenId,
  findSummaryListByAction,
  resolveSummaryListApiEndpoint,
  SUMMARY_LIST_API_ACTION_TYPES,
  generateUniqueFormId,
  getParentIdFromFormData,
  getParentIdDescFromFormData,
  isParentIdBindValue,
  resolveUserCode,
  resolveView,
  screenConfigRegistry,
  walkComponentTree,
  getResolvedScreenConfig,
  mapSubModuleApiRowsToFormRows,
  fetchSubModuleRowsForEdit,
  assignSubModuleRowsToFormData,
  resolveFormForSave,
  isSubformIncludedInSave,
  configCache,
  findSubModulesInScreenConfig,
  isLookupOnlySubModule,
  resolveSubModulePersistedModuleName,
  resolveSubformRowsForSave,
  getPersistedSubModuleRowId,
  mapMultiCheckboxApiRowsToFormRows,
  isPersistedSubModuleRowForEdit,
  isInvoiceLineSubModuleTable,
  reconcileInvoiceLineRowsForEditSave,
} from "../utils/configUtils";
import { buildPDFPayload } from "../../utils/pdfPayloadConfig";
import {
  decodePdfResponseToBlob,
  buildPdfFileName,
} from "../../utils/pdfDeliveryUtils";
import { showPdfDeliveryDialog } from "../utils/pdfDeliveryDialogUtils";
import {
  formatNumericDecimal,
} from "../../utils/numberFormatUtils";
import {
  timeShiftValidationSummary,
  validateTimeShiftTimes,
} from "../../utils/timeShiftValidation";
import {
  isFromStockChecked,
  isPurchaseReturnContext,
  resolvePurchaseIdForValidation,
  showPurchaseReturnBlockedAlert,
  validatePurchaseReturnPartsIssue,
} from "../../utils/purchaseReturnPartsIssueCheck";
import { buildLookupPayload, getLookupCacheKey } from "../../utils/lookupUtils";
import { VOID_CONFIRM_MESSAGE } from "../../utils/voidTransactionConfig";
import { isVoidSummaryRow } from "../../utils/summaryActionVisibility";
import {
  resolvePurchaseSummaryViewTarget,
  applyPurchaseViewSubmoduleHydration,
  applyPurchaseWithOrderViewHydration,
  mergePurchaseOrderLookupIntoFormData,
  loadPurchaseWithoutOrderSubmoduleData,
  findPurchaseDetailsSubModule,
  PURCHASE_SUBMODULE_FORM_KEYS,
  logPurchaseWithoutOrderSubmodule,
} from "../../utils/purchaseSummaryViewUtils";
import {
  confirmPurchasedOverlapProceed,
  extractCodeidsFromPartsPayload,
  isPartsIssueContext,
  isPartsRequestContext,
  isPartsRequestOrIssueContext,
  validatePurchasedOverlapForSave,
} from "../../utils/purchasedItemsOverlapCheck";
import {
  confirmSimilarLedgerNameProceed,
  isLedgerNameSimilarityContext,
  resolveLedgerNameForSimilarityCheck,
  validateSimilarLedgerNameForSave,
} from "../../utils/ledgerNameSimilarityCheck";
import {
  isCustomerBillAllocationContext,
  isVendorBillAllocationContext,
  resolveBillAllocationPartyId,
  showBillAllocationBlockedAlert,
  validateBillAllocationBlockingEntries,
} from "../../utils/billAllocationBlockingCheck";
import {
  buildBillWiseReleaseSubformItems,
  isBillWiseReleaseInvoiceMode,
  resolveBillWiseReleaseVoucherType,
} from "../../utils/billWiseReleaseUtils";
import { toUTC } from "../../utils/dateUtils";
import {
  ComponentType,
  ComponentConfig,
  LayoutTypes,
  ActionType,
  InputType,
} from "../../services/types/ComponentTypes";

/** True when a form field is a numeric input (empty should persist as "0"). */
function isNumericFormFieldConfig(fieldConfig: any): boolean {
  if (!fieldConfig) return false;
  const inputType = String(
    fieldConfig.inputType ?? fieldConfig.InputType ?? ""
  ).toLowerCase();
  if (inputType === "number" || inputType === String(InputType.NUMBER)) {
    return true;
  }
  const controlType = String(
    fieldConfig.ControlType ?? fieldConfig.controlType ?? ""
  ).toLowerCase();
  return controlType === "numeric";
}

/** Prefer explicit cleared "" over stale loaded values; empty → "0". */
function resolveNumericPayloadValue(
  ...candidates: unknown[]
): string {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    if (typeof candidate === "string" && candidate.trim() === "") {
      return "0";
    }
    return String(candidate);
  }
  return "0";
}
import { AppThunk } from "../store";
import type { RootState } from "../store";
import {
  incrementFormInstanceCounter,
  resetScreenFormData,
  setScreenFormData,
  setScreenLoading,
  setScreenSaving,
  setScreenExpressionCalculating,
  updateScreenFormField,
  setScreenEditMode,
  clearScreenEditMode,
} from "../slices/formsSlice";
import { screenHasExpressionV2 } from "../../utils/expressionV2Utils";
import {
  clearHistory,
  navigateToView,
  resetPreviousView,
  setPreviousView,
} from "../slices/navigationSlice";
import { updateUserBranch } from "../slices/authSlice";
import {
  isAddOrEditScreen,
  resolveScreenIdForView,
} from "../../utils/branchSwitchFeature";
import {
  NotificationPayload,
  DEFAULT_NOTIFICATION_TIMEOUT_MS,
  showNotification,
} from "../slices/notificationsSlice";
import {
  setCurrentBranch as setLayoutCurrentBranch,
  toggleSideMenu,
} from "../slices/layoutSlice";
import { clearLookup, fetchSubModuleRawData, addLookupItem, fetchLookup } from "../slices/lookupsSlice";
import {
  normalizeBillAllocationTab,
  referenceSettlementPartyType,
} from "../../utils/billAllocationScreenConfig";

/**
 * After hydrate, keep the form under an expression-settling overlay when the screen
 * declares ExpressionV2. Cleared by ComponentRenderer once SubModules + parent calcs settle.
 */
const beginExpressionCalculatingIfNeeded = (
  dispatch: (action: any) => void,
  screenId: string
) => {
  const screenConfig = getResolvedScreenConfig(String(screenId));
  if (screenConfig && screenHasExpressionV2(screenConfig)) {
    dispatch(
      setScreenExpressionCalculating({
        screenId: String(screenId),
        isCalculating: true,
      })
    );
  }
};

const LEAVE_APPROVAL_DRAWER_SCREEN_ID = "2212";
const REFERENCE_SETTLEMENT_ADD_SCREEN_IDS = new Set(["199", "198"]);

/** Round off from estimate, jobcard, or invoice_jobcard lookup prefill. */
function resolveEstimationRoundOff(est: Record<string, any> | null | undefined): string | null {
  if (!est) return null;
  const v =
    est.round_off_amt ??
    est.round_off ??
    est.txt_round_off;
  if (v === undefined || v === null || v === "") return null;
  return String(v);
}

function applyEstimationRoundOffToFormData(
  data: Record<string, any>,
  est: Record<string, any> | null | undefined
): void {
  const ro = resolveEstimationRoundOff(est);
  if (ro !== null) {
    data.txt_round_off = ro;
    data.round_off_amt = ro;
  }
}

/** True when value looks like a persisted id (not a display name like "Albin"). */
function looksLikePersistedId(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  const s = String(value).trim();
  if (!s) return false;
  // Plain person/company names must not be treated as FK ids.
  if (/^[A-Za-z][A-Za-z .'-]*$/.test(s)) return false;
  return true;
}

function firstPersistedId(
  ...candidates: unknown[]
): string | null {
  for (const c of candidates) {
    if (looksLikePersistedId(c)) return String(c).trim();
  }
  return null;
}

function matchLookupOptionById(
  options: any[] | undefined,
  targetId: string
): any | null {
  if (!options?.length || !targetId) return null;
  const matched = options.find((opt: any) => {
    const candidates = [
      opt?.id,
      opt?.value,
      opt?.inspection_id,
      opt?.parent_id,
      opt?.parentid,
    ];
    return candidates.some(
      (c) => c != null && String(c) === String(targetId)
    );
  });
  return matched ?? null;
}

/**
 * Load proforma jobcard option for BindParent (customer_id / customertype).
 * parentid1 must be the jobcard lookup id, with parentid1_selectedData set.
 */
async function fetchProformaJobcardOption(
  dispatch: (action: any) => any,
  serviceOrInspectionId: string
): Promise<any | null> {
  const id = String(serviceOrInspectionId ?? "").trim();
  if (!id) return null;
  try {
    const lookupResult = await dispatch(
      fetchLookup({
        lookupName: "proforma_jobcard_no_search_Select",
        params: {
          parentid: "",
          lookuptype: "Search_Select",
          search_arg: "",
          selectedid: "",
          additionalSelectedId: id,
        },
      })
    ).unwrap();
    return (
      matchLookupOptionById(lookupResult?.options, id) ??
      lookupResult?.options?.[0] ??
      null
    );
  } catch {
    return null;
  }
}

function applyProformaJobcardPrefill(
  patchData: Record<string, any>,
  jobcardOption: Record<string, any> | null | undefined,
  dispatch?: (action: any) => any
): void {
  if (!jobcardOption) return;

  const jobcardId = firstPersistedId(
    jobcardOption.id,
    jobcardOption.value,
    jobcardOption.inspection_id
  );
  if (jobcardId) {
    patchData.parentid1 = jobcardId;
    patchData.parentid1_selectedData = jobcardOption;
    const jobcardNo =
      jobcardOption.name ??
      jobcardOption.jobcard_no ??
      jobcardOption.label;
    if (jobcardNo != null && jobcardNo !== "") {
      patchData.jobcard_no = jobcardNo;
    }
  }

  const customerTypeValue = firstPersistedId(
    jobcardOption.customertype,
    jobcardOption.customer_type,
    jobcardOption.customer_type_id
  );
  if (customerTypeValue) {
    patchData.s_select_customertype_ = customerTypeValue;
    patchData.customertype = customerTypeValue;
  }

  // insurance_name must store customer id (never display name).
  const customerId = firstPersistedId(
    jobcardOption.customer_id,
    jobcardOption.insurance_name,
    jobcardOption.insurance_id
  );
  if (customerId) {
    patchData.s_select_insurance_customertype_ = customerId;
    patchData.insurance_name = customerId;

    const customerLabel =
      jobcardOption.customer_name ??
      jobcardOption.customername ??
      "";
    if (dispatch && customerTypeValue) {
      const lookupKey = getLookupCacheKey(
        "insurance_customer",
        customerTypeValue
      );
      const seedItem = {
        id: customerId,
        value: customerId,
        label: customerLabel || customerId,
        customername: customerLabel || customerId,
        name: customerLabel || customerId,
      };
      dispatch(addLookupItem({ lookupKey, item: seedItem }));
      dispatch(
        addLookupItem({
          lookupKey,
          item: { ...jobcardOption, ...seedItem },
          isRawData: true,
        })
      );
    }
  }
}

/** Latest estimate round off for a service_details id (estimation_items lookup key). */
async function fetchEstimationItemByServiceDetailsId(
  serviceDetailsId: string
): Promise<Record<string, any> | null> {
  const id = String(serviceDetailsId ?? "").trim();
  if (!id) return null;
  const { apiService } = await import("../../services/api");
  const estimationResponse = await apiService.get(
    `/pabws/utility/estimation_items`,
    { id },
    {
      includeAuthToken: true,
      authHeaderType: "Bearer",
      errorHandling: "return-empty",
      transformResponse: (raw: any) => {
        if (!raw) return null;
        if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
          return raw.data[0];
        }
        if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
          return raw.data[0];
        }
        return raw;
      },
    }
  );
  return estimationResponse && typeof estimationResponse === "object"
    ? estimationResponse
    : null;
}

async function applyLatestEstimationRoundOffForInvoice(
  data: Record<string, any>,
  serviceDetailsId: string | undefined,
  jobcardFallback?: Record<string, any> | null
): Promise<void> {
  const sdId =
    serviceDetailsId ??
    data.parentid ??
    data.parent_id ??
    jobcardFallback?.id;
  if (!sdId) {
    applyEstimationRoundOffToFormData(data, jobcardFallback);
    return;
  }
  try {
    const estimationRecord = await fetchEstimationItemByServiceDetailsId(String(sdId));
    if (estimationRecord) {
      applyEstimationRoundOffToFormData(data, estimationRecord);
      return;
    }
  } catch {
    // Fall back to jobcard snapshot when estimate fetch fails.
  }
  applyEstimationRoundOffToFormData(data, jobcardFallback);
}

/** Invoice line tables/bind keys seeded from estimation on inspection Create invoice reopen. */
const INSPECTION_INVOICE_LINE_MODULE_NAMES = new Set([
  "service_invoice_spareparts",
  "service_invoice_labour",
  "service_invoice_miscellaneous",
]);

const INSPECTION_INVOICE_LINE_FORM_KEYS = [
  "service_invoice_spareparts",
  "service_invoice_labour",
  "service_invoice_miscellaneous",
  "sub_spareparts",
  "sub_labour",
  "sub_miscellaneous",
] as const;

/**
 * Inspection workflow → Create invoice (screen 158) with an existing invoice_id.
 * Header stays in edit mode; line submodules must re-seed from estimation lookups
 * instead of loading saved service_invoice_* rows (Invoice module 173 is unaffected).
 */
function isInspectionCreateInvoiceEdit(
  actionId: string | undefined,
  targetScreenId: string | undefined,
  childRecordId: string | number | null | undefined
): boolean {
  const hasChild =
    childRecordId !== undefined &&
    childRecordId !== null &&
    String(childRecordId).trim().length > 0;
  return actionId === "Create invoice" && targetScreenId === "158" && hasChild;
}

function stripInspectionInvoiceLineKeys(formData: Record<string, any>): void {
  for (const key of INSPECTION_INVOICE_LINE_FORM_KEYS) {
    delete formData[key];
  }
}

function isInspectionInvoiceLineModuleName(moduleName: string): boolean {
  return INSPECTION_INVOICE_LINE_MODULE_NAMES.has(String(moduleName || "").trim());
}

/** Expression-driven invoice footer keys that must not keep saved invoice totals on inspection reopen. */
const INSPECTION_INVOICE_EXPRESSION_FOOTER_KEYS = [
  "txt_discount",
  "discount",
  "gross_vat_amount",
  "gross_amount",
  "txt_total_amount",
  "amount",
] as const;

/**
 * Inspection Create invoice reopen: footer from estimation (round off / excess),
 * and clear sticky invoice totals so ExpressionV2 recomputes from estimation-seeded lines.
 */
async function seedInspectionInvoiceFooterFromEstimation(
  data: Record<string, any>,
  serviceDetailsId: string | undefined,
  jobcardFallback?: Record<string, any> | null
): Promise<void> {
  const sdId =
    serviceDetailsId ??
    data.parentid ??
    data.parent_id ??
    jobcardFallback?.id;

  let estimationRecord: Record<string, any> | null = null;
  if (sdId) {
    try {
      estimationRecord = await fetchEstimationItemByServiceDetailsId(String(sdId));
    } catch {
      estimationRecord = null;
    }
  }

  const source = estimationRecord ?? jobcardFallback ?? null;
  applyEstimationRoundOffToFormData(data, source);

  const excess = source?.excess_charge ?? source?.txt_excess_charge;
  if (excess !== undefined && excess !== null && String(excess).trim() !== "") {
    const excessStr = String(excess);
    data.txt_excess_charge = excessStr;
    data.excess_charge = excessStr;
  } else {
    // Excess is not stored on estimation; clear invoice-edited residue.
    data.txt_excess_charge = "0";
    data.excess_charge = "0";
  }

  for (const key of INSPECTION_INVOICE_EXPRESSION_FOOTER_KEYS) {
    delete data[key];
  }
}

/**
 * Parts Issue Payload Builder
 * Builds a payload that matches Blazor format exactly:
 * - Wrapped in data object
 * - Includes next_action and next_state ONLY at root level (not in data)
 * - issued_qty = 1, bal_qty = 0 for all items (as numbers)
 * - All numeric values as numbers (not strings)
 * - Removes duplicate fields (keeps parent_id, removes parentid)
 */
interface PartsIssuePayload {
  data: {
    id?: string;
    issue_date?: string;
    parent_id?: string;
    parent_id_desc?: string;
    job_card_customername?: string;
    txt_customername?: string;
    job_card_reg_no?: string;
    txt_reg_no?: string;
    created_by?: string;
    created_at?: string;
    transaction_branch_id?: string;
    subform: Array<{
      tablename: string;
      name: string;
      data: Array<Record<string, any>>;
      manadatoryResult?: {
        success: boolean;
        fields: string[];
      };
    }>;
    [key: string]: any;
  };
  next_action?: string;
  next_state?: string;
}

/**
 * Converts a value to a number (for numeric fields)
 * Returns 0 if value is null, undefined, empty, or NaN
 */
/** Screen 180 always creates a new BankReconciliation voucher (never updates PDC/source row id). */
const BR_VOUCHER_SCREEN_ID = "180";
const BR_VOUCHER_MODULE = "bank_reconciliation_voucher";

const isBankReconVoucherInsertOnly = (
  screenId?: string | number | null,
  moduleName?: string | null
): boolean =>
  String(screenId ?? "") === BR_VOUCHER_SCREEN_ID ||
  String(moduleName ?? "").toLowerCase() === BR_VOUCHER_MODULE;

/** Extract FastAPI `detail` (or similar) from failed API calls for user-facing toasts. */
const parseApiErrorDetail = (err: unknown, fallback: string): string => {
  if (!err || typeof err !== "object") {
    return fallback;
  }
  const e = err as { message?: string; response?: string };
  const raw = String(e.response || e.message || "").trim();
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]) as { detail?: unknown };
      if (typeof parsed.detail === "string") {
        return parsed.detail;
      }
      if (Array.isArray(parsed.detail) && parsed.detail.length > 0) {
        const first = parsed.detail[0] as { msg?: string };
        if (first?.msg) {
          return String(first.msg);
        }
      }
    } catch {
      /* ignore parse errors */
    }
  }
  if (raw.includes("message:")) {
    const tail = raw.split("message:").pop()?.trim();
    if (tail) {
      return tail.replace(/^["']|["']$/g, "");
    }
  }
  return e.message || fallback;
};

/**
 * Strip voucher `id` from form state so UI/save stay in create mode; keep PDC source id separately.
 * PDC summary rows use `voucher_no` for the source receipt/payment/journal — map that to
 * `source_voucher_no` only and leave reconciliation `voucher_no` empty for BRK_ auto-sequence on save.
 */
const stripBankReconVoucherFormId = (
  data: Record<string, any>,
  pdcSourceId?: string | number
): Record<string, any> => {
  const out = { ...data };
  delete out.id;

  const nested = out.bank_reconciliation_voucher_details;
  const nestedObj =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, any>)
      : null;

  const sourceVno =
    out.source_voucher_no ??
    out.txt_source_voucher_no ??
    nestedObj?.source_voucher_no ??
    nestedObj?.txt_source_voucher_no ??
    out.voucher_no ??
    out.txt_voucher_no ??
    nestedObj?.voucher_no ??
    nestedObj?.txt_voucher_no;
  const src =
    sourceVno !== undefined &&
    sourceVno !== null &&
    String(sourceVno).trim() !== ""
      ? String(sourceVno).trim()
      : "";
  if (src) {
    out.source_voucher_no = src;
    out.txt_source_voucher_no = src;
  }

  out.voucher_no = "";
  out.txt_voucher_no = "";

  // Details is user-entered; do not prefill from source transaction narration.
  out.reconciliation_narration = "";
  out.txt_reconciliation_narration = "";

  if (nestedObj) {
    out.bank_reconciliation_voucher_details = {
      ...nestedObj,
      ...(src
        ? { source_voucher_no: src, txt_source_voucher_no: src }
        : {}),
      voucher_no: "",
      txt_voucher_no: "",
      reconciliation_narration: "",
      txt_reconciliation_narration: "",
    };
  }

  if (pdcSourceId !== undefined && pdcSourceId !== null && String(pdcSourceId).trim() !== "") {
    out.pdc_reminder_source_id = String(pdcSourceId).trim();
  }
  return out;
};

/** DEV-only: trace Party / account (drp_party_ledger) from PDC summary → screen 180. */
const logBrPartyPrefill = (
  stage: string,
  data: Record<string, any> | null | undefined,
  extra?: Record<string, any>
) => {
  if (!import.meta.env.DEV) return;
  const o = data && typeof data === "object" ? data : {};
  const nested = o.bank_reconciliation_voucher_details;
  const nestedSnap =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? {
          party_ledger_id: nested.party_ledger_id,
          drp_party_ledger: nested.drp_party_ledger,
          party_name: nested.party_name,
          drp_party_ledger_desc: nested.drp_party_ledger_desc,
        }
      : undefined;
  console.log(`[BR Party Prefill] ${stage}`, {
    party_ledger_id: o.party_ledger_id,
    drp_party_ledger: o.drp_party_ledger,
    party_name: o.party_name,
    txt_party_name: o.txt_party_name,
    party_ledger_id_desc: o.party_ledger_id_desc,
    drp_party_ledger_desc: o.drp_party_ledger_desc,
    drp_party_ledger_selectedData: o.drp_party_ledger_selectedData,
    nested: nestedSnap,
    ...extra,
  });
};

const normalizeBrPartyId = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

/** Resolve party ledger id for bank recon voucher (summary row, API, or journal_ledgers label). */
const resolveBrPartyLedgerId = (
  state: RootState,
  lookupName: string,
  sources: { id?: unknown; name?: unknown }
): { id: string; label: string; source: string } => {
  const id = normalizeBrPartyId(sources.id);
  const label = normalizeBrPartyId(sources.name);
  if (id) {
    return { id, label, source: "explicit_id" };
  }
  if (!label || lookupName !== "journal_ledgers") {
    return { id: "", label, source: "none" };
  }
  const options = state.lookups.lookups[lookupName] || [];
  const needle = label.toLowerCase();
  const exact = options.find(
    (opt) => String(opt.label || "").trim().toLowerCase() === needle
  );
  const partial =
    exact ||
    options.find((opt) => {
      const hay = String(opt.label || "").trim().toLowerCase();
      return hay.includes(needle) || needle.includes(hay);
    });
  if (partial) {
    const matchedId = normalizeBrPartyId(partial.value ?? (partial as any).id);
    if (matchedId) {
      return {
        id: matchedId,
        label: String(partial.label || label),
        source: exact ? "lookup_exact_name" : "lookup_partial_name",
      };
    }
  }
  return { id: "", label, source: "none" };
};

const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const numValue = parseFloat(String(value));
  return isNaN(numValue) ? 0 : numValue;
};

/** Coerce lookup / multicheckbox selection to a scalar string for API subform rows (avoids "[object Object]"). */
const normalizeLookupSelectionToId = (selectedValue: any, jsonAttribute?: string): string => {
  if (selectedValue === null || selectedValue === undefined) {
    return "";
  }
  if (typeof selectedValue === "object") {
    const attr = jsonAttribute || "id";
    const raw =
      (selectedValue as any)[attr] ??
      (selectedValue as any).id ??
      (selectedValue as any).value ??
      (selectedValue as any).codeid ??
      "";
    if (raw !== null && raw !== undefined && typeof raw === "object") {
      return normalizeLookupSelectionToId(raw, jsonAttribute);
    }
    return raw === null || raw === undefined ? "" : String(raw);
  }
  if (typeof selectedValue === "boolean") {
    return selectedValue ? "True" : "False";
  }
  return String(selectedValue);
};

/**
 * Formats date to ISO 8601 UTC format for API payload.
 * For issue_date: if no time component is provided, use current time (not midnight)
 * For created_at: always use current time
 */
const formatDateForPartsIssue = (dateValue: any, useCurrentTime: boolean = false): string => {
  if (!dateValue || dateValue === "") {
    // If useCurrentTime is true and no date provided, use current time
    if (useCurrentTime) {
      return new Date().toISOString();
    }
    return "";
  }

  // If useCurrentTime is true, always return current time ISO
  if (useCurrentTime) {
    // Try to extract just the date part and combine with current time
    const parsed = toUTC(dateValue);
    if (parsed) {
      // We have a valid date; but the caller wants current time applied
      // For created_at: always current, for issue_date: current time if no time component
      const dateObj = new Date(parsed);
      const hasTimeComponent = dateObj.getUTCHours() !== 0 || dateObj.getUTCMinutes() !== 0 || dateObj.getUTCSeconds() !== 0;
      if (!hasTimeComponent) {
        // No time component: use current time with the parsed date
        const now = new Date();
        dateObj.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
        return dateObj.toISOString();
      }
      return parsed;
    }
    return new Date().toISOString();
  }

  return toUTC(dateValue) || "";
};

/**
 * Builds Parts Issue payload matching Blazor format exactly
 */
export const buildPartsIssuePayload = (
  formState: any,
  nextAction: string = "",
  nextState: string = ""
): PartsIssuePayload => {
  // Initialize payload with data object and workflow fields at root level only
  const payload: PartsIssuePayload = {
    data: {
      subform: [],
    },
    next_action: nextAction,
    next_state: nextState,
  };

  // Process root-level fields - copy to data object, handling duplicates and formatting
  Object.keys(formState).forEach((key) => {
    // Skip subform data and workflow fields (they're handled separately)
    if (key === "service_spare_parts_issue" || key === "subform" || key === "next_action" || key === "next_state") {
      return;
    }

    const value = formState[key];

    // Handle duplicate parent fields - keep parent_id, remove parentid
    if (key === "parentid") {
      // Only set parent_id if it doesn't already exist
      if (!payload.data.parent_id) {
        payload.data.parent_id = value || "";
      }
      return; // Skip parentid, we use parent_id
    }

    // Handle duplicate customer/reg fields - keep both variants if they exist
    if (key === "txt_customername" || key === "txt_reg_no") {
      payload.data[key] = value;
      return;
    }

    // Format date fields consistently
    // For issue_date: use current time if no time component provided
    // For created_at: always use current time
    if (key === "issue_date") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Use current time if no time component
    } else if (key === "created_at") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Always use current time
    } else if (key.includes("date") || key.includes("Date")) {
      payload.data[key] = formatDateForPartsIssue(value);
    } else {
      payload.data[key] = value;
    }
  });

  // Process subform
  const subformData = formState.service_spare_parts_issue || formState.subform?.[0]?.data || [];

  if (Array.isArray(subformData) && subformData.length > 0) {
    payload.data.subform = [
      {
        tablename: "service_spare_parts_issue_details",
        name: "service_spare_parts_issue",
        data: subformData.map((item: any) => {
          const normalizedItem: Record<string, any> = { ...item };

          // Define numeric fields that should be converted to numbers
          const numericFields = [
            "qty",
            "stock_qty",
            "issued_qty",
            "bal_qty",
            "rate",
            "purchase_rate",
            "amount",
            "vat_id", // vat_id is also numeric
            "vat_amt",
            "net_amt",
          ];

          // Convert all numeric fields to numbers (not strings)
          numericFields.forEach((field) => {
            if (normalizedItem[field] !== undefined) {
              normalizedItem[field] = toNumber(normalizedItem[field]);
            }
          });



          // Remove UI-only fields and duplicates
          delete normalizedItem.next_action;
          delete normalizedItem.next_state;
          delete normalizedItem.parentid; // Remove if exists, we use parent_id

          return normalizedItem;
        }),
        manadatoryResult: {
          success: true,
          fields: [],
        },
      },
    ];
  }

  // Ensure next_action and next_state are NOT in data object
  delete payload.data.next_action;
  delete payload.data.next_state;

  return payload;
};

/**
 * Validates Parts Issue payload before submission
 */
export const validatePartsIssuePayload = (payload: PartsIssuePayload): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!payload.data) {
    errors.push("Payload must have a data object");
    return { valid: false, errors };
  }

  if (!payload.data.subform || !Array.isArray(payload.data.subform)) {
    errors.push("Payload.data.subform must be an array");
    return { valid: false, errors };
  }

  if (payload.data.subform.length === 0) {
    errors.push("Payload.data.subform.length must be > 0");
    return { valid: false, errors };
  }

  const partsIssueSubform = payload.data.subform.find(
    (sf) => sf.name === "service_spare_parts_issue" || sf.tablename === "service_spare_parts_issue_details"
  );

  if (!partsIssueSubform || !partsIssueSubform.data || partsIssueSubform.data.length === 0) {
    errors.push("Parts Issue subform must have at least one item");
    return { valid: false, errors };
  }

  // Validate each item
  partsIssueSubform.data.forEach((item: any, index: number) => {


    // Validate numeric fields are numbers, not strings
    const numericFields = ["qty", "stock_qty", "rate", "purchase_rate", "amount", "vat_id", "vat_amt", "net_amt"];
    numericFields.forEach((field) => {
      if (item[field] !== undefined && typeof item[field] !== "number") {
        errors.push(`Item ${index}: ${field} must be a number, got ${typeof item[field]}`);
      }
    });
  });

  // Validate workflow fields are only at root level, not in data
  if (payload.data.next_action !== undefined || payload.data.next_state !== undefined) {
    errors.push("next_action and next_state must only be at root level, not in data object");
  }

  // Validate no duplicate parent fields
  if (payload.data.parentid && payload.data.parent_id) {
    errors.push("Both parentid and parent_id exist - should only have parent_id");
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Converts a numeric value to a decimal string (e.g., 1 -> "1.0", 1000 -> "1000.0")
 */
const toDecimalString = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "0.0";
  }
  const numValue = parseFloat(String(value));
  if (isNaN(numValue)) {
    return "0.0";
  }
  // Format as decimal string (always include .0 for whole numbers)
  return numValue % 1 === 0 ? `${numValue}.0` : numValue.toString();
};

/**
 * Builds Parts Request payload matching Blazor format exactly
 * Similar to Parts Issue but with different subform structure
 */
export const buildPartsRequestPayload = (
  formState: any,
  nextAction: string = "",
  nextState: string = ""
): any => {
  // Initialize payload with data object and workflow fields at root level only
  const payload: any = {
    data: {
      subform: [],
    },
    next_action: nextAction,
    next_state: nextState,
  };

  // Process root-level fields - copy to data object, handling duplicates and formatting
  Object.keys(formState).forEach((key) => {
    const value = formState[key];

    // Skip subform data and workflow fields (they're handled separately)
    if (key === "subform" || key === "next_action" || key === "next_state" || Array.isArray(value)) {
      return;
    }

    // Handle duplicate parent fields - keep both parent_id and parentid
    if (key === "parentid") {
      payload.data.parentid = value || "";
      // Also set parent_id if not already set
      if (!payload.data.parent_id) {
        payload.data.parent_id = value || "";
      }
      return;
    }

    // Format date fields consistently with full datetime (not midnight)
    // For issue_date: use current time if no time component provided
    // For created_at: always use current time
    if (key === "issue_date") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Use current time if no time component
    } else if (key === "created_at") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Always use current time
    } else if (key.includes("date") || key.includes("Date")) {
      payload.data[key] = formatDateForPartsIssue(value);
    } else {
      payload.data[key] = value;
    }
  });

  // Process subforms dynamically
  const subforms = formState.subform || [];

  if (Array.isArray(subforms)) {
    subforms.forEach((sf: any) => {
      if (!sf.data || !Array.isArray(sf.data) || sf.data.length === 0) return;

      const subformData = sf.data.map((item: any) => {
        const normalizedItem: Record<string, any> = { ...item };

        // Define numeric fields that should be converted to decimal strings
        const numericFields = [
          "req_qty",
          "qty",
          "issued_qty",
          "rate",
          "amount",
          "vat_id",
          "vat_amt",
          "net_amt",
        ];

        // Convert all numeric fields to decimal strings
        numericFields.forEach((field) => {
          if (normalizedItem[field] !== undefined) {
            normalizedItem[field] = toDecimalString(normalizedItem[field]);
          }
        });

        // Ensure required fields are present
        if (!normalizedItem.act && !normalizedItem.soft_delete) {
          normalizedItem.act = "add";
        }

        return normalizedItem;
      });

      payload.data.subform.push({
        tablename: sf.tablename || sf.tableName || sf.name, // Use dynamically from config
        name: sf.name,
        data: subformData,
        manadatoryResult: sf.manadatoryResult || {
          success: true,
          fields: [],
        },
      });
    });
  }

  // Ensure issue_date is formatted correctly (not midnight - use current time if no time component)
  if (payload.data.issue_date) {
    payload.data.issue_date = formatDateForPartsIssue(payload.data.issue_date, true);
  }

  // Ensure created_at is formatted correctly (always use current time)
  if (payload.data.created_at) {
    payload.data.created_at = formatDateForPartsIssue(payload.data.created_at, true);
  }

  return payload;
};

import {
  openPDFViewer,
  setPDFViewerLoading,
  setPDFViewerError,
} from "../slices/pdfViewerSlice";
import {
  openHTMLDrawer,
  setHTMLDrawerLoading,
  setHTMLDrawerError,
} from "../slices/htmlDrawerSlice";
import { closeScreenPopup, openScreenPopup } from "../slices/screenPopupSlice";
import {
  showConfirmDialog,
  showConfirmDialogWithReason,
} from "../utils/confirmationDialogUtils";
import {
  closeProfileEditDrawer,
  openProfileEditDrawer,
} from "../slices/profileEditDrawerSlice";
import { openJobcardManpowerModal } from "../slices/jobcardManpowerModalSlice";
import { openBillReleaseLinkedModal } from "../slices/billReleaseLinkedModalSlice";
import {
  buildProfileSectionPayload,
  canEditProfileSection,
  EMPLOYEE_PROFILE_SCREEN_ID,
  findProfileSectionByEditDrawerId,
  findProfileSectionBySaveActionId,
  getProfileSaveEndpoint,
  pickProfileDrawerFormData,
  PROFILE_EDIT_DRAWER_SCREEN_ID,
} from "../../utils/hrmsProfileEdit";
import { parseHrmsEmployeeProfileGetResponse } from "../../utils/parseHrmsEmployeeProfileResponse";

const autoHideNotification = (
  dispatch: (action: any) => void,
  notification: NotificationPayload,
  timeoutMs?: number
) => {
  dispatch(
    showNotification({
      ...notification,
      timeoutMs:
        timeoutMs ?? notification.timeoutMs ?? DEFAULT_NOTIFICATION_TIMEOUT_MS,
    })
  );
};

const ensureSlidingTimeout = (
  dispatch: (action: any) => void,
  notification: NotificationPayload,
  timeoutMs?: number
) => {
  dispatch(
    showNotification({
      ...notification,
      timeoutMs:
        timeoutMs ?? notification.timeoutMs ?? DEFAULT_NOTIFICATION_TIMEOUT_MS,
    })
  );
};

const isPurchaseWithoutOrderContext = (
  screenId: string,
  moduleName?: string
): boolean =>
  String(screenId) === "139" || moduleName === "pur_without_order_add";

/** Fetch purchase_details lines for screen 139 and push into Redux form state. */
const hydratePurchaseWithoutOrderSubmodules = async (
  dispatch: (action: any) => void,
  getState: () => RootState,
  options: {
    screenId: string;
    recordId: string;
    formData?: Record<string, unknown>;
    source?: string;
  }
): Promise<boolean> => {
  const screenId = String(options.screenId);
  const recordId = String(options.recordId || "").trim();
  const source = options.source || "unknown";

  logPurchaseWithoutOrderSubmodule("hydrate_start", {
    source,
    screenId,
    recordId: recordId || "(empty)",
  });

  if (!recordId) {
    logPurchaseWithoutOrderSubmodule("hydrate_skip_no_record_id", { source, screenId });
    return false;
  }

  const screenConfig = getResolvedScreenConfig(screenId);
  const subModules = screenConfig
    ? findSubModulesInScreenConfig(screenConfig)
    : [];

  logPurchaseWithoutOrderSubmodule("hydrate_config", {
    source,
    screenId,
    recordId,
    hasScreenConfig: Boolean(screenConfig),
    subModuleCount: subModules.length,
    subModules: subModules.map((sm) => ({
      name: sm?.name || sm?.Name,
      tableName: sm?.TableName || sm?.tableName,
      bindValue: sm?.BindValue,
      lookupOnly: isLookupOnlySubModule(sm),
    })),
  });

  const mergedForm: Record<string, unknown> = {
    ...(options.formData ??
      getState().forms.formDataByScreen[screenId] ??
      {}),
  };

  const loaded = await loadPurchaseWithoutOrderSubmoduleData(mergedForm, {
    recordId,
    purchaseDetailsSubModule: findPurchaseDetailsSubModule(subModules),
    getSubModuleData: (name, id) => {
      logPurchaseWithoutOrderSubmodule("api_call", {
        source,
        endpoint: `/pabws/submodule/${name}/${id}`,
        moduleName: name,
        recordId: String(id),
      });
      return apiService.getSubModuleData(name, id);
    },
    mapRows: mapSubModuleApiRowsToFormRows,
    assignRows: assignSubModuleRowsToFormData,
    parentModuleName: "pur_without_order_add",
  });

  if (!loaded) {
    logPurchaseWithoutOrderSubmodule("hydrate_failed", { source, screenId, recordId });
    return false;
  }

  const dispatchedKeys: string[] = [];
  for (const key of PURCHASE_SUBMODULE_FORM_KEYS) {
    const value = mergedForm[key];
    if (value === undefined) continue;
    dispatchedKeys.push(key);
    dispatch(
      updateScreenFormField({
        screenId,
        name: key,
        value: Array.isArray(value)
          ? (value as Record<string, unknown>[]).map((row) => ({ ...row }))
          : value,
      })
    );
  }

  logPurchaseWithoutOrderSubmodule("hydrate_success", {
    source,
    screenId,
    recordId,
    dispatchedKeys,
    lineCount: Array.isArray(mergedForm.jobcard_details)
      ? mergedForm.jobcard_details.length
      : 0,
    job_card_id_selected: mergedForm.job_card_id_selected,
  });
  return true;
};

export const executeConfigAction =
  (actionId: string, data?: any): AppThunk =>
    async (dispatch, getState) => {
      const state = getState() as RootState;
      const { currentView, previousView } = state.navigation;

      const summaryAction = data?.__summaryAction;

      // Attendance: open session log screen (2208) as a popup modal and scope by attendance_id.
      // This is used by the Attendance "Log" button from the CheckInOut widget.
      if (actionId === "open_popup_2208_attendance_sessions") {
        const attendanceId =
          data?.attendance_id ||
          data?.attendanceId ||
          data?.attendanceID ||
          data?.formData?.attendance_id ||
          data?.formData?.attendanceId ||
          data?.formData?.attendanceID;

        dispatch(resetScreenFormData("2208"));
        if (attendanceId) {
          dispatch(
            setScreenFormData({
              screenId: "2208",
              data: {
                attendance_id: String(attendanceId),
                attendance_date: data?.attendance_date ?? data?.formData?.attendance_date,
              },
            })
          );
        }
        dispatch(openScreenPopup({ screenId: "2208", title: "Attendance Session Log" }));
        return;
      }

      if (actionId === "close_drawer") {
        dispatch(resetScreenFormData(LEAVE_APPROVAL_DRAWER_SCREEN_ID));
        dispatch(navigateToView({ view: "2201", rememberPrevious: false }));
        return;
      }

      if (actionId === "close_profile_edit_drawer") {
        dispatch(closeProfileEditDrawer());
        dispatch(resetScreenFormData(PROFILE_EDIT_DRAWER_SCREEN_ID));
        return;
      }

      if (
        actionId.startsWith("profile-") &&
        actionId.endsWith("-edit")
      ) {
        const section = findProfileSectionByEditDrawerId(actionId);
        if (!section?.fields?.length || !section.saveActionId) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Profile section not found.", type: "error" },
            4000
          );
          return;
        }
        if (!canEditProfileSection(section)) {
          ensureSlidingTimeout(
            dispatch,
            { message: "You do not have permission to edit this section.", type: "error" },
            4000
          );
          return;
        }

        const parentForm =
          state.forms.formDataByScreen[EMPLOYEE_PROFILE_SCREEN_ID] || {};
        const drawerData = pickProfileDrawerFormData(
          section.fields,
          parentForm
        );

        dispatch(resetScreenFormData(PROFILE_EDIT_DRAWER_SCREEN_ID));
        dispatch(
          setScreenFormData({
            screenId: PROFILE_EDIT_DRAWER_SCREEN_ID,
            data: drawerData,
          })
        );
        dispatch(
          openProfileEditDrawer({
            title: (data?.editTitle as string) || section.editTitle || section.title,
            saveActionId: section.saveActionId,
            editDrawerId: actionId,
            fields: section.fields,
          })
        );
        return;
      }

      if (actionId.startsWith("save_profile_")) {
        const section = findProfileSectionBySaveActionId(actionId);
        if (!section) {
          return;
        }
        if (!canEditProfileSection(section)) {
          ensureSlidingTimeout(
            dispatch,
            { message: "You do not have permission to edit this section.", type: "error" },
            4000
          );
          return;
        }

        const endpoint = getProfileSaveEndpoint(actionId);
        if (!endpoint) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Document upload is not available yet. Contact HR.",
              type: "error",
            },
            5000
          );
          return;
        }

        const drawerForm =
          getState().forms.formDataByScreen[PROFILE_EDIT_DRAWER_SCREEN_ID] || {};
        const parentForm =
          getState().forms.formDataByScreen[EMPLOYEE_PROFILE_SCREEN_ID] || {};
        const employeeId =
          parentForm.employee_id || parentForm.id || state.auth.user?.employee_id;

        if (!employeeId && endpoint !== "about") {
          ensureSlidingTimeout(
            dispatch,
            { message: "Employee profile is not loaded yet.", type: "error" },
            5000
          );
          return;
        }

        if (getState().forms.savingByScreen[PROFILE_EDIT_DRAWER_SCREEN_ID]) {
          return;
        }

        dispatch(
          setScreenSaving({
            screenId: PROFILE_EDIT_DRAWER_SCREEN_ID,
            isSaving: true,
          })
        );

        try {
          const payload = buildProfileSectionPayload(section.fields, drawerForm);
          const response = await apiService.post(
            `/pabws/hrms/employee/profile/${endpoint}`,
            {
              employee_id: employeeId || null,
              data: payload,
            },
            {
              includeAuthToken: true,
              authHeaderType: "Bearer",
              errorHandling: "throw",
            }
          );

          if (!(response && (response as any).success)) {
            throw new Error("Failed to save profile section.");
          }

          const result = response as Record<string, any>;
          const newEmployeeId = result.employee_id || employeeId;
          const mergedDrawer = {
            ...drawerForm,
            ...pickProfileDrawerFormData(section.fields, payload),
          };
          const mergedParent = {
            ...parentForm,
            ...mergedDrawer,
            id: newEmployeeId || parentForm.id,
            employee_id: newEmployeeId || parentForm.employee_id,
            primary_name: result.name || parentForm.primary_name,
          };

          dispatch(
            setScreenFormData({
              screenId: EMPLOYEE_PROFILE_SCREEN_ID,
              data: mergedParent,
            })
          );

          try {
            const profileRaw = await apiService.get<any>(
              newEmployeeId
                ? `/pabws/hrms/employee/profile/${encodeURIComponent(String(newEmployeeId))}`
                : `/pabws/hrms/employee/profile/me`,
              undefined,
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "return-null",
              }
            );
            const record = parseHrmsEmployeeProfileGetResponse(profileRaw);
            if (record) {
              const mapped = buildFormDataFromRecord(record, {
                screenId: EMPLOYEE_PROFILE_SCREEN_ID,
              });
              const rowId = record.id ?? record.employee_id ?? newEmployeeId;
              dispatch(
                setScreenFormData({
                  screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                  data: {
                    ...mapped,
                    id: rowId,
                    employee_id: String(rowId),
                  },
                })
              );
            }
          } catch {
            /* optional refresh */
          }

          dispatch(closeProfileEditDrawer());
          dispatch(resetScreenFormData(PROFILE_EDIT_DRAWER_SCREEN_ID));
          ensureSlidingTimeout(
            dispatch,
            { message: "Profile updated.", type: "success" },
            3000
          );
        } catch (err) {
          console.error("[HRMS] save_profile section:", err);
          ensureSlidingTimeout(
            dispatch,
            {
              message: parseApiErrorDetail(err, "Failed to save profile."),
              type: "error",
            },
            5000
          );
        } finally {
          dispatch(
            setScreenSaving({
              screenId: PROFILE_EDIT_DRAWER_SCREEN_ID,
              isSaving: false,
            })
          );
        }
        return;
      }

      if (actionId === "submit_leave_approval") {
        const fd = getState().forms.formDataByScreen[LEAVE_APPROVAL_DRAWER_SCREEN_ID] || {};
        const leaveId = fd.id ?? fd.ID;
        const raw = String(fd.drp_approval_action || "").toLowerCase();
        const statusNorm =
          raw === "approve" ? "approved" : raw === "reject" ? "rejected" : "";
        if (!leaveId) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Missing leave record id.", type: "error" },
            4000
          );
          return;
        }
        if (!statusNorm) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Please select Approve or Reject.", type: "error" },
            4000
          );
          return;
        }
        const remarks = (fd.txt_remarks ?? fd.rejection_reason ?? "").toString().trim();
        if (statusNorm === "rejected" && !remarks) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Remarks are required when rejecting.", type: "error" },
            4000
          );
          return;
        }
        try {
          await apiService.post(
            `/pabws/hrms/leave/${encodeURIComponent(String(leaveId))}/decision`,
            {
              status: statusNorm,
              rejection_reason: statusNorm === "rejected" ? remarks : null,
            },
            { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
          );
          dispatch(resetScreenFormData(LEAVE_APPROVAL_DRAWER_SCREEN_ID));
          ensureSlidingTimeout(dispatch, { message: "Leave decision saved.", type: "success" }, 4000);
          // If this screen is opened inside a popup, close the popup instead of navigating away.
          const popupState = (getState() as any).screenPopup as { isOpen?: boolean; screenId?: string } | undefined;
          const isInPopup = !!popupState?.isOpen && popupState?.screenId === LEAVE_APPROVAL_DRAWER_SCREEN_ID;
          if (isInPopup) {
            dispatch(closeScreenPopup());
          } else {
            dispatch(navigateToView({ view: "2201", rememberPrevious: false }));
          }

          const parentScreenId = "2201";
          try {
            const stats = await apiService.get<Record<string, any>>(
              `/pabws/hrms/leave/statistics`,
              undefined,
              { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "return-null" }
            );
            if (stats && typeof stats === "object") {
              const cur = getState().forms.formDataByScreen[parentScreenId] || {};
              dispatch(
                setScreenFormData({
                  screenId: parentScreenId,
                  data: {
                    ...cur,
                    total_leaves: stats.total_leaves ?? cur.total_leaves,
                    pending_leaves: stats.pending_leaves ?? cur.pending_leaves,
                    approved_leaves: stats.approved_leaves ?? cur.approved_leaves,
                    rejected_leaves: stats.rejected_leaves ?? cur.rejected_leaves,
                  },
                })
              );
            }
          } catch {
            /* optional refresh */
          }
        } catch (e: any) {
          console.error("[submit_leave_approval]", e);
          ensureSlidingTimeout(
            dispatch,
            {
              message: e?.message || "Failed to save leave decision.",
              type: "error",
            },
            5000
          );
        }
        return;
      }

      // Attendance: open session log screen (2208) as a full screen.
      // If attendance_id is available from the check-in/out widget, scope the route to that record.
      if (actionId === "attendanceSessionSummarylist") {
        const todayIso = new Date().toISOString().split("T")[0];
        const attendanceId =
          data?.attendance_id ||
          data?.attendanceId ||
          data?.attendanceID;
        dispatch(resetScreenFormData("2208"));
        if (attendanceId) {
          dispatch(
            setScreenFormData({
              screenId: "2208",
              data: {
                attendance_id: String(attendanceId),
                // Used by the DateFilter in the popup; default to today if missing.
                attendance_date: data?.attendance_date ?? data?.formData?.attendance_date ?? todayIso,
              },
            })
          );
        } else {
          // Still default the date filter to today even when not scoped by attendance_id.
          dispatch(
            setScreenFormData({
              screenId: "2208",
              data: {
                attendance_date: data?.attendance_date ?? data?.formData?.attendance_date ?? todayIso,
              },
            })
          );
        }
        dispatch(openScreenPopup({ screenId: "2208", title: "Attendance Session Log" }));
        return;
      }

      const summaryActionTypeLower = summaryAction?.ActionType
        ?.toString()
        .trim()
        .toLowerCase();
      const summaryTargetScreenId =
        deriveScreenId(summaryAction?.ScreenId || "") || summaryAction?.ScreenId;

      const summaryApiEndpoint =
        summaryAction && summaryActionTypeLower
          ? resolveSummaryListApiEndpoint(summaryAction, summaryActionTypeLower)
          : undefined;

      if (
        summaryApiEndpoint &&
        summaryActionTypeLower &&
        SUMMARY_LIST_API_ACTION_TYPES.has(summaryActionTypeLower)
      ) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;
        const recordId = deriveRecordIdFromRow(cleanRowData, { summaryAction });
        if (!recordId) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Missing record id.", type: "error" },
            4000
          );
          return;
        }
        const defaultConfirm =
          summaryActionTypeLower === "authorize"
            ? "Approve invoicing for this job card?"
            : "Submit this request?";
        const confirmMessage =
          summaryAction.confirmMessage ||
          (summaryAction as { ConfirmMessage?: string }).ConfirmMessage ||
          defaultConfirm;
        const needsApprovalReason =
          summaryActionTypeLower === "authorize" &&
          summaryAction.requireApprovalReason !== false;
        const allowsRequestReason =
          summaryActionTypeLower === "request" &&
          summaryAction.allowRequestReason === true;

        let approvalReason = "";
        let requestReason = "";

        if (needsApprovalReason) {
          const dialogResult = await showConfirmDialogWithReason(
            dispatch,
            confirmMessage,
            summaryActionTypeLower === "authorize"
              ? "Approve job card"
              : undefined,
            {
              requireReason: true,
              reasonLabel:
                summaryAction.approvalReasonLabel ||
                "Reason for job card approval",
            }
          );
          if (!dialogResult.confirmed) {
            return;
          }
          approvalReason = dialogResult.reason;
          if (!approvalReason) {
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Reason for job card approval is required.",
                type: "error",
              },
              4000
            );
            return;
          }
        } else if (allowsRequestReason) {
          const needsRequestReason =
            summaryAction.requireRequestReason !== false;
          const dialogResult = await showConfirmDialogWithReason(
            dispatch,
            confirmMessage,
            "Request authorization",
            {
              requireReason: needsRequestReason,
              reasonLabel:
                summaryAction.requestReasonLabel ||
                "Reason for job card authorization request",
            }
          );
          if (!dialogResult.confirmed) {
            return;
          }
          requestReason = dialogResult.reason;
          if (needsRequestReason && !requestReason) {
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Reason for job card authorization request is required.",
                type: "error",
              },
              4000
            );
            return;
          }
        } else {
          const confirmed = await showConfirmDialog(dispatch, confirmMessage);
          if (!confirmed) {
            return;
          }
        }

        const recordIdStr = String(recordId);
        const body: Record<string, string> = { id: recordIdStr };
        const idFieldKey =
          summaryAction.Action?.RecordIdKey ||
          summaryAction.Action?.recordIdKey ||
          summaryAction.Action?.BodyIdKey ||
          summaryAction.Action?.bodyIdKey;
        if (idFieldKey && idFieldKey !== "id") {
          body[idFieldKey] = recordIdStr;
        }
        body.job_card_id = recordIdStr;
        if (approvalReason) {
          body.approval_reason = approvalReason;
        }
        if (requestReason) {
          body.request_reason = requestReason;
        }
        try {
          const response = await apiService.post<{
            success: boolean;
            message?: string;
          }>(summaryApiEndpoint, body, {
            includeAuthToken: true,
            authHeaderType: "Bearer",
            errorHandling: "throw",
          });
          if (!response?.success) {
            throw new Error(response?.message || "Action failed.");
          }
          ensureSlidingTimeout(
            dispatch,
            {
              message: response.message || "Action completed successfully.",
              type: "success",
            },
            4000
          );
          dispatch(
            navigateToView({
              view: currentView,
              rememberPrevious: false,
            })
          );
        } catch (e: any) {
          console.error("[summaryApiAction]", summaryApiEndpoint, e);
          ensureSlidingTimeout(
            dispatch,
            {
              message: e?.message || "Action failed. Please try again.",
              type: "error",
            },
            5000
          );
        }
        return;
      }

      const isSummaryNavOrEdit =
        summaryAction?.ActionType &&
        (summaryActionTypeLower === "edit" ||
          summaryActionTypeLower === "view" ||
          (summaryActionTypeLower === "add" &&
            isBankReconVoucherInsertOnly(summaryTargetScreenId, null)));

      if (isSummaryNavOrEdit) {
        const actionTypeLower = summaryActionTypeLower || "";
        const wantsPopup = Boolean((summaryAction as any)?.popup);

        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        const summaryComponent = findSummaryListByAction(summaryAction);

        if (
          summaryComponent?.Name === "reference_settlement_summary" &&
          actionTypeLower === "view" &&
          wantsPopup
        ) {
          const targetScreenId = "200";
          const row = cleanRowData;
          const partyType =
            String(row.party_type ?? "").toLowerCase() === "vendor"
              ? "vendor"
              : "customer";
          const formPatch: Record<string, any> = {
            reference_settlement_view_record: row,
            party_type: partyType,
            ...(partyType === "customer"
              ? {
                  reference_settlement_customer_party_id: row.party_id,
                  reference_settlement_customer_party_name: row.party_name,
                }
              : {
                  reference_settlement_vendor_party_id: row.party_id,
                  reference_settlement_vendor_party_name: row.party_name,
                }),
          };
          const settlementNo = String(row.settlement_no ?? "").trim();
          dispatch(resetScreenFormData(targetScreenId));
          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: formPatch,
            })
          );
          dispatch(
            openScreenPopup({
              screenId: targetScreenId,
              title: settlementNo
                ? `Reference settlement · ${settlementNo}`
                : "Reference settlement",
            })
          );
          return;
        }

        // Log vehicle summary edit action data
        const isVehicleSummary = summaryComponent?.Name === "vehicle_summary" ||
          summaryAction.Action?.Module === "Vehicle_Add" ||
          summaryAction.Action?.Navigate?.includes("vehicle_add");

        const deriveModuleName = (): string | undefined => {
          const moduleFromAction =
            summaryAction.Action?.Module ||
            summaryAction.Action?.module ||
            summaryAction.Action?.ModuleName ||
            summaryAction.Action?.modulename;

          if (moduleFromAction && typeof moduleFromAction === "string") {
            return moduleFromAction;
          }

          const navigatePath =
            summaryAction.Action?.Navigate || summaryAction.Action?.navigate;

          if (navigatePath && typeof navigatePath === "string") {
            const segments = navigatePath.split("/").filter(Boolean);
            if (segments.length >= 1) {
              // For routes like "modulev1/vehiclein_add", module name is second segment.
              if (segments[0] === "modulev1" && segments.length >= 2) {
                return segments[1];
              }
              // For routes like "hrm/time_shift_add/<id>", module name is second segment.
              // (First segment is a domain/group prefix, not the module itself.)
              if (segments[0] === "hrm" && segments.length >= 2) {
                return segments[1];
              }
              // For routes like "emp_profile_add/<id>", module name is first segment.
              return segments[0];
            }
          }

          return undefined;
        };

        let moduleName = deriveModuleName();
        const recordId = deriveRecordIdFromRow(cleanRowData, {
          summaryAction,
        });

        if (!moduleName || !recordId) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to load record for editing.",
              type: "error",
            },
            5000
          );
          return;
        }

        let targetScreenId =
          deriveScreenId(summaryAction.ScreenId || "") ||
          summaryAction.ScreenId ||
          deriveScreenId(currentView) ||
          currentView;

        if (
          actionTypeLower === "view" &&
          (summaryComponent?.Name === "purchase_summary" ||
            summaryComponent?.Form?.Name === "purchase_summary" ||
            deriveScreenId(currentView) === "138")
        ) {
          const purchaseView = resolvePurchaseSummaryViewTarget(cleanRowData);
          targetScreenId = purchaseView.screenId;
          moduleName = purchaseView.moduleName;
          logPurchaseWithoutOrderSubmodule("view_route_resolved", {
            purchaseNo:
              cleanRowData.purchase_no ??
              cleanRowData.txt_prno ??
              cleanRowData.txt_purchaseno,
            targetScreenId,
            moduleName,
            recordId,
          });
        }

        // Backend expects PascalCase "Customer_Add"; Navigate path gives "customer_add"
        // Same for "Supplier_Add"
        const apiModuleName =
          targetScreenId === "123" && moduleName === "customer_add"
            ? "Customer_Add"
            : targetScreenId === "128" && moduleName === "supplier_add"
              ? "Supplier_Add"
              : String(targetScreenId) === "124" &&
                  (moduleName === "sales_return_add" || moduleName === "sales_ret_add")
                ? "sales_ret_add"
              : (String(targetScreenId) === "126" ||
                  String(targetScreenId) === "18") &&
                  (moduleName === "purchase_return_add" ||
                    moduleName === "pur_ret_add" ||
                    moduleName === "purchase_return_voucher_add")
                ? "pur_ret_add"
              : moduleName;

        if (apiModuleName === "bank_reconciliation_voucher") {
          logBrPartyPrefill("1_summary_row", cleanRowData, {
            recordId,
            moduleName,
            targetScreenId,
          });
        }

        const targetView = resolveView(targetScreenId);

        dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: true }));

        const brInsertOnly = isBankReconVoucherInsertOnly(targetScreenId, apiModuleName);
        const pdcSourceId = recordId;
        const isViewAction = actionTypeLower === "view";
        const isVoidedRow = isVoidSummaryRow(cleanRowData);

        // Screen 180: always insert (Reconcile passes ActionType Edit but must not update source row).
        dispatch(
          setScreenEditMode({
            screenId: targetScreenId,
            isEditMode: brInsertOnly
              ? false
              : !isVoidedRow && (actionTypeLower === "edit" || isViewAction),
            isViewOnly: isViewAction || isVoidedRow,
            recordId: brInsertOnly ? undefined : recordId,
          })
        );

        dispatch(
          setScreenFormData({
            screenId: targetScreenId,
            data: stripBankReconVoucherFormId(
              buildFormDataFromRecord(null, {
                fallback: cleanRowData,
                summaryComponent,
                screenId: targetScreenId,
              }),
              brInsertOnly ? pdcSourceId : undefined
            ),
          })
        );
        if (!wantsPopup) {
          dispatch(navigateToView({ view: targetView }));
        }

        let purchaseSubmoduleFieldPatch: Record<string, unknown> | null = null;

        try {
          // Employee profile has its own dedicated GET endpoint because the generic module
          // handler builds a dynamic SELECT from the module JSON fields. Some of those
          // fields (document_*, asset_*, same_as_current_address) are UI-only and do not
          // exist as columns in view_employee_profile, causing the generic query to fail.
          //
          // We detect this by targetScreenId === "2205" rather than apiModuleName because
          // the API-stored screen 2203 config may have a legacy Navigate value (e.g.
          // "emp_add/<id>") that derives moduleName as "emp_add", not "emp_profile_add".
          // ScreenId in the action is always authoritative and is already "2205".
          const isEmpProfile = targetScreenId === "2205";
          let endpoint = isEmpProfile
            ? `/pabws/hrms/employee/profile/${recordId}`
            : `/pabws/module/${apiModuleName}/${pdcSourceId}`;

          // Bank recon voucher: pass list-row amount and party ledger (PDC reminder grid) for header prefill.
          if (!isEmpProfile && apiModuleName === "bank_reconciliation_voucher") {
            const params = new URLSearchParams();
            const rawAmt =
              cleanRowData.amount ??
              cleanRowData.Amount ??
              cleanRowData.txt_amount ??
              cleanRowData.txtAmount;
            if (rawAmt !== undefined && rawAmt !== null && String(rawAmt).trim() !== "") {
              params.set("amount", String(rawAmt));
            }
            const rawPartyLedger =
              cleanRowData.party_ledger_id ??
              cleanRowData.txt_party_ledger_id ??
              cleanRowData.drp_party_ledger;
            if (
              rawPartyLedger !== undefined &&
              rawPartyLedger !== null &&
              String(rawPartyLedger).trim() !== ""
            ) {
              params.set("party_ledger_id", String(rawPartyLedger).trim());
            }
            const rawPartyName =
              cleanRowData.party_name ?? cleanRowData.txt_party_name ?? cleanRowData.PartyName;
            if (
              rawPartyName !== undefined &&
              rawPartyName !== null &&
              String(rawPartyName).trim() !== ""
            ) {
              params.set("party_name", String(rawPartyName).trim());
            }
            const rawVoucherNo =
              cleanRowData.voucher_no ??
              cleanRowData.txt_voucher_no ??
              cleanRowData.VoucherNo;
            if (
              rawVoucherNo !== undefined &&
              rawVoucherNo !== null &&
              String(rawVoucherNo).trim() !== ""
            ) {
              params.set("source_voucher_no", String(rawVoucherNo).trim());
            }
            const rawTxnType =
              cleanRowData.transaction_type ??
              cleanRowData.txt_transaction_type ??
              cleanRowData.pdc_transaction_type ??
              cleanRowData.txt_pdc_transaction_type;
            if (
              rawTxnType !== undefined &&
              rawTxnType !== null &&
              String(rawTxnType).trim() !== ""
            ) {
              const txn = String(rawTxnType).trim();
              params.set("pdc_transaction_type", txn);
              params.set("transaction_type", txn);
            }
            const rawBankLedger =
              cleanRowData.bank_ledger_id ??
              cleanRowData.drp_bank ??
              cleanRowData.txt_bank_ledger_id;
            if (
              rawBankLedger !== undefined &&
              rawBankLedger !== null &&
              String(rawBankLedger).trim() !== ""
            ) {
              params.set("bank_ledger_id", String(rawBankLedger).trim());
            }
            const rawNarration =
              cleanRowData.narration ??
              cleanRowData.txt_narration ??
              cleanRowData.txt_source_narration;
            if (
              rawNarration !== undefined &&
              rawNarration !== null &&
              String(rawNarration).trim() !== ""
            ) {
              const narr = String(rawNarration).trim();
              params.set("narration", narr);
              params.set("txt_narration", narr);
              params.set("txt_source_narration", narr);
            }
            const rawChequeNo =
              cleanRowData.cheque_no ??
              cleanRowData.cheque_number ??
              cleanRowData.txt_cheque_no ??
              cleanRowData.txt_cheque_number;
            if (
              rawChequeNo !== undefined &&
              rawChequeNo !== null &&
              String(rawChequeNo).trim() !== ""
            ) {
              const chq = String(rawChequeNo).trim();
              params.set("cheque_no", chq);
              params.set("cheque_number", chq);
            }
            const rawChequeDate =
              cleanRowData.cheque_date ?? cleanRowData.txt_cheque_date;
            if (
              rawChequeDate !== undefined &&
              rawChequeDate !== null &&
              String(rawChequeDate).trim() !== ""
            ) {
              params.set("cheque_date", String(rawChequeDate).trim());
            }
            const rawRefNo =
              cleanRowData.ref_no ??
              cleanRowData.txt_Ref_no ??
              cleanRowData.txt_ref_no;
            if (
              rawRefNo !== undefined &&
              rawRefNo !== null &&
              String(rawRefNo).trim() !== ""
            ) {
              const refNo = String(rawRefNo).trim();
              params.set("ref_no", refNo);
              params.set("txt_Ref_no", refNo);
            }
            const rawRefDate =
              cleanRowData.reference_date ??
              cleanRowData.ref_date ??
              cleanRowData.txt_reference_date;
            if (
              rawRefDate !== undefined &&
              rawRefDate !== null &&
              String(rawRefDate).trim() !== ""
            ) {
              const refDate = String(rawRefDate).trim();
              params.set("reference_date", refDate);
              params.set("ref_date", refDate);
            }
            const q = params.toString();
            if (q) {
              endpoint = `${endpoint}?${q}`;
            }
            logBrPartyPrefill("2_get_endpoint", cleanRowData, {
              endpoint,
              queryString: q || "(none)",
              rawPartyLedger:
                cleanRowData.party_ledger_id ??
                cleanRowData.txt_party_ledger_id ??
                cleanRowData.drp_party_ledger,
              rawPartyName:
                cleanRowData.party_name ?? cleanRowData.txt_party_name,
            });
          }

          if (import.meta.env.DEV && isEmpProfile) {
            console.log("[EmpProfile Edit] Using dedicated endpoint:", endpoint, "| moduleName was:", apiModuleName);
          }
          // Capture isVehicleSummary for use in transformResponse closure
          const vehicleSummaryFlag = isVehicleSummary;
          const response = await apiService.get(endpoint, undefined, {
            includeAuthToken: true,
            authHeaderType: "Bearer",
            errorHandling: "throw",
            transformResponse: (raw: any) => {
              if (!raw) return null;

              let finalData = raw;

              // Check if raw itself is an encrypted string (Base64-like and doesn't look like JSON)
              if (typeof raw === 'string' && raw.length > 20 && !raw.trim().startsWith('{')) {
                try {
                  const decrypted = authService.decryptData(raw);
                  if (decrypted) {
                    finalData = decrypted;

                    // Log decrypted data for vehicle summary edit
                    if (vehicleSummaryFlag) {
                      console.log("=== VEHICLE SUMMARY EDIT - DECRYPTED DATA (raw string) ===");
                      console.log("Raw Encrypted String Length:", raw.length);
                      console.log("Raw Encrypted String Preview:", raw.substring(0, 100) + "...");
                      console.log("Decrypted Data:", JSON.stringify(decrypted, null, 2));
                      console.log("========================================================");
                    }
                  }
                } catch (e) {
                  if (isVehicleSummary) {
                    console.error("[Vehicle Summary] Failed to decrypt raw string:", e);
                  }
                }
              }
              // Check if raw.data is the encrypted string
              else if (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'string' && raw.data.length > 20 && !raw.data.trim().startsWith('{')) {
                try {
                  const decrypted = authService.decryptData(raw.data);
                  if (decrypted) {
                    finalData = decrypted;

                    // Log decrypted data for vehicle summary edit
                    if (vehicleSummaryFlag) {
                      console.log("=== VEHICLE SUMMARY EDIT - DECRYPTED DATA (raw.data string) ===");
                      console.log("Raw Object:", JSON.stringify(raw, null, 2));
                      console.log("Encrypted String Length:", raw.data.length);
                      console.log("Encrypted String Preview:", raw.data.substring(0, 100) + "...");
                      console.log("Decrypted Data:", JSON.stringify(decrypted, null, 2));
                      console.log("=============================================================");
                    }
                  }
                } catch (e) {
                  if (isVehicleSummary) {
                    console.error("[Vehicle Summary] Failed to decrypt raw.data string:", e);
                  }
                }
              }
              // Otherwise use raw.data if it exists
              else if (raw && typeof raw === 'object' && raw.data) {
                finalData = raw.data;

                // Log if data is already decrypted (object format)
                if (isVehicleSummary) {
                  console.log("=== VEHICLE SUMMARY EDIT - RESPONSE DATA (already decrypted) ===");
                  console.log("Raw Response:", JSON.stringify(raw, null, 2));
                  console.log("Final Data (raw.data):", JSON.stringify(finalData, null, 2));
                  console.log("==============================================================");
                }
              }

              // After decryption/extraction, check for nested data property
              // Pattern: { data: { ...record fields... } }
              if (finalData && typeof finalData === 'object' && !Array.isArray(finalData) && finalData.data && typeof finalData.data === 'object' && !Array.isArray(finalData.data)) {
                if (isVehicleSummary) {
                  console.log("=== VEHICLE SUMMARY EDIT - NESTED DATA EXTRACTION ===");
                  console.log("Final Data (before extraction):", JSON.stringify(finalData, null, 2));
                  console.log("Extracted Data (finalData.data):", JSON.stringify(finalData.data, null, 2));
                  console.log("====================================================");
                }
                return finalData.data;
              }

              // If it's an array, return first record
              if (Array.isArray(finalData)) {
                if (isVehicleSummary) {
                  console.log("=== VEHICLE SUMMARY EDIT - ARRAY DATA ===");
                  console.log("Array Length:", finalData.length);
                  console.log("First Record:", JSON.stringify(finalData[0] || finalData, null, 2));
                  console.log("========================================");
                }
                return finalData[0] || finalData;
              }

              if (isVehicleSummary) {
                console.log("=== VEHICLE SUMMARY EDIT - FINAL TRANSFORMED DATA ===");
                console.log("Final Data:", JSON.stringify(finalData, null, 2));
                console.log("====================================================");
              }

              return finalData;
            },
          });

          // Build form data from API response
          let formDataFromRecord = buildFormDataFromRecord(response, {
            fallback: cleanRowData,
            summaryComponent,
            screenId: targetScreenId,
          });
          if (apiModuleName === "bank_reconciliation_voucher") {
            logBrPartyPrefill("3_api_response", response as Record<string, any>);
            logBrPartyPrefill("4_after_buildFormDataFromRecord", formDataFromRecord);
          }
          // Purchase Order vendor prefill fallback (summary -> edit path).
          if (String(targetScreenId) === "130") {
            const supplierId =
              (response as any)?.supplier_id ??
              (response as any)?.sup_id ??
              (formDataFromRecord as any)?.supplier_id ??
              (cleanRowData as any)?.supplier_id ??
              (cleanRowData as any)?.sup_id;
            const supplierName =
              (response as any)?.supplier_id_desc ??
              (response as any)?.supplier_name ??
              (response as any)?.suppliername ??
              (cleanRowData as any)?.supplier_id_desc ??
              (cleanRowData as any)?.supplier_name ??
              (cleanRowData as any)?.suppliername;
            if (supplierId !== undefined && supplierId !== null && String(supplierId).trim() !== "") {
              (formDataFromRecord as any).drp_supplier = supplierId;
              if (supplierName && String(supplierName).trim() !== "") {
                const supplierSelected = {
                  id: supplierId,
                  value: supplierId,
                  label: supplierName,
                  name: supplierName,
                };
                (formDataFromRecord as any).drp_supplier_selectedData = supplierSelected;
                dispatch(
                  addLookupItem({
                    lookupKey: "supplier",
                    item: supplierSelected,
                  })
                );
              }
            }
          }
          // Ensure SearchableDropdown edit prefill has both ID and selectedData label.
          // Without selectedData, dropdown can appear blank even when ID exists (e.g. drp_supplier).
          if (apiModuleName === "bank_reconciliation_voucher") {
            const jlOptions = getState().lookups.lookups.journal_ledgers;
            if (!jlOptions || jlOptions.length === 0) {
              try {
                await dispatch(fetchLookup({ lookupName: "journal_ledgers" })).unwrap();
              } catch {
                // Name-based fallback is best-effort; API/query overrides may still supply the id.
              }
            }
            const bankOptions = getState().lookups.lookups.bank_ledgers;
            if (!bankOptions || bankOptions.length === 0) {
              try {
                await dispatch(fetchLookup({ lookupName: "bank_ledgers" })).unwrap();
              } catch {
                // Banks field remains editable once lookup loads.
              }
            }
          }
          const searchableScreenConfig = getResolvedScreenConfig(targetScreenId);
          if (searchableScreenConfig) {
            walkComponentTree(searchableScreenConfig, (component) => {
              if (component.componentType !== ComponentType.SEARCHABLE_DROPDOWN) return false;

              const compAny = component as any;
              const fieldName = compAny.name || compAny.BindValue;
              const jsonAttr = compAny.Json_Attribute || compAny.json_Attribute;
              const lookupName = compAny.LookupName || compAny.lookupName;
              if (!fieldName || !jsonAttr || !lookupName) return false;

              const brDetails =
                (response as any)?.bank_reconciliation_voucher_details &&
                typeof (response as any).bank_reconciliation_voucher_details === "object"
                  ? (response as any).bank_reconciliation_voucher_details
                  : undefined;

              let descValue =
                (response as any)?.[`${jsonAttr}_desc`] ??
                (response as any)?.[`${fieldName}_desc`] ??
                (response as any)?.[`${jsonAttr}_name`] ??
                (response as any)?.[`${fieldName}_name`] ??
                brDetails?.[`${jsonAttr}_desc`] ??
                brDetails?.[`${fieldName}_desc`] ??
                brDetails?.drp_party_ledger_desc ??
                brDetails?.party_ledger_id_desc ??
                brDetails?.drp_bank_desc ??
                brDetails?.bank_ledger_id_desc;

              // Sales return (124): invoice dropdown label from summary or GET row.
              if (
                !descValue &&
                (jsonAttr === "invoice_id" || fieldName === "drp_sales_ret")
              ) {
                descValue =
                  (response as any)?.invoice_id_desc ??
                  (response as any)?.invoice_no ??
                  (cleanRowData as any)?.invoice_no ??
                  (formDataFromRecord as any)?.invoice_no;
              }

              // Purchase with order (140): PO dropdown label from summary or GET row.
              if (
                !descValue &&
                (jsonAttr === "purchase_order_id" || fieldName === "drp_purchase")
              ) {
                descValue =
                  (response as any)?.purchase_order_id_desc ??
                  (cleanRowData as any)?.purchase_order_no ??
                  (cleanRowData as any)?.txt_pr_ord_no ??
                  (formDataFromRecord as any)?.purchase_order_no;
              }

              // Purchase order vendor-specific fallbacks seen in summary/module responses.
              if (!descValue && jsonAttr === "supplier_id") {
                descValue =
                  (response as any)?.supplier_id_desc ??
                  (response as any)?.supplier_name ??
                  (response as any)?.suppliername ??
                  (response as any)?.vendor_name ??
                  (response as any)?.vendor;
              }

              // Bank reconciliation party dropdown (journal_ledgers id + display name).
              if (
                !descValue &&
                (jsonAttr === "party_ledger_id" || fieldName === "drp_party_ledger")
              ) {
                descValue =
                  (response as any)?.party_name ??
                  (response as any)?.txt_party_name ??
                  brDetails?.party_name ??
                  brDetails?.txt_party_name ??
                  (cleanRowData as any)?.party_name ??
                  (cleanRowData as any)?.txt_party_name;
              }

              if (
                !descValue &&
                (jsonAttr === "bank_ledger_id" || fieldName === "drp_bank")
              ) {
                descValue =
                  (response as any)?.bank_ledger_id_desc ??
                  (response as any)?.drp_bank_desc ??
                  brDetails?.bank_ledger_id_desc ??
                  brDetails?.drp_bank_desc ??
                  (cleanRowData as any)?.bank_name ??
                  (cleanRowData as any)?.txt_bank_name;
              }

              const rawIdValue =
                (response as any)?.[jsonAttr] ??
                (response as any)?.[fieldName] ??
                brDetails?.[jsonAttr] ??
                brDetails?.[fieldName] ??
                brDetails?.drp_party_ledger ??
                brDetails?.drp_bank ??
                (formDataFromRecord as any)?.[jsonAttr] ??
                (formDataFromRecord as any)?.[fieldName] ??
                (cleanRowData as any)?.party_ledger_id ??
                (cleanRowData as any)?.drp_party_ledger ??
                (cleanRowData as any)?.bank_ledger_id ??
                (cleanRowData as any)?.drp_bank ??
                ((jsonAttr === "invoice_id" || fieldName === "drp_sales_ret") &&
                (cleanRowData as any)?.invoice_id != null
                  ? (cleanRowData as any).invoice_id
                  : undefined);

              const isBrPartyField =
                apiModuleName === "bank_reconciliation_voucher" &&
                (fieldName === "drp_party_ledger" || jsonAttr === "party_ledger_id");

              const isBrBankField =
                apiModuleName === "bank_reconciliation_voucher" &&
                (fieldName === "drp_bank" || jsonAttr === "bank_ledger_id");

              const resolvedLookup = isBrPartyField
                ? resolveBrPartyLedgerId(getState(), lookupName, {
                    id: rawIdValue,
                    name: descValue,
                  })
                : isBrBankField
                  ? resolveBrPartyLedgerId(getState(), "bank_ledgers", {
                      id: rawIdValue,
                      name: descValue,
                    })
                  : {
                      id: normalizeBrPartyId(rawIdValue),
                      label: normalizeBrPartyId(descValue),
                      source: "explicit_id",
                    };

              const idValue = resolvedLookup.id;
              if (!descValue && resolvedLookup.label) {
                descValue = resolvedLookup.label;
              }

              if (!idValue) {
                if (isBrPartyField || isBrBankField) {
                  logBrPartyPrefill("5_searchable_skip_no_id", cleanRowData, {
                    fieldName,
                    jsonAttr,
                    resolveSource: resolvedLookup.source,
                    responseParty: {
                      party_ledger_id: (response as any)?.party_ledger_id,
                      drp_party_ledger: (response as any)?.drp_party_ledger,
                      party_name: (response as any)?.party_name,
                    },
                    brDetails,
                  });
                }
                return false;
              }

              (formDataFromRecord as any)[fieldName] = idValue;
              if (jsonAttr && jsonAttr !== fieldName) {
                (formDataFromRecord as any)[jsonAttr] = idValue;
              }
              if (descValue) {
                const selectedDataKey = `${fieldName}_selectedData`;
                const lookupItem = {
                  id: idValue,
                  value: idValue,
                  label: descValue,
                  name: descValue,
                };
                (formDataFromRecord as any)[selectedDataKey] = lookupItem;
                dispatch(
                  addLookupItem({
                    lookupKey: lookupName,
                    item: lookupItem,
                  })
                );
              }
              if (
                apiModuleName === "bank_reconciliation_voucher" &&
                (isBrPartyField || isBrBankField)
              ) {
                logBrPartyPrefill("5_searchable_applied", formDataFromRecord, {
                  fieldName,
                  idValue,
                  descValue: descValue || "(none)",
                  lookupName,
                  resolveSource: resolvedLookup.source,
                  seededLookup: Boolean(descValue),
                });
              }
              return false;
            });
          }
          if (import.meta.env.DEV && isEmpProfile) {
            console.log("[EmpProfile Edit] API response sample:", {
              first_name: (response as any)?.first_name,
              last_name: (response as any)?.last_name,
              date_of_birth: (response as any)?.date_of_birth,
            });
            console.log("[EmpProfile Edit] Mapped formData sample:", {
              primary_first_name: formDataFromRecord["primary_first_name"],
              primary_last_name: formDataFromRecord["primary_last_name"],
              primary_date_of_birth: formDataFromRecord["primary_date_of_birth"],
            });
          }

          // Purchase with order (140): vendor name + PO dropdown use BindParent from drp_purchase.
          // Linked POs are excluded from purchare_order lookup, so hydrate selectedData on view/edit.
          if (apiModuleName === "pur_add" || String(targetScreenId) === "140") {
            let purchaseOrderLookupItem = applyPurchaseWithOrderViewHydration(
              formDataFromRecord as Record<string, unknown>,
              {
                response: response as Record<string, unknown>,
                fallback: cleanRowData,
              }
            );
            const purchaseOrderId =
              (formDataFromRecord as any)?.drp_purchase ??
              (formDataFromRecord as any)?.purchase_order_id;
            if (
              purchaseOrderId &&
              !String((formDataFromRecord as any)?.sup_name ?? "").trim()
            ) {
              try {
                const rawRows = await apiService.getRawLookupData(
                  "purchase_data",
                  buildLookupPayload({ parentid: "", lookuptype: "Select" })
                );
                const matchedRow = rawRows.find(
                  (row: any) =>
                    String(row?.id ?? "") === String(purchaseOrderId) ||
                    String(row?.value ?? "") === String(purchaseOrderId)
                );
                if (matchedRow) {
                  purchaseOrderLookupItem = mergePurchaseOrderLookupIntoFormData(
                    formDataFromRecord as Record<string, unknown>,
                    matchedRow as Record<string, unknown>,
                    String(purchaseOrderId)
                  );
                }
              } catch {
                // Summary row / module response may still supply supplier name.
              }
            }
            if (purchaseOrderLookupItem) {
              dispatch(
                addLookupItem({
                  lookupKey: "purchare_order",
                  item: purchaseOrderLookupItem as any,
                })
              );
            }
          }

          // Purchase return (126): purchase parent + draft before submodule fetch.
          if (
            String(targetScreenId) === "126" ||
            String(targetScreenId) === "18" ||
            apiModuleName === "pur_ret_add"
          ) {
            const purchaseId =
              (response as any)?.purchase_id ??
              (formDataFromRecord as any)?.purchase_id ??
              (formDataFromRecord as any)?.drp_purchase_ret ??
              (cleanRowData as any)?.purchase_id;
            const purchaseLabel =
              (response as any)?.purchase_id_desc ??
              (response as any)?.purchase_no ??
              (cleanRowData as any)?.purchase_no ??
              (cleanRowData as any)?.purchase_id_desc;
            if (
              purchaseId !== undefined &&
              purchaseId !== null &&
              String(purchaseId).trim() !== ""
            ) {
              const idStr = String(purchaseId);
              (formDataFromRecord as any).drp_purchase_ret = idStr;
              (formDataFromRecord as any).purchase_id = idStr;
              if (purchaseLabel && String(purchaseLabel).trim() !== "") {
                const label = String(purchaseLabel);
                const lookupItem = {
                  id: idStr,
                  value: idStr,
                  label,
                  name: label,
                };
                (formDataFromRecord as any).drp_purchase_ret_selectedData = lookupItem;
                dispatch(
                  addLookupItem({
                    lookupKey: "purchase_datas",
                    item: lookupItem,
                  })
                );
              }
            }
            const vendorName =
              (response as any)?.txt_vendor_name ??
              (response as any)?.suppliername ??
              (response as any)?.vendor_name ??
              (cleanRowData as any)?.suppliername ??
              (cleanRowData as any)?.txt_vendor_name;
            if (vendorName !== undefined && vendorName !== null && String(vendorName).trim() !== "") {
              (formDataFromRecord as any).txt_vendor_name = String(vendorName);
            }
            const draftRawPur =
              (response as any)?.is_draft ??
              (response as any)?.chk_add_to_unadjusted ??
              (formDataFromRecord as any)?.is_draft ??
              (formDataFromRecord as any)?.chk_add_to_unadjusted ??
              (cleanRowData as any)?.is_draft;
            if (draftRawPur !== undefined && draftRawPur !== null) {
              const isDraftPur =
                draftRawPur === true ||
                draftRawPur === 1 ||
                draftRawPur === "1" ||
                String(draftRawPur).toLowerCase() === "true";
              (formDataFromRecord as any).is_draft = isDraftPur ? 1 : 0;
              (formDataFromRecord as any).chk_add_to_unadjusted = isDraftPur ? 1 : 0;
            }
            const fromStockRawPur =
              (response as any)?.from_stock ??
              (response as any)?.chk_from_stock ??
              (formDataFromRecord as any)?.from_stock ??
              (formDataFromRecord as any)?.chk_from_stock ??
              (cleanRowData as any)?.from_stock;
            if (fromStockRawPur !== undefined && fromStockRawPur !== null) {
              const isFromStockPur =
                fromStockRawPur === true ||
                fromStockRawPur === 1 ||
                fromStockRawPur === "1" ||
                String(fromStockRawPur).toLowerCase() === "true";
              (formDataFromRecord as any).from_stock = isFromStockPur ? 1 : 0;
              (formDataFromRecord as any).chk_from_stock = isFromStockPur ? 1 : 0;
            }
          }

          // Sales return (124): header fields before submodule fetch so invoice parent is stable.
          if (String(targetScreenId) === "124" || apiModuleName === "sales_ret_add") {
            const invoiceId =
              (response as any)?.invoice_id ??
              (formDataFromRecord as any)?.invoice_id ??
              (formDataFromRecord as any)?.drp_sales_ret ??
              (cleanRowData as any)?.invoice_id;
            const invoiceLabel =
              (response as any)?.invoice_id_desc ??
              (response as any)?.invoice_no ??
              (cleanRowData as any)?.invoice_no;
            if (
              invoiceId !== undefined &&
              invoiceId !== null &&
              String(invoiceId).trim() !== ""
            ) {
              const idStr = String(invoiceId);
              (formDataFromRecord as any).drp_sales_ret = idStr;
              (formDataFromRecord as any).invoice_id = idStr;
              if (invoiceLabel && String(invoiceLabel).trim() !== "") {
                const label = String(invoiceLabel);
                const lookupItem = {
                  id: idStr,
                  value: idStr,
                  label,
                  name: label,
                };
                (formDataFromRecord as any).drp_sales_ret_selectedData = lookupItem;
                dispatch(
                  addLookupItem({
                    lookupKey: "sales_invoice",
                    item: lookupItem,
                  })
                );
              }
            }
            const draftRaw =
              (response as any)?.is_draft ??
              (response as any)?.chk_add_to_unadjusted ??
              (formDataFromRecord as any)?.is_draft ??
              (formDataFromRecord as any)?.chk_add_to_unadjusted ??
              (cleanRowData as any)?.is_draft;
            if (draftRaw !== undefined && draftRaw !== null) {
              const isDraft =
                draftRaw === true ||
                draftRaw === 1 ||
                draftRaw === "1" ||
                String(draftRaw).toLowerCase() === "true";
              (formDataFromRecord as any).is_draft = isDraft ? 1 : 0;
              (formDataFromRecord as any).chk_add_to_unadjusted = isDraft ? 1 : 0;
            }
          }

          // FETCH SUBMODULE DATA AUTOMATICALLY
          const ensureScreenConfigCached = async (
            screenId: string
          ): Promise<ComponentConfig[] | undefined> => {
            const sid = String(screenId);
            const cached = getResolvedScreenConfig(sid);
            if (cached) return cached;
            try {
              const response = await apiService.getScreenConfig(sid);
              if (response?.success && response?.data?.settings) {
                const apiSettings = response.data.settings as any;
                const apiConfig = Array.isArray(apiSettings)
                  ? apiSettings
                  : [apiSettings];
                configCache[sid] = { config: apiConfig, source: "API" };
                return apiConfig;
              }
            } catch {
              // Fall back to static registry below.
            }
            return screenConfigRegistry[sid];
          };

          const screenConfig = await ensureScreenConfigCached(targetScreenId);
          const subModulesToFetch = screenConfig
            ? findSubModulesInScreenConfig(screenConfig).sort((a, b) => {
                const aLookupOnly = isLookupOnlySubModule(a);
                const bLookupOnly = isLookupOnlySubModule(b);
                if (aLookupOnly === bLookupOnly) return 0;
                return aLookupOnly ? 1 : -1;
              })
            : [];

          if (isPurchaseWithoutOrderContext(targetScreenId, apiModuleName)) {
            logPurchaseWithoutOrderSubmodule("summary_view_submodule_phase", {
              targetScreenId,
              apiModuleName,
              pdcSourceId,
              brInsertOnly,
              hasScreenConfig: Boolean(screenConfig),
              subModulesToFetch: subModulesToFetch.map((sm) => ({
                name: sm?.name || sm?.Name,
                tableName: sm?.TableName || sm?.tableName,
                bindValue: sm?.BindValue,
                lookupOnly: isLookupOnlySubModule(sm),
              })),
            });
          }

          if (!brInsertOnly) {
            if (subModulesToFetch.length > 0) {
              // Fetch all submodule data in parallel
              const subModuleDataPromises = subModulesToFetch.map(async (sm) => {
                const subModuleName = sm.name || sm.Name || sm.BindValue;
                const subModuleBindValue = sm.BindValue || sm.name || sm.Name;
                if (!subModuleName) {
                  return { name: null, bindValue: null, data: [], subModule: sm };
                }

                try {
                  const data = await fetchSubModuleRowsForEdit(
                    (name, id) => apiService.getSubModuleData(name, id),
                    sm,
                    pdcSourceId,
                    apiModuleName
                  );
                  return {
                    name: subModuleName,
                    bindValue: subModuleBindValue || null,
                    data,
                    subModule: sm,
                  };
                } catch (e) {

                  return {
                    name: subModuleName,
                    bindValue: subModuleBindValue || null,
                    data: [],
                    subModule: sm,
                  };
                }
              });

              const subModuleResults = await Promise.all(subModuleDataPromises);

              // Merge submodule data into formData — do not replace prefilled `subform` rows
              // (e.g. bank_reconciliation_voucher from PDC) with an empty `/submodule/...` response.
              subModuleResults.forEach((res) => {
                if (!res.name) return;
                const rawIncoming = Array.isArray(res.data) ? res.data : [];
                let incoming = mapSubModuleApiRowsToFormRows(
                  res.subModule || {},
                  rawIncoming
                );
                if (
                  res.name === "purchase_return_details" ||
                  res.bindValue === "purchase_return_details"
                ) {
                  incoming = incoming.filter((row) => {
                    const qty = Number(row.qty ?? row.txt_qty);
                    return !Number.isNaN(qty) && qty > 0;
                  });
                }
                const bindVal = res.bindValue || res.name;
                const nameKey = res.name;
                const existing =
                  (formDataFromRecord as any)[bindVal] ?? (formDataFromRecord as any)[nameKey];
                const existingHas =
                  Array.isArray(existing) && existing.length > 0;
                if (incoming.length === 0 && existingHas) {
                  return;
                }
                if (
                  incoming.length === 0 &&
                  isPurchaseWithoutOrderContext(targetScreenId, apiModuleName)
                ) {
                  const isPurchaseLineSubmodule =
                    res.name === "purchase_details" ||
                    res.bindValue === "jobcard_details" ||
                    res.bindValue === "purchase_details";
                  if (isPurchaseLineSubmodule) {
                    return;
                  }
                }
                assignSubModuleRowsToFormData(
                  formDataFromRecord as Record<string, any>,
                  res.subModule || { name: nameKey, BindValue: bindVal },
                  incoming,
                  apiModuleName
                );
              });

            }

            if (isPurchaseWithoutOrderContext(targetScreenId, apiModuleName)) {
              await hydratePurchaseWithoutOrderSubmodules(dispatch, getState, {
                screenId: String(targetScreenId),
                recordId: String(pdcSourceId),
                formData: formDataFromRecord as Record<string, unknown>,
                source: "summary_view_try",
              });
            } else if (apiModuleName === "pur_add") {
              applyPurchaseViewSubmoduleHydration(
                formDataFromRecord as Record<string, unknown>
              );
            }
          }

          // CRITICAL: Ensure approve_status is explicitly included if present in response
          // This matches Blazor behavior where approve_status is directly mapped from ItemData
          if (response && (response.approve_status !== undefined || (response.data && response.data.approve_status !== undefined))) {
            const approveStatusValue = response.approve_status || (response.data && response.data.approve_status);
            if (approveStatusValue !== undefined && approveStatusValue !== null && approveStatusValue !== "") {
              formDataFromRecord.approve_status = approveStatusValue;


            }
          }

          if (String(targetScreenId) === "2224") {
            const parentScreenId = deriveScreenId(currentView) || currentView;
            const parentForm =
              getState().forms.formDataByScreen["2220"] ||
              (parentScreenId === "2220"
                ? getState().forms.formDataByScreen[parentScreenId]
                : undefined) ||
              {};
            const calendarYear =
              parentForm?.calendar_year ??
              parentForm?.calendarYear ??
              (formDataFromRecord as any)?.calendar_year;
            const parentCalendarId =
              parentForm?.id ??
              (formDataFromRecord as any)?.parent_id ??
              (formDataFromRecord as any)?.parentid ??
              (cleanRowData as any)?.parent_id ??
              (cleanRowData as any)?.parentid;
            if (calendarYear !== undefined && calendarYear !== null && calendarYear !== "") {
              (formDataFromRecord as any).calendar_year = calendarYear;
            }
            if (parentCalendarId) {
              (formDataFromRecord as any).parent_id = String(parentCalendarId);
              (formDataFromRecord as any).parentid = String(parentCalendarId);
            }
          }

          const finalFormPatch = brInsertOnly
            ? stripBankReconVoucherFormId(formDataFromRecord, pdcSourceId)
            : { ...formDataFromRecord, id: pdcSourceId };
          if (apiModuleName === "bank_reconciliation_voucher") {
            logBrPartyPrefill("6_final_setScreenFormData", finalFormPatch);
          }
          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: finalFormPatch,
            })
          );

          if (isPurchaseWithoutOrderContext(targetScreenId, apiModuleName)) {
            purchaseSubmoduleFieldPatch = {};
            for (const key of PURCHASE_SUBMODULE_FORM_KEYS) {
              const fieldValue = (finalFormPatch as Record<string, unknown>)[key];
              if (fieldValue === undefined) continue;
              if (Array.isArray(fieldValue) && fieldValue.length === 0) continue;
              purchaseSubmoduleFieldPatch[key] = Array.isArray(fieldValue)
                ? (fieldValue as Record<string, unknown>[]).map((row) => ({
                    ...row,
                  }))
                : fieldValue;
            }
            if (Object.keys(purchaseSubmoduleFieldPatch).length === 0) {
              purchaseSubmoduleFieldPatch = null;
            }
          }
        } catch (error) {
          if (isPurchaseWithoutOrderContext(targetScreenId, apiModuleName)) {
            logPurchaseWithoutOrderSubmodule("summary_view_try_error", {
              targetScreenId,
              apiModuleName,
              pdcSourceId,
              error: error instanceof Error ? error.message : String(error),
            });
          }
          if (apiModuleName === "bank_reconciliation_voucher") {
            console.warn("[BR Party Prefill] GET module failed, using summary row only", error);
            logBrPartyPrefill("error_fallback_row", cleanRowData, { recordId: pdcSourceId });
          }
          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: brInsertOnly
                ? stripBankReconVoucherFormId(
                    buildFormDataFromRecord(null, {
                      fallback: cleanRowData,
                      summaryComponent,
                      screenId: targetScreenId,
                    }),
                    pdcSourceId
                  )
                : {
                    ...buildFormDataFromRecord(null, {
                      fallback: cleanRowData,
                      summaryComponent,
                      screenId: targetScreenId,
                    }),
                    id: pdcSourceId,
                  },
            })
          );
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to load full details. Showing available data.",
              type: "error",
            },
            5000
          );
        } finally {
          if (wantsPopup) {
            const popupTitle =
              (summaryAction as any)?.Label ||
              (summaryAction as any)?.tooltip ||
              `Screen ${targetScreenId}`;
            dispatch(openScreenPopup({ screenId: targetScreenId, title: popupTitle }));
          }
          beginExpressionCalculatingIfNeeded(dispatch, targetScreenId);
          dispatch(
            setScreenLoading({ screenId: targetScreenId, isLoading: false })
          );
          if (purchaseSubmoduleFieldPatch) {
            logPurchaseWithoutOrderSubmodule("summary_view_finally_dispatch_patch", {
              targetScreenId,
              keys: Object.keys(purchaseSubmoduleFieldPatch),
              jobcardLineCount: Array.isArray(purchaseSubmoduleFieldPatch.jobcard_details)
                ? purchaseSubmoduleFieldPatch.jobcard_details.length
                : 0,
            });
            for (const [key, value] of Object.entries(purchaseSubmoduleFieldPatch)) {
              if (value === undefined) continue;
              dispatch(
                updateScreenFormField({
                  screenId: targetScreenId,
                  name: key,
                  value,
                })
              );
            }
          } else if (
            isPurchaseWithoutOrderContext(targetScreenId, apiModuleName) &&
            pdcSourceId &&
            !brInsertOnly
          ) {
            const screenId = String(targetScreenId);
            const recordId = String(pdcSourceId);
            logPurchaseWithoutOrderSubmodule("summary_view_finally_fallback_hydrate", {
              screenId,
              recordId,
              reason: "no_purchaseSubmoduleFieldPatch",
            });
            void hydratePurchaseWithoutOrderSubmodules(dispatch, getState, {
              screenId,
              recordId,
              source: "summary_view_finally",
            }).catch((purchaseSubmoduleError) => {
              logPurchaseWithoutOrderSubmodule("summary_view_finally_error", {
                screenId,
                recordId,
                error:
                  purchaseSubmoduleError instanceof Error
                    ? purchaseSubmoduleError.message
                    : String(purchaseSubmoduleError),
              });
            });
          }
        }

        return;
      }

      const isManpowerAllocationAction =
        summaryAction &&
        (actionId === "manpower_allocation" ||
          actionId === ActionType.MANPOWER_ALLOCATION ||
          summaryAction.ActionType?.toString().toUpperCase() ===
            "MANPOWER_ALLOCATION" ||
          summaryAction.Action?.Action === "manpower_allocation");

      if (isManpowerAllocationAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;
        const jobcardId =
          cleanRowData.jobcard_id ||
          cleanRowData.jobcardId ||
          cleanRowData.service_job_card_id ||
          cleanRowData.id ||
          cleanRowData.Id ||
          "";
        const jobcardNo =
          cleanRowData.jobcard_no ||
          cleanRowData.jobcardNo ||
          cleanRowData.JobcardNo ||
          "";
        if (!jobcardId && !jobcardNo) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Job card id not found on this row", type: "error" },
            4000
          );
          return;
        }
        dispatch(
          openJobcardManpowerModal({
            jobcardId: String(jobcardId || ""),
            jobcardNo: jobcardNo ? String(jobcardNo) : undefined,
            vehicleMake:
              cleanRowData.vehicle_make ||
              cleanRowData.vehicleMake,
            model: cleanRowData.model || cleanRowData.Model,
            regNo:
              cleanRowData.reg_no ||
              cleanRowData.Reg_no ||
              cleanRowData.regno,
            customerName:
              cleanRowData.customer_name ||
              cleanRowData.customerName,
            rowData: cleanRowData,
          })
        );
        return;
      }

      const isBillReleaseLinkedAction =
        summaryAction &&
        (actionId === "bill_release_linked" ||
          summaryAction.ActionType?.toString().toLowerCase() ===
            "bill_release_linked" ||
          summaryAction.Action?.Action === "bill_release_linked");

      if (isBillReleaseLinkedAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;
        const partyId = String(
          cleanRowData.party_id ?? cleanRowData.customer_id ?? cleanRowData.vendor_id ?? ""
        ).trim();
        const documentNo = String(
          cleanRowData.document_no ?? cleanRowData.invoice_no ?? cleanRowData.purchase_no ?? ""
        ).trim();
        const documentType = String(cleanRowData.document_type ?? "sales_invoice").trim();
        const partySide =
          String(cleanRowData.party_side ?? "").toLowerCase() === "vendor" ||
          cleanRowData.vendor_id
            ? "vendor"
            : "customer";
        if (!partyId || !documentNo) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Document or party not found on this row.", type: "error" },
            4000
          );
          return;
        }
        dispatch(
          openBillReleaseLinkedModal({
            partySide: partySide as "customer" | "vendor",
            partyId,
            documentNo,
            documentType,
            partyName: String(
              cleanRowData.party_name ??
                cleanRowData.name ??
                cleanRowData.suppliername ??
                ""
            ),
            rowData: cleanRowData,
          })
        );
        return;
      }

      const isBillReleaseNavigateAction =
        summaryAction &&
        (actionId === "bill_release_release" ||
          summaryAction.ActionType?.toString().toLowerCase() ===
            "bill_release_release" ||
          summaryAction.Action?.Action === "bill_release_release");

      if (isBillReleaseNavigateAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;
        const status = String(cleanRowData.status ?? "").trim();
        if (status === "Released" || status === "Cancelled") {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "This document has already been fully released or cancelled.",
              type: "error",
            },
            4000
          );
          return;
        }
        const partySide =
          String(cleanRowData.party_side ?? "").toLowerCase() === "vendor" ||
          cleanRowData.vendor_id
            ? "vendor"
            : "customer";
        const targetScreenId = partySide === "vendor" ? "195" : "190";
        const targetView =
          partySide === "vendor"
            ? "modulev1/vendor_bill_wise_release"
            : "modulev1/bill_wise_release";
        const partyId = String(
          cleanRowData.party_id ?? cleanRowData.customer_id ?? cleanRowData.vendor_id ?? ""
        ).trim();
        const documentNo = String(
          cleanRowData.document_no ?? cleanRowData.invoice_no ?? cleanRowData.purchase_no ?? ""
        ).trim();
        const documentType = String(
          cleanRowData.document_type ??
            (partySide === "vendor" ? "purchase_bill" : "sales_invoice")
        ).trim();
        const formPatch: Record<string, any> = {
          document_no: documentNo,
          document_type: documentType,
          party_id: partyId,
        };
        if (partySide === "vendor") {
          formPatch.vendor_id = partyId;
          formPatch.drp_vendor = partyId;
        } else {
          formPatch.customer_id = partyId;
          formPatch.drp_customer = partyId;
        }
        dispatch(resetScreenFormData(targetScreenId));
        dispatch(navigateToView({ view: targetView }));
        dispatch(setScreenFormData({ screenId: targetScreenId, data: formPatch }));
        return;
      }

      const isVoidAction =
        summaryAction &&
        (actionId === ActionType.VOID ||
          actionId === "Void" ||
          actionId === "void" ||
          summaryAction.ActionType === ActionType.VOID ||
          summaryAction.ActionType?.toString().toLowerCase() === "void");

      if (isVoidAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;
        const recordId = deriveRecordIdFromRow(cleanRowData, { summaryAction });
        if (!recordId) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Missing record id.", type: "error" },
            4000
          );
          return;
        }
        const isDraft =
          Number(cleanRowData.is_draft) === 1 ||
          cleanRowData.is_draft === true ||
          String(cleanRowData.is_draft).toLowerCase() === "true";
        if (isDraft) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Draft transactions cannot be voided. Use delete instead.", type: "error" },
            4000
          );
          return;
        }
        const confirmMessage =
          summaryAction.confirmMessage ||
          VOID_CONFIRM_MESSAGE;
        const dialogResult = await showConfirmDialogWithReason(
          dispatch,
          confirmMessage,
          "Void transaction",
          {
            requireReason: summaryAction.requireVoidReason !== false,
            reasonLabel: summaryAction.voidReasonLabel || "Void Reason",
          }
        );
        if (!dialogResult.confirmed) return;
        if (summaryAction.requireVoidReason !== false && !dialogResult.reason) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Void reason is required.", type: "error" },
            4000
          );
          return;
        }
        const moduleName =
          summaryAction.Action?.Module || summaryAction.Action?.module;
        if (!moduleName) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Void module is not configured for this screen.", type: "error" },
            4000
          );
          return;
        }
        const txnNoField =
          summaryAction.Action?.TransactionNoField ||
          summaryAction.Action?.transactionNoField;
        const transactionNo = txnNoField
          ? String(cleanRowData[txnNoField] ?? "")
          : undefined;
        try {
          const response = await apiService.post<any>(
            "/pabauto/transaction_void",
            {
              data: {
                module: moduleName,
                transaction_id: String(recordId),
                void_reason: dialogResult.reason,
                transaction_no: transactionNo,
              },
            },
            { errorHandling: "throw" }
          );
          if (response?.success === false) {
            throw new Error(response?.message || "Void failed.");
          }
          ensureSlidingTimeout(
            dispatch,
            {
              message: response?.message || "Transaction voided successfully.",
              type: "success",
            },
            4000
          );
        } catch (error) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: error instanceof Error ? error.message : "Void failed.",
              type: "error",
            },
            4000
          );
        }
        return;
      }

      const isBillReleaseCnDnAction =
        summaryAction &&
        (actionId === "bill_release_unadjusted" ||
          actionId === "bill_release_cancel" ||
          summaryAction.ActionType?.toString().toLowerCase() ===
            "bill_release_unadjusted" ||
          summaryAction.ActionType?.toString().toLowerCase() ===
            "bill_release_cancel");

      if (isBillReleaseCnDnAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;
        const documentType = String(cleanRowData.document_type ?? "").toLowerCase();
        if (documentType !== "credit_note" && documentType !== "debit_note") {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unadjusted/Cancel actions apply to credit/debit notes only.",
              type: "error",
            },
            4000
          );
          return;
        }
        const releaseAction =
          actionId === "bill_release_cancel" ||
          summaryAction.ActionType?.toString().toLowerCase() === "bill_release_cancel"
            ? "cancel"
            : "unadjusted";
        const confirmMessage =
          releaseAction === "cancel"
            ? `Cancel ${documentType} ${cleanRowData.document_no}? This will void the voucher and release allocations.`
            : `Remove matching for ${documentType} ${cleanRowData.document_no} without accounting impact?`;
        const confirmed = await showConfirmDialog(
          dispatch,
          confirmMessage,
          releaseAction === "cancel" ? "Cancel credit/debit note" : "Unadjusted release"
        );
        if (!confirmed) return;

        const partySide =
          String(cleanRowData.party_side ?? "").toLowerCase() === "vendor" ||
          cleanRowData.vendor_id
            ? "vendor"
            : "customer";
        try {
          const response = await apiService.post<any>(
            "/pabauto/credit_debit_note_release",
            {
              data: {
                release_action: releaseAction,
                party_side: partySide,
                party_id: cleanRowData.party_id ?? cleanRowData.customer_id ?? cleanRowData.vendor_id,
                document_no: cleanRowData.document_no,
                document_type: documentType,
              },
            },
            { errorHandling: "throw" }
          );
          if (response?.success === false) {
            throw new Error(response?.message || "Release failed.");
          }
          ensureSlidingTimeout(
            dispatch,
            {
              message: response?.message || "Release completed successfully.",
              type: "success",
            },
            4000
          );
        } catch (error) {
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error ? error.message : "Credit/debit note release failed.",
              type: "error",
            },
            5000
          );
        }
        return;
      }

      // Handle PDF_DOWNLOAD action for SUMMARY_LIST
      // Check if this is a PDF download action (either by actionId or summaryAction)
      const isPdfAction =
        (actionId === "PDF" || actionId === ActionType.PDF_DOWNLOAD) &&
        summaryAction?.ActionType &&
        (summaryAction.ActionType.toString().toUpperCase() === "PDF_DOWNLOAD" ||
          summaryAction.ActionType === ActionType.PDF_DOWNLOAD) &&
        summaryAction.Action?.Action === "PDF" &&
        summaryAction.Action?.Settings;

      if (isPdfAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        const settings = summaryAction.Action.Settings;
        const pdfName = settings;

        // Get user information from encrypted data
        const encryptedUserData =
          typeof window !== "undefined"
            ? localStorage.getItem("encryptedUserData")
            : null;
        if (!encryptedUserData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "User not authenticated. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        const decryptedData = authService.decryptData(encryptedUserData);
        if (!decryptedData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Extract username and user ID from decrypted data
        const username =
          decryptedData.empName ||
          decryptedData.userName ||
          decryptedData.loginName ||
          "";
        const logined_userid =
          decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

        // Build payload dynamically using reusable configuration system.
        const basePayload = buildPDFPayload(pdfName, cleanRowData, logined_userid);

        // Keep legacy voucher fields as safe defaults for voucher-based PDFs.
        const payload = {
          ...basePayload,
          voucher_no:
            (basePayload as any).voucher_no ||
            cleanRowData.voucher_no ||
            cleanRowData.voucherNo ||
            cleanRowData.VoucherNo ||
            "",
          voucher_date:
            (basePayload as any).voucher_date ||
            cleanRowData.voucher_date ||
            cleanRowData.voucherDate ||
            cleanRowData.VoucherDate ||
            "",
          total_debit:
            (basePayload as any).total_debit ??
            cleanRowData.total_debit ??
            cleanRowData.totalDebit ??
            cleanRowData.TotalDebit ??
            0,
          total_credit:
            (basePayload as any).total_credit ??
            cleanRowData.total_credit ??
            cleanRowData.totalCredit ??
            cleanRowData.TotalCredit ??
            0,
          username: username,
          logined_userid: logined_userid,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        try {
          // Make POST request to PDF endpoint
          const endpoint = `/pabreport/pdf`;
          const params = {
            name: pdfName,
          };

          if (import.meta.env.DEV) {
            console.log("[PDF Download] Request:", {
              endpoint,
              params,
              payload,
            });
          }

          // Use apiService.request to make POST request with query params and payload
          // Note: API returns JSON with base64-encoded PDF in data field, not raw blob
          const response = await apiService.request<{
            success?: boolean;
            data?: string; // Base64-encoded PDF string
            [key: string]: any;
          }>({
            endpoint,
            method: "POST",
            params, // Query parameters in URL
            payload, // Request body
            includeAuthToken: true,
            errorHandling: "throw",
            responseType: "json", // API returns JSON with base64 data
          });

          // Decode response and show delivery chooser
          const pdfBlob = decodePdfResponseToBlob(response);
          const fileName = buildPdfFileName(
            pdfName,
            cleanRowData.voucher_no || cleanRowData.id || "document"
          );
          await showPdfDeliveryDialog(dispatch, pdfBlob, fileName);
        } catch (error) {
          console.error("[PDF Download] Error:", error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to generate PDF: ${error.message}`
                  : "Failed to generate PDF. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Handle VIEW_REPORT action for SUMMARY_LIST
      // Check if this is a view report action (opens PDF in modal viewer)
      const isViewReportAction =
        (actionId === "Report" || actionId === ActionType.VIEW_REPORT) &&
        summaryAction?.ActionType &&
        (summaryAction.ActionType.toString().toUpperCase() === "VIEW_REPORT" ||
          summaryAction.ActionType === ActionType.VIEW_REPORT) &&
        summaryAction.Action?.Action === "Report";

      if (isViewReportAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        // Use Api field if available, otherwise use Settings field (like PDF_DOWNLOAD)
        const reportApi =
          summaryAction.Action.Api || summaryAction.Action.Settings;
        const reportName = reportApi || "report";

        // Get user information from encrypted data
        const encryptedUserData =
          typeof window !== "undefined"
            ? localStorage.getItem("encryptedUserData")
            : null;
        if (!encryptedUserData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "User not authenticated. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        const decryptedData = authService.decryptData(encryptedUserData);
        if (!decryptedData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Extract username and user ID from decrypted data
        const username =
          decryptedData.empName ||
          decryptedData.userName ||
          decryptedData.loginName ||
          "";
        const logined_userid =
          decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

        // Build payload from row data - include all row fields plus user info
        const reportScreenId = deriveScreenId(currentView) || currentView;
        const reportCustomerId = String(
          cleanRowData.customer_id ?? cleanRowData.customerId ?? ""
        ).trim();
        const reportReleaseId = String(
          cleanRowData.id ?? cleanRowData.Id ?? ""
        ).trim();
        const useCustomerIdForReport =
          (reportScreenId === "189" ||
            String(reportScreenId).includes("bill_wise_release_summary")) &&
          reportCustomerId !== "";

        const payload = {
          ...cleanRowData, // Include all fields from the row
          // Ensure required fields are present (with fallbacks for different field name variations)
          id: useCustomerIdForReport ? reportCustomerId : reportReleaseId,
          ...(useCustomerIdForReport && reportReleaseId
            ? { release_id: reportReleaseId }
            : {}),
          voucher_no:
            cleanRowData.voucher_no ||
            cleanRowData.voucherNo ||
            cleanRowData.VoucherNo ||
            "",
          voucher_date:
            cleanRowData.voucher_date ||
            cleanRowData.voucherDate ||
            cleanRowData.VoucherDate ||
            "",
          total_debit:
            cleanRowData.total_debit ||
            cleanRowData.totalDebit ||
            cleanRowData.TotalDebit ||
            0,
          total_credit:
            cleanRowData.total_credit ||
            cleanRowData.totalCredit ||
            cleanRowData.TotalCredit ||
            0,
          username: username,
          logined_userid: logined_userid,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        try {
          // Show loading state
          dispatch(setHTMLDrawerLoading(true));

          // Make POST request to HTML report endpoint
          const endpoint = `/pabreport/report`;
          const params = {
            name: reportName,
          };

          if (import.meta.env.DEV) {
            console.log("[View Report] Request:", {
              endpoint,
              params,
              payload,
            });
          }

          // Use apiService.request to make POST request with query params and payload
          const response = await apiService.request<{
            success?: boolean;
            data?: string; // HTML string
            [key: string]: any;
          }>({
            endpoint,
            method: "POST",
            params, // Query parameters in URL
            payload, // Request body
            includeAuthToken: true,
            errorHandling: "throw",
            responseType: "json", // API returns JSON with HTML data
          });

          // Handle HTML response
          let htmlContent: string | null = null;

          if (response) {
            // Check if response is HTML directly (not wrapped in JSON)
            // Note: Cast to any since API can return either JSON object or raw HTML string
            if (typeof response === "string" && (response as any).includes("<html")) {
              htmlContent = response as string;
            }
            // Check if response is the expected format: { success: true, data: "<html>..." }
            else if (typeof response === "object" && response.success && response.data && typeof response.data === "string") {
              htmlContent = response.data;
            }
            // Check if data field contains HTML string
            else if (typeof response === "object" && response.data && typeof response.data === "string" && response.data.includes("<html")) {
              htmlContent = response.data;
            }
            // Fallback: try to extract HTML from any string field
            else if (typeof response === "object") {
              const possibleHtml = response.data || response.content || response.html || response.body;
              if (typeof possibleHtml === "string" && possibleHtml.length > 0) {
                htmlContent = possibleHtml;
              }
            }
          }

          if (htmlContent) {
            // Open HTML drawer with the content
            dispatch(
              openHTMLDrawer({
                htmlContent: htmlContent,
                reportName: reportName,
              })
            );

            if (import.meta.env.DEV) {
              console.log("[View Report] HTML drawer opened successfully");
            }
          } else {
            throw new Error("No HTML content found in response");
          }
        } catch (error) {
          console.error("[View Report] Error:", error);
          dispatch(
            setHTMLDrawerError(
              error instanceof Error
                ? `Failed to load HTML: ${error.message}`
                : "Failed to load HTML. Please try again."
            )
          );
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to view report: ${error.message}`
                  : "Failed to view report. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Handle PDF action from BUTTON components
      // Pattern 1: onPressAction: "pdf_{PDFName}" (e.g., "pdf_Estimation")
      // Pattern 2: onPressAction: "pdf" with Settings field (e.g., Settings: "Invoice_info_basic")
      const isPdfButtonAction = actionId && (
        actionId.startsWith("pdf_") ||
        (actionId === "pdf" && (data?.buttonConfig?.Settings || data?.buttonConfig?.settings))
      );

      if (isPdfButtonAction) {
        let pdfName: string;

        if (actionId.startsWith("pdf_")) {
          // Pattern 1: Extract from actionId (e.g., "Estimation" from "pdf_Estimation")
          pdfName = actionId.replace(/^pdf_/, "");
          if (import.meta.env.DEV) {
            console.log("[PDF Button] Pattern 1 - Extracted PDF name from actionId:", pdfName);
          }
        } else if (actionId === "pdf" && (data?.buttonConfig?.Settings || data?.buttonConfig?.settings)) {
          // Pattern 2: Extract from Settings field (e.g., "Invoice_info_basic" from Settings)
          pdfName = data.buttonConfig.Settings || data.buttonConfig.settings;
          if (import.meta.env.DEV) {
            console.log("[PDF Button] Pattern 2 - Extracted PDF name from Settings:", pdfName);
            console.log("[PDF Button] Button config:", data.buttonConfig);
          }
        } else {
          console.error("[PDF Button] Cannot determine PDF name.", {
            actionId,
            hasButtonConfig: !!data?.buttonConfig,
            settings: data?.buttonConfig?.Settings || data?.buttonConfig?.settings,
            dataKeys: data ? Object.keys(data) : [],
          });
          ensureSlidingTimeout(
            dispatch,
            {
              message: "PDF configuration error: Missing PDF name. Please check button configuration.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Get current form data from state (for button actions, we use form data, not row data)
        const screenId = deriveScreenId(currentView) || currentView;
        const formData = state.forms.formDataByScreen[screenId] || data || {};

        // Get user information from encrypted data
        const encryptedUserData =
          typeof window !== "undefined"
            ? localStorage.getItem("encryptedUserData")
            : null;
        if (!encryptedUserData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "User not authenticated. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        const decryptedData = authService.decryptData(encryptedUserData);
        if (!decryptedData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Extract username and user ID from decrypted data
        const username =
          decryptedData.empName ||
          decryptedData.userName ||
          decryptedData.loginName ||
          "";
        const logined_userid =
          decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

        // Build PDF payload dynamically using reusable configuration system
        // This allows PDF-specific payload transformations to be configured per PDF type
        // The buildPDFPayload function handles default behavior if no config exists
        const payload = {
          ...buildPDFPayload(pdfName, formData, logined_userid),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        try {
          // Make POST request to PDF endpoint
          const endpoint = `/pabreport/pdf`;
          const params = {
            name: pdfName,
          };

          if (import.meta.env.DEV) {
            console.log("[PDF Button] Request:", {
              endpoint,
              params,
              pdfName,
              payload: { ...payload, formDataKeys: Object.keys(formData) },
            });
          }

          // Use apiService.request to make POST request with query params and payload
          const response = await apiService.request<{
            success?: boolean;
            data?: string; // Base64-encoded PDF string
            [key: string]: any;
          }>({
            endpoint,
            method: "POST",
            params, // Query parameters in URL
            payload, // Request body
            includeAuthToken: true,
            errorHandling: "throw",
            responseType: "json", // API returns JSON with base64 data
          });

          // Decode response and show delivery chooser
          const pdfBlob = decodePdfResponseToBlob(response);
          const fileName = buildPdfFileName(
            pdfName,
            formData.invoice_no || formData.id || formData.parentid || "document"
          );
          await showPdfDeliveryDialog(dispatch, pdfBlob, fileName);
        } catch (error) {
          console.error("[PDF Button] Error:", error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to generate PDF: ${error.message}`
                  : "Failed to generate PDF. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Handle DELETE action for SUMMARY_LIST
      // Check if this is a delete action
      const isDeleteAction =
        summaryAction?.ActionType &&
        summaryAction.ActionType.toString().toLowerCase() === "delete" &&
        summaryAction.Action?.Action &&
        summaryAction.Action.Action.toString().toLowerCase() === "delete";

      if (isDeleteAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        // Extract module name from action configuration
        const deriveModuleName = (): string | undefined => {
          const moduleFromAction =
            summaryAction.Action?.Module ||
            summaryAction.Action?.module ||
            summaryAction.Action?.ModuleName ||
            summaryAction.Action?.modulename;

          if (moduleFromAction && typeof moduleFromAction === "string") {
            return moduleFromAction;
          }

          const navigatePath =
            summaryAction.Action?.Navigate || summaryAction.Action?.navigate;

          if (navigatePath && typeof navigatePath === "string") {
            const segments = navigatePath.split("/").filter(Boolean);
            if (segments.length >= 2) {
              const moduleSegment = segments[1];
              if (moduleSegment) {
                return moduleSegment
                  .split("_")
                  .map((segment) =>
                    segment
                      ? segment.charAt(0).toUpperCase() + segment.slice(1)
                      : segment
                  )
                  .join("_");
              }
            }
          }

          return undefined;
        };

        const moduleName = deriveModuleName();
        const recordId = deriveRecordIdFromRow(cleanRowData, {
          summaryAction,
        });

        if (!moduleName || !recordId) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to determine module or record ID for deletion.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Show confirmation dialog
        const TIME_SHIFT_DELETE_MESSAGE =
          "Are you sure you want to delete this shift? This shift may be currently mapped to user groups. Deleting it will remove the assigned schedule for all associated employees.";
        const isTimeShiftModule =
          moduleName === "time_shift_summary" || moduleName === "time_shift";
        const confirmMessage =
          summaryAction.confirmMessage ||
          (summaryAction as { ConfirmMessage?: string }).ConfirmMessage ||
          (isTimeShiftModule
            ? TIME_SHIFT_DELETE_MESSAGE
            : "Are you sure you want to delete this record? This action cannot be undone.");
        const confirmTitle =
          summaryAction.confirmTitle ||
          (summaryAction as { ConfirmTitle?: string }).ConfirmTitle ||
          (isTimeShiftModule ? "Delete shift" : undefined);
        const confirmed = await showConfirmDialog(
          dispatch,
          confirmMessage,
          confirmTitle
        );
        if (!confirmed) {
          return; // User cancelled
        }

        try {
          // Prefer DELETE /pabws/module/{moduleName}/{recordId}.
          // Some deployments/proxies disallow DELETE; in that case, fall back to soft-delete via update.
          const deleteEndpoint = `/pabws/module/${moduleName}/${recordId}`;

          if (import.meta.env.DEV) {
            console.log("[Delete] Request:", {
              endpoint: deleteEndpoint,
              moduleName,
              recordId,
            });
          }

          let didDelete = false;
          try {
            const response = await apiService.delete<{
              success: boolean;
              data?: any;
              [key: string]: any;
            }>(deleteEndpoint, {
              includeAuthToken: true,
              authHeaderType: "Bearer",
              errorHandling: "throw",
            });

            if (import.meta.env.DEV) {
              console.log("[Delete] Response:", response);
            }

            didDelete =
              !!response &&
              (response.success === true || response.success === undefined);
          } catch (deleteErr) {
            if (import.meta.env.DEV) {
              console.warn("[Delete] DELETE failed, trying soft-delete update:", deleteErr);
            }
          }

          if (!didDelete) {
            // Fallback: soft-delete through update endpoint
            const updateEndpoint = `/pabws/module/${moduleName}/update`;
            const response = await apiService.post<{ success: boolean; message?: string }>(
              updateEndpoint,
              { data: { id: recordId, soft_delete: 1 } },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
              }
            );
            didDelete = !!response && (response as any).success === true;
          }

          if (!didDelete) {
            throw new Error("Delete operation returned unsuccessful response");
          }

          ensureSlidingTimeout(
            dispatch,
            {
              message: "Record deleted successfully.",
              type: "success",
            },
            3000
          );

          // Refresh the DataTable by updating refreshTimestamp
          dispatch(
            navigateToView({
              view: currentView,
              rememberPrevious: false,
            })
          );
        } catch (error) {
          console.error("[Delete] Error:", error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to delete record: ${error.message}`
                  : "Failed to delete record. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Support both "navigate_175" and { onPressAction: "Navigate", ScreenId: "175" }
      const isNavigateAction =
        actionId.startsWith("navigate_") ||
        (actionId.toLowerCase() === "navigate" &&
          (data?.buttonConfig?.ScreenId || data?.ScreenId || data?.buttonConfig?.screenId));

      if (isNavigateAction) {
        let view = "";
        if (actionId.startsWith("navigate_")) {
          view = actionId.replace("navigate_", "");
        } else {
          view = data?.buttonConfig?.ScreenId || data?.ScreenId || data?.buttonConfig?.screenId;
        }

        // Extract recordId if present (e.g., "151/6")
        const parts = String(view).split("/").filter(Boolean);
        let targetView = parts[0] || "";
        let recordId: string | undefined;
        if (parts[0] === "modulev1" && parts.length >= 2) {
          targetView = `${parts[0]}/${parts[1]}`;
          recordId = parts.length > 2 ? parts[2] : undefined;
        } else if (parts[0] === "hrm" && parts.length >= 2) {
          targetView = `${parts[0]}/${parts[1]}`;
          recordId = parts.length > 2 ? parts[2] : undefined;
        } else {
          recordId = parts.length > 1 ? parts[1] : undefined;
        }

        if (targetView === "back") {
          dispatch(navigateToView({ view: "101", rememberPrevious: false }));
          dispatch(resetScreenFormData(currentView));
          return;
        }

        const targetScreenId = deriveScreenId(targetView) || targetView;

        // Button-triggered navigation: optionally open target screen in a popup (no navigation)
        // This mirrors SummaryList's `summaryAction.popup` behavior but uses `buttonConfig.popup`.
        const buttonConfig = (data as any)?.buttonConfig as
          | { popup?: boolean; popupTitle?: string; Label?: string; name?: string; toolTip?: string; tooltip?: string }
          | undefined;
        const buttonRequestsPopup = Boolean(buttonConfig?.popup);
        if (buttonRequestsPopup && !recordId) {
          const popupTitle =
            buttonConfig?.popupTitle ||
            buttonConfig?.Label ||
            buttonConfig?.name ||
            buttonConfig?.toolTip ||
            (buttonConfig as any)?.tooltip ||
            `Screen ${targetScreenId}`;

          dispatch(resetScreenFormData(targetScreenId));
          dispatch(openScreenPopup({ screenId: targetScreenId, title: popupTitle }));
          return;
        }

        // SummaryList action: optionally open target screen in a popup (no navigation)
        if (summaryAction?.popup) {
          const popupTitle =
            summaryAction?.Label || summaryAction?.tooltip || `Screen ${targetScreenId}`;

          // Leave approve/reject: prefill from selected summary row (same as full navigation)
          if (
            targetScreenId === LEAVE_APPROVAL_DRAWER_SCREEN_ID &&
            data &&
            typeof data === "object"
          ) {
            const row = { ...(data as Record<string, any>) };
            delete (row as any).__summaryAction;
            const formPatch: Record<string, any> = {
              id: row.id ?? row.ID,
              drp_approval_action: "",
              txt_employee_name: row.employee_name ?? row.employeeName ?? "",
              date_start: row.start_date ?? row.startDate,
              date_end: row.end_date ?? row.endDate,
              txt_total_days: row.total_days ?? row.totalDays,
              txt_reason: row.reason ?? "",
              txt_remarks: "",
            };
            dispatch(resetScreenFormData(targetScreenId));
            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: formPatch,
              })
            );
            dispatch(openScreenPopup({ screenId: targetScreenId, title: popupTitle }));
            return;
          }

          // Generic popup open: reset then seed formData from row (if provided)
          dispatch(resetScreenFormData(targetScreenId));
          if (data && typeof data === "object") {
            const row = { ...(data as Record<string, any>) };
            delete (row as any).__summaryAction;
            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: buildFormDataFromRecord(null, {
                  fallback: row,
                  screenId: targetScreenId,
                }),
              })
            );
          }

          // Note: recordId-based navigation (navigate_123/<id>) still uses full navigation path.
          if (!recordId) {
            dispatch(openScreenPopup({ screenId: targetScreenId, title: popupTitle }));
            return;
          }
        }

        // Leave approve/reject: open as normal screen and prefill from selected summary row.
        if (targetScreenId === LEAVE_APPROVAL_DRAWER_SCREEN_ID && data && typeof data === "object") {
          const row = { ...(data as Record<string, any>) };
          delete (row as any).__summaryAction;
          const formPatch: Record<string, any> = {
            id: row.id ?? row.ID,
            drp_approval_action: "",
            txt_employee_name: row.employee_name ?? row.employeeName ?? "",
            date_start: row.start_date ?? row.startDate,
            date_end: row.end_date ?? row.endDate,
            txt_total_days: row.total_days ?? row.totalDays,
            txt_reason: row.reason ?? "",
            txt_remarks: "",
          };
          dispatch(resetScreenFormData(targetScreenId));
          dispatch(navigateToView({ view: targetView }));
          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: formPatch,
            })
          );
          return;
        }

        // Store estimate_id for Proforma Invoice pre-population (before reset)
        let estimateIdForProforma: string | undefined;
        let proformaSourceData: Record<string, any> = {};
        if (targetScreenId === "175") {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const currentFormData = state.forms.formDataByScreen[currentScreenId] || data?.formData || {};
          proformaSourceData = currentFormData;

          // Try to get estimate_id from various possible fields
          estimateIdForProforma =
            currentFormData.estimate_id ||
            currentFormData.estimation_id ||
            currentFormData.estimateId ||
            data?.estimate_id ||
            data?.estimation_id;
        }

        if (targetView === "100" && currentView === "101") {
          // ... (Special logic for screen 101/100 transition preserved)
          dispatch(setScreenLoading({ screenId: "100", isLoading: true }));

          try {
            const response = await apiService.get(
              "/pabws/screens/get_screens_with_id",
              {
                screenid: "101",
              },
              {
                errorHandling: "throw",
                transformResponse: (data: any) => {
                  if (data) {
                    if (data.data) {
                      return Array.isArray(data.data) ? data.data[0] : data.data;
                    } else if (Array.isArray(data)) {
                      return data[0];
                    }
                    return data;
                  }
                  return null;
                },
              }
            );

            const screenData: any = response;

            dispatch(
              setScreenFormData({
                screenId: "100",
                data: buildFormDataFromRecord(screenData, {
                  screenId: "100",
                }),
              })
            );
          } catch (error) {
            dispatch(resetScreenFormData("100"));
          } finally {
            dispatch(setScreenLoading({ screenId: "100", isLoading: false }));
          }
        } else {
          dispatch(resetScreenFormData(targetView));
        }

        // Navigate to the target view first
        dispatch(navigateToView({ view: targetView }));

        // HRMS Holiday Calendar -> Add Holiday / Add Weekly Off:
        // Carry current holiday_calendar.id into the child add screens as parent_id so inserts
        // link to the correct calendar.
        try {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const currentFormData = state.forms.formDataByScreen[currentScreenId] || data?.formData || {};
          const currentCalendarId = currentFormData?.id;
          const targetScreenId = deriveScreenId(targetView) || targetView;

          if (
            currentCalendarId &&
            (targetScreenId === "2224" || targetScreenId === "2225") &&
            (currentScreenId === "2220" || String(currentView).includes("holiday_calendar_details"))
          ) {
            const calendarYear =
              currentFormData?.calendar_year ??
              currentFormData?.calendarYear ??
              currentFormData?.year;
            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: {
                  parentid: String(currentCalendarId),
                  parent_id: String(currentCalendarId),
                  ...(calendarYear !== undefined && calendarYear !== null && calendarYear !== ""
                    ? { calendar_year: calendarYear }
                    : {}),
                },
              })
            );
          }
        } catch (e) {
          // Non-fatal: navigation should still proceed even if we fail to prefill parent_id
          if (import.meta.env.DEV) {
            console.warn("[Holiday Calendar] Failed to carry parent_id into child screen:", e);
          }
        }

        // Reference settlement add: keep customer vs vendor context from FAB / hub tab
        if (
          REFERENCE_SETTLEMENT_ADD_SCREEN_IDS.has(String(targetScreenId)) &&
          !recordId
        ) {
          try {
            const srcForm = (data?.formData || {}) as Record<string, any>;
            const btn = (buttonConfig || data?.buttonConfig || {}) as Record<string, any>;
            const partyType =
              btn.partyType === "vendor" || btn.partyType === "customer"
                ? btn.partyType
                : String(targetScreenId) === "198"
                  ? "vendor"
                  : String(targetScreenId) === "199"
                    ? "customer"
                    : referenceSettlementPartyType(
                        normalizeBillAllocationTab(srcForm.bill_allocation_tab)
                      );
            const returnTab =
              btn.returnTab === "vendor_ref_settlement" ||
              btn.returnTab === "customer_ref_settlement"
                ? btn.returnTab
                : partyType === "vendor"
                  ? "vendor_ref_settlement"
                  : "customer_ref_settlement";

            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: {
                  reference_settlement_party_type: partyType,
                  reference_settlement_return_tab: returnTab,
                  reference_settlement_add_trigger: Date.now(),
                },
              })
            );
          } catch (e) {
            if (import.meta.env.DEV) {
              console.warn(
                "[Reference settlement] Failed to carry party type into add screen:",
                e
              );
            }
          }
        }

        // Special handling for Proforma Invoice (175) - pre-populate with estimate_id
        // Do this AFTER navigation so the screen is ready to receive the data
        if (targetScreenId === "175" && !recordId) {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const currentFormData = state.forms.formDataByScreen[currentScreenId] || data?.formData || {};

          if (import.meta.env.DEV) {
            console.log("[Proforma Navigation] Current screen:", currentScreenId, "Form data:", currentFormData);
          }

          // Try to get inspection_id from various possible fields
          // The form data already contains inspection_id when QC Approve loads
          let inspectionId =
            currentFormData.inspection_id ||
            currentFormData.inspectionId ||
            currentFormData.id ||
            data?.inspection_id ||
            data?.inspectionId ||
            data?.id;

          if (import.meta.env.DEV) {
            console.log("[Proforma Navigation] inspection_id from form data:", inspectionId);
          }

          // If not found and we're on QC Approve screen (157), try to fetch from parent record
          if (!inspectionId && currentScreenId === "157") {
            const parentId = currentFormData.parentid || currentFormData.parent_id;
            if (import.meta.env.DEV) {
              console.log("[Proforma Navigation] Fetching inspection_id from parent:", parentId);
            }
            if (parentId) {
              // Try to fetch inspection_id from the vehicle summary record
              // The parent_id in QC Approve is the VIN/inspection ID
              try {
                const vehicleSummaryResponse = await apiService.get(
                  `/pabws/module/vehiclein_summary/${parentId}`,
                  {},
                  {
                    errorHandling: "return-null",
                    transformResponse: (raw: any) => {
                      if (raw && raw.data && typeof raw.data === 'string') {
                        const decrypted = authService.decryptData(raw.data);
                        return decrypted ? (Array.isArray(decrypted) ? decrypted[0] : decrypted) : raw;
                      }
                      if (raw && raw.data) {
                        return Array.isArray(raw.data) ? raw.data[0] : raw.data;
                      }
                      return Array.isArray(raw) ? raw[0] : raw;
                    }
                  }
                );
                if (vehicleSummaryResponse) {
                  inspectionId = vehicleSummaryResponse.inspection_id || vehicleSummaryResponse.id;
                  if (import.meta.env.DEV) {
                    console.log("[Proforma Navigation] Found inspection_id:", inspectionId);
                  }
                }
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.log("[Proforma Navigation] Could not fetch inspection_id from parent:", error);
                }
              }
            }
          }

          if (inspectionId) {
            if (import.meta.env.DEV) {
              console.log("[Proforma Navigation] Pre-populating Proforma Invoice with inspection_id:", inspectionId);
            }

            // Fetch the lookup first to find the matching estimate_id.
            // Add/prepopulate: selectedid stays empty; additionalSelectedId ensures the
            // pre-chosen estimate is in the top-10 Search_Select list (not a full Select).
            const lookupName = "estimation_no_proforma_search_Select";

            // Use requestAnimationFrame to avoid blocking render
            requestAnimationFrame(() => {
              dispatch(
                fetchLookup({
                  lookupName: lookupName,
                  params: {
                    parentid: "",
                    lookuptype: "Search_Select",
                    search_arg: "",
                    selectedid: "",
                    additionalSelectedId: inspectionId,
                  },
                })
              )
                .unwrap()
                .then((lookupResult) => {
                  // Find the option where option.id matches inspection_id
                  const matchedOption = lookupResult.options?.find((opt: any) => {
                    const optId = opt.id || (opt as any).inspection_id;
                    return optId === inspectionId || String(optId) === String(inspectionId);
                  });

                  if (matchedOption) {
                    // Resolve estimate id robustly: some lookups return inspection id in value.
                    // Prefer explicit estimate fields when available.
                    let estimateId =
                      (matchedOption as any).estimate_id ??
                      (matchedOption as any).estimation_id ??
                      (matchedOption as any).estimateId ??
                      matchedOption.value;

                    // If the resolved id still equals inspection id, try additional candidates.
                    if (
                      estimateId &&
                      inspectionId &&
                      String(estimateId) === String(inspectionId)
                    ) {
                      const altEstimateId =
                        (matchedOption as any).parent_id ??
                        (matchedOption as any).parentid ??
                        (matchedOption as any).id;
                      if (
                        altEstimateId &&
                        String(altEstimateId) !== String(inspectionId)
                      ) {
                        estimateId = altEstimateId;
                      }
                    }
                    if (import.meta.env.DEV) {
                      console.log("[Proforma Navigation] Found matching option:", {
                        inspectionId,
                        estimateId,
                        option: matchedOption,
                      });
                    }

                    // Set form data with estimation on parentid and jobcard on parentid1
                    requestAnimationFrame(() => {
                      (async () => {
                        // 1) Estimation No. → parentid; Jobcard No. → parentid1 (from jobcard lookup)
                        const currentFormData =
                          getState().forms.formDataByScreen[targetScreenId] || {};
                        const jobcardOption = await fetchProformaJobcardOption(
                          dispatch,
                          String(estimateId || inspectionId)
                        );

                        const initialPatch: Record<string, any> = {
                          ...currentFormData,
                          parentid: estimateId,
                          parent_id: estimateId,
                          parentid_selectedData: matchedOption,
                        };
                        applyProformaJobcardPrefill(
                          initialPatch,
                          jobcardOption,
                          dispatch
                        );
                        // If jobcard lookup missed, keep parentid1 empty rather than
                        // incorrectly assigning the estimation id to the jobcard field.
                        if (!initialPatch.parentid1 && jobcardOption == null) {
                          // leave unset; BindParent needs jobcard selectedData
                        }

                        dispatch(
                          setScreenFormData({
                            screenId: targetScreenId,
                            data: initialPatch,
                          })
                        );

                        if (import.meta.env.DEV) {
                          console.log("[Proforma Navigation] Form data set:", {
                            estimateId,
                            parentid1: initialPatch.parentid1,
                            customer_id: initialPatch.insurance_name,
                            jobcardOption,
                          });
                        }

                        // 2) Also fetch estimation record to pre-populate discount on Proforma
                        try {
                          const { apiService } = await import("../../services/api");
                          const estimationResponse = await apiService.get(
                            `/pabws/utility/estimation_items`,
                            { id: estimateId },
                            {
                              includeAuthToken: true,
                              authHeaderType: "Bearer",
                              errorHandling: "return-empty",
                              transformResponse: (raw: any) => {
                                if (!raw) return null;
                                if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                                  return raw.data[0];
                                }
                                if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                                  return raw.data[0];
                                }
                                return raw;
                              },
                            }
                          );

                          if (import.meta.env.DEV) {
                            console.log("[Proforma Navigation][QC->Proforma] estimation_items response:", {
                              estimateId,
                              hasResponse: !!estimationResponse,
                              keys: estimationResponse ? Object.keys(estimationResponse) : [],
                              sample: estimationResponse,
                            });
                          }

                          if (estimationResponse) {
                            const latestFormData =
                              getState().forms.formDataByScreen[targetScreenId] || {};
                            const patchData: Record<string, any> = {
                              ...latestFormData,
                            };

                            applyEstimationRoundOffToFormData(patchData, estimationResponse);

                            // Re-apply jobcard id/customer id so estimation name fallbacks cannot overwrite.
                            applyProformaJobcardPrefill(
                              patchData,
                              jobcardOption ?? latestFormData.parentid1_selectedData,
                              dispatch
                            );

                            let customerTypeValue =
                              patchData.s_select_customertype_ ??
                              firstPersistedId(
                                estimationResponse.customertype,
                                estimationResponse.customer_type,
                                estimationResponse.customer_type_id,
                                proformaSourceData.customertype,
                                proformaSourceData.customer_type,
                                proformaSourceData.customer_type_id,
                                (matchedOption as any)?.customertype,
                                (matchedOption as any)?.customer_type_id
                              );
                            if (customerTypeValue != null && customerTypeValue !== "") {
                              patchData.s_select_customertype_ = customerTypeValue;
                              patchData.customertype = customerTypeValue;
                            }

                            // Only accept real ids — never customer display names.
                            if (
                              !looksLikePersistedId(patchData.s_select_insurance_customertype_)
                            ) {
                              const insuranceNameValue = firstPersistedId(
                                estimationResponse.insurance_name,
                                estimationResponse.insurance_id,
                                estimationResponse.customer_id,
                                proformaSourceData.insurance_name,
                                proformaSourceData.insurance_id,
                                proformaSourceData.customer_id,
                                proformaSourceData.parentid1_selectedData?.customer_id,
                                proformaSourceData.parentid1_selectedData?.insurance_name,
                                proformaSourceData.parentid1_selectedData?.insurance_id,
                                proformaSourceData.parentid_selectedData?.customer_id,
                                proformaSourceData.parentid_selectedData?.insurance_name,
                                proformaSourceData.parentid_selectedData?.insurance_id
                              );
                              if (insuranceNameValue) {
                                patchData.s_select_insurance_customertype_ =
                                  insuranceNameValue;
                                patchData.insurance_name = insuranceNameValue;
                              }
                            }

                            if (import.meta.env.DEV) {
                              console.log("[Proforma Navigation][QC->Proforma] resolved prefill values:", {
                                estimateId,
                                parentid1: patchData.parentid1,
                                customerTypeValue: patchData.s_select_customertype_,
                                insuranceNameValue: patchData.s_select_insurance_customertype_,
                                discount: estimationResponse.discount,
                              });
                            }

                            dispatch(
                              setScreenFormData({
                                screenId: targetScreenId,
                                data: patchData,
                              })
                            );
                          } else if (import.meta.env.DEV) {
                            console.warn("[Proforma Navigation][QC->Proforma] estimation_items returned empty response", {
                              estimateId,
                            });
                          }
                        } catch (error) {
                          if (import.meta.env.DEV) {
                            console.warn(
                              "[Proforma Navigation] Failed to pre-populate discount from estimation (QC → Proforma):",
                              { estimateId, error }
                            );
                          }
                        }
                      })();
                    });
                  } else {
                    if (import.meta.env.DEV) {
                      console.log("[Proforma Navigation] No matching option found for inspection_id:", inspectionId);
                      console.log("[Proforma Navigation] Available options:", lookupResult.options?.slice(0, 5));
                    }
                    // Fallback: estimation on parentid; resolve jobcard for parentid1
                    requestAnimationFrame(() => {
                      (async () => {
                        const fallbackPatch: Record<string, any> = {
                          parentid: inspectionId,
                          parent_id: inspectionId,
                        };
                        const jobcardOption = await fetchProformaJobcardOption(
                          dispatch,
                          String(inspectionId)
                        );
                        applyProformaJobcardPrefill(
                          fallbackPatch,
                          jobcardOption,
                          dispatch
                        );
                        dispatch(
                          setScreenFormData({
                            screenId: targetScreenId,
                            data: fallbackPatch,
                          })
                        );
                      })();
                    });
                  }
                })
                .catch((error) => {
                  if (import.meta.env.DEV) {
                    console.log("[Proforma Navigation] Lookup fetch failed, using inspection_id:", error);
                  }
                  // Fallback: estimation on parentid; resolve jobcard for parentid1
                  requestAnimationFrame(() => {
                    (async () => {
                      const fallbackPatch: Record<string, any> = {
                        parentid: inspectionId,
                        parent_id: inspectionId,
                      };
                      const jobcardOption = await fetchProformaJobcardOption(
                        dispatch,
                        String(inspectionId)
                      );
                      applyProformaJobcardPrefill(
                        fallbackPatch,
                        jobcardOption,
                        dispatch
                      );
                      dispatch(
                        setScreenFormData({
                          screenId: targetScreenId,
                          data: fallbackPatch,
                        })
                      );
                    })();
                  });
                });
            });
          } else {
            if (import.meta.env.DEV) {
              console.log("[Proforma Navigation] No inspection_id found to pre-populate");
            }
          }
        }

        // If we have a recordId, we need to fetch the data
        if (recordId) {
          const brNavInsertOnly = isBankReconVoucherInsertOnly(targetScreenId, null);
          const pdcNavSourceId = recordId;

          dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: true }));

          try {
            // Screen 180: prefill from PDC row id but always create a new voucher on save.
            dispatch(
              setScreenEditMode({
                screenId: targetScreenId,
                isEditMode: brNavInsertOnly ? false : true,
                recordId: brNavInsertOnly ? undefined : String(recordId),
              })
            );

            // Get the screen configuration to find module names and submodules
            const screenConfig = getResolvedScreenConfig(targetScreenId);
            let mainModuleName = "";
            let mainTableName = "";

            if (screenConfig) {
              walkComponentTree(screenConfig, (component) => {
                if (component.componentType === LayoutTypes.FORM || (component as any).TableName) {
                  mainModuleName = (component as any).Name || (component as any).TableName;
                  mainTableName = (component as any).TableName;
                  return true;
                }
                return false;
              });
            }

            // Customer add (123): backend expects PascalCase "Customer_Add", not "customer_add"
            if (targetScreenId === "123" && (!mainModuleName || mainModuleName === "customer_add")) {
              mainModuleName = "Customer_Add";
            }

            if (mainModuleName) {
              // Fetch main record
              const response = await apiService.get(
                `/pabws/module/${mainModuleName}/${recordId}`,
                {},
                {
                  errorHandling: "throw",
                  transformResponse: (raw: any) => {
                    // Decryption handled by apiService.get transformResponse if it matches standard patterns
                    // In executeConfigAction we often see explicit decryption for workflow actions
                    // but here we can rely on standard apiService transformation if configured.
                    if (raw && raw.data && typeof raw.data === 'string') {
                      const decrypted = authService.decryptData(raw.data);
                      return decrypted ? (Array.isArray(decrypted) ? decrypted[0] : decrypted) : raw;
                    }
                    if (raw && raw.data) {
                      return Array.isArray(raw.data) ? raw.data[0] : raw.data;
                    }
                    return Array.isArray(raw) ? raw[0] : raw;
                  }
                }
              );

              if (response) {
                if (import.meta.env.DEV) {
                  console.log(`[Direct Navigation] Fetched record for ${targetScreenId}:`, response);
                }

                // For Proforma (175), preserve any values already set in formData (e.g. discount from Estimate).
                // For Bank reconciliation voucher (180), merge the selected summary row so prefilled
                // `subform` / `voucher_details` are not lost when GET returns an empty child list.
                let navigateFallback: Record<string, any> =
                  targetScreenId === "175"
                    ? getState().forms.formDataByScreen[targetScreenId] || {}
                    : {};
                if (targetScreenId === "180" && data && typeof data === "object") {
                  const row = { ...(data as Record<string, any>) };
                  delete (row as any).__summaryAction;
                  navigateFallback = row;
                }

                const formData = buildFormDataFromRecord(response, {
                  screenId: targetScreenId,
                  fallback: navigateFallback,
                });

                if (mainModuleName === "pur_add" || String(targetScreenId) === "140") {
                  let purchaseOrderLookupItem = applyPurchaseWithOrderViewHydration(
                    formData as Record<string, unknown>,
                    {
                      response: response as Record<string, unknown>,
                      fallback: navigateFallback,
                    }
                  );
                  const purchaseOrderId =
                    (formData as any)?.drp_purchase ?? (formData as any)?.purchase_order_id;
                  if (
                    purchaseOrderId &&
                    !String((formData as any)?.sup_name ?? "").trim()
                  ) {
                    try {
                      const rawRows = await apiService.getRawLookupData(
                        "purchase_data",
                        buildLookupPayload({ parentid: "", lookuptype: "Select" })
                      );
                      const matchedRow = rawRows.find(
                        (row: any) =>
                          String(row?.id ?? "") === String(purchaseOrderId) ||
                          String(row?.value ?? "") === String(purchaseOrderId)
                      );
                      if (matchedRow) {
                        purchaseOrderLookupItem = mergePurchaseOrderLookupIntoFormData(
                          formData as Record<string, unknown>,
                          matchedRow as Record<string, unknown>,
                          String(purchaseOrderId)
                        );
                      }
                    } catch {
                      // Module response may still supply supplier name.
                    }
                  }
                  if (purchaseOrderLookupItem) {
                    dispatch(
                      addLookupItem({
                        lookupKey: "purchare_order",
                        item: purchaseOrderLookupItem as any,
                      })
                    );
                  }
                }

                if (import.meta.env.DEV) {
                  console.log(`[Direct Navigation] Mapped formData for ${targetScreenId}:`, formData, {
                    existingDiscount: {
                      txt_discount: (navigateFallback as any)?.txt_discount,
                      discount: (navigateFallback as any)?.discount,
                    },
                    finalDiscount: {
                      txt_discount: (formData as any)?.txt_discount,
                      discount: (formData as any)?.discount,
                    },
                  });
                }

                dispatch(
                  setScreenFormData({
                    screenId: targetScreenId,
                    data: brNavInsertOnly
                      ? stripBankReconVoucherFormId(formData, pdcNavSourceId)
                      : { ...formData, id: recordId },
                  })
                );

                // Load submodules — must walk form.Fields (not only childComponents)
                const subModules = screenConfig
                  ? findSubModulesInScreenConfig(screenConfig)
                  : [];

                if (brNavInsertOnly) {
                  // Prefill lines come from module GET / summary row; skip submodule fetch by PDC id.
                } else {
                  for (const subModule of subModules) {
                    const subModuleName = subModule.name || subModule.Name;
                    const bindValue = subModule.BindValue || subModule.name;

                    if (!subModuleName || isLookupOnlySubModule(subModule)) {
                      continue;
                    }

                    try {
                      const subModuleResponse = await fetchSubModuleRowsForEdit(
                        (name, id) => apiService.getSubModuleData(name, id),
                        subModule,
                        String(recordId),
                        mainModuleName
                      );

                      if (subModuleResponse && Array.isArray(subModuleResponse)) {
                        let mappedRows = mapSubModuleApiRowsToFormRows(
                          subModule as any,
                          subModuleResponse
                        );

                        // MULTI_CHECKBOX: keep full rows (persisted id + lookup id)
                        // so edit saves can use act "edit" like MULTI_SUB_LIST.
                        if (
                          subModule.componentType === ComponentType.MULTI_CHECKBOX
                        ) {
                          const jsonAttr =
                            (subModule as any).Json_Attribute ||
                            (subModule as any).json_Attribute ||
                            "work_requested_id";
                          mappedRows = mapMultiCheckboxApiRowsToFormRows(
                            mappedRows,
                            jsonAttr
                          ) as any;
                        }

                        const curForm =
                          getState().forms.formDataByScreen[targetScreenId] || {};
                        const existingArr = (curForm as any)[bindValue];
                        const existingHas =
                          Array.isArray(existingArr) && existingArr.length > 0;
                        if (mappedRows.length === 0 && existingHas) {
                          continue;
                        }
                        if (
                          mappedRows.length === 0 &&
                          isPurchaseWithoutOrderContext(targetScreenId, mainModuleName)
                        ) {
                          const subName = String(subModuleName || "").toLowerCase();
                          const bind = String(bindValue || "").toLowerCase();
                          if (
                            subName === "purchase_details" ||
                            bind === "jobcard_details" ||
                            bind === "purchase_details"
                          ) {
                            continue;
                          }
                        }

                        const mergedForm = { ...(curForm as Record<string, any>) };
                        assignSubModuleRowsToFormData(
                          mergedForm,
                          subModule,
                          mappedRows,
                          mainModuleName
                        );
                        if (
                          mainModuleName === "pur_without_order_add" ||
                          mainModuleName === "pur_add"
                        ) {
                          applyPurchaseViewSubmoduleHydration(
                            mergedForm as Record<string, unknown>
                          );
                        }
                        for (const [key, value] of Object.entries(mergedForm)) {
                          if ((curForm as any)[key] === value) continue;
                          dispatch(
                            updateScreenFormField({
                              screenId: targetScreenId,
                              name: key,
                              value,
                            })
                          );
                        }
                      }
                    } catch (smError) {
                      console.error(`Failed to fetch submodule ${subModuleName}:`, smError);
                    }
                  }

                  if (isPurchaseWithoutOrderContext(targetScreenId, mainModuleName)) {
                    logPurchaseWithoutOrderSubmodule("navigate_record_hydrate", {
                      targetScreenId,
                      mainModuleName,
                      recordId: String(recordId),
                      subModuleCount: subModules.length,
                    });
                    await hydratePurchaseWithoutOrderSubmodules(dispatch, getState, {
                      screenId: String(targetScreenId),
                      recordId: String(recordId),
                      source: "navigate_record",
                    });
                  }
                }
              }
            }
          } catch (error) {
            console.error(`Failed to load record ${recordId} for screen ${targetScreenId}:`, error);
          } finally {
            beginExpressionCalculatingIfNeeded(dispatch, targetScreenId);
            dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: false }));
          }
        } else {
          // No recordId - this is add mode navigation
          // Apply Proforma Invoice pre-population if needed (after navigation)
          if (targetScreenId === "175" && estimateIdForProforma) {
            // Use setTimeout to ensure form data is set after navigation completes
            setTimeout(() => {
              (async () => {
                // 1) Estimation → parentid; Jobcard → parentid1 (from jobcard lookup)
                const currentFormData =
                  getState().forms.formDataByScreen[targetScreenId] || {};
                const jobcardOption = await fetchProformaJobcardOption(
                  dispatch,
                  String(estimateIdForProforma)
                );
                const initialPatch: Record<string, any> = {
                  ...currentFormData,
                  parentid: estimateIdForProforma,
                  parent_id: estimateIdForProforma,
                };
                applyProformaJobcardPrefill(
                  initialPatch,
                  jobcardOption,
                  dispatch
                );
                dispatch(
                  setScreenFormData({
                    screenId: targetScreenId,
                    data: initialPatch,
                  })
                );

                // 2) Additionally, fetch the estimation record to pre-populate discount
                // This mirrors the Jobcard behaviour so Proforma always starts with
                // the same discount as the Estimate.
                try {
                  const { apiService } = await import("../../services/api");
                  const estimationResponse = await apiService.get(
                    `/pabws/utility/estimation_items`,
                    { id: estimateIdForProforma },
                    {
                      includeAuthToken: true,
                      authHeaderType: "Bearer",
                      errorHandling: "return-empty",
                      transformResponse: (raw: any) => {
                        if (!raw) return null;
                        if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                          return raw.data[0];
                        }
                        if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                          return raw.data[0];
                        }
                        return raw;
                      },
                    }
                  );

                  if (import.meta.env.DEV) {
                    console.log("[Proforma Navigation][Generic Navigate] estimation_items response:", {
                      estimateIdForProforma,
                      hasResponse: !!estimationResponse,
                      keys: estimationResponse ? Object.keys(estimationResponse) : [],
                      sample: estimationResponse,
                    });
                  }

                  if (estimationResponse) {
                    const latestFormData =
                      getState().forms.formDataByScreen[targetScreenId] || {};
                    const patchData: Record<string, any> = {
                      ...latestFormData,
                    };

                    applyEstimationRoundOffToFormData(patchData, estimationResponse);

                    // Keep parentid1 = jobcard id and insurance_name = customer id
                    applyProformaJobcardPrefill(
                      patchData,
                      jobcardOption ?? latestFormData.parentid1_selectedData,
                      dispatch
                    );

                    const customerTypeValue =
                      patchData.s_select_customertype_ ??
                      firstPersistedId(
                        estimationResponse.customertype,
                        estimationResponse.customer_type,
                        estimationResponse.customer_type_id,
                        proformaSourceData.customertype,
                        proformaSourceData.customer_type,
                        proformaSourceData.customer_type_id
                      );
                    if (customerTypeValue != null && customerTypeValue !== "") {
                      patchData.s_select_customertype_ = customerTypeValue;
                      patchData.customertype = customerTypeValue;
                    }

                    if (
                      !looksLikePersistedId(patchData.s_select_insurance_customertype_)
                    ) {
                      const insuranceNameValue = firstPersistedId(
                        estimationResponse.insurance_name,
                        estimationResponse.insurance_id,
                        estimationResponse.customer_id,
                        proformaSourceData.insurance_name,
                        proformaSourceData.insurance_id,
                        proformaSourceData.customer_id,
                        proformaSourceData.parentid1_selectedData?.customer_id,
                        proformaSourceData.parentid1_selectedData?.insurance_name,
                        proformaSourceData.parentid1_selectedData?.insurance_id,
                        proformaSourceData.parentid_selectedData?.customer_id,
                        proformaSourceData.parentid_selectedData?.insurance_name,
                        proformaSourceData.parentid_selectedData?.insurance_id
                      );
                      if (insuranceNameValue) {
                        patchData.s_select_insurance_customertype_ =
                          insuranceNameValue;
                        patchData.insurance_name = insuranceNameValue;
                      }
                    }

                    if (import.meta.env.DEV) {
                      console.log("[Proforma Navigation][Generic Navigate] resolved prefill values:", {
                        estimateIdForProforma,
                        parentid1: patchData.parentid1,
                        customerTypeValue: patchData.s_select_customertype_,
                        insuranceNameValue: patchData.s_select_insurance_customertype_,
                        discount: estimationResponse.discount,
                      });
                    }

                    dispatch(
                      setScreenFormData({
                        screenId: targetScreenId,
                        data: patchData,
                      })
                    );
                  } else if (import.meta.env.DEV) {
                    console.warn("[Proforma Navigation][Generic Navigate] estimation_items returned empty response", {
                      estimateIdForProforma,
                    });
                  }
                } catch (error) {
                  if (import.meta.env.DEV) {
                    console.warn(
                      "[Proforma Navigation] Failed to pre-populate discount from estimation:",
                      { estimateIdForProforma, error }
                    );
                  }
                }
              })();
            }, 100);
          }
          dispatch(navigateToView({ view }));
        }
        return;
      }

      // Handle generic workflow actions from summary lists
      // Check if this is a workflow action by looking for workflow configuration in current screen
      const currentScreenConfig =
        getResolvedScreenConfig(currentView) ||
        getResolvedScreenConfig(deriveScreenId(currentView) || "");
      let workflowItem: any = null;

      if (currentScreenConfig && data && typeof data === "object") {
        walkComponentTree(currentScreenConfig, (component) => {
          if (component.componentType === ComponentType.SUMMARY_LIST) {
            const summaryList = component as any;
            if (summaryList.workflow && Array.isArray(summaryList.workflow)) {
              workflowItem = summaryList.workflow.find(
                (w: any) => w.Action === actionId
              );
              if (workflowItem) return true; // Stop searching
            }
          }
          return false;
        });
      }

      // If this is a workflow action, handle it generically
      if (workflowItem) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;

        // Extract screen ID from workflow Screen property (e.g., "modulev1/vehiclein_add" -> "107")
        let targetScreenId = "";
        if (workflowItem.Screen) {
          const screenPath = workflowItem.Screen;
          // Map common screen paths to screen IDs
          const screenPathMap: Record<string, string> = {
            "modulev1/vehiclein_add": "107",
            "modulev1/estimation_add": "116",
            "modulev1/estimateapproval": "150",
            "modulev1/jobcard_add": "156",
            "modulev1/qc_approve": "157",
            "modulev1/QualityCheckApproval": "qc_approval",
            "modulev1/invoice_add": "158",
            "modulev1/close_jobcard": "39",
          };
          targetScreenId = screenPathMap[screenPath] || deriveScreenId(screenPath) || "107"; // Try map, then dynamic, fallback to 107
        }

        if (!targetScreenId) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to determine target screen for workflow action.",
              type: "error",
            },
            5000
          );
          return;
        }

        const targetView = resolveView(targetScreenId);
        dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: true }));

        // Get the record ID (inspection ID)
        const recordId = cleanRowData.id || cleanRowData.vehicle_in_id;

        // Process ActionArgs if present - replace placeholders like <id>, <jobcard_id>, etc.
        const processedActionArgs: Record<string, string> = {};
        if (workflowItem.ActionArgs) {
          Object.keys(workflowItem.ActionArgs).forEach((key) => {
            let value = workflowItem.ActionArgs[key];
            if (typeof value === "string") {
              // Replace <id> specifically (legacy support)
              if (recordId) {
                value = value.replace("<id>", recordId.toString());
              }
              // Replace any <field_name> with value from row data (e.g., <jobcard_id>)
              const placeholders = value.match(/<([^>]+)>/g);
              if (placeholders) {
                placeholders.forEach((placeholder: string) => {
                  const fieldName = placeholder.substring(
                    1,
                    placeholder.length - 1
                  );
                  if (
                    cleanRowData[fieldName] !== undefined &&
                    cleanRowData[fieldName] !== null
                  ) {
                    value = value.replace(
                      placeholder,
                      cleanRowData[fieldName].toString()
                    );
                  }
                });
              }
              processedActionArgs[key] = value;
            } else {
              processedActionArgs[key] = value;
            }
          });
        }

        // Check if child record already exists (for edit/view mode)
        // Priority: Check __workflowChildId (from completed state click), then Action_ref_column, then action-specific logic
        let childRecordId: string | number | null | undefined = null;

        // First, check if this is a workflow action with a child record ID passed from DataTable
        if (cleanRowData.__workflowChildId) {
          childRecordId = cleanRowData.__workflowChildId;
          if (import.meta.env.DEV) {
            console.log("[Workflow Navigation] Using child record ID from workflow click:", {
              actionId,
              childRecordId,
              source: "__workflowChildId",
            });
          }
        } else if (workflowItem.Action_ref_column) {
          // Check Action_ref_column (e.g., estimate_id, approved_id, etc.)
          const refColumnName = workflowItem.Action_ref_column;
          childRecordId =
            cleanRowData[refColumnName] ||
            cleanRowData[refColumnName.toLowerCase()] ||
            cleanRowData[refColumnName.toUpperCase()];

          if (import.meta.env.DEV && childRecordId) {
            console.log("[Workflow Navigation] Using child record ID from Action_ref_column:", {
              actionId,
              refColumnName,
              childRecordId,
            });
          }
        } else {
          // Fallback to action-specific logic (for backward compatibility)
          if (actionId === "Estimate") {
            childRecordId = cleanRowData.estimate_id || cleanRowData.estimation_id;
          }
        }

        let initialFormData: Record<string, any> = {};

        // CRITICAL: Only set edit mode if childRecordId exists AND it's from __workflowChildId
        // If childRecordId comes from Action_ref_column (like estimate_id), we need to verify
        // it's actually a valid record ID for the target screen before setting edit mode
        // For CREATE operations (like "Estimate" from inspection), childRecordId might be undefined
        // even if Action_ref_column exists (because no estimation record exists yet)
        const isEditModeFromWorkflow = !!(cleanRowData.__workflowChildId);
        const hasChildRecordId = !!(childRecordId && String(childRecordId).trim().length > 0);

        // Only set edit mode if we have a childRecordId AND it came from workflow (not Action_ref_column)
        // OR if Action_ref_column exists AND childRecordId exists (meaning record already exists)
        // For CREATE operations, childRecordId will be undefined/null even if Action_ref_column exists
        if (hasChildRecordId && (isEditModeFromWorkflow || !!workflowItem.Action_ref_column)) {
          // For edit/view mode, include the child record ID
          // Initially set mode to "view" (user can switch to "edit" later)
          initialFormData = {
            ...buildFormDataFromRecord(null, {
              fallback: cleanRowData,
              screenId: targetScreenId,
            }),
            id: childRecordId, // Set the child record ID for edit mode
            // Carry workflow fields for submit (edit/view)
            next_action: workflowItem.Action || actionId,
            next_state: (workflowItem.States && workflowItem.States[0]) || "",
          };

          // Set edit mode in Redux store ONLY if childRecordId exists
          // This means we're editing an existing record
          if (childRecordId) {
            dispatch(
              setScreenEditMode({
                screenId: targetScreenId,
                isEditMode: true,
                recordId: String(childRecordId),
              })
            );
          }

          if (import.meta.env.DEV) {
            console.log("[Workflow Navigation] Setting edit mode:", {
              screenId: targetScreenId,
              recordId: childRecordId,
              actionId,
              source: isEditModeFromWorkflow ? "__workflowChildId" : "Action_ref_column",
            });
          }
        } else {
          // For add mode, set parent_id from ActionArgs
          initialFormData = {
            ...buildFormDataFromRecord(null, {
              fallback: cleanRowData,
              screenId: targetScreenId,
            }),
            // Carry workflow fields for submit (add)
            next_action: workflowItem.Action || actionId,
            next_state: (workflowItem.States && workflowItem.States[0]) || "",
          };

          // Set parent_id from processed ActionArgs
          if (processedActionArgs.parentid) {
            initialFormData.parentid = processedActionArgs.parentid;
            initialFormData.parent_id = processedActionArgs.parentid;
          }

          // Job Card add (156): use inspection id as parent id (not approved_id / estimation id).
          const isJobCardAdd =
            (targetScreenId === "156" || (workflowItem.Screen && String(workflowItem.Screen).includes("jobcard_add"))) &&
            !childRecordId;
          if (isJobCardAdd && recordId) {
            const inspectionId = recordId; // cleanRowData.id || cleanRowData.vehicle_in_id
            initialFormData.parentid = inspectionId;
            initialFormData.parent_id = inspectionId;
            if (import.meta.env.DEV) {
              console.log("[Job Card add] Set parentid to inspection id:", { inspectionId });
            }
          }

          const isCloseJobcardNav =
            targetScreenId === "39" ||
            targetScreenId === "159" ||
            (workflowItem.Screen &&
              String(workflowItem.Screen).includes("close_jobcard"));
          if (isCloseJobcardNav) {
            const issueStatus =
              cleanRowData.issue_status ??
              cleanRowData.issueStatus ??
              cleanRowData.ISSUE_STATUS;
            if (issueStatus !== undefined && issueStatus !== null) {
              initialFormData.issue_status = issueStatus;
            }
            const parentId =
              initialFormData.parentid ??
              initialFormData.parent_id ??
              processedActionArgs.parentid;
            if (parentId) {
              initialFormData.parentid_selectedData = {
                ...(typeof initialFormData.parentid_selectedData === "object" &&
                initialFormData.parentid_selectedData !== null
                  ? (initialFormData.parentid_selectedData as Record<string, unknown>)
                  : {}),
                ...cleanRowData,
                id: parentId,
                issue_status: issueStatus ?? cleanRowData.issue_status,
              };
            }
          }
        }

        // For Estimate action in add mode, fetch inspection record BEFORE setting form data
        // This ensures the lookup item is available when ComponentRenderer renders
        if (actionId === "Estimate" && !childRecordId && processedActionArgs.parentid) {
          try {
            const { apiService } = await import("../../services/api");

            // Fetch the specific inspection record by ID (like Blazor's GetLookupItem)
            const inspectionResponse = await apiService.get(
              `/pabws/utility/inspection_items`,
              { id: processedActionArgs.parentid },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "return-empty",
                transformResponse: (raw: any) => {
                  if (!raw) return null;
                  if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0]; // Return the first item
                  }
                  if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0];
                  }
                  return raw;
                },
              }
            );

            if (inspectionResponse) {
              // CRITICAL: Add the inspection record to lookup options FIRST (before setting form data)
              // This ensures SearchableDropdown can find it when it renders
              // Add to lookups (as LookupOption) for dropdown display
              dispatch(
                addLookupItem({
                  lookupKey: "inspection_items",
                  item: {
                    ...inspectionResponse,
                    label: inspectionResponse.name || inspectionResponse.id,
                    value: inspectionResponse.id,
                  },
                })
              );

              // Also add to rawData for full record access
              dispatch(
                addLookupItem({
                  lookupKey: "inspection_items",
                  item: inspectionResponse,
                  isRawData: true,
                })
              );

              // Store the full inspection record in formData for BindParent to access
              // This mimics Blazor's args.SelectedJson.Data
              initialFormData.parentid_selectedData = inspectionResponse;

              // Also set customer_remarks directly if available (for BindParent)
              if (inspectionResponse.customer_remarks !== undefined) {
                initialFormData.customer_remarks = inspectionResponse.customer_remarks;
              }

              if (import.meta.env.DEV) {
                console.log(
                  "[Estimate Action] Fetched and added inspection record BEFORE form data:",
                  {
                    parentid: processedActionArgs.parentid,
                    inspectionRecord: {
                      id: inspectionResponse.id,
                      name: inspectionResponse.name,
                      label: inspectionResponse.name || inspectionResponse.id,
                      value: inspectionResponse.id,
                      customer_remarks: inspectionResponse.customer_remarks,
                    },
                    addedToLookups: true,
                    addedToRawData: true,
                  }
                );
              }
            } else {
              if (import.meta.env.DEV) {
                console.warn(
                  "[Estimate Action] Inspection record not found:",
                  {
                    parentid: processedActionArgs.parentid,
                  }
                );
              }
            }
          } catch (lookupError) {
            console.error(
              "[Estimate Action] Failed to fetch inspection record for BindParent:",
              lookupError
            );
            // Continue without the inspection record - BindParent will work when user selects
          }
        }

        // For Jobcard action in add mode, fetch estimation record BEFORE setting form data
        const jobCardEstimationId =
          (actionId === "Jobcard" || actionId === "Job card" || targetScreenId === "156") && !childRecordId
            ? (initialFormData.parentid || initialFormData.parent_id || processedActionArgs.parentid)
            : null;
        if (jobCardEstimationId) {
          try {
            const { apiService } = await import("../../services/api");

            // Fetch the specific estimation record by ID (like Blazor's GetLookupItem)
            const estimationResponse = await apiService.get(
              `/pabws/utility/estimation_items`,
              { id: jobCardEstimationId },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "return-empty",
                transformResponse: (raw: any) => {
                  if (!raw) return null;
                  if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0]; // Return the first item
                  }
                  if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0];
                  }
                  return raw;
                },
              }
            );

            if (estimationResponse) {
              // CRITICAL: Add the estimation record to lookup options FIRST (before setting form data)
              // This ensures SearchableDropdown can find it when it renders
              dispatch(
                addLookupItem({
                  lookupKey: "estimation_items",
                  item: {
                    ...estimationResponse,
                    label: estimationResponse.name || estimationResponse.id,
                    value: estimationResponse.id,
                  },
                })
              );

              // Also add to rawData for full record access
              dispatch(
                addLookupItem({
                  lookupKey: "estimation_items",
                  item: estimationResponse,
                  isRawData: true,
                })
              );

              // Store the full estimation record in formData for BindParent to access
              initialFormData.parentid_selectedData = estimationResponse;
              applyEstimationRoundOffToFormData(initialFormData, estimationResponse);

              if (import.meta.env.DEV) {
                console.log(
                  "[Jobcard Action] Fetched and added estimation record BEFORE form data:",
                  {
                    parentid: jobCardEstimationId,
                    estimationRecord: {
                      id: estimationResponse.id,
                      name: estimationResponse.name,
                      label: estimationResponse.name || estimationResponse.id,
                      value: estimationResponse.id,
                      discount: estimationResponse.discount,
                      round_off_amt: estimationResponse.round_off_amt,
                    },
                    addedToLookups: true,
                    addedToRawData: true,
                  }
                );
              }
            } else {
              if (import.meta.env.DEV) {
                console.warn(
                  "[Jobcard Action] Estimation record not found:",
                  jobCardEstimationId
                );
              }
            }
          } catch (lookupError) {
            console.error(
              "[Jobcard Action] Failed to fetch estimation record:",
              lookupError
            );
            // Continue without the estimation record - SubModules will fetch when parentid is available
          }
        }

        // For Approve action in add mode, fetch estimation record BEFORE setting form data
        // This ensures the lookup item is available when ComponentRenderer renders
        if (actionId === "Approve" && !childRecordId) {
          // Get estimation ID from row data (estimate_id) or ActionArgs
          const estimationId = cleanRowData.estimate_id || cleanRowData.estimation_id || processedActionArgs.parentid;

          if (estimationId) {
            try {
              const { apiService } = await import("../../services/api");

              // Fetch the specific estimation record by ID (like Blazor's GetLookupItem)
              const estimationResponse = await apiService.get(
                `/pabws/utility/estimation_items`,
                { id: estimationId },
                {
                  includeAuthToken: true,
                  authHeaderType: "Bearer",
                  errorHandling: "return-empty",
                  transformResponse: (raw: any) => {
                    if (!raw) return null;
                    if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0]; // Return the first item
                    }
                    if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0];
                    }
                    return raw;
                  },
                }
              );

              if (estimationResponse) {
                // CRITICAL: Add the estimation record to lookup options FIRST (before setting form data)
                // This ensures SearchableDropdown can find it when it renders
                // Add to lookups (as LookupOption) for dropdown display
                dispatch(
                  addLookupItem({
                    lookupKey: "estimation_items",
                    item: {
                      ...estimationResponse,
                      label: estimationResponse.name || estimationResponse.est_no || estimationResponse.id,
                      value: estimationResponse.id,
                    },
                  })
                );

                // Also add to rawData for full record access
                dispatch(
                  addLookupItem({
                    lookupKey: "estimation_items",
                    item: estimationResponse,
                    isRawData: true,
                  })
                );

                // IMPORTANT: Also fetch the full lookup to ensure dropdown has all options
                // This ensures the dropdown works properly even if the single item wasn't enough
                const { fetchLookup } = await import("../slices/lookupsSlice");
                dispatch(
                  fetchLookup({
                    lookupName: "estimation_items",
                    params: {
                      parentid: "",
                      lookuptype: "Select",
                    },
                  })
                );

                // Store the full estimation record in formData for BindParent to access
                // This mimics Blazor's args.SelectedJson.Data
                initialFormData.parentid_selectedData = estimationResponse;

                // Set parent_id from estimation ID
                initialFormData.parentid = estimationId;
                initialFormData.parent_id = estimationId;

                if (import.meta.env.DEV) {
                  console.log(
                    "[Approve Action] Fetched and added estimation record BEFORE form data:",
                    {
                      parentid: estimationId,
                      estimationRecord: {
                        id: estimationResponse.id,
                        name: estimationResponse.name,
                        est_no: estimationResponse.est_no,
                        label: estimationResponse.name || estimationResponse.est_no || estimationResponse.id,
                        value: estimationResponse.id,
                      },
                      addedToLookups: true,
                      addedToRawData: true,
                    }
                  );
                }
              } else {
                if (import.meta.env.DEV) {
                  console.warn(
                    "[Approve Action] Estimation record not found:",
                    {
                      parentid: estimationId,
                    }
                  );
                }
              }
            } catch (lookupError) {
              console.error(
                "[Approve Action] Failed to fetch estimation record for BindParent:",
                lookupError
              );
              // Continue without the estimation record - BindParent will work when user selects
            }
          }
        }

        // For Create invoice action in add mode, fetch jobcard record BEFORE setting form data
        // This ensures the lookup item is available when ComponentRenderer renders
        if (actionId === "Create invoice" && !childRecordId) {
          // Priority: processedActionArgs.parentid (if regex replacement worked)
          // If it's still a placeholder (contains <), try to find ID in row data directly
          // Fallback: cleanRowData.jobcard_id (direct Jobcard ID)
          // Final Fallback: cleanRowData.id (Inspection / service_details ID)
          const inspectionId =
            cleanRowData.id || cleanRowData.vehicle_in_id || cleanRowData.parent_id;
          let parentid = processedActionArgs.parentid;

          if (!parentid || (typeof parentid === "string" && parentid.includes("<"))) {
            parentid =
              cleanRowData.jobcard_id ||
              cleanRowData.jobcardid ||
              cleanRowData.job_card_id;
          }

          if (!parentid) {
            parentid = inspectionId;
          }

          // Workflow on inspection grid used <jobcard_no> — lookups need service_details.id
          const rowJobcardNo =
            cleanRowData.jobcard_no ||
            cleanRowData.jobcardNo ||
            cleanRowData.JobcardNo;
          if (
            parentid &&
            inspectionId &&
            rowJobcardNo &&
            String(parentid).trim() === String(rowJobcardNo).trim()
          ) {
            parentid = inspectionId;
          }

          if (parentid) {
            try {
              const { apiService } = await import("../../services/api");

              // Fetch the specific jobcard record for the invoice (like Blazor's GetLookupItem)
              const jobcardResponse = await apiService.get(
                `/pabws/utility/invoice_jobcard`,
                { id: parentid },
                {
                  includeAuthToken: true,
                  authHeaderType: "Bearer",
                  errorHandling: "return-empty",
                  transformResponse: (raw: any) => {
                    if (!raw) return null;
                    if (
                      raw.success &&
                      raw.data &&
                      Array.isArray(raw.data) &&
                      raw.data.length > 0
                    ) {
                      return raw.data[0]; // Return the first item
                    }
                    if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0];
                    }
                    return raw;
                  },
                }
              );

              // Retry with inspection id when first lookup used jobcard no / wrong key
              let resolvedJobcard = jobcardResponse;
              if (
                (!resolvedJobcard || !resolvedJobcard.id) &&
                inspectionId &&
                String(parentid) !== String(inspectionId)
              ) {
                resolvedJobcard = await apiService.get(
                  `/pabws/utility/invoice_jobcard`,
                  { id: inspectionId },
                  {
                    includeAuthToken: true,
                    authHeaderType: "Bearer",
                    errorHandling: "return-empty",
                    transformResponse: (raw: any) => {
                      if (!raw) return null;
                      if (
                        raw.success &&
                        raw.data &&
                        Array.isArray(raw.data) &&
                        raw.data.length > 0
                      ) {
                        return raw.data[0];
                      }
                      if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                        return raw.data[0];
                      }
                      return raw;
                    },
                  }
                );
              }

              if (resolvedJobcard && resolvedJobcard.id) {
                const jobcardResponse = resolvedJobcard;
                const jobcardLookupItem = {
                  ...jobcardResponse,
                  label:
                    jobcardResponse.name ||
                    jobcardResponse.jobcard_no ||
                    jobcardResponse.id,
                  value: jobcardResponse.id,
                };
                const invoiceJobcardLookupKeys = [
                  "invoice_jobcard",
                  "invoice_jobcard_search_Select",
                ];
                for (const lookupKey of invoiceJobcardLookupKeys) {
                  dispatch(
                    addLookupItem({
                      lookupKey,
                      item: jobcardLookupItem,
                    })
                  );
                  dispatch(
                    addLookupItem({
                      lookupKey,
                      item: jobcardResponse,
                      isRawData: true,
                    })
                  );
                }

                // Store the full record in formData for BindParent
                initialFormData.parentid_selectedData = jobcardResponse;

                // CRITICAL FIX: Use the actual Jobcard ID from the response for the form field.
                // If we looked up using Inspection ID (fallback), 'parentid' var is Inspection ID.
                // But the dropdown expects Jobcard ID (which is 'jobcardResponse.id').
                const actualJobcardId = jobcardResponse.id;
                initialFormData.parentid = actualJobcardId;
                initialFormData.parent_id = actualJobcardId;

                // Pre-populate customer type, customer name, and invoice date from jobcard
                const customerTypeValue =
                  jobcardResponse.customertype ??
                  jobcardResponse.customer_type ??
                  jobcardResponse.customer_type_id;
                if (customerTypeValue !== undefined && customerTypeValue !== null) {
                  initialFormData.s_select_customertype = customerTypeValue;
                  initialFormData.s_select_customertype_ = customerTypeValue;
                  initialFormData.customertype = customerTypeValue;
                }
                const insuranceNameValue =
                  jobcardResponse.insurance_name ??
                  jobcardResponse.insurance_id ??
                  jobcardResponse.customer_id;
                if (insuranceNameValue !== undefined && insuranceNameValue !== null) {
                  initialFormData.s_select_insurance_customertype = insuranceNameValue;
                  initialFormData.s_select_insurance_customertype_ = insuranceNameValue;
                  initialFormData.insurance_name = insuranceNameValue;
                }
                // insurance_customer filters by customertype (related_column), not jobcard id
                if (customerTypeValue !== undefined && customerTypeValue !== null) {
                  dispatch(
                    fetchLookup({
                      lookupName: "insurance_customer",
                      params: {
                        parentid: String(customerTypeValue),
                        lookuptype: "Select",
                      },
                    })
                  );
                }
                const invoiceDateValue = jobcardResponse.invoice_date ?? jobcardResponse.job_date ?? jobcardResponse.date;
                if (invoiceDateValue !== undefined && invoiceDateValue !== null) {
                  const dateStr = typeof invoiceDateValue === "string" ? invoiceDateValue : String(invoiceDateValue);
                  initialFormData.txt_jobdate = dateStr;
                  initialFormData.invoice_date = dateStr;
                }

                await applyLatestEstimationRoundOffForInvoice(
                  initialFormData,
                  String(actualJobcardId),
                  jobcardResponse
                );

                // Always log for debugging prepopulation
                console.log("[Create Invoice] Jobcard prepopulation check:", {
                  jobcardId: actualJobcardId,
                  jobcardKeys: Object.keys(jobcardResponse),
                  customertype: {
                    customertype: jobcardResponse.customertype,
                    customer_type: jobcardResponse.customer_type,
                    customer_type_id: jobcardResponse.customer_type_id,
                    found: customerTypeValue,
                    set: initialFormData.s_select_customertype,
                  },
                  insurance_name: {
                    insurance_name: jobcardResponse.insurance_name,
                    insurance_id: jobcardResponse.insurance_id,
                    found: insuranceNameValue,
                    set: initialFormData.s_select_insurance_customertype,
                    lookupFetched: true,
                  },
                  invoice_date: {
                    invoice_date: jobcardResponse.invoice_date,
                    job_date: jobcardResponse.job_date,
                    date: jobcardResponse.date,
                    found: invoiceDateValue,
                    set: initialFormData.txt_jobdate,
                  },
                  round_off_amt: {
                    round_off_amt: jobcardResponse.round_off_amt,
                    set: initialFormData.txt_round_off,
                  },
                });
              }
            } catch (error) {
              console.error(
                "[Create Invoice Action] Failed to fetch jobcard record:",
                error
              );
            }
          }
        }

        // Create Gate Pass (176): resolve service_details id and prefill jobcard + report parent
        if (actionId === "Create Gate Pass" && !childRecordId) {
          const inspectionId =
            cleanRowData.id || cleanRowData.vehicle_in_id || cleanRowData.parent_id;
          let parentid = processedActionArgs.parentid;

          if (!parentid || (typeof parentid === "string" && parentid.includes("<"))) {
            parentid =
              cleanRowData.jobcard_id ||
              cleanRowData.jobcardid ||
              cleanRowData.job_card_id;
          }
          if (!parentid) {
            parentid = inspectionId;
          }

          const rowJobcardNo =
            cleanRowData.jobcard_no ||
            cleanRowData.jobcardNo ||
            cleanRowData.JobcardNo;
          if (
            parentid &&
            inspectionId &&
            rowJobcardNo &&
            String(parentid).trim() === String(rowJobcardNo).trim()
          ) {
            parentid = inspectionId;
          }

          if (parentid) {
            try {
              const { apiService } = await import("../../services/api");
              const jobcardResponse = await apiService.get(
                `/pabws/utility/gate_pass_jobcard`,
                { id: parentid },
                {
                  includeAuthToken: true,
                  authHeaderType: "Bearer",
                  errorHandling: "return-empty",
                  transformResponse: (raw: any) => {
                    if (!raw) return null;
                    if (
                      raw.success &&
                      raw.data &&
                      Array.isArray(raw.data) &&
                      raw.data.length > 0
                    ) {
                      return raw.data[0];
                    }
                    if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0];
                    }
                    return raw;
                  },
                }
              );

              let resolved = jobcardResponse;
              if (
                (!resolved || !resolved.id) &&
                inspectionId &&
                String(parentid) !== String(inspectionId)
              ) {
                resolved = await apiService.get(
                  `/pabws/utility/gate_pass_jobcard`,
                  { id: inspectionId },
                  {
                    includeAuthToken: true,
                    authHeaderType: "Bearer",
                    errorHandling: "return-empty",
                    transformResponse: (raw: any) => {
                      if (!raw) return null;
                      if (
                        raw.success &&
                        raw.data &&
                        Array.isArray(raw.data) &&
                        raw.data.length > 0
                      ) {
                        return raw.data[0];
                      }
                      if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                        return raw.data[0];
                      }
                      return raw;
                    },
                  }
                );
              }

              if (resolved && resolved.id) {
                const serviceParentId = String(resolved.id);
                initialFormData.parentid = serviceParentId;
                initialFormData.parent_id = serviceParentId;
                initialFormData.parentid_selectedData = resolved;
                initialFormData.gate_pass_status = "Gate Pass Issued";

                const lookupItem = {
                  ...resolved,
                  label:
                    resolved.name ||
                    resolved.jobcard_no ||
                    rowJobcardNo ||
                    serviceParentId,
                  value: serviceParentId,
                };
                dispatch(
                  addLookupItem({
                    lookupKey: "gate_pass_jobcard",
                    item: lookupItem,
                  })
                );
                dispatch(
                  addLookupItem({
                    lookupKey: "gate_pass_jobcard",
                    item: resolved,
                    isRawData: true,
                  })
                );
              }
            } catch (error) {
              console.error(
                "[Create Gate Pass] Failed to fetch gate_pass_jobcard:",
                error
              );
            }
          }
        }

        // Invoice create (158 / 173): when jobcard has no insurance fields, use customername from row
        if (
          (targetScreenId === "158" || targetScreenId === "173") &&
          !childRecordId
        ) {
          const currentCustomerName =
            initialFormData.s_select_insurance_customertype ??
            initialFormData.s_select_insurance_customertype_ ??
            initialFormData.insurance_name;
          const rowCustomerName =
            cleanRowData.customername ?? cleanRowData.customer_name;
          if (
            (currentCustomerName === undefined ||
              currentCustomerName === null ||
              currentCustomerName === "") &&
            rowCustomerName !== undefined &&
            rowCustomerName !== null &&
            rowCustomerName !== ""
          ) {
            initialFormData.s_select_insurance_customertype = rowCustomerName;
            initialFormData.s_select_insurance_customertype_ = rowCustomerName;
            initialFormData.insurance_name = rowCustomerName;
            const customerTypeForLookup =
              initialFormData.s_select_customertype_ ??
              initialFormData.s_select_customertype ??
              initialFormData.customertype;
            if (customerTypeForLookup) {
              dispatch(
                fetchLookup({
                  lookupName: "insurance_customer",
                  params: {
                    parentid: String(customerTypeForLookup),
                    lookuptype: "Select",
                  },
                })
              );
            }
          }
        }

        // Initially populate with row data and ActionArgs
        dispatch(
          setScreenFormData({
            screenId: targetScreenId,
            data: initialFormData,
          })
        );

        // Job Card add: put inspection id in URL so SubModule/Report use same parent id
        const viewToUse =
          (targetScreenId === "156" || (workflowItem.Screen && String(workflowItem.Screen).includes("jobcard_add"))) &&
            !childRecordId &&
            (initialFormData.parentid || initialFormData.parent_id)
            ? `${targetView}/${String(initialFormData.parentid || initialFormData.parent_id)}`
            : targetView;
        dispatch(navigateToView({ view: viewToUse }));

        try {
          let shouldFetchRecord = false;
          let fetchEndpoint = "";
          let queryParams: Record<string, any> | undefined = undefined;

          // For Inspect action, use the specific API endpoint with record ID
          if (actionId === "Inspect") {
            fetchEndpoint = "/pabws/module/vehicle_in/" + recordId;
            shouldFetchRecord = true;
            // Pass the record ID as query parameter if available
            // if (recordId) {
            //   queryParams = { id: recordId };
            // }
          } else if (actionId === "Estimate") {
            // For Estimate action, check if we're in edit mode or add mode
            if (childRecordId && childRecordId.toString().length > 0) {
              // Edit/view mode: fetch existing estimation record via MODULE API (like Inspect)
              // This ensures we get the full estimation record (including discount, totals, etc.)
              const estimationId = childRecordId.toString();
              const modulePath = workflowItem.Screen
                ? workflowItem.Screen.replace("modulev1/", "")
                : "estimation_add";
              fetchEndpoint = `/pabws/module/${modulePath}/${estimationId}`;
              shouldFetchRecord = true;
              // ID is already part of the URL, no queryParams needed
            } else {
              // Add mode: fetch with parent_id to load parent data using SCREEN API
              fetchEndpoint = "/pabws/screen/estimation_add";
              shouldFetchRecord = true;
              // Pass parent_id as query parameter to load parent inspection data
              if (processedActionArgs.parentid) {
                queryParams = { parentid: processedActionArgs.parentid };
              }
            }
          } else if (actionId === "Approve") {
            // For Approve action, check if we're in edit mode or add mode
            if (childRecordId && childRecordId.toString().length > 0) {
              // Edit/view mode: fetch existing approval record via MODULE API
              // CRITICAL: Use only the approval record ID (service_est_approval PK), NOT the inspection/parent id.
              // Do NOT use cleanRowData.id - in inspection summary rows that is the vehicle_in/inspection id.
              const approvalId =
                cleanRowData.service_est_approval_id ??
                cleanRowData.approved_id ??
                cleanRowData.approval_id ??
                cleanRowData.__workflowChildId ??
                childRecordId;
              const approvalIdStr = String(approvalId).trim();
              if (approvalIdStr) {
                fetchEndpoint = `/pabws/module/approval_add/${approvalIdStr}`;
                shouldFetchRecord = true;
              }
              // ID is part of the URL; no query params needed
            } else {
              // Add mode: fetch with parent_id to load parent estimation data via SCREEN API
              fetchEndpoint = "/pabws/screen/approval_add";
              shouldFetchRecord = true;
              // Pass parent_id as query parameter to load parent estimation data
              // parent_id should be the estimation_id from the row data
              const estimationId = cleanRowData.estimate_id || cleanRowData.estimation_id;
              if (estimationId) {
                queryParams = { parentid: estimationId };
              } else if (processedActionArgs.parentid) {
                queryParams = { parentid: processedActionArgs.parentid };
              }
            }
          } else if (actionId === "QC Approve" && childRecordId && childRecordId.toString().length > 0) {
            // Quality Check Approval edit: use /module/qc_approval_add/ (no pabws - backend expects api/module/qc_approval_add/{id})
            const qcApprovalId =
              cleanRowData.service_qc_approval_id ??
              cleanRowData.qc_approved_id ??
              cleanRowData.__workflowChildId ??
              childRecordId;
            const qcApprovalIdStr = String(qcApprovalId).trim();
            if (qcApprovalIdStr) {
              fetchEndpoint = `/pabws/module/qc_approval_add/${qcApprovalIdStr}`;
              shouldFetchRecord = true;
            }
          } else if (childRecordId && childRecordId.toString().length > 0 && workflowItem.Screen) {
            // Generic workflow action with child record ID - fetch the child record
            let modulePath = workflowItem.Screen.replace("modulev1/", "");
            // QC Approval: backend expects /module/qc_approval_add/{id} (not /pabws/module/qc_approve)
            if (modulePath === "qc_approve") {
              fetchEndpoint = `/pabws/module/qc_approval_add/${childRecordId}`;
            } else {
              fetchEndpoint = `/pabws/module/${modulePath}/${childRecordId}`;
            }
            shouldFetchRecord = true;
            // queryParams = { id: childRecordId }; // ID is already in the URL

            if (import.meta.env.DEV) {
              console.log("[Workflow Navigation] Generic workflow - Edit/View mode:", {
                actionId,
                childRecordId,
                modulePath,
                endpoint: fetchEndpoint,
                queryParams,
              });
            }
          } else if (
            (actionId === "Jobcard" || actionId === "Job card" || targetScreenId === "156") &&
            !childRecordId &&
            workflowItem.Screen &&
            (initialFormData.parentid || initialFormData.parent_id)
          ) {
            // Job Card add: main fetch uses inspection id (parentid)
            const jobCardAddId = String(initialFormData.parentid || initialFormData.parent_id);
            const modulePath = workflowItem.Screen.replace("modulev1/", "");
            fetchEndpoint = `/pabws/module/${modulePath}/${jobCardAddId}`;
            shouldFetchRecord = true;
            queryParams = { id: jobCardAddId };
            if (import.meta.env.DEV) {
              console.log("[Workflow Navigation] Job card add - main fetch with inspection id:", { jobCardAddId, fetchEndpoint });
            }
          } else if (recordId && workflowItem.Screen) {
            // For other workflow actions without child record, try to fetch the parent record using the module path
            const modulePath = workflowItem.Screen.replace("modulev1/", "");
            fetchEndpoint = `/pabws/module/${modulePath}/${recordId}`;
            shouldFetchRecord = true;
          }

          if (shouldFetchRecord && fetchEndpoint) {
            if (import.meta.env.DEV) {
              console.log("[Edit Mode] Fetching record data:", {
                endpoint: fetchEndpoint,
                queryParams,
                screenId: targetScreenId,
                actionId,
                childRecordId,
                isEditMode: !!childRecordId,
              });
            }

            const response = await apiService.get(fetchEndpoint, queryParams, {
              includeAuthToken: true,
              authHeaderType: "Bearer",
              errorHandling: "throw",
              transformResponse: (raw: any) => {
                if (!raw) return null;

                // CRITICAL: Handle encrypted response (like Inspect action)
                if (raw.data && typeof raw.data === 'string') {
                  if (import.meta.env.DEV) {
                    console.log("[HandleEdit] 🔐 Decryption Check:", { endpoint: fetchEndpoint, rawLength: raw.data.length });
                  }
                  const decrypted = authService.decryptData(raw.data);
                  if (decrypted) {
                    if (import.meta.env.DEV) {
                      console.log("[HandleEdit] Decrypted successfully");
                    }
                    raw.data = decrypted;
                  }
                }

                // Handle Blazor-style nested structure: { data: { data: { ... } } }
                // This matches Blazor's: ItemData = moduleData?["data"]?["data"]?.AsObject();
                if (raw.data) {
                  // Check for nested data.data structure (Blazor format)
                  if (raw.data.data && typeof raw.data.data === "object" && !Array.isArray(raw.data.data)) {
                    if (import.meta.env.DEV) {
                      console.log("[Edit Mode] Found nested data.data structure (Blazor format)");
                    }
                    return raw.data.data;
                  }

                  // Handle array response
                  if (Array.isArray(raw.data)) {
                    return raw.data[0] || raw.data;
                  }

                  // Handle direct data object
                  return raw.data;
                }

                // Handle array response
                if (Array.isArray(raw)) {
                  return raw[0];
                }

                // Handle direct object response
                return raw;
              },
            });

            if (import.meta.env.DEV) {
              console.log("[Edit Mode] ===== API Response Structure =====");
              console.log("[Edit Mode] Endpoint:", fetchEndpoint);
              console.log("[Edit Mode] Query Params:", queryParams);
              console.log("[Edit Mode] Response Type:", typeof response);
              console.log("[Edit Mode] Response Keys:", Object.keys(response || {}));
              console.log("[Edit Mode] Response Values Sample:", response ? Object.keys(response).slice(0, 20).reduce((acc, key) => {
                const value = response[key];
                if (Array.isArray(value)) {
                  acc[key] = `[Array(${value.length})]`;
                } else if (typeof value === 'object' && value !== null) {
                  acc[key] = `[Object with keys: ${Object.keys(value).slice(0, 5).join(', ')}]`;
                } else {
                  acc[key] = String(value).substring(0, 100);
                }
                return acc;
              }, {} as Record<string, any>) : null);
              console.log("[Edit Mode] Full Response:", response);
              console.log("[Edit Mode] ====================================");
            }

            // Update form data with API response, preserving filled data
            // Get current form data from Redux store to preserve these values (initialFormData)
            const currentStoreFormData = getState().forms.formDataByScreen[targetScreenId] || {};

            const updatedFormData = buildFormDataFromRecord(response, {
              fallback: { ...currentStoreFormData, ...cleanRowData },
              screenId: targetScreenId,
            });

            // Inspection Create invoice reopen: drop any leaked invoice line arrays so
            // SubModule seeds from estimation lookups instead of saved service_invoice_* rows.
            // Also reseed footer (round off / excess) from estimation and clear sticky totals.
            if (isInspectionCreateInvoiceEdit(actionId, targetScreenId, childRecordId)) {
              stripInspectionInvoiceLineKeys(updatedFormData);
              await seedInspectionInvoiceFooterFromEstimation(
                updatedFormData,
                String(
                  response?.parentid ??
                    response?.parent_id ??
                    updatedFormData.parentid ??
                    updatedFormData.parent_id ??
                    ""
                ),
                (initialFormData.parentid_selectedData as Record<string, any>) ??
                  response
              );
              if (import.meta.env.DEV) {
                console.log(
                  "[Create Invoice] Inspection reopen — stripped line keys + seeded footer from estimation",
                  {
                    childRecordId,
                    screenId: targetScreenId,
                    round_off: updatedFormData.txt_round_off,
                    excess_charge: updatedFormData.txt_excess_charge,
                  }
                );
              }
            }

            // Job Card add: keep inspection id as parentid after merge (do not use response/cleanRowData id)
            if (
              targetScreenId === "156" &&
              !childRecordId &&
              (initialFormData.parentid || initialFormData.parent_id)
            ) {
              const jobCardAddId = initialFormData.parentid || initialFormData.parent_id;
              updatedFormData.parentid = jobCardAddId;
              updatedFormData.parent_id = jobCardAddId;
              applyEstimationRoundOffToFormData(
                updatedFormData,
                initialFormData.parentid_selectedData as Record<string, any> | undefined
              );
              if (import.meta.env.DEV) {
                console.log("[Workflow Navigation] Job card add - preserved parentid after merge:", jobCardAddId);
              }
            }

            // Invoice (158 / 173): ensure customer type and customer name are set for dropdowns from loaded invoice record
            if (response && (targetScreenId === "158" || targetScreenId === "173")) {
              const custType = response.customertype ?? response.customer_type ?? response.customer_type_id;
              if (custType !== undefined && custType !== null) {
                updatedFormData.s_select_customertype = custType;
                updatedFormData.customertype = custType;
              }
              const insName = response.insurance_name ?? response.insurance_id;
              if (insName !== undefined && insName !== null) {
                updatedFormData.s_select_insurance_customertype = insName;
                updatedFormData.insurance_name = insName;
              }
              if (
                !isInspectionCreateInvoiceEdit(actionId, targetScreenId, childRecordId)
              ) {
                await applyLatestEstimationRoundOffForInvoice(
                  updatedFormData,
                  String(
                    response.parentid ??
                      response.parent_id ??
                      initialFormData.parentid ??
                      initialFormData.parent_id ??
                      ""
                  ),
                  (initialFormData.parentid_selectedData as Record<string, any>) ??
                    response
                );
              }
              // Debug logging for edit mode prepopulation
              console.log("[Invoice Edit] Customer prepopulation check:", {
                responseKeys: Object.keys(response),
                customertype: {
                  customertype: response.customertype,
                  customer_type: response.customer_type,
                  customer_type_id: response.customer_type_id,
                  found: custType,
                  set: updatedFormData.s_select_customertype,
                },
                insurance_name: {
                  insurance_name: response.insurance_name,
                  insurance_id: response.insurance_id,
                  found: insName,
                  set: updatedFormData.s_select_insurance_customertype,
                },
              });
            }

            // CRITICAL: Ensure approve_status is explicitly included if present in response (Estimate Approval screen 150)
            // This matches Blazor behavior where approve_status is directly mapped from ItemData
            // Also accept next_state / status from module API response
            const approveStatusRaw =
              response?.approve_status ??
              response?.data?.approve_status ??
              response?.next_state ??
              response?.Next_State ??
              response?.status ??
              response?.Status;
            if (response && (approveStatusRaw !== undefined && approveStatusRaw !== null && approveStatusRaw !== "")) {
              const approveStatusValue = approveStatusRaw;
              if (approveStatusValue !== undefined && approveStatusValue !== null && approveStatusValue !== "") {
                updatedFormData.approve_status = approveStatusValue;

                if (import.meta.env.DEV) {
                  console.log(`[Workflow Navigation] ✓ Explicitly set approve_status from API response:`, {
                    approveStatusValue,
                    fromResponse: response.approve_status,
                    fromNestedData: response.data?.approve_status,
                    finalFormDataValue: updatedFormData.approve_status,
                    actionId,
                    screenId: targetScreenId,
                  });
                }

                // CRITICAL: Pre-fetch workflow options when approve_status has a value (edit mode)
                // This ensures options are available when ComponentRenderer renders the dropdown
                if (actionId === "Approve" || targetScreenId === "150") {
                  try {
                    const { apiService } = await import("../../services/api");
                    const workflow = await apiService.getWorkflow("service");
                    if (workflow && workflow.States && Array.isArray(workflow.States)) {
                      const workflowState = workflow.States.find(
                        (state: any) => state.Action === "Approve"
                      );
                      if (workflowState && workflowState.States && Array.isArray(workflowState.States)) {
                        const workflowOptions = workflowState.States.map((state: string) => ({
                          id: state,
                          name: state,
                          value: state,
                          label: state,
                        }));
                        const workflowLookupKey = `workflow_service_Approve`;
                        // Store in lookupsState using addLookupItem for each option
                        workflowOptions.forEach((option: any) => {
                          dispatch(
                            addLookupItem({
                              lookupKey: workflowLookupKey,
                              item: option,
                            })
                          );
                        });

                        if (import.meta.env.DEV) {
                          console.log(`[Workflow Navigation] ✓ Pre-fetched workflow options for approve_status:`, {
                            workflowLookupKey,
                            optionsCount: workflowOptions.length,
                            options: workflowOptions.map((opt: any) => ({ value: opt.value, label: opt.label })),
                            approveStatusValue,
                            valueMatches: workflowOptions.some((opt: any) =>
                              String(opt.value).toLowerCase() === String(approveStatusValue).toLowerCase()
                            ),
                          });
                        }
                      }
                    }
                  } catch (workflowError) {
                    console.error(`[Workflow Navigation] Failed to pre-fetch workflow options:`, workflowError);
                    // Continue - ComponentRenderer will fetch them later
                  }
                }
              }
            }

            if (import.meta.env.DEV) {
              console.log("[Edit Mode] Form data mapped from API response:", {
                mappedKeys: Object.keys(updatedFormData),
                mappedSample: Object.keys(updatedFormData).slice(0, 15).reduce((acc, key) => {
                  const value = updatedFormData[key];
                  acc[key] = Array.isArray(value)
                    ? `[Array(${value.length})]`
                    : typeof value === 'object'
                      ? '[Object]'
                      : value;
                  return acc;
                }, {} as Record<string, any>),
              });

              // Special logging for estimation fields
              if (actionId === "Estimate" || targetScreenId === "116") {
                console.log("[Edit Mode] ===== Estimation Fields Check =====");
                console.log("[Edit Mode] txt_estimate_number (est_no):", {
                  inFormData: updatedFormData.txt_estimate_number,
                  inResponse: response?.est_no,
                  mapped: !!updatedFormData.txt_estimate_number,
                });
                console.log("[Edit Mode] dt_est_date (est_date):", {
                  inFormData: updatedFormData.dt_est_date,
                  inResponse: response?.est_date,
                  mapped: !!updatedFormData.dt_est_date,
                });
                console.log("[Edit Mode] ====================================");
              }
            }

            // Purchase Order vendor prefill fallback (workflow edit path).
            if (String(targetScreenId) === "130") {
              const supplierId =
                (response as any)?.supplier_id ??
                (response as any)?.sup_id ??
                (updatedFormData as any)?.supplier_id ??
                (initialFormData as any)?.supplier_id ??
                (initialFormData as any)?.sup_id;
              const supplierName =
                (response as any)?.supplier_id_desc ??
                (response as any)?.supplier_name ??
                (response as any)?.suppliername ??
                (initialFormData as any)?.supplier_id_desc ??
                (initialFormData as any)?.supplier_name ??
                (initialFormData as any)?.suppliername;
              if (supplierId !== undefined && supplierId !== null && String(supplierId).trim() !== "") {
                (updatedFormData as any).drp_supplier = supplierId;
                if (supplierName && String(supplierName).trim() !== "") {
                  const supplierSelected = {
                    id: supplierId,
                    value: supplierId,
                    label: supplierName,
                    name: supplierName,
                  };
                  (updatedFormData as any).drp_supplier_selectedData = supplierSelected;
                  dispatch(
                    addLookupItem({
                      lookupKey: "supplier",
                      item: supplierSelected,
                    })
                  );
                }
              }
            }

            // CRITICAL: Only set edit mode if we actually fetched a record with an ID
            // If childRecordId exists, we're in edit mode (record already exists)
            // If childRecordId doesn't exist but we fetched data, check if response has an ID
            const responseHasId = response && response.id && String(response.id).trim().length > 0;
            const shouldBeEditMode = !!(childRecordId && childRecordId.toString().length > 0) || responseHasId;

            if (shouldBeEditMode) {
              const actualRecordId = childRecordId || response?.id;
              updatedFormData.id = actualRecordId;

              // Set edit mode in Redux store if not already set
              const editModeInfo = getState().forms.editModeByScreen[targetScreenId];
              if (!editModeInfo?.isEditMode) {
                dispatch(
                  setScreenEditMode({
                    screenId: targetScreenId,
                    isEditMode: true,
                    recordId: actualRecordId?.toString() || "",
                  })
                );
              }

              if (import.meta.env.DEV) {
                console.log("[Workflow Navigation] Loaded child record in edit/view mode:", {
                  screenId: targetScreenId,
                  childRecordId,
                  responseId: response?.id,
                  actualRecordId,
                  actionId,
                  recordData: response,
                });
              }
            } else {
              // CREATE mode: ensure edit mode is NOT set
              const editModeInfo = getState().forms.editModeByScreen[targetScreenId];
              if (editModeInfo?.isEditMode) {
                dispatch(
                  setScreenEditMode({
                    screenId: targetScreenId,
                    isEditMode: false,
                    recordId: "",
                  })
                );
              }

              if (import.meta.env.DEV) {
                console.log("[Workflow Navigation] CREATE mode - edit mode cleared:", {
                  screenId: targetScreenId,
                  actionId,
                  childRecordId,
                  responseHasId,
                });
              }
            }

            // Ensure parent_id is set from ActionArgs in add mode
            if (actionId === "Estimate" && !childRecordId && processedActionArgs.parentid) {
              updatedFormData.parentid = processedActionArgs.parentid;
              updatedFormData.parent_id = processedActionArgs.parentid;
            }

            // Job Card add: ensure parentid stays inspection id after all merges
            if (
              (actionId === "Jobcard" || actionId === "Job card" || targetScreenId === "156") &&
              !childRecordId &&
              (initialFormData.parentid || initialFormData.parent_id)
            ) {
              const jobCardAddId = initialFormData.parentid || initialFormData.parent_id;
              updatedFormData.parentid = jobCardAddId;
              updatedFormData.parent_id = jobCardAddId;
              applyEstimationRoundOffToFormData(
                updatedFormData,
                initialFormData.parentid_selectedData as Record<string, any> | undefined
              );
            }

            // CRITICAL: In edit mode, handle Search_Select fields with _id and _id_desc pattern
            // This ensures SearchableDropdown, BindParent, and Report components work correctly
            if (childRecordId) {
              // Get screen config to find Search_Select fields
              const screenConfig =
                getResolvedScreenConfig(targetScreenId) ||
                getResolvedScreenConfig(targetView) ||
                getResolvedScreenConfig(deriveScreenId(targetScreenId) || "") ||
                null;

              if (screenConfig) {
                // Helper function to find all SearchableDropdown fields in config
                const findSearchableDropdownFields = (components: any[]): any[] => {
                  const fields: any[] = [];
                  const walk = (comps: any[]) => {
                    comps.forEach((comp: any) => {
                      if (comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) {
                        fields.push(comp);
                      }
                      if (comp.Fields && Array.isArray(comp.Fields)) {
                        walk(comp.Fields);
                      }
                      if (comp.childComponents && Array.isArray(comp.childComponents)) {
                        walk(comp.childComponents);
                      }
                      if (comp.childControls && Array.isArray(comp.childControls)) {
                        walk(comp.childControls);
                      }
                    });
                  };
                  walk(components);
                  return fields;
                };

                const searchableFields = findSearchableDropdownFields(screenConfig);

                // Process each SearchableDropdown field
                for (const field of searchableFields) {
                  const fieldName = field.name || field.BindValue;
                  const jsonAttr = field.Json_Attribute || field.json_Attribute;
                  const lookupName = field.LookupName || field.lookupName;

                  if (!fieldName || !jsonAttr || !lookupName) continue;

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] Processing Search_Select field:", {
                      fieldName,
                      jsonAttr,
                      lookupName,
                      componentType: field.componentType,
                    });
                  }

                  // Check if we have both _id and _id_desc in the response
                  // Check original response first, then updatedFormData (which may have mapped fields)
                  // Special handling: For parent_id field, also check inspection_id (common in estimation forms)
                  let idValue = response?.[jsonAttr] || response?.[fieldName] ||
                    updatedFormData[jsonAttr] || updatedFormData[fieldName];

                  // Special case: If looking for parent_id and not found, try inspection_id
                  if ((!idValue || idValue === undefined || idValue === null || idValue === "") &&
                    (jsonAttr === "parent_id" || isParentIdBindValue(fieldName))) {
                    idValue = response?.inspection_id || updatedFormData?.inspection_id;
                    if (import.meta.env.DEV && idValue) {
                      console.log("[Edit Mode] Mapped inspection_id to parent_id:", {
                        inspection_id: response?.inspection_id,
                        parent_id: idValue,
                      });
                    }
                  }

                  let descValue = response?.[`${jsonAttr}_desc`] || response?.[`${fieldName}_desc`] ||
                    updatedFormData[`${jsonAttr}_desc`] || updatedFormData[`${fieldName}_desc`] ||
                    response?.[`${jsonAttr.toLowerCase()}_desc`] || response?.[`${fieldName.toLowerCase()}_desc`];

                  // Special case: If looking for parent_id_desc and not found, try inspection_id_desc or vin_no
                  if ((!descValue || descValue === undefined || descValue === null || descValue === "") &&
                    (jsonAttr === "parent_id" || isParentIdBindValue(fieldName))) {
                    descValue = response?.inspection_id_desc || response?.vin_no || updatedFormData?.vin_no;
                    if (import.meta.env.DEV && descValue) {
                      console.log("[Edit Mode] Mapped vin_no/inspection_id_desc to parent_id_desc:", {
                        vin_no: response?.vin_no,
                        inspection_id_desc: response?.inspection_id_desc,
                        parent_id_desc: descValue,
                      });
                    }
                  }

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] Search_Select field values:", {
                      fieldName,
                      jsonAttr,
                      idValue,
                      descValue,
                      checkedKeys: {
                        [`response[${jsonAttr}]`]: response?.[jsonAttr],
                        [`response[${jsonAttr}_desc]`]: response?.[`${jsonAttr}_desc`],
                        [`updatedFormData[${fieldName}]`]: updatedFormData[fieldName],
                      },
                    });
                  }

                  if (idValue && String(idValue).trim().length > 0) {
                    // Set the field value
                    updatedFormData[fieldName] = idValue;

                    // Try to get parent record from lookup rawData first
                    let parentRecord = null;
                    const lookupsState = getState().lookups;
                    const lookupRawData = lookupsState.rawData[lookupName] || [];
                    parentRecord = lookupRawData.find((item: any) =>
                      item.id === idValue || String(item.id) === String(idValue) ||
                      item.value === idValue || String(item.value) === String(idValue)
                    );

                    // If not found and we have desc value, create a LookupItem from backend data
                    if (!parentRecord && descValue) {
                      // Create LookupItem from backend response (parent_id + parent_id_desc)
                      const lookupItem = {
                        id: idValue,
                        value: idValue,
                        label: descValue,
                        name: descValue,
                      };

                      // Add to lookup options for SearchableDropdown display
                      dispatch(
                        addLookupItem({
                          lookupKey: lookupName,
                          item: lookupItem,
                        })
                      );

                      if (import.meta.env.DEV) {
                        console.log("[Workflow Navigation] Created LookupItem from _id_desc:", {
                          fieldName,
                          jsonAttr,
                          lookupName,
                          idValue,
                          descValue,
                        });
                      }
                    }

                    // Fetch full parent record for BindParent and Report
                    if (!parentRecord) {
                      try {
                        const parentResponse = await apiService.get(
                          `/pabws/utility/${lookupName}`,
                          { id: idValue },
                          {
                            includeAuthToken: true,
                            authHeaderType: "Bearer",
                            errorHandling: "return-empty",
                            transformResponse: (raw: any) => {
                              if (!raw || !raw.success) return null;
                              if (Array.isArray(raw.data)) return raw.data[0] || null;
                              return raw.data || null;
                            },
                          }
                        );
                        parentRecord = parentResponse;

                        // Add to lookup options and rawData
                        if (parentRecord) {
                          dispatch(
                            addLookupItem({
                              lookupKey: lookupName,
                              item: {
                                ...parentRecord,
                                label: parentRecord.name || parentRecord.id || descValue,
                                value: parentRecord.id || idValue,
                              },
                            })
                          );

                          dispatch(
                            addLookupItem({
                              lookupKey: lookupName,
                              item: parentRecord,
                              isRawData: true,
                            })
                          );
                        }
                      } catch (parentError) {
                        console.error(`[Workflow Navigation] Failed to load parent record for ${fieldName}:`, parentError);
                      }
                    }

                    // Store parent record in formData for Report component and BindParent
                    if (parentRecord) {
                      if (import.meta.env.DEV) {
                        console.log("[Edit Mode] Storing parent record for Search_Select:", {
                          fieldName,
                          jsonAttr,
                          lookupName,
                          parentRecordKeys: Object.keys(parentRecord),
                          parentRecordSample: Object.keys(parentRecord).slice(0, 9999).reduce((acc, key) => {
                            acc[key] = parentRecord[key];
                            return acc;
                          }, {} as Record<string, any>),
                        });
                      }
                      const selectedDataKey = `${fieldName}_selectedData`;
                      updatedFormData[selectedDataKey] = parentRecord;

                      // Process BindParent fields
                      const findBindParentFields = (components: any[]): any[] => {
                        const bindParentFields: any[] = [];
                        const walk = (comps: any[]) => {
                          comps.forEach((comp: any) => {
                            if (comp.BindParent && comp.BindParent[fieldName]) {
                              bindParentFields.push({
                                field: comp,
                                bindColumn: comp.BindParent[fieldName],
                              });
                            }
                            if (comp.Fields && Array.isArray(comp.Fields)) {
                              walk(comp.Fields);
                            }
                            if (comp.childComponents && Array.isArray(comp.childComponents)) {
                              walk(comp.childComponents);
                            }
                            if (comp.childControls && Array.isArray(comp.childControls)) {
                              walk(comp.childControls);
                            }
                          });
                        };
                        walk(components);
                        return bindParentFields;
                      };

                      const bindParentFields = findBindParentFields(screenConfig);
                      bindParentFields.forEach(({ field, bindColumn }) => {
                        const targetFieldName = field.name || field.BindValue;
                        if (targetFieldName && parentRecord[bindColumn] !== undefined) {
                          updatedFormData[targetFieldName] = parentRecord[bindColumn];

                          if (import.meta.env.DEV) {
                            console.log("[Workflow Navigation] Populated BindParent field:", {
                              sourceField: fieldName,
                              targetField: targetFieldName,
                              bindColumn,
                              value: parentRecord[bindColumn],
                            });
                          }
                        }
                      });

                      if (import.meta.env.DEV) {
                        console.log("[Workflow Navigation] Loaded parent record for Search_Select field:", {
                          fieldName,
                          jsonAttr,
                          lookupName,
                          parentId: idValue,
                          parentRecordName: parentRecord.name || descValue,
                          selectedDataKey,
                        });
                      }
                    }
                  }
                }
              }
            }

            // CRITICAL: Preserve workflow fields (next_action, next_state) from current form data
            // Get current form data from Redux store to preserve these values
            const currentFormData = getState().forms.formDataByScreen[targetScreenId] || {};
            if (currentFormData.next_action) {
              updatedFormData.next_action = currentFormData.next_action;
            } else if (initialFormData.next_action) {
              // Fallback to initialFormData if not in store yet
              updatedFormData.next_action = initialFormData.next_action;
            }
            if (currentFormData.next_state) {
              updatedFormData.next_state = currentFormData.next_state;
            } else if (initialFormData.next_state) {
              // Fallback to initialFormData if not in store yet
              updatedFormData.next_state = initialFormData.next_state;
            }

            // Also preserve from workflowItem as final fallback
            if (!updatedFormData.next_action && (workflowItem.Action || actionId)) {
              updatedFormData.next_action = workflowItem.Action || actionId;
            }
            if (!updatedFormData.next_state && workflowItem.States && workflowItem.States[0]) {
              updatedFormData.next_state = workflowItem.States[0];
            }

            if (import.meta.env.DEV) {
              console.log("[Workflow Navigation] Preserved workflow fields:", {
                next_action: updatedFormData.next_action,
                next_state: updatedFormData.next_state,
                source: {
                  fromStore: !!(currentFormData.next_action || currentFormData.next_state),
                  fromInitial: !!(initialFormData.next_action || initialFormData.next_state),
                  fromWorkflowItem: !!(workflowItem.Action || workflowItem.States?.[0]),
                },
              });
            }

            // CRITICAL: Update formData BEFORE loading submodules/reports so components can detect the data
            if (import.meta.env.DEV) {
              console.log("[Edit Mode] ===== Setting Form Data in Redux =====");
              console.log("[Edit Mode] Screen ID:", targetScreenId);
              console.log("[Edit Mode] Form data keys:", Object.keys(updatedFormData));
              console.log("[Edit Mode] Form data summary:", {
                id: updatedFormData.id,
                recordId: childRecordId,
                isEditMode: !!childRecordId,
                fieldCount: Object.keys(updatedFormData).length,
                subModuleFields: Object.keys(updatedFormData).filter(k =>
                  k.startsWith('sub_') || k.startsWith('multi_')
                ),
                searchSelectFields: Object.keys(updatedFormData).filter(k =>
                  k.includes('_selectedData')
                ),
                sampleFields: Object.keys(updatedFormData).slice(0, 20).reduce((acc, key) => {
                  const value = updatedFormData[key];
                  acc[key] = Array.isArray(value)
                    ? `[Array(${value.length})]`
                    : typeof value === 'object' && value !== null
                      ? '[Object]'
                      : String(value).substring(0, 50);
                  return acc;
                }, {} as Record<string, any>),
              });
              console.log("[Edit Mode] Full form data:", updatedFormData);
              console.log("[Edit Mode] =======================================");
            }

            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: updatedFormData,
              })
            );

            // CRITICAL: In edit/view mode, fetch submodule data separately (like Blazor)
            // This is a GENERIC implementation that works for ANY screen with submodules
            // Pattern: GET /pabws/submodule/{tableName}/{recordId}
            // Works for: estimation_add, jobcard_add, invoice_add, or any other screen with SUB_MODULE components
            if (childRecordId && childRecordId.toString().length > 0) {
              if (import.meta.env.DEV) {
                console.log("[Edit Mode] ===== Loading SubModule Data =====");
                console.log("[Edit Mode] Record ID:", childRecordId);
                console.log("[Edit Mode] Screen ID:", targetScreenId);
              }
              try {
                // Get the screen config to find submodules (generic - works for any sc                // Use getResolvedScreenConfig to get proper API configuration if loaded locally
                const screenConfig =
                  getResolvedScreenConfig(targetScreenId) ||
                  getResolvedScreenConfig(targetView) ||
                  getResolvedScreenConfig(deriveScreenId(targetScreenId) || "") ||
                  null;

                if (screenConfig) {
                  // GENERIC: Find all REPORT components to load parent data
                  const findReportComponents = (components: any[]): any[] => {
                    const reports: any[] = [];
                    components.forEach((comp) => {
                      if (comp.componentType === ComponentType.REPORT) {
                        reports.push(comp);
                      }
                      if (comp.childComponents && Array.isArray(comp.childComponents)) {
                        reports.push(...findReportComponents(comp.childComponents));
                      }
                      if (comp.Fields && Array.isArray(comp.Fields)) {
                        reports.push(...findReportComponents(comp.Fields));
                      }
                      if (comp.childControls && Array.isArray(comp.childControls)) {
                        reports.push(...findReportComponents(comp.childControls));
                      }
                    });
                    return reports;
                  };

                  // GENERIC: Recursively find all SUB_MODULE components in the config
                  // This works for any screen structure (nested groups, forms, etc.)
                  const findSubModules = (components: any[]): any[] => {
                    const subModules: any[] = [];
                    components.forEach((comp) => {
                      if (
                        comp.componentType === ComponentType.SUB_MODULE ||
                        comp.componentType === ComponentType.MULTI_SUB_LIST ||
                        (comp.componentType === ComponentType.MULTI_CHECKBOX &&
                          (comp.TableName || comp.tableName))
                      ) {
                        subModules.push(comp);
                      }
                      // Recursively search nested components
                      if (comp.childComponents && Array.isArray(comp.childComponents)) {
                        subModules.push(...findSubModules(comp.childComponents));
                      }
                      if (comp.Fields && Array.isArray(comp.Fields)) {
                        subModules.push(...findSubModules(comp.Fields));
                      }
                      if (comp.childControls && Array.isArray(comp.childControls)) {
                        subModules.push(...findSubModules(comp.childControls));
                      }
                    });
                    return subModules;
                  };

                  const subModules = findSubModules(screenConfig);
                  const reportComponents = findReportComponents(screenConfig);

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] Found SubModules:", {
                      count: subModules.length,
                      subModules: subModules.map((sm: any) => ({
                        name: sm.Name || sm.name,
                        tableName: sm.TableName || sm.tableName,
                        bindValue: sm.BindValue || sm.bindValue,
                        parentField: sm.Parent_Field || sm.parentField,
                      })),
                    });
                    console.log("[Edit Mode] Found Report components:", {
                      count: reportComponents.length,
                      reports: reportComponents.map((r: any) => ({
                        settings: r.Settings || r.settings,
                        parentField: r.Parent_Field || r.parentField,
                      })),
                    });
                  }

                  // Load parent records for Report components in edit mode
                  if (reportComponents.length > 0) {
                    const reportDataPromises = reportComponents.map(async (reportComp) => {
                      const parentField = reportComp.Parent_Field || reportComp.parentField;
                      const parentFieldLookupName = reportComp.LookupName || reportComp.lookupName;

                      if (!parentField) {
                        return null;
                      }

                      // Get parentId from updatedFormData or from current formData (which was just updated)
                      // In edit mode, parent_id comes from the loaded record
                      const currentFormData = getState().forms.formDataByScreen[targetScreenId] || {};
                      // Get parent ID using the general helper function
                      // Check both updatedFormData and currentFormData (updatedFormData takes precedence)
                      const combinedFormData = { ...currentFormData, ...updatedFormData };
                      const parentId = getParentIdFromFormData(combinedFormData, parentField);

                      if (!parentId || parentId === "" || parentId === null || parentId === undefined) {
                        if (import.meta.env.DEV) {
                          console.log("[Workflow Navigation] Report component parent field not set:", {
                            parentField,
                            reportSettings: reportComp.Settings || reportComp.settings,
                            availableFields: Object.keys({ ...updatedFormData, ...currentFormData }),
                            checkedInspectionId: parentField === "parentid",
                          });
                        }
                        return null;
                      }

                      try {
                        // Try to get from lookup rawData first
                        let parentRecord = null;
                        if (parentFieldLookupName) {
                          const lookupKey = parentFieldLookupName;
                          const rawData = getState().lookups.rawData[lookupKey] || [];
                          parentRecord = rawData.find((item: any) =>
                            item.id === parentId || String(item.id) === String(parentId)
                          );
                        }

                        // If not found, fetch from API
                        if (!parentRecord && parentFieldLookupName) {
                          try {
                            if (parentFieldLookupName === "inspection_items") {
                              const parentResponse = await apiService.get(
                                `/pabws/utility/${parentFieldLookupName}`,
                                { id: parentId },
                                {
                                  includeAuthToken: true,
                                  authHeaderType: "Bearer",
                                  errorHandling: "return-empty",
                                  transformResponse: (raw: any) => {
                                    if (!raw || !raw.success) return null;
                                    if (Array.isArray(raw.data)) return raw.data[0] || null;
                                    return raw.data || null;
                                  },
                                }
                              );
                              parentRecord = parentResponse;
                            }
                          } catch (fetchError) {
                            console.error(`[Workflow Navigation] Failed to fetch parent for Report:`, fetchError);
                          }
                        }

                        if (parentRecord) {
                          const selectedDataKey = `${parentField}_selectedData`;
                          updatedFormData[selectedDataKey] = parentRecord;

                          // Also ensure parentid field is set for SearchableDropdown
                          updatedFormData[parentField] = parentId;
                          updatedFormData.parentid = parentId;

                          // Add to lookup options for SearchableDropdown display
                          if (parentFieldLookupName) {
                            dispatch(
                              addLookupItem({
                                lookupKey: parentFieldLookupName,
                                item: {
                                  ...parentRecord,
                                  label: parentRecord.name || parentRecord.id,
                                  value: parentRecord.id,
                                },
                              })
                            );

                            dispatch(
                              addLookupItem({
                                lookupKey: parentFieldLookupName,
                                item: parentRecord,
                                isRawData: true,
                              })
                            );
                          }

                          // Populate customer_remarks via BindParent if available
                          if (parentRecord.customer_remarks !== undefined) {
                            updatedFormData.customer_remarks = parentRecord.customer_remarks;
                          }

                          if (import.meta.env.DEV) {
                            console.log("[Workflow Navigation] Loaded parent record for Report:", {
                              parentField,
                              parentId,
                              reportSettings: reportComp.Settings || reportComp.settings,
                              parentRecordName: parentRecord.name,
                              customer_remarks: parentRecord.customer_remarks,
                              selectedDataKey,
                            });
                          }
                        }

                        return { parentField, parentRecord };
                      } catch (error) {
                        return null;
                      }
                    });

                    await Promise.all(reportDataPromises);

                    // Update formData with Report parent data
                    dispatch(
                      setScreenFormData({
                        screenId: targetScreenId,
                        data: updatedFormData,
                      })
                    );
                  }

                  if (subModules.length > 0 && import.meta.env.DEV) {
                    console.log("[Workflow Navigation] Found submodules to fetch:", {
                      screenId: targetScreenId,
                      childRecordId,
                      subModules: subModules.map((sm) => ({
                        name: sm.name || sm.Name,
                        tableName: sm.TableName || sm.tableName,
                        bindValue: sm.BindValue || sm.name,
                      })),
                    });
                  }

                  // Fetch each submodule's data separately
                  const skipInvoiceLinesForEstimationSeed =
                    isInspectionCreateInvoiceEdit(actionId, targetScreenId, childRecordId);
                  const subModuleDataPromises = subModules.map(async (subModule) => {
                    if (isLookupOnlySubModule(subModule)) {
                      return null;
                    }

                    const moduleName =
                      resolveSubModulePersistedModuleName(subModule) ||
                      String(
                        subModule?.Name ||
                          subModule?.name ||
                          subModule?.TableName ||
                          subModule?.tableName ||
                          ""
                      ).trim();
                    // Match ComponentRenderer fieldName: name || BindValue
                    const bindValue =
                      subModule.name ||
                      subModule.BindValue ||
                      subModule.bindValue;
                    const alternateBindValue =
                      subModule.BindValue || subModule.bindValue;

                    // Inspection Create invoice: do not load saved invoice lines — SubModule
                    // will auto-populate from invoice_labour / invoice_miscellaneous / invoice_items.
                    if (
                      skipInvoiceLinesForEstimationSeed &&
                      isInspectionInvoiceLineModuleName(moduleName)
                    ) {
                      if (import.meta.env.DEV) {
                        console.log(
                          "[Create Invoice] Inspection reopen — skip submodule fetch for estimation seed",
                          { moduleName, bindValue, recordId: childRecordId }
                        );
                      }
                      return null;
                    }

                    if (import.meta.env.DEV) {
                      console.log("[Workflow Navigation] Submodule field mapping:", {
                        moduleName,
                        name: subModule.name,
                        Name: subModule.Name,
                        BindValue: subModule.BindValue,
                        usingBindValue: bindValue,
                      });
                    }

                    if (!moduleName) {
                      if (import.meta.env.DEV) {
                        console.warn("[Workflow Navigation] Submodule missing module name:", {
                          subModule,
                          bindValue,
                        });
                      }
                      return null;
                    }

                    try {
                      if (import.meta.env.DEV) {
                        console.log("[Workflow Navigation] Fetching submodule data:", {
                          moduleName,
                          bindValue,
                          recordId: childRecordId,
                        });
                      }

                      const subModuleResponse = await fetchSubModuleRowsForEdit(
                        (name, id) => apiService.getSubModuleData(name, id),
                        subModule,
                        childRecordId
                      );

                      if (subModuleResponse && Array.isArray(subModuleResponse) && subModuleResponse.length > 0) {
                        if (import.meta.env.DEV) {
                          console.log("[Workflow Navigation] Loaded submodule data:", {
                            moduleName,
                            bindValue,
                            recordCount: subModuleResponse.length,
                            firstRecord: subModuleResponse[0],
                          });
                        }

                        // MULTI_CHECKBOX: keep full rows (persisted id + lookup id)
                        // so edit saves can use act "edit" like MULTI_SUB_LIST.
                        let data: any = subModuleResponse;
                        if (
                          subModule.componentType === ComponentType.MULTI_CHECKBOX
                        ) {
                          const jsonAttr =
                            subModule.Json_Attribute ||
                            subModule.json_Attribute ||
                            "work_requested_id";
                          data = mapMultiCheckboxApiRowsToFormRows(
                            subModuleResponse,
                            jsonAttr
                          );
                        }

                        return {
                          bindValue,
                          alternateBindValue,
                          tableName: subModule.TableName || subModule.tableName,
                          data,
                        };
                      } else {
                        if (import.meta.env.DEV) {
                          console.log("[Workflow Navigation] Submodule returned empty data:", {
                            moduleName,
                            bindValue,
                          });
                        }
                        return {
                          tableName: subModule.TableName || subModule.tableName,
                          bindValue,
                          alternateBindValue,
                          data: [],
                        };
                      }
                    } catch (subModuleError) {
                      console.error(
                        `[Workflow Navigation] Failed to fetch submodule ${moduleName}:`,
                        subModuleError
                      );
                      return {
                        bindValue,
                        data: [],
                      };
                    }
                  });

                  // Wait for all submodule data to be fetched
                  const subModuleDataResults = await Promise.all(subModuleDataPromises);

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] SubModule data fetch results:", {
                      totalSubModules: subModuleDataResults.length,
                      successful: subModuleDataResults.filter(r => r && r.data).length,
                      results: subModuleDataResults.map((r: any) => ({
                        bindValue: r?.bindValue,
                        recordCount: Array.isArray(r?.data) ? r.data.length : 0,
                        hasData: !!r?.data,
                      })),
                    });
                  }

                  // Update form data with submodule data
                  // CRITICAL: Merge with existing formData to preserve all fields (including Report parent data)
                  const currentFormData = getState().forms.formDataByScreen[targetScreenId] || {};
                  const updatedFormDataWithSubModules = { ...currentFormData, ...updatedFormData };

                  // Inspection Create invoice reopen: ensure line keys stay absent so estimation
                  // lookups populate (currentFormData may still hold prior service_invoice_* rows).
                  if (skipInvoiceLinesForEstimationSeed) {
                    stripInspectionInvoiceLineKeys(updatedFormDataWithSubModules);
                    for (const key of INSPECTION_INVOICE_EXPRESSION_FOOTER_KEYS) {
                      delete updatedFormDataWithSubModules[key];
                    }
                    // Keep estimation-seeded editable footer if present on updatedFormData.
                    if (updatedFormData.txt_round_off != null) {
                      updatedFormDataWithSubModules.txt_round_off =
                        updatedFormData.txt_round_off;
                      updatedFormDataWithSubModules.round_off_amt =
                        updatedFormData.round_off_amt ?? updatedFormData.txt_round_off;
                    }
                    if (updatedFormData.txt_excess_charge != null) {
                      updatedFormDataWithSubModules.txt_excess_charge =
                        updatedFormData.txt_excess_charge;
                      updatedFormDataWithSubModules.excess_charge =
                        updatedFormData.excess_charge ??
                        updatedFormData.txt_excess_charge;
                    }
                  }

                  subModuleDataResults.forEach((result) => {
                    if (result && result.bindValue && result.data) {
                      // Ensure data is an array
                      const subModuleData = Array.isArray(result.data) ? result.data : [];

                      // CRITICAL: If the dedicated submodule endpoint returns an empty array,
                      // do NOT overwrite already-prefilled saved rows from the main record payload.
                      // This prevents template auto-population from kicking in during edit/draft.
                      const existing = updatedFormDataWithSubModules[result.bindValue];
                      const existingHasRows =
                        Array.isArray(existing) && existing.length > 0;
                      if (subModuleData.length === 0 && existingHasRows) {
                        return;
                      }

                      updatedFormDataWithSubModules[result.bindValue] = subModuleData;

                      // Also store under alternate BindValue if it exists and differs
                      if (result.alternateBindValue && result.alternateBindValue !== result.bindValue) {
                        updatedFormDataWithSubModules[result.alternateBindValue] = subModuleData;
                      }

                      // Also store under TableName if available (for robustness)
                      if (result.tableName && result.tableName !== result.bindValue) {
                        updatedFormDataWithSubModules[result.tableName] = subModuleData;
                      }
                    }
                  });

                  // Re-apply SearchableDropdown selectedData after submodule merge.
                  // Workflow edit flow can overwrite form state and lose display label objects.
                  const ensureSearchableSelectedData = (components: any[]) => {
                    const walk = (comps: any[]) => {
                      comps.forEach((comp: any) => {
                        if (comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) {
                          const fieldName = comp.name || comp.BindValue;
                          const jsonAttr = comp.Json_Attribute || comp.json_Attribute;
                          const lookupName = comp.LookupName || comp.lookupName;
                          if (!fieldName || !jsonAttr || !lookupName) return;

                          const idValue =
                            updatedFormDataWithSubModules[fieldName] ??
                            updatedFormDataWithSubModules[jsonAttr] ??
                            (response as any)?.[jsonAttr] ??
                            (response as any)?.[fieldName];
                          if (idValue === undefined || idValue === null || String(idValue).trim() === "") return;

                          let descValue =
                            (response as any)?.[`${jsonAttr}_desc`] ??
                            (response as any)?.[`${fieldName}_desc`] ??
                            updatedFormDataWithSubModules[`${jsonAttr}_desc`] ??
                            updatedFormDataWithSubModules[`${fieldName}_desc`] ??
                            (response as any)?.[`${jsonAttr}_name`] ??
                            (response as any)?.[`${fieldName}_name`];

                          if (!descValue && jsonAttr === "supplier_id") {
                            descValue =
                              (response as any)?.supplier_id_desc ??
                              (response as any)?.supplier_name ??
                              (response as any)?.suppliername ??
                              (response as any)?.vendor_name ??
                              (response as any)?.vendor;
                          }

                          updatedFormDataWithSubModules[fieldName] = idValue;
                          if (descValue) {
                            const selectedDataKey = `${fieldName}_selectedData`;
                            const lookupItem = {
                              id: idValue,
                              value: idValue,
                              label: descValue,
                              name: descValue,
                            };
                            updatedFormDataWithSubModules[selectedDataKey] = lookupItem;
                            dispatch(
                              addLookupItem({
                                lookupKey: lookupName,
                                item: lookupItem,
                              })
                            );
                          }
                        }

                        if (comp.Fields && Array.isArray(comp.Fields)) walk(comp.Fields);
                        if (comp.childComponents && Array.isArray(comp.childComponents)) walk(comp.childComponents);
                        if (comp.childControls && Array.isArray(comp.childControls)) walk(comp.childControls);
                      });
                    };
                    walk(components);
                  };
                  ensureSearchableSelectedData(screenConfig);



                  // Update form data with submodule data - this triggers re-render
                  dispatch(
                    setScreenFormData({
                      screenId: targetScreenId,
                      data: updatedFormDataWithSubModules,
                    })
                  );

                  if (import.meta.env.DEV) {
                    console.log("[Workflow Navigation] Final formData after submodule update:", {
                      screenId: targetScreenId,
                      subModuleKeys: subModuleDataResults
                        .filter((r): r is NonNullable<typeof r> => r !== null && r !== undefined && !!r.bindValue)
                        .map((r) => ({ key: r.bindValue, count: Array.isArray(r.data) ? r.data.length : 0 })),
                      hasParentData: !!updatedFormDataWithSubModules.parentid_selectedData,
                    });
                  }

                  if (import.meta.env.DEV) {
                    console.log("[Workflow Navigation] All submodules loaded:", {
                      screenId: targetScreenId,
                      childRecordId,
                      subModulesLoaded: subModuleDataResults.filter((r) => r !== null).length,
                    });
                  }
                }
              } catch (subModuleError) {
                console.error(
                  "[Workflow Navigation] Error fetching submodules:",
                  subModuleError
                );
                // Continue even if submodule fetch fails - main record is already loaded
              }
            }
          }
        } catch (error) {
          // On error, keep the initial data from the row
          console.error(`Failed to fetch workflow data for ${actionId}:`, error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                "Unable to load details. Showing available data from the list.",
              type: "error",
            },
            5000
          );
        } finally {
          beginExpressionCalculatingIfNeeded(dispatch, targetScreenId);
          dispatch(
            setScreenLoading({ screenId: targetScreenId, isLoading: false })
          );
        }

        return;
      }

      // Handle other specific actions
      switch (actionId) {
        case "publish_calendar_start": {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const viewStr = typeof currentView === "string" ? currentView : "";
          const { forms } = getState();
          const formDataForScreen =
            (forms.formDataByScreen && forms.formDataByScreen[String(currentScreenId)]) || {};

          // Prefer the loaded record id from form state. Falling back to parsing the URL
          // can accidentally pick up the screen id (e.g. "2220") instead of the row id.
          const urlLastSegment = viewStr.split("/").filter(Boolean).pop() || "";
          const recordId =
            (formDataForScreen as any)?.id ||
            (urlLastSegment && urlLastSegment !== String(currentScreenId) ? urlLastSegment : "");

          if (!recordId) {
            ensureSlidingTimeout(dispatch, { message: "Missing holiday calendar id.", type: "error" }, 5000);
            return;
          }

          const encryptedUserData =
            typeof window !== "undefined" ? localStorage.getItem("encryptedUserData") : null;
          if (!encryptedUserData) {
            ensureSlidingTimeout(dispatch, { message: "User not authenticated. Please login again.", type: "error" }, 5000);
            return;
          }

          const decryptedData = authService.decryptData(encryptedUserData);
          if (!decryptedData) {
            ensureSlidingTimeout(dispatch, { message: "Failed to decrypt user data. Please login again.", type: "error" }, 5000);
            return;
          }

          const userId =
            decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

          // Update holiday_calendar.status to "Published"
          try {
            ensureSlidingTimeout(
              dispatch,
              { message: "Publishing calendar...", type: "success" },
              3000
            );

            const response = await apiService.post(
              `/pabws/module/holiday_calendar_details/update`,
              {
                data: {
                  id: recordId,
                  status: "Published",
                  modified_by: userId,
                },
              },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
              }
            );

            if (response && (response as any).success) {
              const resAny = response as any;
              dispatch(
                setScreenFormData({
                  screenId: String(currentScreenId),
                  data: {
                    ...(getState() as RootState).forms.formDataByScreen[String(currentScreenId)] || {},
                    id: recordId,
                    status: "Published",
                  },
                })
              );

              ensureSlidingTimeout(
                dispatch,
                { message: resAny.message || "Calendar published successfully.", type: "success" },
                5000
              );

              // After a successful publish, return to the summary list.
              dispatch(navigateToView({ view: "holiday_calendar_summary" }));
            } else {
              ensureSlidingTimeout(
                dispatch,
                { message: (response as any)?.message || "Failed to publish calendar.", type: "error" },
                5000
              );
            }
          } catch (err: any) {
            console.error("[publish_calendar_start] Failed:", err);
            ensureSlidingTimeout(
              dispatch,
              { message: err?.message || "Failed to publish calendar. Please try again.", type: "error" },
              6000
            );
          }

          return;
        }
        // ── CONSOLIDATED INVOICE: Complete Print ─────────────────────────────
        case "consolidated_invoice_print": {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const { forms } = getState();
          const formDataForScreen = forms.formDataByScreen[currentScreenId] || {};
          const customerId = String(
            formDataForScreen.drp_customer ?? formDataForScreen.customer_id ?? ""
          ).trim();
          const selectedRaw = formDataForScreen.consolidated_selected_jobcard_ids;
          const selectedIds = Array.isArray(selectedRaw)
            ? selectedRaw.map((id: unknown) => String(id).trim()).filter(Boolean)
            : [];

          if (!customerId) {
            ensureSlidingTimeout(
              dispatch,
              { message: "Please select a customer.", type: "error" },
              5000
            );
            return;
          }
          if (selectedIds.length === 0) {
            ensureSlidingTimeout(
              dispatch,
              { message: "Please select at least one job card.", type: "error" },
              5000
            );
            return;
          }

          const encryptedUserData =
            typeof window !== "undefined"
              ? localStorage.getItem("encryptedUserData")
              : null;
          if (!encryptedUserData) {
            ensureSlidingTimeout(
              dispatch,
              { message: "User not authenticated. Please login again.", type: "error" },
              5000
            );
            return;
          }
          const decryptedData = authService.decryptData(encryptedUserData);
          if (!decryptedData) {
            ensureSlidingTimeout(
              dispatch,
              { message: "Failed to decrypt user data. Please login again.", type: "error" },
              5000
            );
            return;
          }
          const logined_userid =
            decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

          const pdfName = "Consolidated_jobcard_invoice_info";
          const payload = {
            ...buildPDFPayload(pdfName, formDataForScreen, logined_userid),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };

          try {
            const response = await apiService.request<{
              success?: boolean;
              data?: string;
              [key: string]: any;
            }>({
              endpoint: `/pabreport/pdf`,
              method: "POST",
              params: { name: pdfName },
              payload,
              includeAuthToken: true,
              errorHandling: "throw",
              responseType: "json",
            });

            // Decode response and show delivery chooser
            const pdfBlob = decodePdfResponseToBlob(response);
            const fileName = buildPdfFileName(pdfName, customerId);
            await showPdfDeliveryDialog(dispatch, pdfBlob, fileName);
          } catch (error) {
            console.error("[Consolidated Invoice Print] Error:", error);
            ensureSlidingTimeout(
              dispatch,
              {
                message:
                  error instanceof Error
                    ? `Failed to generate PDF: ${error.message}`
                    : "Failed to generate consolidated invoice PDF.",
                type: "error",
              },
              5000
            );
          }
          return;
        }
        // ── PAYROLL: Run Payroll for current month ─────────────────────────
        case "run_payroll": {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const { forms } = getState();
          const formDataForScreen = forms.formDataByScreen[currentScreenId] || {};

          ensureSlidingTimeout(
            dispatch,
            { message: "Processing payroll for this month, please wait...", type: "success" },
            8000
          );

          try {
            const rawMonth = String(formDataForScreen.payroll_month_filter || "").trim();
            const defaultYm = () => {
              const d = new Date();
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            };
            const payrollMonth = rawMonth || defaultYm();
            const selectedRaw = formDataForScreen.payroll_selected_employee_ids;
            const selectedIds = Array.isArray(selectedRaw)
              ? selectedRaw.map((id: unknown) => String(id).trim()).filter(Boolean)
              : [];
            const runBody: { payroll_month: string; employee_ids?: string[] } = {
              payroll_month: payrollMonth,
            };
            if (selectedIds.length > 0) {
              runBody.employee_ids = selectedIds;
            }
            const response = await apiService.post(
              "/pabws/hrms/payroll/run",
              runBody,
              { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
            );

            if (response && (response as any).success) {
              const result = response as any;
              const summary = result.data || {};
              const ranForSelection = selectedIds.length > 0;

              let statsPatch: Record<string, unknown> = {};

              if (ranForSelection) {
                const runSuccess = Number(summary.success ?? 0);
                const selectedCount = selectedIds.length;
                let monthTotalEmployees: number | null = null;
                let pendingApprovals = formDataForScreen.pending_approvals ?? 0;

                try {
                  const statsRes = await apiService.get<Record<string, unknown>>(
                    "/pabws/hrms/payroll/statistics",
                    { payroll_month: payrollMonth },
                    {
                      includeAuthToken: true,
                      authHeaderType: "Bearer",
                      errorHandling: "return-null",
                    }
                  );
                  if (statsRes && typeof statsRes === "object") {
                    const data =
                      statsRes.data &&
                      typeof statsRes.data === "object" &&
                      !Array.isArray(statsRes.data)
                        ? (statsRes.data as Record<string, unknown>)
                        : statsRes;
                    if (data.pending_approvals !== undefined) {
                      pendingApprovals = data.pending_approvals as number;
                    }
                    const ep = String(data.employees_processed ?? "");
                    const totalMatch = ep.match(/\/(\d+)\s*$/);
                    if (totalMatch) {
                      monthTotalEmployees = Number(totalMatch[1]);
                    }
                  }
                } catch {
                  /* use form fallbacks below */
                }

                if (monthTotalEmployees === null) {
                  const existingEp = String(formDataForScreen.employees_processed ?? "");
                  const totalMatch = existingEp.match(/\/(\d+)\s*$/);
                  if (totalMatch) {
                    monthTotalEmployees = Number(totalMatch[1]);
                  }
                }

                const processedNumerator =
                  selectedCount > 0 ? selectedCount : runSuccess;
                const processedDenominator =
                  monthTotalEmployees ?? processedNumerator;

                // Selected run: payout = this run; processed = selected / all active employees.
                statsPatch = {
                  payroll_stats_scope: "selection",
                  total_payout:
                    summary.total_net_pay !== undefined
                      ? String(Number(summary.total_net_pay).toFixed(2))
                      : "0.00",
                  employees_processed: `${processedNumerator}/${processedDenominator}`,
                  pending_approvals: pendingApprovals,
                };
              } else {
                try {
                  const statsRes = await apiService.get<Record<string, unknown>>(
                    "/pabws/hrms/payroll/statistics",
                    { payroll_month: payrollMonth },
                    {
                      includeAuthToken: true,
                      authHeaderType: "Bearer",
                      errorHandling: "return-null",
                    }
                  );
                  if (statsRes && typeof statsRes === "object") {
                    const data =
                      statsRes.data &&
                      typeof statsRes.data === "object" &&
                      !Array.isArray(statsRes.data)
                        ? (statsRes.data as Record<string, unknown>)
                        : statsRes;
                    if (data.total_payout !== undefined) statsPatch.total_payout = data.total_payout;
                    if (data.pending_approvals !== undefined) {
                      statsPatch.pending_approvals = data.pending_approvals;
                    }
                    if (data.employees_processed !== undefined) {
                      statsPatch.employees_processed = data.employees_processed;
                    }
                  }
                } catch {
                  /* fall back to run summary below */
                }

                if (Object.keys(statsPatch).length === 0) {
                  statsPatch = {
                    total_payout:
                      summary.total_net_pay !== undefined
                        ? String(Number(summary.total_net_pay).toFixed(2))
                        : formDataForScreen.total_payout,
                    pending_approvals: formDataForScreen.pending_approvals ?? 0,
                    employees_processed: `${summary.success ?? 0}/${summary.total_employees ?? 0}`,
                  };
                }
                statsPatch.payroll_stats_scope = "month";
              }

              dispatch(
                setScreenFormData({
                  screenId: currentScreenId,
                  data: {
                    ...formDataForScreen,
                    ...statsPatch,
                    payroll_selected_employee_ids: [],
                  },
                })
              );

              dispatch(
                navigateToView({
                  view: currentView,
                  rememberPrevious: false,
                })
              );

              const periodLabel = result.period_name || payrollMonth;
              const successCount = Number(summary.success ?? 0);
              let processedToastLabel = `${successCount}/${summary.total_employees ?? 0}`;
              if (typeof statsPatch.employees_processed === "string") {
                const ep = String(statsPatch.employees_processed);
                const totalMatch = ep.match(/\/(\d+)\s*$/);
                if (ranForSelection && totalMatch) {
                  processedToastLabel = `${successCount}/${totalMatch[1]}`;
                } else {
                  processedToastLabel = ep;
                }
              }

              ensureSlidingTimeout(
                dispatch,
                {
                  message: `Payroll processed for ${periodLabel}: ${processedToastLabel} employees succeeded`,
                  type: "success",
                },
                6000
              );
            }
          } catch (err) {
            console.error("[Payroll] Failed to run payroll:", err);
            ensureSlidingTimeout(
              dispatch,
              { message: "Failed to run payroll. Please try again.", type: "error" },
              5000
            );
          }
          return;
        }

        case "save_tab_data": {
          const { forms, navigation } = getState();
          const currentScreenId = deriveScreenId(currentView) || currentView;

          // Only handle HRMS Employee Profile Add (screen 2205 / emp_profile_add) for now
          const isEmpProfileScreen =
            currentScreenId === "2205" ||
            currentView === "emp_profile_add" ||
            currentView === "modulev1/emp_profile_add" ||
            currentView.startsWith("modulev1/emp_profile_add/");

          if (!isEmpProfileScreen) {
            // For non-HRMS screens, fall back to existing behaviour (no-op here)
            return;
          }

          const tab = (data && (data as any).tab) || "";
          const isLastTab = (data && (data as any).isLastTab) === true;
          const formDataForScreen =
            forms.formDataByScreen[currentScreenId] || {};

          if (tab !== "about" && tab !== "job" && tab !== "assets" && tab !== "salary" && tab !== "leave") {
            return;
          }

          if (forms.savingByScreen[currentScreenId]) {
            return;
          }
          dispatch(setScreenSaving({ screenId: currentScreenId, isSaving: true }));

          try {
          // ── ABOUT TAB ───────────────────────────────────────────────────────
          if (tab === "about") {
            // Map formData BindValues (from 2205_emp_profile_add.ts) to backend Json_Attributes
            const aboutPayload: any = {
              // Primary details
              first_name: formDataForScreen.primary_first_name,
              middle_name: formDataForScreen.primary_middle_name,
              last_name: formDataForScreen.primary_last_name,
              gender: formDataForScreen.primary_gender,
              date_of_birth: formDataForScreen.primary_date_of_birth,
              marital_status: formDataForScreen.primary_marital_status,
              marriage_date: formDataForScreen.primary_marriage_date,
              nationality: formDataForScreen.primary_nationality,
              languages_read: formDataForScreen.primary_languages_read,
              languages_write: formDataForScreen.primary_languages_write,
              languages_speak: formDataForScreen.primary_languages_speak,
              physically_handicapped: formDataForScreen.primary_physically_handicapped,
              blood_group: formDataForScreen.primary_blood_group,

              // Contact details
              email: formDataForScreen.contact_email,
              mobile: formDataForScreen.contact_phone,
              personal_email: formDataForScreen.contact_personal_email,
              residence_number: formDataForScreen.contact_residence_number,
              current_address: formDataForScreen.address_current_address,
              permanent_address: formDataForScreen.address_permanent_address,

              // Emergency contacts
              emergency_contact_1_name: formDataForScreen.emergency_contact_1_name,
              emergency_contact_1_phone: formDataForScreen.emergency_contact_1_phone,
              emergency_contact_1_address: formDataForScreen.emergency_contact_1_address,
              emergency_contact_1_relationship: formDataForScreen.emergency_contact_1_relationship,
              emergency_contact_2_name: formDataForScreen.emergency_contact_2_name,
              emergency_contact_2_phone: formDataForScreen.emergency_contact_2_phone,
              emergency_contact_2_address: formDataForScreen.emergency_contact_2_address,
              emergency_contact_2_relationship: formDataForScreen.emergency_contact_2_relationship,

              // Identity Documents (Emirates ID, Passport, Visa)
              // BindValues: identity_emirates_id -> backend key: emirates_id
              emirates_id: formDataForScreen.identity_emirates_id,
              emirates_id_issue_date: formDataForScreen.identity_emirates_issue_date,
              emirates_id_expiry_date: formDataForScreen.identity_emirates_expiry_date,
              passport_no: formDataForScreen.identity_passport_no,
              passport_issue_date: formDataForScreen.identity_passport_issue_date,
              passport_expiry_date: formDataForScreen.identity_passport_expiry_date,
              visa_no: formDataForScreen.identity_visa_no,

              // PAN / Aadhaar
              pan_number: formDataForScreen.pan_number,
              pan_name: formDataForScreen.pan_name,
              pan_dob: formDataForScreen.pan_dob,
              pan_parent_name: formDataForScreen.pan_parent_name,
              aadhaar_type: formDataForScreen.aadhaar_type,
              aadhaar_name: formDataForScreen.aadhaar_name,
              aadhaar_number: formDataForScreen.aadhaar_number,
              aadhaar_dob: formDataForScreen.aadhaar_dob,
              aadhaar_parent_name: formDataForScreen.aadhaar_parent_name,

              // Personal / about sections
              about: formDataForScreen.about_about,
              what_i_love: formDataForScreen.about_what_i_love,
              interests: formDataForScreen.about_interests,
              employment_history_previous_company_name: formDataForScreen.employment_history_previous_company_name,
              employment_history_previous_employer_id: formDataForScreen.employment_history_previous_employer_id,
              employment_history_previous_joining_date: formDataForScreen.employment_history_previous_joining_date,
              employment_history_previous_ending_date: formDataForScreen.employment_history_previous_ending_date,
              employment_history_total_employment: formDataForScreen.employment_history_total_employment,
              employment_history_reason_for_leaving: formDataForScreen.employment_history_reason_for_leaving,
            };

            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/about",
                { employee_id: employeeId || null, data: aboutPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                const result = response as any;
                const newEmployeeId = result.employee_id || employeeId;
                const newName = result.name;

                if (newEmployeeId) {
                  dispatch(
                    setScreenFormData({
                      screenId: currentScreenId,
                      data: {
                        ...formDataForScreen,
                        id: newEmployeeId,
                        employee_id: newEmployeeId,
                        primary_name: newName || formDataForScreen.primary_name,
                      },
                    })
                  );
                }
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "About tab saved.", type: "success" }, 2500);
                }
              } else {
                throw new Error("Failed to save employee profile (About tab).");
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee about tab:", err);
              const message = parseApiErrorDetail(
                err,
                "Failed to save employee profile (About tab)."
              );
              ensureSlidingTimeout(dispatch, { message, type: "error" }, 5000);
              throw err;
            }
          }

          // ── JOB TAB ─────────────────────────────────────────────────────────
          if (tab === "job") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before saving Job details.", type: "error" },
                5000
              );
              return;
            }

            const jobPayload: any = {
              // Job Details
              employee_number: formDataForScreen.job_employee_number,
              date_of_joining: formDataForScreen.job_date_of_joining,
              job_title_primary: formDataForScreen.job_job_title_primary,
              job_title_secondary: formDataForScreen.job_job_title_secondary,
              in_probation: formDataForScreen.job_in_probation,
              notice_period: formDataForScreen.job_notice_period,
              worker_type: formDataForScreen.job_worker_type,
              time_type: formDataForScreen.job_time_type,
              contract_status: formDataForScreen.job_contract_status,
              pay_band: formDataForScreen.job_pay_band,
              pay_grade: formDataForScreen.job_pay_grade,

              // Organization
              business_unit: formDataForScreen.organization_business_unit,
              group_id: formDataForScreen.organization_group_id,
              location: formDataForScreen.organization_location,
              cost_center: formDataForScreen.organization_cost_center,
              legal_entity: formDataForScreen.organization_legal_entity,
              reports_to: formDataForScreen.organization_reports_to,
              manager_of_manager: formDataForScreen.organization_manager_of_manager,
              direct_reports: formDataForScreen.organization_direct_reports,
              visa_branch: formDataForScreen.organization_visa_branch,
              branch_id: formDataForScreen.organization_branch_id,

              // Employee Time
              shift: formDataForScreen.employee_time_shift,
              weekly_off_policy: formDataForScreen.employee_time_weekly_off_policy,
              leave_plan: formDataForScreen.employee_time_leave_plan,
              holiday_calendar: formDataForScreen.employee_time_holiday_calendar,
              attendance_number: formDataForScreen.employee_time_attendance_number,
              attendance_capture_scheme: formDataForScreen.employee_time_attendance_capture_scheme,
              attendance_penalization_policy: formDataForScreen.employee_time_attendance_penalization_policy,
              shift_weekly_off_rule: formDataForScreen.employee_time_shift_weekly_off_rule,
              shift_allowance_policy: formDataForScreen.employee_time_shift_allowance_policy,
              overtime: formDataForScreen.employee_time_overtime,

              // Other / Policies
              expense_policy: formDataForScreen.other_expense_policy,
              loan_policy: formDataForScreen.other_loan_policy,
              air_ticket_policy: formDataForScreen.other_air_ticket_policy,
              sales_included: formDataForScreen.other_sales_included,
              user_access: formDataForScreen.other_user_access,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/job",
                { employee_id: employeeId, data: jobPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: { ...formDataForScreen, id: employeeId, employee_id: employeeId },
                  })
                );
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "Job tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee job tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Job tab).", type: "error" },
                5000
              );
            }
          }

          // ── ASSETS TAB ──────────────────────────────────────────────────────
          if (tab === "assets") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before assigning assets.", type: "error" },
                5000
              );
              return;
            }

            const assetsPayload: any = {
              // asset_id present only when editing an existing asset record
              asset_id: formDataForScreen.asset_id,

              // Asset fields – BindValue -> Json_Attribute (backend key)
              asset_type: formDataForScreen.asset_type,
              asset_name: formDataForScreen.asset_name,
              asset_category: formDataForScreen.asset_category,
              assigned_on: formDataForScreen.asset_assigned_on,
              acknowledgement_status: formDataForScreen.asset_acknowledgement_status,
              latest_condition: formDataForScreen.asset_latest_condition,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/assets",
                { employee_id: employeeId, data: assetsPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                const result = response as any;
                // Store the returned asset_id so a re-save edits the same record
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: {
                      ...formDataForScreen,
                      id: employeeId,
                      employee_id: employeeId,
                      asset_id: result.asset_id || formDataForScreen.asset_id,
                    },
                  })
                );
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "Assets tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee assets tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Assets tab).", type: "error" },
                5000
              );
            }
          }

          // ── SALARY TAB ──────────────────────────────────────────────────────
          if (tab === "salary") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before saving Salary details.", type: "error" },
                5000
              );
              return;
            }

            const salaryPayload: any = {
              // Basic salary info
              currency: formDataForScreen.salary_currency,
              salary_frequency: formDataForScreen.salary_frequency,
              effective_date: formDataForScreen.salary_effective_date,

              // Allowances
              housing_allowance: formDataForScreen.salary_housing_allowance,
              transport_allowance: formDataForScreen.salary_transport_allowance,
              food_allowance: formDataForScreen.salary_food_allowance,
              medical_allowance: formDataForScreen.salary_medical_allowance,
              other_allowances: formDataForScreen.salary_other_allowances,
              total_allowances: formDataForScreen.salary_total_allowances,

              // Deductions
              income_tax: formDataForScreen.salary_income_tax,
              provident_fund: formDataForScreen.salary_provident_fund,
              insurance: formDataForScreen.salary_insurance,
              other_deductions: formDataForScreen.salary_other_deductions,
              total_deductions: formDataForScreen.salary_total_deductions,

              // Salary summary
              gross_salary: formDataForScreen.salary_gross_salary,
              basic_salary: formDataForScreen.salary_basic_salary,
              net_salary: formDataForScreen.salary_net_salary,
              per_day_salary: formDataForScreen.salary_per_day_salary,

              // Bank details
              bank_name: formDataForScreen.salary_bank_name,
              bank_account_number: formDataForScreen.salary_bank_account_number,
              bank_ifsc_code: formDataForScreen.salary_bank_ifsc_code,
              bank_branch_name: formDataForScreen.salary_bank_branch_name,
              bank_account_type: formDataForScreen.salary_bank_account_type,

              // Additional salary info
              pay_grade: formDataForScreen.salary_pay_grade,
              pay_scale: formDataForScreen.salary_pay_scale,
              increment_date: formDataForScreen.salary_increment_date,
              next_increment_date: formDataForScreen.salary_next_increment_date,
              increment_percentage: formDataForScreen.salary_increment_percentage,
              bonus_eligibility: formDataForScreen.salary_bonus_eligibility,
              bonus_percentage: formDataForScreen.salary_bonus_percentage,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/salary",
                { employee_id: employeeId, data: salaryPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: { ...formDataForScreen, id: employeeId, employee_id: employeeId },
                  })
                );
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "Salary tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee salary tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Salary tab).", type: "error" },
                5000
              );
            }
          }

          // ── LEAVE TAB ───────────────────────────────────────────────────────
          if (tab === "leave") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before configuring leave.", type: "error" },
                5000
              );
              return;
            }

            const leavePayload: any = {
              // Bereavement
              bereavement_allocated_days: formDataForScreen.leave_bereavement_allocated_days,
              bereavement_carry_forward_days: formDataForScreen.leave_bereavement_carry_forward_days,
              bereavement_carryover_enabled: formDataForScreen.leave_bereavement_carryover_enabled,
              // Casual
              casual_allocated_days: formDataForScreen.leave_casual_allocated_days,
              casual_carry_forward_days: formDataForScreen.leave_casual_carry_forward_days,
              casual_carryover_enabled: formDataForScreen.leave_casual_carryover_enabled,
              // Comp Offs
              comp_offs_allocated_days: formDataForScreen.leave_comp_offs_allocated_days,
              comp_offs_carry_forward_days: formDataForScreen.leave_comp_offs_carry_forward_days,
              comp_offs_carryover_enabled: formDataForScreen.leave_comp_offs_carryover_enabled,
              // Paternity
              paternity_allocated_days: formDataForScreen.leave_paternity_allocated_days,
              paternity_carry_forward_days: formDataForScreen.leave_paternity_carry_forward_days,
              paternity_carryover_enabled: formDataForScreen.leave_paternity_carryover_enabled,
              // Privilege Casual
              privilege_casual_allocated_days: formDataForScreen.leave_privilege_casual_allocated_days,
              privilege_casual_carry_forward_days: formDataForScreen.leave_privilege_casual_carry_forward_days,
              privilege_casual_carryover_enabled: formDataForScreen.leave_privilege_casual_carryover_enabled,
              // Sick
              sick_allocated_days: formDataForScreen.leave_sick_allocated_days,
              sick_carry_forward_days: formDataForScreen.leave_sick_carry_forward_days,
              sick_carryover_enabled: formDataForScreen.leave_sick_carryover_enabled,
              // Unpaid
              unpaid_allocated_days: formDataForScreen.leave_unpaid_allocated_days,
              unpaid_carry_forward_days: formDataForScreen.leave_unpaid_carry_forward_days,
              unpaid_carryover_enabled: formDataForScreen.leave_unpaid_carryover_enabled,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/leave",
                { employee_id: employeeId, data: leavePayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: {
                      ...formDataForScreen,
                      id: employeeId,
                      employee_id: employeeId,
                    },
                  })
                );

                // ── Final Submit: show success toast + navigate away ─────────
                if (isLastTab) {
                  ensureSlidingTimeout(
                    dispatch,
                    { message: "Employee profile saved successfully!", type: "success" },
                    4000
                  );
                  const prevView = getState().navigation.previousView;
                  setTimeout(() => {
                    dispatch(
                      navigateToView({
                        view: prevView || "modulev1/hrms_emp_summary",
                        rememberPrevious: false,
                      })
                    );
                  }, 700);
                }
                // ─────────────────────────────────────────────────────────────
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee leave tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Leave tab).", type: "error" },
                5000
              );
              throw err;
            }
          }
          } finally {
            dispatch(setScreenSaving({ screenId: currentScreenId, isSaving: false }));
          }

          return;
        }

        case "toggle_sidebar":
          dispatch(toggleSideMenu());
          return;
        case "html_drawer": {
          const { htmlDrawer } = getState();
          const { forms } = getState();
          const activeScreenId = deriveScreenId(currentView) || currentView;
          const formData = forms.formDataByScreen[activeScreenId] || {};

          // Config is passed in data.buttonConfig
          const buttonConfig = data?.buttonConfig;
          const reportName = buttonConfig?.Settings || buttonConfig?.settings;

          if (!reportName) {
            dispatch(showNotification({ message: "Report name not configured", type: "error" }));
            return;
          }

          // Open drawer with empty content and set loading
          dispatch(openHTMLDrawer({ htmlContent: "", reportName }));
          dispatch(setHTMLDrawerLoading(true));

          try {
            // Get ID from formData - try multiple fields
            const id = formData.id || formData.recordId || formData._id || formData.jobcard_id || formData.job_card_id;

            if (!id) {
              throw new Error("Record ID not found");
            }

            const response = await apiService.post(
              `/pabreport/report?name=${reportName}`,
              { id, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
            );

            if (response.success) {
              dispatch(
                openHTMLDrawer({
                  htmlContent: response.data,
                  reportName,
                })
              );
            } else {
              throw new Error(response.message || "Failed to fetch report");
            }
          } catch (error: any) {
            console.error("[HTML Drawer] Error fetching report:", error);
            dispatch(setHTMLDrawerError(error.message || "An unknown error occurred"));
            dispatch(showNotification({ message: error.message || "Failed to load report", type: "error" }));
          }
          return;
        }

        case "set_branch":
          if (typeof data === "string") {
            const { availableBranches } = getState().layout;
            const branchObj = availableBranches.find(
              (b: any) => b.id === data || b.name === data
            );

            if (branchObj) {
              const { navigation: navState, forms: formsState } = getState();
              const activeView = navState.currentView;
              // Leave add/edit forms before reload so persisted navigation restores Home
              if (isAddOrEditScreen(activeView, formsState.editModeByScreen)) {
                const screenId = resolveScreenIdForView(activeView);
                dispatch(resetScreenFormData(screenId));
                dispatch(
                  navigateToView({ view: "dashboard", rememberPrevious: false })
                );
                dispatch(clearHistory());
              }

              dispatch(setLayoutCurrentBranch(branchObj));
              dispatch(
                updateUserBranch({
                  branch_id: branchObj.id,
                  branch_name: branchObj.name,
                })
              );

              // Reload to refresh data context, matching App.tsx handleBranchChange
              setTimeout(() => {
                if (typeof window !== "undefined") {
                  window.location.reload();
                }
              }, 100);
            }

            dispatch(
              updateScreenFormField({
                screenId: currentView,
                name: "branch",
                value: data,
              })
            );
          }
          return;
        case "save": {
          const { forms, auth, navigation, layout } = getState();
          // If a screen is open in popup, save should target that popup screen
          // (navigation.currentView remains the underlying screen).
          const popupState = (getState() as any).screenPopup as
            | { isOpen?: boolean; screenId?: string }
            | undefined;
          const popupScreenId =
            popupState?.isOpen && popupState?.screenId
              ? String(popupState.screenId)
              : null;

          const activeScreenId = popupScreenId
            ? popupScreenId
            : navigation.currentView === "garage"
              ? deriveScreenId("garage") || "100"
              : deriveScreenId(navigation.currentView) || navigation.currentView;

          // Prevent double submission
          if (forms.savingByScreen[activeScreenId]) {
            console.warn(`[Save Action] execution prevented: Already loading for screen ${activeScreenId}`);
            return;
          }
          if (forms.expressionCalculatingByScreen[activeScreenId]) {
            console.warn(
              `[Save Action] execution prevented: ExpressionV2 still calculating for screen ${activeScreenId}`
            );
            return;
          }

          // Set loading state immediately
          dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: true }));

          const formData = forms.formDataByScreen[activeScreenId] || {};
          const editModeInfo = forms.editModeByScreen[activeScreenId];
          if (editModeInfo?.isViewOnly) {
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          const encryptedUserData =
            typeof window !== "undefined"
              ? localStorage.getItem("encryptedUserData")
              : null;
          if (!encryptedUserData) {
            autoHideNotification(dispatch, {
              message: "User not authenticated. Please login again.",
              type: "error",
            });
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          const decryptedData = authService.decryptData(encryptedUserData);
          if (!decryptedData) {
            autoHideNotification(dispatch, {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            });
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          // Get form configuration
          // Use resolved config: API cache first, local fallback
          const screenConfig = getResolvedScreenConfig(activeScreenId);
          if (!screenConfig) {
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Form configuration not found.",
                type: "error",
              },
              5000
            );
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          const {
            formComponent,
            tableName,
            moduleEndpoint,
          } = resolveFormForSave(screenConfig, activeScreenId);

          if (!formComponent || !tableName) {
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Form configuration not found or missing TableName.",
                type: "error",
              },
              5000
            );
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          const isPaymentsModule =
            moduleEndpoint === "pur_payment" ||
            activeScreenId === "144" ||
            tableName === "purchase_payments";
          if (isPaymentsModule) {
            const rawUnregistered =
              formData.txt_unregistered_amount ?? formData.unregistered_amount;
            const unregisteredAmount = parseFloat(
              String(rawUnregistered ?? "")
                .replace(/,/g, "")
                .trim()
            );
            if (Number.isFinite(unregisteredAmount) && unregisteredAmount > 0) {
              const confirmed = await showConfirmDialog(
                dispatch,
                "You entered an amount in the Unregistered (unadjusted) amount field. " +
                  "It will be saved as an unallocated payment and will appear on the supplier statement until allocated to purchases. " +
                  "Do you want to continue?",
                "Unadjusted payment amount"
              );
              if (!confirmed) {
                dispatch(
                  setScreenSaving({ screenId: activeScreenId, isSaving: false })
                );
                return;
              }
            }
          }

          const isReceivablesModule =
            moduleEndpoint === "invoice_receivables" ||
            activeScreenId === "10" ||
            tableName === "service_invoice_receivables";
          if (isReceivablesModule) {
            const rawUnregistered =
              formData.txt_unregistered_amount ?? formData.unregistered_amount;
            const unregisteredAmount = parseFloat(
              String(rawUnregistered ?? "")
                .replace(/,/g, "")
                .trim()
            );
            if (Number.isFinite(unregisteredAmount) && unregisteredAmount > 0) {
              const confirmed = await showConfirmDialog(
                dispatch,
                "You entered an amount in the Unadjusted amount field. " +
                  "It will be saved as an unallocated receipt and will appear on the customer statement until allocated to invoices. " +
                  "Do you want to continue?",
                "Unadjusted receipt amount"
              );
              if (!confirmed) {
                dispatch(
                  setScreenSaving({ screenId: activeScreenId, isSaving: false })
                );
                return;
              }
            }
          }

          const isBillReleaseSaveModule =
            moduleEndpoint === "bill_wise_release" ||
            moduleEndpoint === "vendor_bill_wise_release" ||
            activeScreenId === "190" ||
            activeScreenId === "195" ||
            tableName === "bill_wise_release" ||
            tableName === "vendor_bill_wise_release";
          if (isBillReleaseSaveModule) {
            const confirmed = await showConfirmDialog(
              dispatch,
              "This will release bill allocations and restore unregistered balances. " +
                "Original vouchers and ledger postings will not be changed. Continue?",
              "Confirm bill release"
            );
            if (!confirmed) {
              dispatch(
                setScreenSaving({ screenId: activeScreenId, isSaving: false })
              );
              return;
            }
          }

          const isCloseJobcardModule =
            tableName === "service_job_approval" ||
            activeScreenId === "39" ||
            activeScreenId === "159";
          if (isCloseJobcardModule) {
            const {
              isPartsIssueBlockingClose,
              isForceCloseWithoutPartsIssueChecked,
            } = await import("../../utils/jobcardCloseUtils");
            if (isPartsIssueBlockingClose(formData)) {
              if (!isForceCloseWithoutPartsIssueChecked(formData)) {
                autoHideNotification(dispatch, {
                  message:
                    "Parts issue is pending or partially issued. Tick 'Close without completing parts issue' to submit.",
                  type: "error",
                });
                dispatch(
                  setScreenSaving({ screenId: activeScreenId, isSaving: false })
                );
                return;
              }
              const confirmed = await showConfirmDialog(
                dispatch,
                "Parts issue is still pending or partially issued. Close this job card anyway? This action will be recorded.",
                "Close without parts issue"
              );
              if (!confirmed) {
                dispatch(
                  setScreenSaving({ screenId: activeScreenId, isSaving: false })
                );
                return;
              }
            }
          }

          const brVoucherInsertOnly = isBankReconVoucherInsertOnly(
            activeScreenId,
            moduleEndpoint
          );

          // Determine recordId: prioritize edit mode recordId, then formData.id, only generate new ID for new records
          let recordId: string | number | undefined;

          if (brVoucherInsertOnly) {
            recordId = undefined;
          } else if (editModeInfo?.isEditMode) {
            // In edit mode: use recordId from edit mode info, fallback to formData.id
            recordId = editModeInfo?.recordId || formData.id;
          } else {
            // GLOBAL FIX: Always generate new ID for create mode, ignoring any potentially reused formData.id
            // This ensures that subsequent submits on the same screen instance create NEW records
            recordId = undefined;
          }

          // CRITICAL: Edit mode detection - only use editModeByScreen state
          // Do NOT use recordId as fallback because new records may have generated IDs
          // isEditMode should only be true if explicitly set in Redux store (when opening existing record)
          const isEditMode = brVoucherInsertOnly
            ? false
            : editModeInfo?.isEditMode || false;

          // CRITICAL: Only generate new ID if we're creating a new record (not in edit mode) and no ID exists
          // Also ensure the ID is not the same as parent_id (which would cause conflicts)
          if ((!recordId || String(recordId).trim() === "") && !isEditMode) {
            dispatch(incrementFormInstanceCounter());
            const instance = getState().forms.formInstanceCounter;
            const generatedId = generateUniqueFormId(
              resolveUserCode(decryptedData),
              instance
            );

            // Double-check: ensure generated ID is not the same as parent_id
            const parentId = formData.parent_id || formData.parentid;
            if (generatedId === parentId) {
              // If somehow the generated ID matches parent_id, generate again
              dispatch(incrementFormInstanceCounter());
              const newInstance = getState().forms.formInstanceCounter;
              recordId = generateUniqueFormId(
                resolveUserCode(decryptedData),
                newInstance
              );
            } else {
              recordId = generatedId;
            }

            if (import.meta.env.DEV) {
              console.log("[Save Action] Generated new unique ID for create mode:", {
                recordId,
                parentId,
                isDifferent: recordId !== parentId,
              });
            }
            dispatch(
              setScreenFormData({
                screenId: activeScreenId,
                data: {
                  ...formData,
                  id: recordId,
                },
              })
            );
          }

          const now = new Date();
          const formattedDate = now.toISOString();

          // Helper to generate subform item ID
          const generateSubformItemId = (
            index: number,
            userCode: string | undefined,
            instance: number
          ): string => {
            return generateUniqueFormId(userCode, instance + index);
          };

          // Extract all fields from form configuration dynamically.
          // Must support both Json_Attribute (static/local config) and json_Attribute (API config).
          // When screen config is loaded from API (e.g. payment screen 144), the API returns
          // camelCase property names; using only Json_Attribute would miss them and fall back
          // to bindValue, causing payload keys like "txt_amount" instead of "amount", so
          // amount/discount would appear "missing" to the backend.
          const extractFieldMappings = (
            fields: any[]
          ): Record<string, string> => {
            const mappings: Record<string, string> = {};
            const processField = (field: any) => {
              if (field.excludeFromSave === true) {
                return;
              }
              const bindValue =
                field.BindValue ?? field.bindValue ?? field.name;
              const jsonAttribute =
                field.Json_Attribute ?? field.json_Attribute ?? bindValue;
              if (bindValue && jsonAttribute) {
                mappings[bindValue] = jsonAttribute;
              }
              // Process nested fields
              if (field.Fields && Array.isArray(field.Fields)) {
                field.Fields.forEach(processField);
              }
              if (field.childComponents && Array.isArray(field.childComponents)) {
                field.childComponents.forEach(processField);
              }
              // SubModule uses childControls
              if (field.childControls && Array.isArray(field.childControls)) {
                field.childControls.forEach(processField);
              }
              // SubModule footer totals (e.g. round_off_amt) — same as child line fields
              if (field.FooterControls && Array.isArray(field.FooterControls)) {
                field.FooterControls.forEach(processField);
              }
            };
            if (fields && Array.isArray(fields)) {
              fields.forEach(processField);
            }
            return mappings;
          };

          // Extract subform components dynamically
          const extractSubformComponents = (fields: any[]): any[] => {
            const subforms: any[] = [];
            const processComponent = (component: any) => {
              if (
                component.componentType === ComponentType.SUB_MODULE ||
                component.componentType === ComponentType.MULTI_SUB_LIST ||
                component.componentType === ComponentType.MULTI_CHECKBOX
              ) {
                subforms.push(component);
              }
              // Process nested components
              if (component.Fields && Array.isArray(component.Fields)) {
                component.Fields.forEach(processComponent);
              }
              if (
                component.childComponents &&
                Array.isArray(component.childComponents)
              ) {
                component.childComponents.forEach(processComponent);
              }
              // SubModule uses childControls
              if (
                component.childControls &&
                Array.isArray(component.childControls)
              ) {
                component.childControls.forEach(processComponent);
              }
            };
            if (fields && Array.isArray(fields)) {
              fields.forEach(processComponent);
            }
            return subforms;
          };

          // Get fields from form component - support both Fields and childComponents
          const formFields =
            (formComponent as any).Fields ||
            (formComponent as any).childComponents ||
            [];
          const fieldMappings = extractFieldMappings(formFields);
          const subformComponents = extractSubformComponents(formFields);
          const formVisibilityRules = (formComponent as any)?.fieldVisibilityRules;

          // Helper function to get field config by bindValue
          const getFieldConfig = (bindValue: string): any => {
            const findField = (fields: any[]): any => {
              for (const field of fields) {
                if ((field.BindValue ?? field.bindValue ?? field.name) === bindValue) {
                  return field;
                }
                if (field.Fields && Array.isArray(field.Fields)) {
                  const found = findField(field.Fields);
                  if (found) return found;
                }
                if (
                  field.childComponents &&
                  Array.isArray(field.childComponents)
                ) {
                  const found = findField(field.childComponents);
                  if (found) return found;
                }
                // SubModule uses childControls
                if (field.childControls && Array.isArray(field.childControls)) {
                  const found = findField(field.childControls);
                  if (found) return found;
                }
              }
              return null;
            };
            return findField(formFields);
          };

          // Helper function to format date to ISO 8601 UTC format for API
          const formatDateForAPI = (dateValue: any): string => {
            if (!dateValue || dateValue === "") return "";
            return toUTC(dateValue) || "";
          };

          const userIdentifier = decryptedData.userid || decryptedData.uuid || decryptedData.empId || decryptedData.userId || "1";

          const resolveSaveTransactionBranchId = (): string => {
            const layoutBranch = layout.currentBranch as
              | { id?: string }
              | string
              | null
              | undefined;
            const layoutBranchId =
              layoutBranch && typeof layoutBranch === "object"
                ? layoutBranch.id
                : layoutBranch;
            return String(
              decryptedData.branch_id ||
                decryptedData.branchId ||
                decryptedData.transaction_branch_id ||
                decryptedData.transactionBranchId ||
                decryptedData.transaction_branchid ||
                (decryptedData.loginUser &&
                typeof decryptedData.loginUser === "object"
                  ? (decryptedData.loginUser as any).branch_id ||
                    (decryptedData.loginUser as any).transaction_branch_id
                  : "") ||
                auth.user?.branch_id ||
                layoutBranchId ||
                ""
            ).trim();
          };
          const saveTransactionBranchId = resolveSaveTransactionBranchId();
          const isLookupMasterScreen =
            activeScreenId === "151" || activeScreenId === "152";

          // Build payload dynamically
          const payload: any = {
            data: {
              subform: [],
              id: recordId, // Always include recordId
            },
            next_action: "",
            next_state: "",
          };

          if (isEditMode) {
            payload.data.modified_by = userIdentifier;
            payload.data.modified_at = formattedDate;
          } else {
            payload.data.created_by = userIdentifier;
            payload.data.created_at = formattedDate;
            payload.data.transaction_branch_id =
              saveTransactionBranchId || "1";
          }

          // Vehicle make/type updates still insert new lookup_items rows; parent
          // transaction_branch_id is the source of truth for those child inserts.
          if (isLookupMasterScreen && saveTransactionBranchId) {
            payload.data.transaction_branch_id = saveTransactionBranchId;
          }

          // CRITICAL: Ensure parentid is included in payload from the start
          // Backend needs parentid to update service_details table (for jobcard)
          // Use getParentIdFromFormData utility to robustly find parentid
          // This handles various field name variations (parentid, parent_id, estimate_id, etc.)
          let parentId = getParentIdFromFormData(formData, "parentid");

          // If not found, also check Redux store for this screen
          if (!parentId) {
            const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
            parentId = getParentIdFromFormData(storedFormData, "parentid");
          }

          // CRITICAL: Only add parentid if it's different from the record's own id
          // This prevents master records (like lookups) from sending their own id as parentid
          if (parentId && parentId !== recordId) {
            payload.data.parentid = parentId;
            payload.data.parent_id = parentId;

            // Also initialize parent_id_desc if available
            const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
            const parentIdDesc = getParentIdDescFromFormData(formData, "parentid") || getParentIdDescFromFormData(storedFormData, "parentid");
            if (parentIdDesc) {
              payload.data.parent_id_desc = parentIdDesc;
            }
            if (import.meta.env.DEV) {
              console.log("[Save Action] Initialized payload with parentid:", parentId, {
                source: "formData",
                formDataKeys: Object.keys(formData).filter(k => k.toLowerCase().includes("parent") || k.toLowerCase().includes("estimat")),
              });
            }
          }
          else if (import.meta.env.DEV) {
            console.warn("[Save Action] parentid not found in formData:", {
              formDataKeys: Object.keys(formData),
              activeScreenId,
              storedFormDataKeys: Object.keys(getState().forms.formDataByScreen[activeScreenId] || {}),
            });
          }

          // Proforma safety-net: if discount is empty in form state, try to pull it
          // from the selected estimation just before payload mapping.
          if (String(activeScreenId) === "175") {
            const currentDiscount =
              formData.txt_discount ??
              formData.discount;
            const hasDiscount =
              currentDiscount !== undefined &&
              currentDiscount !== null &&
              String(currentDiscount).trim() !== "";

            if (!hasDiscount && payload.data.parentid) {
              try {
                const estimationResponse = await apiService.get(
                  `/pabws/utility/estimation_items`,
                  { id: payload.data.parentid },
                  {
                    includeAuthToken: true,
                    authHeaderType: "Bearer",
                    errorHandling: "return-empty",
                    transformResponse: (raw: any) => {
                      if (!raw) return null;
                      if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                        return raw.data[0];
                      }
                      if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                        return raw.data[0];
                      }
                      return raw;
                    },
                  }
                );

                const resolvedDiscount =
                  estimationResponse?.discount ??
                  estimationResponse?.discount_amount ??
                  estimationResponse?.discount_amt ??
                  estimationResponse?.disc_amount ??
                  estimationResponse?.disc_amt ??
                  estimationResponse?.dis_amt;

                if (resolvedDiscount !== undefined && resolvedDiscount !== null) {
                  const discountStr = String(resolvedDiscount);
                  formData.txt_discount = discountStr;
                  formData.discount = discountStr;
                  if (import.meta.env.DEV) {
                    console.log("[Save Action][Proforma] Backfilled discount from estimation before payload:", {
                      parentid: payload.data.parentid,
                      discount: discountStr,
                    });
                  }
                }
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.warn("[Save Action][Proforma] Failed to backfill discount before payload:", {
                    parentid: payload.data.parentid,
                    error,
                  });
                }
              }
            }
          }

          // Get lookups state for dropdown descriptions
          const lookupsState = getState().lookups;

          // Check if form contains file components
          let hasFileComponents = false;
          walkComponentTree(screenConfig, (component) => {
            const ct = String(
              (component as any).componentType ??
                (component as any).controlType ??
                ""
            ).toLowerCase();
            if (ct === String(ComponentType.FILES).toLowerCase()) {
              hasFileComponents = true;
              return true; // Stop traversal once found
            }
          });

          // Upload pending image files (from FileUpload) before building payload
          const pendingFiles = (data && typeof data === "object" && data.pendingFiles) ? data.pendingFiles as Record<string, File> : {};
          const uploadedImageIdsByField: Record<string, string> = {};
          const uploadErrors: Array<{ fieldName: string; error: string }> = [];

          if (Object.keys(pendingFiles).length > 0) {
            // Upload all files and track any failures
            await Promise.allSettled(
              Object.entries(pendingFiles).map(async ([fieldName, file]) => {
                try {
                  const imageId = await uploadImage(file);
                  uploadedImageIdsByField[fieldName] = imageId;
                } catch (err: any) {
                  const errorMessage = err?.message || "Unknown error occurred";
                  uploadErrors.push({ fieldName, error: errorMessage });
                  if (import.meta.env.DEV) {
                    console.error(`[Save Action] Image upload failed for field ${fieldName}:`, err);
                  }
                }
              })
            );

            // If there are upload errors, show alert and stop the save process
            if (uploadErrors.length > 0) {
              const errorMessages = uploadErrors.map(e => `${e.fieldName}: ${e.error}`).join("\n");
              const alertMessage = uploadErrors.length === 1
                ? `Failed to upload file for ${uploadErrors[0].fieldName}. ${uploadErrors[0].error}`
                : `Failed to upload ${uploadErrors.length} file(s). Please check and try again.\n\n${errorMessages}`;

              ensureSlidingTimeout(dispatch, {
                message: alertMessage,
                type: "error",
              }, 8000);

              // Also show browser alert for critical errors
              alert(alertMessage);
              dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
              return; // Stop save process if uploads failed
            }
          }

          // Map direct fields dynamically
          Object.entries(fieldMappings).forEach(([bindValue, jsonAttribute]) => {
            // Skip subform bind values (they are handled separately)
            const isSubformField = subformComponents.some(
              (subform) => (subform.BindValue || subform.name) === bindValue
            );
            if (!isSubformField) {
              const fieldConfig = getFieldConfig(bindValue);
              const isDateField =
                fieldConfig?.componentType === ComponentType.DATE_FIELD ||
                fieldConfig?.componentType === ComponentType.DATETIME_FIELD;
              const isDropdown =
                fieldConfig?.componentType === ComponentType.SELECT_DROPDOWN ||
                fieldConfig?.componentType === ComponentType.SEARCHABLE_DROPDOWN;
              const isMandatory =
                fieldConfig?.IsMandatory || fieldConfig?.required;
              const fieldTypeNorm = String(
                fieldConfig?.componentType ?? fieldConfig?.controlType ?? ""
              ).toLowerCase();
              const isCheckbox =
                fieldConfig?.componentType === ComponentType.CHECKBOX ||
                fieldTypeNorm === String(ComponentType.CHECKBOX).toLowerCase();
              const isNumericField = isNumericFormFieldConfig(fieldConfig);
              let value = formData[bindValue];
              // Edit mode fallback: some fields are stored under Json_Attribute key in formData.
              // For numeric fields, do NOT treat "" as missing — user cleared the field and
              // it must become 0 in the payload (not the previously loaded Json_Attribute value).
              const bindValueMissing = isNumericField
                ? value === undefined || value === null
                : value === undefined || value === null || value === "";
              if (
                bindValueMissing &&
                jsonAttribute &&
                formData[jsonAttribute] !== undefined &&
                formData[jsonAttribute] !== null &&
                !(isNumericField && formData[jsonAttribute] === "")
              ) {
                value = formData[jsonAttribute];
              }
              if (fieldTypeNorm === String(ComponentType.FILES).toLowerCase()) {
                const uploadedId =
                  uploadedImageIdsByField[bindValue] ??
                  (fieldConfig?.name
                    ? uploadedImageIdsByField[fieldConfig.name]
                    : undefined) ??
                  (fieldConfig?.BindValue
                    ? uploadedImageIdsByField[fieldConfig.BindValue]
                    : undefined) ??
                  (fieldConfig?.bindValue
                    ? uploadedImageIdsByField[fieldConfig.bindValue]
                    : undefined);
                if (uploadedId !== undefined) {
                  value = uploadedId;
                }
              }

              // Debug logging for date fields
              if (isDateField && import.meta.env.DEV) {
                console.log(
                  `[Save Action] Date field: ${bindValue} -> ${jsonAttribute}`,
                  {
                    value,
                    isMandatory,
                    formatted: value ? formatDateForAPI(value) : "empty",
                  }
                );
              }

              // Handle dropdown fields - include both ID and description
              if (isDropdown) {
                const descAttribute = `${jsonAttribute}_desc`;

                if (value !== null && value !== undefined && value !== "") {
                  // Get description from lookup options
                  const lookupName =
                    fieldConfig?.lookupName || fieldConfig?.LookupName;
                  let description = "";
                  let actualValue = value; // Default to the value from formData

                  if (lookupName) {
                    // Determine lookup key - handle dependent lookups
                    let lookupOptions: any[] = [];
                    let usedLookupKey = lookupName;

                    // Check if this is a dependent lookup
                    const parentField =
                      fieldConfig?.parentField ||
                      (fieldConfig?.BindParent
                        ? Object.keys(fieldConfig.BindParent)[0]
                        : undefined);

                    if (parentField) {
                      const parentValue = formData[parentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        usedLookupKey = `${lookupName}_${parentValue}`;
                        lookupOptions = lookupsState.lookups[usedLookupKey] || [];

                        // Fallback: If specific dependent lookup is empty, try the base lookup name
                        // This handles cases where data might be in the base lookup (e.g. from initial load)
                        if (lookupOptions.length === 0) {
                          const baseOptions = lookupsState.lookups[lookupName] || [];
                          if (baseOptions.length > 0) {
                            if (import.meta.env.DEV) {
                              console.log(`[Save Action] Using base lookup as fallback for ${bindValue}:`, {
                                dependentKey: usedLookupKey,
                                baseKey: lookupName,
                              });
                            }
                            lookupOptions = baseOptions;
                          }
                        }
                      } else {
                        // Parent value missing, fallback to base lookup
                        lookupOptions = lookupsState.lookups[lookupName] || [];
                      }
                    } else {
                      // Not dependent, or parent value missing
                      lookupOptions = lookupsState.lookups[lookupName] || [];
                    }

                    if (import.meta.env.DEV && isDropdown && lookupName === "vehicletype") {
                      console.log(`[Save Action] Debug dependent lookup for vehicletype:`, {
                        bindValue,
                        lookupName,
                        parentField,
                        parentValue: parentField ? formData[parentField] : "N/A",
                        usedLookupKey,
                        optionsCount: lookupOptions.length,
                      });
                    }

                    // Try to find option by value/id first (normal case)
                    let selectedOption = lookupOptions.find(
                      (opt: any) =>
                        opt.value === value ||
                        String(opt.value) === String(value) ||
                        opt.id === value ||
                        String(opt.id) === String(value)
                    );

                    // If not found, try matching by label (when formData has label instead of ID)
                    if (!selectedOption) {
                      selectedOption = lookupOptions.find(
                        (opt: any) =>
                          opt.label === value ||
                          String(opt.label) === String(value) ||
                          (opt as any).name === value ||
                          String((opt as any).name) === String(value)
                      );

                      // If found by label, use the ID (value or id) for the payload
                      if (selectedOption) {
                        actualValue =
                          selectedOption.value ||
                          (selectedOption as any).id ||
                          value;
                        if (import.meta.env.DEV) {
                          console.log(
                            `[Save Action] Dropdown value was label, converted to ID for ${bindValue} (${jsonAttribute}):`,
                            {
                              originalValue: value,
                              convertedValue: actualValue,
                              lookupName,
                            }
                          );
                        }
                      }
                    }

                    if (selectedOption) {
                      description =
                        selectedOption.label ||
                        (selectedOption as any).name ||
                        "";
                    } else if (import.meta.env.DEV) {
                      console.warn(
                        `[Save Action] Dropdown option not found for ${bindValue} (${jsonAttribute}):`,
                        {
                          lookupName,
                          value,
                          availableOptions: lookupOptions.slice(0, 3), // Log first 3 options
                        }
                      );
                    }
                  }

                  // Use the actual ID value (not the label) for the payload.
                  // Preserve numeric/boolean dropdown values (e.g. 1/0) instead of coercing to string.
                  payload.data[jsonAttribute] =
                    actualValue === undefined || actualValue === null
                      ? ""
                      : typeof actualValue === "number" || typeof actualValue === "boolean"
                        ? actualValue
                        : String(actualValue);
                  // Always include description field (even if empty)
                  payload.data[descAttribute] = description;

                  // Duplicate field with BindValue if it differs from Json_Attribute
                  if (bindValue && bindValue !== jsonAttribute) {
                    payload.data[bindValue] = actualValue;
                  }

                  // CRITICAL: If this is the parentid field, also add it as parentid (not just parent_id)
                  // Backend needs parentid in params for SQL queries (e.g., update service_details where id = %(parentid)s)
                  if (isParentIdBindValue(bindValue) || jsonAttribute === "parent_id") {
                    // Only overwrite if actualValue is not empty, OR if we don't have a parentid yet
                    if ((actualValue !== undefined && actualValue !== null && actualValue !== "") || !payload.data.parentid) {
                      payload.data.parentid = actualValue || payload.data.parentid || "";
                      payload.data.parent_id = actualValue || payload.data.parentid || "";
                    }
                    // CRITICAL: Ensure parent_id_desc is always included (backend expects it)
                    // If description is empty, try to get it from lookup options or formData
                    if (!description || description === "") {
                      // Try to get description from formData (might be stored as parentid_selectedData)
                      const selectedData =
                        (bindValue ? formData[`${bindValue}_selectedData`] : null) ||
                        formData.parentid1_selectedData ||
                        formData.parentid_selectedData ||
                        formData.parent_id_selectedData;
                      if (selectedData && typeof selectedData === 'object') {
                        description = (selectedData as any).name || (selectedData as any).label || (selectedData as any).estimation_no || "";
                      }
                      // If still empty, try to get from lookup options
                      if (!description && lookupName) {
                        // Try both the dependent lookup key and base lookup name
                        let lookupOptions: any[] = [];
                        const fieldParentField = fieldConfig?.parentField || fieldConfig?.Parent_Field;
                        if (fieldParentField && formData[fieldParentField]) {
                          const dependentKey = `${lookupName}_${formData[fieldParentField]}`;
                          lookupOptions = lookupsState.lookups[dependentKey] || [];
                        }
                        if (lookupOptions.length === 0) {
                          lookupOptions = lookupsState.lookups[lookupName] || [];
                        }
                        const foundOption = lookupOptions.find((opt: any) =>
                          opt.value === actualValue || String(opt.value) === String(actualValue) ||
                          opt.id === actualValue || String(opt.id) === String(actualValue)
                        );
                        if (foundOption) {
                          description = foundOption.label || (foundOption as any).name || "";
                        }
                      }
                    }
                    payload.data[descAttribute] = description || "";
                    if (import.meta.env.DEV) {
                      console.log("[Save Action] Added parentid from field mapping:", {
                        parentid: payload.data.parentid,
                        parent_id: payload.data.parent_id,
                        parent_id_desc: description || payload.data.parent_id_desc || "",
                      });
                    }

                    // Final assignment to ensure consistency
                    payload.data[jsonAttribute] = payload.data.parent_id;
                    payload.data[descAttribute] = description || payload.data.parent_id_desc || "";
                  }
                } else if (isMandatory) {
                  // For mandatory dropdowns, include empty values
                  payload.data[jsonAttribute] = "";
                  payload.data[descAttribute] = "";
                } else if (isParentIdBindValue(bindValue) || jsonAttribute === "parent_id") {
                  // CRITICAL: For parentid field (even if not mandatory), always include parent_id and parent_id_desc
                  // Backend expects these fields even if empty
                  payload.data[jsonAttribute] = value || "";
                  payload.data[descAttribute] = "";
                  payload.data.parentid = value || "";
                  if (import.meta.env.DEV) {
                    console.log("[Save Action] Added parentid field (no value):", {
                      parentid: value || "",
                      parent_id: value || "",
                      parent_id_desc: "",
                    });
                  }
                }
              }
              // For mandatory date fields, always include them
              else if (isMandatory && isDateField) {
                if (
                  value &&
                  value !== "" &&
                  value !== null &&
                  value !== undefined
                ) {
                  payload.data[jsonAttribute] = formatDateForAPI(value);
                } else {
                  // Include empty string for mandatory date fields (API will validate)
                  payload.data[jsonAttribute] = "";
                }
              }
              // Checkbox fields: persist as 1/0 (branch_switching and similar flags)
              else if (isCheckbox) {
                const checked =
                  value === true ||
                  value === 1 ||
                  value === "1" ||
                  (typeof value === "string" &&
                    ["true", "yes", "on"].includes(value.trim().toLowerCase()));
                payload.data[jsonAttribute] = checked ? 1 : 0;
                if (bindValue && bindValue !== jsonAttribute) {
                  payload.data[bindValue] = checked;
                }
              }
              // Handle boolean/checkbox fields - convert to "True"/"False" string
              else if (typeof value === "boolean") {
                payload.data[jsonAttribute] = value ? "True" : "False";
              }
              // Numeric fields: cleared / empty must persist as "0" (not omitted, not stale loaded value)
              else if (isNumericField) {
                const raw =
                  value === null || value === undefined
                    ? ""
                    : String(value).trim();
                const finalNumeric = raw === "" ? "0" : raw;
                payload.data[jsonAttribute] = finalNumeric;
                if (bindValue && bindValue !== jsonAttribute) {
                  payload.data[bindValue] = finalNumeric;
                }
              }
              // For non-mandatory date fields or other fields
              else if (value !== null && value !== undefined) {
                if (value !== "") {
                  // Format date fields
                  if (isDateField) {
                    const formattedDateValue = formatDateForAPI(value);
                    payload.data[jsonAttribute] = formattedDateValue;
                    if (bindValue && bindValue !== jsonAttribute) {
                      payload.data[bindValue] = formattedDateValue;
                    }
                  } else {
                    // Fix: Ensure empty arrays are sent as null
                    // And for image/file uploads, ensure single items are sent as strings, not arrays
                    let finalValue = value;

                    if (Array.isArray(value)) {
                      if (value.length === 0) {
                        finalValue = null;
                      } else if (
                        value.length === 1 &&
                        (fieldTypeNorm ===
                          String(ComponentType.IMAGE_UPLOAD).toLowerCase() ||
                          fieldTypeNorm ===
                            String(ComponentType.FILES).toLowerCase())
                      ) {
                        finalValue = value[0];
                      }
                    }

                    // Convert numbers to strings for consistency in generic payloads,
                    // specifically ensuring fields like 'discount', 'amount' are strings.
                    if (typeof finalValue === "number") {
                      finalValue = String(finalValue);
                    }

                    payload.data[jsonAttribute] = finalValue;
                    if (bindValue && bindValue !== jsonAttribute) {
                      payload.data[bindValue] = finalValue;
                    }
                  }
                }
                // For date/time fields (non-mandatory), include empty string
                else if (
                  (jsonAttribute.toLowerCase().includes("date") ||
                    jsonAttribute.toLowerCase().includes("time")) &&
                  !isMandatory
                ) {
                  payload.data[jsonAttribute] = "";
                }
              }
              // Handle string values that represent booleans (for checkbox fields using TEXT_FIELD)
              else if (
                jsonAttribute === "is_cash" &&
                (value === "true" ||
                  value === "True" ||
                  value === "false" ||
                  value === "False")
              ) {
                payload.data[jsonAttribute] =
                  value === "true" || value === "True" ? "True" : "False";
              }
              // For mandatory non-date fields that are missing, include empty string
              else if (isMandatory && !isDateField && !isDropdown) {
                payload.data[jsonAttribute] = "";
              }
              // For is_cash field, always include "False" if not provided
              else if (
                jsonAttribute === "is_cash" &&
                (value === null || value === undefined || value === "")
              ) {
                payload.data[jsonAttribute] = "False";
              }
            }
          });

          // Uploaded file ids: merge from raw form field tree. API configs often use camelCase
          // (bindValue, json_Attribute, componentType "files") so getFieldConfig / strict enum checks
          // can miss FILES in the mapping loop above.
          if (Object.keys(uploadedImageIdsByField).length > 0) {
            const visitForUploadedFiles = (fields: any[] | undefined) => {
              if (!fields || !Array.isArray(fields)) return;
              for (const field of fields) {
                const ct = String(
                  field?.componentType ?? field?.controlType ?? ""
                ).toLowerCase();
                if (ct === "files") {
                  const bk = field.BindValue ?? field.bindValue ?? field.name;
                  const jsonAttr =
                    field.Json_Attribute ?? field.json_Attribute ?? bk;
                  const imgId =
                    (bk != null && bk !== ""
                      ? uploadedImageIdsByField[String(bk)]
                      : undefined) ??
                    (field.name != null && field.name !== ""
                      ? uploadedImageIdsByField[String(field.name)]
                      : undefined);
                  if (imgId != null && String(imgId) !== "" && jsonAttr) {
                    payload.data[String(jsonAttr)] = imgId;
                  }
                }
                visitForUploadedFiles(field.Fields);
                visitForUploadedFiles(field.childComponents);
                visitForUploadedFiles(field.childControls);
              }
            };
            visitForUploadedFiles(formFields);
          }

          // Proforma safeguard: always include discount fields in payload.
          // Some flows keep discount only in derived UI state and it may be skipped by generic mapping.
          if (String(activeScreenId) === "175") {
            // Prefer BindValue when user cleared the field ("" → 0); do not revive stale Json_Attribute.
            const resolvedProformaDiscount = resolveNumericPayloadValue(
              formData.txt_discount,
              payload.data.txt_discount,
              payload.data.discount,
              formData.discount
            );
            payload.data.discount = resolvedProformaDiscount;
            payload.data.txt_discount = resolvedProformaDiscount;

            const resolvedProformaRoundOff = resolveNumericPayloadValue(
              formData.txt_round_off,
              payload.data.txt_round_off,
              payload.data.round_off_amt,
              formData.round_off_amt
            );
            payload.data.round_off_amt = resolvedProformaRoundOff;
            payload.data.txt_round_off = resolvedProformaRoundOff;

            payload.data.spare_line_discount_total = resolveNumericPayloadValue(
              payload.data.spare_line_discount_total,
              formData.spare_line_discount_total
            );
            payload.data.labour_line_discount_total = resolveNumericPayloadValue(
              payload.data.labour_line_discount_total,
              formData.labour_line_discount_total
            );
            payload.data.misc_line_discount_total = resolveNumericPayloadValue(
              payload.data.misc_line_discount_total,
              formData.misc_line_discount_total
            );

            if (import.meta.env.DEV) {
              console.log("[Save Action][Proforma] Final enforced summary fields:", {
                discount: payload.data.discount,
                txt_discount: payload.data.txt_discount,
                round_off_amt: payload.data.round_off_amt,
              });
            }
          }

          // Add Estimate (116): persist header discount + round off (empty → "0").
          if (String(activeScreenId) === "116") {
            const resolvedEstDiscount = resolveNumericPayloadValue(
              formData.txt_discount,
              payload.data.txt_discount,
              payload.data.discount,
              formData.discount
            );
            payload.data.discount = resolvedEstDiscount;
            payload.data.txt_discount = resolvedEstDiscount;

            const resolvedEstRoundOff = resolveNumericPayloadValue(
              formData.txt_round_off,
              payload.data.txt_round_off,
              payload.data.round_off_amt,
              formData.round_off_amt
            );
            payload.data.round_off_amt = resolvedEstRoundOff;
            payload.data.txt_round_off = resolvedEstRoundOff;
          }

          // Jobcard (156): persist round off from estimate prefill on save.
          if (String(activeScreenId) === "156") {
            const resolvedRoundOff = resolveNumericPayloadValue(
              formData.txt_round_off,
              payload.data.txt_round_off,
              payload.data.round_off_amt,
              formData.round_off_amt
            );
            payload.data.round_off_amt = resolvedRoundOff;
            payload.data.txt_round_off = resolvedRoundOff;
          }

          // Invoice (158 / 173): persist round off from jobcard/estimate prefill on save.
          if (String(activeScreenId) === "158" || String(activeScreenId) === "173") {
            const resolvedInvRoundOff = resolveNumericPayloadValue(
              formData.txt_round_off,
              payload.data.txt_round_off,
              payload.data.round_off_amt,
              formData.round_off_amt
            );
            payload.data.round_off_amt = resolvedInvRoundOff;
            payload.data.txt_round_off = resolvedInvRoundOff;
          }

          // ROBUST FIX: Explicitly remove parentid for Product Add (106)
          // This handles cases where it might have been added by initial injection or field mapping (drp_group)
          if (activeScreenId === "106_add_product" || moduleEndpoint === "product_add") {
            if (payload.data.parentid) {
              delete payload.data.parentid;
              if (import.meta.env.DEV) {
                console.log("[Save Action] Explicitly removed parentid for Product Add");
              }
            }
            // Also ensure purchase_rate and sale_rate are strings if present
            if (payload.data.purchase_rate !== undefined && typeof payload.data.purchase_rate === 'number') {
              payload.data.purchase_rate = String(payload.data.purchase_rate);
            }
            if (payload.data.sale_rate !== undefined && typeof payload.data.sale_rate === 'number') {
              payload.data.sale_rate = String(payload.data.sale_rate);
            }
            // Also fix the bind values if they exist
            if (payload.data.txt_purchase_rate !== undefined && typeof payload.data.txt_purchase_rate === 'number') {
              payload.data.txt_purchase_rate = String(payload.data.txt_purchase_rate);
            }
            if (payload.data.txt_sale_rate !== undefined && typeof payload.data.txt_sale_rate === 'number') {
              payload.data.txt_sale_rate = String(payload.data.txt_sale_rate);
            }
          }

          // Process subforms dynamically
          const instance = getState().forms.formInstanceCounter;
          const subformItems: any[] = [];

          for (const subformConfig of subformComponents) {
            if ((subformConfig as any).excludeFromSave === true) {
              continue;
            }
            const bindValue = subformConfig.BindValue || subformConfig.name;
            const nameValue = subformConfig.name || subformConfig.BindValue;
            const tableName =
              subformConfig.TableName || subformConfig.tableName || "";
            const subformName = subformConfig.Name || subformConfig.name || "";

            if (
              !isSubformIncludedInSave(
                subformConfig,
                formVisibilityRules,
                formData as Record<string, unknown>,
                lookupsState.lookups
              )
            ) {
              if (import.meta.env.DEV) {
                console.log(
                  `[Save Action] Skipping hidden subform from save: ${subformName}`
                );
              }
              continue;
            }

            const rawFormDataValue = formData[bindValue] ?? formData[nameValue];
            // SUB_MODULE may store rows under BindValue and/or name (e.g. jobcard_details vs purchase_order_details).
            const formDataValue =
              subformConfig.componentType === ComponentType.SUB_MODULE
                ? resolveSubformRowsForSave(
                    formData as Record<string, any>,
                    bindValue,
                    nameValue
                  )
                : rawFormDataValue;

            // Handle empty arrays - include subform with empty data array instead of skipping
            const isEmptySubform =
              !formDataValue ||
              !Array.isArray(formDataValue) ||
              formDataValue.length === 0;

            if (subformConfig.componentType === ComponentType.SUB_MODULE) {
              const isInvoiceLineSubformEarly =
                isInvoiceLineSubModuleTable(tableName) ||
                isInvoiceLineSubModuleTable(subformName);
              const invoiceIdForLineReconcile = String(
                formData?.id ?? payload?.data?.id ?? ""
              ).trim();
              // Invoice edit with no visible lines still needs orphan soft-deletes.
              const skipEmptyInvoiceLineReconcile =
                isEmptySubform &&
                isEditMode &&
                isInvoiceLineSubformEarly &&
                invoiceIdForLineReconcile !== "";

              // For SUB_MODULE, include even if empty (with empty data array)
              if (isEmptySubform && !skipEmptyInvoiceLineReconcile) {
                if (
                  !isSubformIncludedInSave(
                    subformConfig,
                    formVisibilityRules,
                    formData as Record<string, unknown>,
                    lookupsState.lookups
                  )
                ) {
                  continue;
                }
                subformItems.push({
                  tablename: tableName,
                  name: subformName,
                  data: [],
                  manadatoryResult: {
                    success: true,
                    fields: [],
                  },
                });
                continue;
              }
              // Extract field mappings for subform
              // SubModule uses childControls, but also check childComponents and Fields for compatibility
              const subformFields =
                subformConfig.childControls ||
                subformConfig.childComponents ||
                subformConfig.Fields ||
                [];
              const subformFieldMappings = extractFieldMappings(subformFields);

              // Resolve value from row trying all keys the UI might use (SubModule uses control.name || control.BindValue;
              // API config can use different casing, so try bindVal, jsonAttr, and field config keys).
              const getValueFromRow = (
                row: Record<string, any>,
                bv: string,
                ja: string,
                fieldCfg?: any
              ): any => {
                const keys = [
                  bv,
                  ja,
                  fieldCfg?.name,
                  fieldCfg?.BindValue,
                  fieldCfg?.Json_Attribute,
                  fieldCfg?.json_Attribute,
                  ja.charAt(0).toUpperCase() + ja.slice(1),
                  bv.charAt(0).toUpperCase() + bv.slice(1),
                ].filter(Boolean) as string[];
                for (const k of keys) {
                  if (row[k] !== undefined && row[k] !== null) return row[k];
                }
                return undefined;
              };

              // Debug: Log if no field mappings found
              if (
                Object.keys(subformFieldMappings).length === 0 &&
                import.meta.env.DEV
              ) {
                console.warn(
                  `[Subform] No field mappings found for ${subformName}`,
                  {
                    hasChildControls: !!subformConfig.childControls,
                    hasChildComponents: !!subformConfig.childComponents,
                    hasFields: !!subformConfig.Fields,
                    childControlsLength: subformConfig.childControls?.length || 0,
                    childComponentsLength:
                      subformConfig.childComponents?.length || 0,
                    fieldsLength: subformConfig.Fields?.length || 0,
                  }
                );
              }

              const isPurchaseReturnLines =
                subformName === "purchase_return_details" ||
                tableName === "purchase_return_details";
              const purchaseReturnUiOnlyKeys = new Set([
                "inv_qty",
                "txt_inv_qty",
              ]);
              const excludedSubformSaveKeys = new Set<string>();
              const collectExcludedSubformKeys = (fields: any[]) => {
                fields?.forEach((field: any) => {
                  if (field.excludeFromSave === true) {
                    [
                      field.BindValue,
                      field.bindValue,
                      field.name,
                      field.Json_Attribute,
                      field.json_Attribute,
                    ]
                      .filter(Boolean)
                      .forEach((key) => excludedSubformSaveKeys.add(String(key)));
                  }
                  if (field.Fields && Array.isArray(field.Fields)) {
                    collectExcludedSubformKeys(field.Fields);
                  }
                  if (
                    field.childComponents &&
                    Array.isArray(field.childComponents)
                  ) {
                    collectExcludedSubformKeys(field.childComponents);
                  }
                  if (field.childControls && Array.isArray(field.childControls)) {
                    collectExcludedSubformKeys(field.childControls);
                  }
                  if (
                    field.FooterControls &&
                    Array.isArray(field.FooterControls)
                  ) {
                    collectExcludedSubformKeys(field.FooterControls);
                  }
                });
              };
              collectExcludedSubformKeys(subformFields);
              if (isPurchaseReturnLines) {
                purchaseReturnUiOnlyKeys.forEach((key) =>
                  excludedSubformSaveKeys.add(key)
                );
              }
              const rowsForSaveSource = Array.isArray(formDataValue)
                ? formDataValue
                : [];
              const rowsForSave = isPurchaseReturnLines
                ? rowsForSaveSource.filter((item: any) => {
                    const qty = parseFloat(
                      String(item?.qty ?? item?.txt_qty ?? 0)
                    );
                    return !Number.isNaN(qty) && qty > 0;
                  })
                : rowsForSaveSource;

              // Create (PUT/insert) cannot soft-delete existing DB rows. Sending those
              // rows with their persisted ids makes InsertSubForm hit PK conflicts
              // ("A record with this information already exists"). Drop them here;
              // edit mode still sends them as act=edit + soft_delete=1.
              const isLookupItemsSubform =
                String(tableName || "").toLowerCase() === "lookup_items" ||
                String(subformName || "").toLowerCase() ===
                  "vehiclemake_items" ||
                String(subformName || "").toLowerCase() === "vehicletype_items";

              let rowsEligibleForSave = isEditMode
                ? rowsForSave
                : rowsForSave.filter((item: any) => {
                    const softDeleted =
                      item?.soft_delete === 1 ||
                      item?.soft_delete === "1" ||
                      item?.soft_delete === true;
                    return !softDeleted;
                  });

              // Invoice edit: same product → act=edit (reuse DB id); missing lines → soft_delete.
              // Needed when inspection Create Invoice reseeds from estimation (no invoice line ids).
              const isInvoiceLineSubform =
                isInvoiceLineSubModuleTable(tableName) ||
                isInvoiceLineSubModuleTable(subformName);
              const invoiceParentIdForReconcile = String(
                formData?.id ?? payload?.data?.id ?? ""
              ).trim();
              if (
                isEditMode &&
                isInvoiceLineSubform &&
                invoiceParentIdForReconcile !== ""
              ) {
                try {
                  const existingInvoiceLines = await apiService.getSubModuleData(
                    String(tableName || subformName),
                    invoiceParentIdForReconcile
                  );
                  rowsEligibleForSave = reconcileInvoiceLineRowsForEditSave(
                    rowsEligibleForSave,
                    existingInvoiceLines,
                    String(tableName || subformName)
                  );
                  if (import.meta.env.DEV) {
                    console.log(
                      "[Save Action] Reconciled invoice lines for edit",
                      {
                        tableName,
                        invoiceId: invoiceParentIdForReconcile,
                        formRows: rowsForSaveSource.length,
                        reconciled: rowsEligibleForSave.length,
                        softDeletes: rowsEligibleForSave.filter(
                          (r: any) =>
                            r?.soft_delete === 1 ||
                            r?.soft_delete === "1" ||
                            r?.soft_delete === true
                        ).length,
                      }
                    );
                  }
                } catch (reconcileErr) {
                  console.warn(
                    "[Save Action] Invoice line reconcile failed; continuing without orphan soft-deletes",
                    reconcileErr
                  );
                }
              }

              // Vehicle make/type: drop blank names; collapse duplicate names so
              // historical double-inserts are soft-deleted instead of kept forever.
              if (isLookupItemsSubform && isLookupMasterScreen) {
                const normalizeLookupName = (row: any): string =>
                  String(row?.name ?? row?.txt_subName ?? "")
                    .trim()
                    .toLowerCase();

                rowsEligibleForSave = rowsEligibleForSave.flatMap(
                  (item: any) => {
                    const softDeleted =
                      item?.soft_delete === 1 ||
                      item?.soft_delete === "1" ||
                      item?.soft_delete === true;
                    const nameKey = normalizeLookupName(item);
                    if (!nameKey) {
                      // Blank name: soft-delete if it already exists, otherwise omit.
                      if (
                        isEditMode &&
                        getPersistedSubModuleRowId(item) &&
                        !softDeleted
                      ) {
                        return [{ ...item, soft_delete: 1, act: "edit" }];
                      }
                      return [];
                    }
                    return [item];
                  }
                );

                const seenNames = new Map<string, number>();
                const deduped: any[] = [];
                for (const item of rowsEligibleForSave) {
                  const softDeleted =
                    item?.soft_delete === 1 ||
                    item?.soft_delete === "1" ||
                    item?.soft_delete === true;
                  const nameKey = normalizeLookupName(item);
                  if (!nameKey || softDeleted) {
                    deduped.push(item);
                    continue;
                  }
                  if (seenNames.has(nameKey)) {
                    // Extra copy of same make/type → soft-delete on this save.
                    if (isEditMode && getPersistedSubModuleRowId(item)) {
                      deduped.push({
                        ...item,
                        soft_delete: 1,
                        act: "edit",
                      });
                    }
                    // Brand-new duplicate add with no id: drop it.
                    continue;
                  }
                  seenNames.set(nameKey, deduped.length);
                  deduped.push(item);
                }
                rowsEligibleForSave = deduped;
              }

              let subformData = rowsEligibleForSave.map(
                (item: any, index: number) => {
                  // In edit mode for MULTI_SUB_LIST, "id" must be the persisted row id
                  // returned by edit API (e.g. service_checklist row id), while "itemid"
                  // stays as the checklist master item reference.
                  const isSoftDeletedRow =
                    item.soft_delete === 1 ||
                    item.soft_delete === "1" ||
                    item.soft_delete === true;
                  const existingItemId = getPersistedSubModuleRowId(item);
                  // Lookup masters: always trust persisted child ids in edit mode.
                  // Treating them as act=add regenerates ids and inserts duplicates
                  // under parent_id (e.g. vehicle make "6").
                  const invoiceLineTables = new Set([
                    "service_invoice_spareparts",
                    "service_invoice_labour",
                    "service_invoice_miscellaneous",
                  ]);
                  const isInvoiceLineSubform =
                    invoiceLineTables.has(
                      String(tableName || "").toLowerCase()
                    ) ||
                    invoiceLineTables.has(
                      String(subformName || "").toLowerCase()
                    );
                  // Invoice lines that already belong to this invoice (parent_id match
                  // or loaded with created_at) must update via act=edit even if act was
                  // incorrectly left as "add".
                  const invoiceParentId = String(
                    formData?.id ?? formDataValue?.[0]?.parent_id ?? ""
                  ).trim();
                  const rowParentId = String(
                    item?.parent_id ?? item?.parentid ?? ""
                  ).trim();
                  const invoiceLineAlreadyPersisted =
                    isInvoiceLineSubform &&
                    isEditMode &&
                    existingItemId !== "" &&
                    (isPersistedSubModuleRowForEdit(item, invoiceParentId) ||
                      (invoiceParentId !== "" &&
                        rowParentId !== "" &&
                        rowParentId === invoiceParentId));

                  const isExistingItem =
                    isEditMode &&
                    existingItemId !== "" &&
                    (isLookupItemsSubform ||
                      isSoftDeletedRow ||
                      !item.act ||
                      item.act !== "add" ||
                      invoiceLineAlreadyPersisted);

                  // CRITICAL FIX: Always generate new ID for new items.
                  // Do not use item.id as it might be from the lookup source (dup) which causes "record already exists" error.
                  const itemId = isExistingItem
                    ? existingItemId
                    : generateSubformItemId(
                      index,
                      resolveUserCode(decryptedData),
                      instance
                    );

                  const mappedItem: any = {
                    id: itemId,
                    act: isSoftDeletedRow
                      ? "edit"
                      : isExistingItem
                        ? item.act && item.act !== "add"
                          ? item.act
                          : "edit"
                        : item.act || "add",
                    created_at: formattedDate,
                    created_by:
                      decryptedData.userid || decryptedData.uuid || decryptedData.empId || decryptedData.userId || "1",
                  };

                  // Preserve itemid for existing items in edit mode
                  if (isExistingItem && item.itemid) {
                    mappedItem.itemid = item.itemid;
                  }

                  // CRITICAL: First pass - convert all dropdown labels to IDs
                  // This ensures VAT ID and other dropdowns are converted before saving or ExpressionV2 calculations
                  const convertedItem: Record<string, any> = { ...item };
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      const fieldConfig = subformFields.find(
                        (f: any) =>
                          f.BindValue === bindVal ||
                          f.name === bindVal ||
                          f.Json_Attribute === jsonAttr ||
                          f.json_Attribute === jsonAttr
                      );
                      if (fieldConfig?.excludeFromSave === true) {
                        return;
                      }
                      const isDropdown =
                        fieldConfig?.componentType ===
                        ComponentType.SELECT_DROPDOWN ||
                        fieldConfig?.componentType ===
                        ComponentType.SEARCHABLE_DROPDOWN;
                      const lookupName =
                        fieldConfig?.lookupName || fieldConfig?.LookupName;

                      if (isDropdown && lookupName) {
                        let value = getValueFromRow(item, bindVal, jsonAttr, fieldConfig);
                        if (
                          value &&
                          typeof value === "string" &&
                          value !== ""
                        ) {
                          const lookupOptions =
                            lookupsState.lookups[lookupName] || [];

                          const selectedOption = lookupOptions.find(
                            (opt: any) =>
                              opt.label === value ||
                              String(opt.label) === String(value) ||
                              (opt as any).name === value ||
                              String((opt as any).name) === String(value)
                          );
                          if (selectedOption) {
                            const idValue =
                              selectedOption.value ||
                              (selectedOption as any).id ||
                              value;
                            convertedItem[bindVal] = idValue;
                            convertedItem[jsonAttr] = idValue;
                            if (import.meta.env.DEV && idValue !== value) {
                              console.log(
                                `[Subform Save] Pre-converted ${jsonAttr} from label to ID:`,
                                { original: value, converted: idValue, lookupName }
                              );
                            }
                          }
                        }
                      }
                    }
                  );

                  // Map fields dynamically - use convertedItem which has IDs instead of labels
                  // Note: For Parts Issue, business logic (issued_qty="1.0", bal_qty="0.0") is handled by buildPartsIssuePayload
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      const fieldConfig = subformFields.find(
                        (f: any) =>
                          f.BindValue === bindVal ||
                          f.name === bindVal ||
                          f.Json_Attribute === jsonAttr ||
                          f.json_Attribute === jsonAttr
                      );
                      if (fieldConfig?.excludeFromSave === true) {
                        return;
                      }
                      // Resolve value from row using all keys UI might use (fixes amount/discount not passed when API config or different casing)
                      let value = getValueFromRow(convertedItem, bindVal, jsonAttr, fieldConfig);
                      if (value === undefined || value === null) {
                        value = getValueFromRow(item, bindVal, jsonAttr, fieldConfig);
                      }
                      // CRITICAL FIX: Include calculated numeric fields even if they're "0" or "0.00"
                      // This ensures amount, discount, vat_amt, net_amt etc. are always included for SUM calculations
                      const isNumericField =
                        jsonAttr === "amount" ||
                        jsonAttr === "discount" ||
                        jsonAttr === "discounted_amount" ||
                        jsonAttr === "net_discount" ||
                        jsonAttr === "net_amount" ||
                        jsonAttr === "total_amount" ||
                        jsonAttr === "amount_paid" ||
                        jsonAttr === "due_amount" ||
                        jsonAttr === "vat_amt" ||
                        jsonAttr === "net_amt" ||
                        jsonAttr === "rate" ||
                        jsonAttr === "qty" ||
                        jsonAttr === "issued_qty" ||
                        jsonAttr === "bal_qty" ||
                        jsonAttr === "purchase_rate" ||
                        jsonAttr === "stock_qty" ||
                        jsonAttr === "return_qty" ||
                        jsonAttr === "return_net_amt" ||
                        jsonAttr === "inv_qty" ||
                        jsonAttr === "unit_discount" ||
                        jsonAttr === "line_discount" ||
                        jsonAttr === "src_line_id";

                      // For numeric fields, always include (even when value is missing - default to "0")
                      // so that submodule payloads (e.g. payment split amount) are never missing the column
                      if (isNumericField && (value === undefined || value === null)) {
                        value = "0";
                      }
                      const shouldInclude =
                        value !== undefined &&
                        value !== null &&
                        (value !== "" || isNumericField);

                      if (shouldInclude) {
                        // For Parts Issue, normalize numeric fields to strings (backend expects string format)
                        const isPartsIssue = activeScreenId === "120_add_parts_issue" || moduleEndpoint === "parts_issue";

                        let finalValue = value;
                        if (isNumericField) {
                          if (value === "" || value === null || value === undefined) {
                            finalValue = "0";
                          } else {
                            const numValue = parseFloat(String(value));
                            if (!isNaN(numValue)) {
                              // For Parts Issue, send as string (backend expects string format)
                              finalValue = String(numValue);
                            } else {
                              finalValue = "0";
                            }
                          }
                        }

                        // If multiple fields map to same Json_Attribute, combine them
                        if (mappedItem[jsonAttr]) {
                          mappedItem[jsonAttr] = [
                            mappedItem[jsonAttr],
                            finalValue,
                          ]
                            .filter(Boolean)
                            .join(" ");
                        } else {
                          if (Array.isArray(finalValue) || (typeof finalValue === "object" && finalValue !== null)) {
                            mappedItem[jsonAttr] = finalValue;
                          } else {
                            mappedItem[jsonAttr] = (finalValue !== undefined && finalValue !== null) ? String(finalValue) : "";
                          }
                        }
                      }
                    }
                  );
                  // FINAL SAFETY NET (GENERIC, AGGRESSIVE):
                  // Whatever is present on the submodule row object MUST go to the payload.
                  // We don't rely on field mappings here; we just mirror the entire row (except meta keys)
                  // into mappedItem so that amount/discount and any other user-edited values are never lost.
                  const rawRow = item || {};
                  Object.entries(rawRow).forEach(([rawKey, rawValue]) => {
                    if (
                      rawKey === "id" ||
                      rawKey === "itemid" ||
                      rawKey === "act" ||
                      rawKey === "created_at" ||
                      rawKey === "created_by" ||
                      excludedSubformSaveKeys.has(rawKey)
                    ) {
                      return;
                    }

                    if (Array.isArray(rawValue) || (typeof rawValue === "object" && rawValue !== null)) {
                      mappedItem[rawKey] = rawValue;
                    } else {
                      mappedItem[rawKey] = rawValue === undefined || rawValue === null
                        ? ""
                        : String(rawValue);
                    }
                  });

                  if (isSoftDeletedRow) {
                    mappedItem.soft_delete = "1";
                    mappedItem.act = "edit";
                    // Only reuse persisted ids on update; create path must never
                    // re-insert an existing lookup_items / child row id.
                    if (isEditMode && existingItemId) {
                      mappedItem.id = existingItemId;
                    }
                  }

                  return mappedItem;
                }
              );

              // For Parts Issue, ensure correct tablename and name
              let finalTableName = tableName;
              let finalSubformName = subformName;

              if (activeScreenId === "120_add_parts_issue" || moduleEndpoint === "parts_issue") {
                // For service_spare_parts_issue subform, use correct structure
                if (subformName === "service_spare_parts_issue" ||
                  tableName === "service_spare_parts_issue_details" ||
                  subformName === "service_spare_parts_issue_details") {
                  finalTableName = "service_spare_parts_issue_details";
                  finalSubformName = "service_spare_parts_issue";
                }
              }

              // Screens 188 / 190 / 225 / 227: legacy DB JSON may still use invoice_recivebles / purchase_recivebles
              if (
                activeScreenId === "188" ||
                activeScreenId === "190" ||
                activeScreenId === "193" ||
                activeScreenId === "195" ||
                moduleEndpoint === "bill_allocations" ||
                moduleEndpoint === "bill_wise_release" ||
                moduleEndpoint === "vendor_bill_allocations" ||
                moduleEndpoint === "vendor_bill_wise_release"
              ) {
                const isVendorRelease =
                  activeScreenId === "195" ||
                  moduleEndpoint === "vendor_bill_wise_release";
                const isVendorAllocation =
                  activeScreenId === "193" ||
                  moduleEndpoint === "vendor_bill_allocations";
                const isCustomerRelease =
                  activeScreenId === "190" ||
                  moduleEndpoint === "bill_wise_release";
                if (
                  subformName === "invoice_recivebles" ||
                  subformName === "invoice_receivables" ||
                  subformName === "purchase_recivebles" ||
                  tableName === "invoice_recivebles_split" ||
                  subformName === "bill_wise_release_invoice" ||
                  tableName === "bill_wise_release_invoice_split" ||
                  subformName === "bill_wise_release_payment_sources" ||
                  tableName === "bill_wise_release_payment_sources_split" ||
                  subformName === "vendor_bill_allocations_purchase" ||
                  tableName === "vendor_bill_allocations_purchase_split" ||
                  tableName === "vendor_bill_wise_release_purchase_split"
                ) {
                  if (isVendorRelease) {
                    finalTableName = "vendor_bill_wise_release_purchase_split";
                    finalSubformName = "vendor_bill_wise_release_purchase";
                  } else if (isVendorAllocation) {
                    finalTableName = "vendor_bill_allocations_purchase_split";
                    finalSubformName = "vendor_bill_allocations_purchase";
                  } else if (isCustomerRelease) {
                    const isPaymentSourcesSubmodule =
                      subformName === "bill_wise_release_payment_sources" ||
                      tableName === "bill_wise_release_payment_sources_split" ||
                      tableName === "bill_wise_release_unregistered_split" ||
                      bindValue === "bill_wise_release_unregistered_split";
                    if (isPaymentSourcesSubmodule) {
                      finalTableName = "bill_wise_release_unregistered_split";
                      finalSubformName = "bill_wise_release_payment_sources";
                    } else {
                      finalTableName = "bill_wise_release_invoice_split";
                      finalSubformName = "bill_wise_release_invoice";
                    }
                  } else {
                    finalTableName = "bill_allocations_invoice_split";
                    finalSubformName = "bill_allocations_invoice";
                  }
                }
              }

              subformItems.push({
                tablename: finalTableName,
                name: finalSubformName,
                data: subformData,
                manadatoryResult: {
                  success: true,
                  fields: [],
                },
              });
            } else if (
              subformConfig.componentType === ComponentType.MULTI_SUB_LIST
            ) {
              // For MULTI_SUB_LIST, skip if empty
              if (isEmptySubform) {
                continue;
              }

              // Extract field mappings for multi-sub-list
              const subformFields =
                subformConfig.childComponents || subformConfig.Fields || [];
              const subformFieldMappings = extractFieldMappings(subformFields);

              const subformData = formDataValue.map(
                (item: any, index: number) => {
                  // In edit mode for MULTI_SUB_LIST, use persisted row id first.
                  // "itemid" is only the checklist master reference and must not replace row id.
                  const existingItemId = item.id || item.itemid;
                  const isExistingItem =
                    isEditMode &&
                    existingItemId &&
                    String(existingItemId).trim() !== "";

                  const itemId = isExistingItem
                    ? existingItemId
                    : item.id ||
                    generateSubformItemId(
                      index,
                      resolveUserCode(decryptedData),
                      instance
                    );

                  const mappedItem: any = {
                    id: itemId,
                    act: isExistingItem ? item.act || "edit" : item.act || "add",
                    created_at: formattedDate,
                    created_by:
                      decryptedData.userid || decryptedData.uuid || decryptedData.empId || decryptedData.userId || "1",
                  };

                  // Keep itemid as checklist master reference; do not derive it from persisted row id.
                  if (item.itemid !== undefined && item.itemid !== null && String(item.itemid).trim() !== "") {
                    mappedItem.itemid = item.itemid;
                  } else if (!isExistingItem) {
                    mappedItem.itemid = String(index + 1);
                  }

                  // CRITICAL: First pass - convert all dropdown labels to IDs
                  // This ensures VAT ID and other dropdowns are converted before ExpressionV2 calculations
                  const convertedItem: Record<string, any> = { ...item };
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      const fieldConfig = subformFields.find(
                        (f: any) =>
                          f.BindValue === bindVal ||
                          f.name === bindVal ||
                          f.Json_Attribute === jsonAttr
                      );
                      const isDropdown =
                        fieldConfig?.componentType ===
                        ComponentType.SELECT_DROPDOWN ||
                        fieldConfig?.componentType ===
                        ComponentType.SEARCHABLE_DROPDOWN;
                      const lookupName =
                        fieldConfig?.lookupName || fieldConfig?.LookupName;

                      if (isDropdown && lookupName) {
                        let value = item[bindVal] ?? item[jsonAttr];
                        if (value && typeof value === "string" && value !== "") {
                          const lookupOptions = lookupsState.lookups[lookupName] || [];

                          // If lookup is empty, log warning (might need to fetch it)
                          if (lookupOptions.length === 0 && import.meta.env.DEV && jsonAttr === "vat_id") {
                            console.warn(
                              `[Subform Save] Lookup ${lookupName} is empty for VAT ID conversion:`,
                              { value, bindVal, jsonAttr, availableLookups: Object.keys(lookupsState.lookups) }
                            );
                          }

                          const selectedOption = lookupOptions.find(
                            (opt: any) =>
                              opt.label === value ||
                              String(opt.label) === String(value) ||
                              (opt as any).name === value ||
                              String((opt as any).name) === String(value)
                          );
                          if (selectedOption) {
                            const idValue =
                              selectedOption.value || (selectedOption as any).id || value;
                            convertedItem[bindVal] = idValue;
                            convertedItem[jsonAttr] = idValue;
                            if (import.meta.env.DEV && idValue !== value) {
                              console.log(
                                `[Subform Save] Pre-converted ${jsonAttr} from label to ID:`,
                                { original: value, converted: idValue, lookupName }
                              );
                            }
                          }
                        }
                      }
                    }
                  );

                  // Map fields dynamically - try both BindValue and Json_Attribute (direct field name)
                  // Use convertedItem which has IDs instead of labels
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      // Try BindValue first, then fallback to Json_Attribute (direct field name)
                      // Use convertedItem which has IDs converted from the first pass
                      // Also check original item as fallback
                      let value = convertedItem[bindVal];
                      if (value === undefined || value === null) {
                        value = convertedItem[jsonAttr]; // Fallback to direct field name
                      }
                      // If still not found, try original item (shouldn't happen if conversion worked)
                      if (value === undefined || value === null) {
                        value = item[bindVal] ?? item[jsonAttr];
                      }

                      if (value !== undefined && value !== null) {
                        // Attempt to convert dropdown labels to IDs for subform fields
                        const fieldConfig = subformFields.find(
                          (f: any) =>
                            f.BindValue === bindVal ||
                            f.name === bindVal ||
                            f.Json_Attribute === jsonAttr
                        );
                        const isDropdown =
                          fieldConfig?.componentType ===
                          ComponentType.SELECT_DROPDOWN ||
                          fieldConfig?.componentType ===
                          ComponentType.SEARCHABLE_DROPDOWN;
                        const lookupName =
                          fieldConfig?.lookupName || fieldConfig?.LookupName;

                        if (
                          isDropdown &&
                          lookupName &&
                          typeof value === "string" &&
                          value !== ""
                        ) {
                          const lookupOptions = lookupsState.lookups[lookupName] || [];

                          // Try to find by value/id first
                          let selectedOption = lookupOptions.find(
                            (opt: any) =>
                              opt.value === value ||
                              String(opt.value) === String(value) ||
                              opt.id === value ||
                              String(opt.id) === String(value)
                          );

                          // If not found, try matching by label/name (e.g., "5%" -> "1")
                          if (!selectedOption) {
                            selectedOption = lookupOptions.find(
                              (opt: any) =>
                                opt.label === value ||
                                String(opt.label) === String(value) ||
                                (opt as any).name === value ||
                                String((opt as any).name) === String(value)
                            );

                            // If found by label, convert to ID
                            if (selectedOption) {
                              const convertedValue =
                                selectedOption.value ||
                                (selectedOption as any).id ||
                                value;
                              value = convertedValue;
                              if (import.meta.env.DEV) {
                                console.log(
                                  `[Subform Save] Converted dropdown label to ID for ${bindVal}/${jsonAttr}:`,
                                  {
                                    originalValue: item[bindVal] ?? item[jsonAttr],
                                    convertedValue,
                                    lookupName,
                                    fieldName: bindVal,
                                  }
                                );
                              }
                            } else if (import.meta.env.DEV) {
                              console.warn(
                                `[Subform Save] Dropdown label not found in lookup for ${jsonAttr}:`,
                                {
                                  originalValue: value,
                                  lookupName,
                                  availableLabels: lookupOptions.slice(0, 5).map((opt: any) => opt.label),
                                }
                              );
                            }
                          }

                          // CRITICAL: Always set the value for dropdowns (either original ID or converted from label)
                          // Don't set empty strings
                          // FINAL SAFETY CHECK: If value still looks like a label (contains %), try one more conversion
                          if (value !== "" && value !== undefined && value !== null) {
                            // If value still looks like a label (contains % or is short non-numeric string), 
                            // try one more conversion attempt as a safety net
                            if (typeof value === "string" && lookupName &&
                              (value.includes("%") || (value.length < 10 && !/^\d+$/.test(value)))) {
                              const lookupOptions = lookupsState.lookups[lookupName] || [];
                              const labelOption = lookupOptions.find(
                                (opt: any) =>
                                  opt.label === value ||
                                  String(opt.label) === String(value) ||
                                  (opt as any).name === value ||
                                  String((opt as any).name) === String(value)
                              );
                              if (labelOption) {
                                const finalIdValue = labelOption.value || (labelOption as any).id || value;
                                if (finalIdValue !== value) {
                                  value = finalIdValue;
                                  if (import.meta.env.DEV && jsonAttr === "vat_id") {
                                    console.log(`[Subform Save] Final safety conversion for vat_id:`, {
                                      original: item[bindVal] ?? item[jsonAttr],
                                      converted: value,
                                      lookupName,
                                    });
                                  }
                                }
                              } else if (import.meta.env.DEV && jsonAttr === "vat_id") {
                                console.warn(`[Subform Save] VAT ID label not found in lookup (final safety check):`, {
                                  value,
                                  lookupName,
                                  availableLabels: lookupOptions.slice(0, 5).map((opt: any) => opt.label),
                                  availableValues: lookupOptions.slice(0, 5).map((opt: any) => opt.value),
                                });
                              }
                            }

                            // Use the converted value (which should be the ID now)
                            mappedItem[jsonAttr] = String(value);

                            if (import.meta.env.DEV && jsonAttr === "vat_id") {
                              console.log(`[Subform Save] Final vat_id value set:`, {
                                jsonAttr,
                                value,
                                valueType: typeof value,
                                isNumeric: /^\d+$/.test(String(value)),
                              });
                            }
                          }
                        } else {
                          // Not a dropdown - handle boolean/checkbox fields - convert to "True"/"False" string
                          if (typeof value === "boolean") {
                            mappedItem[jsonAttr] = value ? "True" : "False";
                          } else if (value !== "") {
                            mappedItem[jsonAttr] = String(value);
                          }
                        }
                      }
                    }
                  );

                  // CRITICAL: Recalculate vat_amt and net_amt if VAT ID was converted
                  // ExpressionV2: [txt_amount] * ([drp_vat.taxrate]/100)
                  // ExpressionV2: [txt_amount] + [txt_vat_amt]
                  const vatIdField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "drp_vat" || f.name === "drp_vat") &&
                      f.Json_Attribute === "vat_id"
                  );
                  const amountField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "txt_amount" || f.name === "txt_amount") &&
                      f.Json_Attribute === "amount"
                  );
                  const vatAmtField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "txt_vat_amt" || f.name === "txt_vat_amt") &&
                      f.Json_Attribute === "vat_amt"
                  );
                  const netAmtField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "txt_net_amt" || f.name === "txt_net_amt") &&
                      f.Json_Attribute === "net_amt"
                  );

                  if (vatIdField && amountField && vatAmtField && netAmtField) {
                    // Get VAT ID from mappedItem (already converted to ID)
                    const vatId = mappedItem["vat_id"];
                    // Get amount from mappedItem
                    const amount = parseFloat(String(mappedItem["amount"] || 0));

                    if (vatId && amount > 0) {
                      // Get tax rate from lookup
                      const lookupName = vatIdField.lookupName || vatIdField.LookupName || "tax_rates";
                      const lookupOptions = lookupsState.lookups[lookupName] || [];
                      const vatOption = lookupOptions.find(
                        (opt: any) =>
                          opt.value === vatId ||
                          String(opt.value) === String(vatId) ||
                          opt.id === vatId ||
                          String(opt.id) === String(vatId)
                      );

                      if (vatOption) {
                        const taxRate = parseFloat(String((vatOption as any).taxrate || (vatOption as any).tax_rate || (vatOption as any).rate || 0));
                        if (taxRate > 0) {
                          // Recalculate vat_amt: amount * (taxrate / 100)
                          const vatAmt = (amount * taxRate) / 100;
                          mappedItem["vat_amt"] = formatNumericDecimal(vatAmt);

                          // Recalculate net_amt: amount + vat_amt
                          const netAmt = amount + vatAmt;
                          mappedItem["net_amt"] = formatNumericDecimal(netAmt);

                          if (import.meta.env.DEV) {
                            console.log(
                              `[Subform Save] Recalculated VAT and Net amounts:`,
                              {
                                vatId,
                                amount,
                                taxRate,
                                vatAmt: formatNumericDecimal(vatAmt),
                                netAmt: formatNumericDecimal(netAmt),
                                lookupName,
                              }
                            );
                          }
                        } else if (import.meta.env.DEV) {
                          console.warn(
                            `[Subform Save] Tax rate is 0 or not found for VAT ID:`,
                            { vatId, vatOption, lookupName }
                          );
                        }
                      } else if (import.meta.env.DEV) {
                        console.warn(
                          `[Subform Save] VAT option not found in lookup:`,
                          { vatId, lookupName, availableOptions: lookupOptions.slice(0, 3) }
                        );
                      }
                    }
                  }

                  // Also include any fields that directly match Json_Attribute names (fallback)
                  // This handles cases where data uses direct field names instead of BindValue
                  subformFields.forEach((field: any) => {
                    const jsonAttr = field.Json_Attribute;
                    if (jsonAttr && !mappedItem.hasOwnProperty(jsonAttr)) {
                      const value = item[jsonAttr];
                      if (value !== undefined && value !== null) {
                        if (typeof value === "boolean") {
                          mappedItem[jsonAttr] = value ? "True" : "False";
                        } else if (value !== "") {
                          mappedItem[jsonAttr] = String(value);
                        }
                      }
                    }
                  });

                  return mappedItem;
                }
              );

              subformItems.push({
                tablename: tableName,
                name: subformName,
                data: subformData,
                manadatoryResult: {
                  success: true,
                  fields: [],
                },
              });
            } else if (
              subformConfig.componentType === ComponentType.MULTI_CHECKBOX
            ) {
              // For MULTI_CHECKBOX, skip if empty (no selections and no soft-deletes)
              if (isEmptySubform) {
                continue;
              }

              // Same edit/add/soft_delete logic as MULTI_SUB_LIST: keep persisted row ids.
              const jsonAttribute =
                subformConfig.Json_Attribute || "work_requested_id";
              const subformData = formDataValue
                .map((selectedValue: any, index: number) => {
                  if (
                    selectedValue != null &&
                    typeof selectedValue === "object" &&
                    !Array.isArray(selectedValue)
                  ) {
                    const isSoftDeletedRow =
                      selectedValue.soft_delete === 1 ||
                      selectedValue.soft_delete === "1" ||
                      selectedValue.soft_delete === true;
                    // Create path cannot soft-delete persisted rows via insert.
                    if (!isEditMode && isSoftDeletedRow) {
                      return null;
                    }
                    const existingItemId =
                      getPersistedSubModuleRowId(selectedValue);
                    const isExistingItem =
                      isEditMode &&
                      existingItemId !== "" &&
                      (isSoftDeletedRow ||
                        !selectedValue.act ||
                        selectedValue.act !== "add");

                    const itemId = isExistingItem
                      ? existingItemId
                      : generateSubformItemId(
                          index,
                          resolveUserCode(decryptedData),
                          instance
                        );

                    const lookupId = normalizeLookupSelectionToId(
                      selectedValue[jsonAttribute] ?? selectedValue.value,
                      jsonAttribute
                    );
                    if (!lookupId) {
                      return null;
                    }

                    const mappedItem: any = {
                      id: itemId,
                      act: isSoftDeletedRow
                        ? "edit"
                        : isExistingItem
                          ? selectedValue.act || "edit"
                          : selectedValue.act || "add",
                      [jsonAttribute]: lookupId,
                      created_by:
                        decryptedData.userid ||
                        decryptedData.empId ||
                        decryptedData.userId ||
                        "1",
                      created_at: formattedDate,
                    };
                    if (isSoftDeletedRow) {
                      mappedItem.soft_delete = "1";
                    }
                    return mappedItem;
                  }

                  // Primitive selection (add mode / legacy string ids)
                  const lookupId = normalizeLookupSelectionToId(
                    selectedValue,
                    jsonAttribute
                  );
                  if (!lookupId) {
                    return null;
                  }
                  return {
                    [jsonAttribute]: lookupId,
                    act: "add",
                    id: generateSubformItemId(
                      index,
                      resolveUserCode(decryptedData),
                      instance
                    ),
                    created_by:
                      decryptedData.userid ||
                      decryptedData.empId ||
                      decryptedData.userId ||
                      "1",
                    created_at: formattedDate,
                  };
                })
                .filter(Boolean);

              if (subformData.length === 0) {
                continue;
              }

              subformItems.push({
                tablename: tableName,
                name: subformName,
                data: subformData,
                manadatoryResult: {
                  success: true,
                  fields: [],
                },
              });
            }
          }

          payload.data.subform = subformItems;

          if (
            moduleEndpoint === "bill_wise_release" ||
            activeScreenId === "190" ||
            tableName === "bill_wise_release"
          ) {
            const voucherType = resolveBillWiseReleaseVoucherType(
              formData as Record<string, unknown>
            );
            payload.data.voucher_type = voucherType;
            payload.data.drp_voucher_type = voucherType;
            payload.data.subform = buildBillWiseReleaseSubformItems(
              formData as Record<string, unknown>,
              subformItems
            );
            const billReleaseUserId =
              decryptedData.userid ||
              decryptedData.uuid ||
              decryptedData.empId ||
              decryptedData.userId ||
              "1";
            const billReleaseUserCode = resolveUserCode(decryptedData);
            const billReleaseInstance = getState().forms.formInstanceCounter;
            payload.data.subform = (payload.data.subform || []).map(
              (block: any) => {
                if (!Array.isArray(block?.data)) return block;
                return {
                  ...block,
                  data: block.data.map((row: any, index: number) => {
                    const existingId = row?.id;
                    if (
                      existingId != null &&
                      String(existingId).trim() !== ""
                    ) {
                      return {
                        ...row,
                        act: row.act || "add",
                        created_at: row.created_at || formattedDate,
                        created_by: row.created_by || billReleaseUserId,
                      };
                    }
                    return {
                      ...row,
                      id: generateSubformItemId(
                        index,
                        billReleaseUserCode,
                        billReleaseInstance
                      ),
                      act: "add",
                      created_at: formattedDate,
                      created_by: billReleaseUserId,
                    };
                  }),
                };
              }
            );

            const activeReleaseSubform = payload.data.subform.find((block: any) => {
              const name = String(block?.name ?? "").toLowerCase();
              return isBillWiseReleaseInvoiceMode(formData as Record<string, unknown>)
                ? name === "bill_wise_release_payment_sources"
                : name === "bill_wise_release_invoice";
            });
            let releaseNetTotal = 0;
            if (Array.isArray(activeReleaseSubform?.data)) {
              for (const row of activeReleaseSubform.data) {
                const amount = parseFloat(
                  String(row?.amount ?? row?.txt_release_amount ?? 0)
                );
                if (!Number.isNaN(amount)) releaseNetTotal += amount;
              }
            }
            const releaseNetStr = (Math.round(releaseNetTotal * 100) / 100).toFixed(
              2
            );
            payload.data.net_amount = releaseNetStr;
            payload.data.txt_net_amount = releaseNetStr;

            if (import.meta.env.DEV) {
              console.log("[Save Action] Bill wise release subform payload:", {
                voucherType,
                subforms: payload.data.subform?.map((s: any) => ({
                  name: s?.name,
                  tablename: s?.tablename,
                  rowCount: Array.isArray(s?.data) ? s.data.length : 0,
                })),
              });
            }
          }

          // Sales return (124): line VAT/net from taxable × rate; header returned_amount + return_vat_amount.
          if (
            String(activeScreenId) === "124" ||
            moduleEndpoint === "sales_ret_add"
          ) {
            const lineSubmoduleKeys = [
              "sales_return_spareparts",
              "sales_return_labour",
              "sales_return_miscellaneous",
            ];
            const round2 = (n: number) => Math.round(n * 100) / 100;
            const resolveSalesReturnTaxRate = (row: Record<string, any>): number => {
              const drpVat = row.drp_vat;
              if (drpVat && typeof drpVat === "object") {
                const tr = parseFloat(
                  String(
                    (drpVat as any).taxrate ??
                      (drpVat as any).tax_rate ??
                      (drpVat as any).rate ??
                      0
                  )
                );
                if (!Number.isNaN(tr) && tr >= 0) return tr;
              }
              const vatId = row.vat_id ?? row.drp_vat;
              if (vatId !== undefined && vatId !== null && vatId !== "") {
                const lookupOptions = lookupsState.lookups["tax_rates"] || [];
                const vatOption = lookupOptions.find(
                  (opt: any) =>
                    opt.value === vatId ||
                    String(opt.value) === String(vatId) ||
                    opt.id === vatId ||
                    String(opt.id) === String(vatId)
                );
                if (vatOption) {
                  const tr = parseFloat(
                    String(
                      (vatOption as any).taxrate ||
                        (vatOption as any).tax_rate ||
                        (vatOption as any).rate ||
                        0
                    )
                  );
                  if (!Number.isNaN(tr) && tr >= 0) return tr;
                }
              }
              return 0;
            };
            const computeSalesReturnLineAmounts = (
              row: Record<string, any>
            ): { vat: number; net: number } => {
              const rq = parseFloat(String(row.return_qty ?? row.txt_return_qty ?? 0));
              if (Number.isNaN(rq) || rq <= 0) return { vat: 0, net: 0 };
              const amount =
                parseFloat(String(row.amount ?? row.txt_amount ?? 0)) || 0;
              const lineDiscount =
                parseFloat(
                  String(row.line_discount ?? row.txt_line_discount ?? 0)
                ) || 0;
              const invQ = parseFloat(String(row.inv_qty ?? row.txt_inv_qty ?? 0));
              if (Number.isNaN(invQ) || invQ <= 0) return { vat: 0, net: 0 };
              const taxable = ((amount - lineDiscount) / invQ) * rq;
              const taxRate = resolveSalesReturnTaxRate(row);
              const vat = round2(taxable * (taxRate / 100));
              const net = round2(taxable + vat);
              return { vat, net };
            };
            const applySalesReturnLineAmounts = (row: Record<string, any>) => {
              const { vat, net } = computeSalesReturnLineAmounts(row);
              const rq = parseFloat(String(row.return_qty ?? row.txt_return_qty ?? 0));
              if (Number.isNaN(rq) || rq <= 0) return { vat: 0, net: 0 };
              const vatStr = String(vat);
              const netStr = String(net);
              row.vat_amt = vatStr;
              row.txt_vat_amt = vatStr;
              row.return_net_amt = netStr;
              row.txt_return_net_amt = netStr;
              return { vat, net };
            };
            let lineReturnTotal = 0;
            let lineVatTotal = 0;
            for (const key of lineSubmoduleKeys) {
              const subformEntry = subformItems.find(
                (s) => s?.name === key || s?.tablename === key
              );
              const rows =
                subformEntry?.data ||
                (Array.isArray(formData[key]) ? formData[key] : []) ||
                [];
              for (const row of rows) {
                if (row?.soft_delete === 1 || row?.soft_delete === "1") continue;
                const action = String(row?._action || row?.act || "").toLowerCase();
                if (action === "delete" || action === "remove") continue;
                const { vat, net } = applySalesReturnLineAmounts(row);
                lineReturnTotal += net;
                lineVatTotal += vat;
              }
            }
            const footerTotal =
              parseFloat(String(formData.return_spare_net ?? 0)) +
              parseFloat(String(formData.return_labour_net ?? 0)) +
              parseFloat(String(formData.return_misc_net ?? 0));
            const footerVatTotal =
              parseFloat(String(formData.return_spare_vat ?? 0)) +
              parseFloat(String(formData.return_labour_vat ?? 0)) +
              parseFloat(String(formData.return_misc_vat ?? 0));
            if (footerTotal > 0 && lineReturnTotal <= 0) {
              lineReturnTotal = footerTotal;
            }
            if (footerVatTotal > 0 && lineVatTotal <= 0) {
              lineVatTotal = footerVatTotal;
            }
            if (lineReturnTotal > 0) {
              const totalStr = String(round2(lineReturnTotal));
              payload.data.returned_amount = totalStr;
              payload.data.txt_return_amount = totalStr;
            }
            if (lineVatTotal > 0) {
              const vatStr = String(round2(lineVatTotal));
              payload.data.return_vat_amount = vatStr;
              payload.data.txt_return_vat_amount = vatStr;
            }
            const draftChecked =
              formData.chk_add_to_unadjusted ??
              formData.is_draft ??
              payload.data.chk_add_to_unadjusted ??
              payload.data.is_draft;
            const isDraft =
              draftChecked === true ||
              draftChecked === 1 ||
              draftChecked === "1" ||
              String(draftChecked).toLowerCase() === "true";
            payload.data.is_draft = isDraft ? 1 : 0;
            payload.data.chk_add_to_unadjusted = isDraft ? 1 : 0;
          }

          // Purchase return (126): persist draft flag + header totals from return lines.
          if (
            String(activeScreenId) === "126" ||
            moduleEndpoint === "pur_ret_add"
          ) {
            const prLineRows =
              subformItems.find(
                (s) =>
                  s?.name === "purchase_return_details" ||
                  s?.tablename === "purchase_return_details"
              )?.data || formData.purchase_return_details || [];
            let grossTotal = 0;
            let vatTotal = 0;
            let netTotal = 0;
            for (const row of prLineRows) {
              if (row?.soft_delete === 1 || row?.soft_delete === "1") continue;
              const action = String(row?._action || row?.act || "").toLowerCase();
              if (action === "delete" || action === "remove") continue;
              const qty = parseFloat(String(row?.qty ?? row?.txt_qty ?? 0));
              if (Number.isNaN(qty) || qty <= 0) continue;
              grossTotal += parseFloat(String(row?.amount ?? row?.txt_amount ?? 0)) || 0;
              vatTotal += parseFloat(String(row?.vat_amt ?? row?.txt_vat_amt ?? 0)) || 0;
              netTotal += parseFloat(String(row?.net_amt ?? row?.txt_net_amt ?? 0)) || 0;
            }
            if (netTotal <= 0) {
              const footerNet = parseFloat(
                String(formData.txt_sub_net_amount ?? formData.net_amount ?? 0)
              );
              const footerGross = parseFloat(
                String(formData.txt_sub_amount ?? formData.amount ?? 0)
              );
              const footerVat = parseFloat(
                String(formData.spare_vat_amount ?? formData.vat_amount ?? 0)
              );
              if (!Number.isNaN(footerNet) && footerNet > 0) netTotal = footerNet;
              if (!Number.isNaN(footerGross) && footerGross > 0) grossTotal = footerGross;
              if (!Number.isNaN(footerVat) && footerVat > 0) vatTotal = footerVat;
            }
            if (netTotal > 0) {
              const round2 = (n: number) => String(Math.round(n * 100) / 100);
              payload.data.amount = round2(grossTotal);
              payload.data.txt_sub_amount = round2(grossTotal);
              payload.data.vat_amount = round2(vatTotal);
              payload.data.spare_vat_amount = round2(vatTotal);
              payload.data.net_amount = round2(netTotal);
              payload.data.txt_sub_net_amount = round2(netTotal);
            }
            const draftCheckedPur =
              formData.chk_add_to_unadjusted ??
              formData.is_draft ??
              payload.data.chk_add_to_unadjusted ??
              payload.data.is_draft;
            const isDraftPur =
              draftCheckedPur === true ||
              draftCheckedPur === 1 ||
              draftCheckedPur === "1" ||
              String(draftCheckedPur).toLowerCase() === "true";
            payload.data.is_draft = isDraftPur ? 1 : 0;
            payload.data.chk_add_to_unadjusted = isDraftPur ? 1 : 0;
            const fromStockCheckedPur =
              formData.chk_from_stock ??
              formData.from_stock ??
              payload.data.chk_from_stock ??
              payload.data.from_stock;
            const isFromStockPur =
              fromStockCheckedPur === true ||
              fromStockCheckedPur === 1 ||
              fromStockCheckedPur === "1" ||
              String(fromStockCheckedPur).toLowerCase() === "true";
            payload.data.from_stock = isFromStockPur ? 1 : 0;
            payload.data.chk_from_stock = isFromStockPur ? 1 : 0;
          }

          // CRITICAL: Ensure parentid is always included in payload.data
          // Even if it was processed as a field, make sure it's present
          // Backend needs parentid to update service_details table (for jobcard)
          if (!payload.data.parentid && !payload.data.parent_id) {
            // Try to get from formData using utility function
            let parentId = getParentIdFromFormData(formData, "parentid");

            // If not found, check Redux store
            if (!parentId) {
              const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
              parentId = getParentIdFromFormData(storedFormData, "parentid");
            }

            // If still not found, check if parentid field exists in formData (might be from dropdown)
            if (!parentId) {
              parentId = formData.parentid || formData.parent_id || formData.parentId;
            }

            if (parentId) {
              payload.data.parentid = parentId;
              payload.data.parent_id = parentId;
              if (import.meta.env.DEV) {
                console.log("[Save Action] Added parentid after field processing:", parentId);
              }
            } else if (import.meta.env.DEV) {
              console.warn("[Save Action] parentid still missing after field processing:", {
                payloadDataKeys: Object.keys(payload.data),
                formDataKeys: Object.keys(formData).filter(k => k.toLowerCase().includes("parent") || k.toLowerCase().includes("estimat")),
              });
            }
          } else if (import.meta.env.DEV) {
            console.log("[Save Action] parentid already in payload:", {
              parentid: payload.data.parentid,
              parent_id: payload.data.parent_id,
            });
          }

          // Evaluate ExpressionV2 expressions for fields
          const evaluateExpressionV2 = (
            expression: string,
            formData: Record<string, any>,
            lookupsState: any
          ): any => {
            if (!expression || typeof expression !== "string") {
              return "";
            }

            // Helper function to extract property value (robustly handling 0 values and variations)
            const extractValue = (item: any, property: string): any => {
              if (!item) return null;
              const variations = [
                property,
                property.toLowerCase(),
                property.toUpperCase(),
                property.replace(/_/g, ""), // e.g. purchase_rate -> purchaserate
                property.charAt(0).toUpperCase() + property.slice(1), // e.g. rate -> Rate
              ];

              for (const v of variations) {
                if (item[v] !== undefined && item[v] !== null) {
                  return item[v];
                }
              }
              return null;
            };

            /** Convert time string (HH:mm or HH:mm:ss) to decimal hours for ExpressionV2 duration math. */
            const timeStringToDecimalHours = (val: any): number | null => {
              if (val === undefined || val === null || val === "") return null;
              const s = String(val).trim();
              const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
              if (!match) return null;
              const hours = parseInt(match[1], 10);
              const minutes = parseInt(match[2], 10);
              const seconds = match[3] ? parseInt(match[3], 10) : 0;
              return hours + minutes / 60 + seconds / 3600;
            };

            // Handle cross-subform expressions like [(service_spare_est_req).txt_sub_amount]
            // Pattern: [(subformName).fieldBindValue]
            // DYNAMIC: Resolve BindValue to Json_Attribute from FooterControls config
            const subformRefMatch = expression.match(/\[\(([^)]+)\)\.([^\]]+)\]/g);
            if (subformRefMatch) {
              let result = expression;
              for (const match of subformRefMatch) {
                const subformMatch = match.match(/\[\(([^)]+)\)\.([^\]]+)\]/);
                if (subformMatch) {
                  const subformName = subformMatch[1]; // e.g., "service_spare_est_req"
                  const fieldBindValue = subformMatch[2]; // e.g., "txt_sub_amount" (BindValue)

                  // DYNAMIC: Resolve BindValue to Json_Attribute by finding SubModule config
                  let jsonAttribute = fieldBindValue; // Default to BindValue if not found

                  // Find SubModule config to get FooterControls
                  const screenConfig = getResolvedScreenConfig(activeScreenId);
                  if (screenConfig) {
                    let subModuleConfig: any = null;
                    const foundSubModules: any[] = [];

                    walkComponentTree(screenConfig, (component) => {
                      if (component.componentType === ComponentType.SUB_MODULE) {
                        const compAny = component as any;
                        foundSubModules.push({
                          name: compAny.name,
                          Name: compAny.Name,
                          TableName: compAny.TableName,
                          tableName: compAny.tableName,
                          BindValue: compAny.BindValue,
                        });

                        // Match by name, Name, TableName, or tableName (case-insensitive)
                        const matches =
                          compAny.name?.toLowerCase() === subformName.toLowerCase() ||
                          compAny.Name?.toLowerCase() === subformName.toLowerCase() ||
                          compAny.TableName?.toLowerCase() === subformName.toLowerCase() ||
                          compAny.tableName?.toLowerCase() === subformName.toLowerCase();

                        if (matches) {
                          subModuleConfig = component;
                          return true; // Stop searching
                        }
                      }
                      return false;
                    });

                    if (subModuleConfig) {
                      const footerControls = (subModuleConfig as any).FooterControls || [];

                      if (import.meta.env.DEV) {
                        console.log(`[Save Action] Found SubModule config:`, {
                          subformName,
                          subModuleConfig: {
                            name: (subModuleConfig as any).name,
                            Name: (subModuleConfig as any).Name,
                            TableName: (subModuleConfig as any).TableName,
                            FooterControlsCount: footerControls.length,
                          },
                          footerControls: footerControls.map((fc: any) => ({
                            name: fc.name,
                            BindValue: fc.BindValue,
                            Json_Attribute: fc.Json_Attribute,
                          })),
                        });
                      }

                      // Find footer field by BindValue
                      const footerField = footerControls.find((fc: any) =>
                        (fc.name || fc.BindValue) === fieldBindValue
                      );

                      if (footerField) {
                        jsonAttribute = footerField.Json_Attribute || footerField.json_Attribute || fieldBindValue;

                        if (import.meta.env.DEV) {
                          console.log(`[Save Action] ✓ Resolved cross-subform reference:`, {
                            match,
                            subformName,
                            fieldBindValue,
                            jsonAttribute,
                            formDataHasKey: jsonAttribute in formData,
                            formDataValue: formData[jsonAttribute],
                            formDataType: typeof formData[jsonAttribute],
                          });
                        }
                      } else if (import.meta.env.DEV) {
                        console.warn(`[Save Action] Footer field not found:`, {
                          subformName,
                          fieldBindValue,
                          availableFooterFields: footerControls.map((fc: any) => ({
                            name: fc.name,
                            BindValue: fc.BindValue,
                            Json_Attribute: fc.Json_Attribute,
                          })),
                        });
                      }
                    } else if (import.meta.env.DEV) {
                      console.warn(`[Save Action] SubModule config not found:`, {
                        subformName,
                        fieldBindValue,
                        foundSubModules,
                        activeScreenId,
                      });
                    }
                  } else if (import.meta.env.DEV) {
                    console.warn(`[Save Action] Screen config not found:`, {
                      activeScreenId,
                      availableScreens: Object.keys(screenConfigRegistry),
                    });
                  }

                  // Get value from formData using Json_Attribute (what SubModule actually stores)
                  const value = formData[jsonAttribute];
                  const numValue = value !== undefined && value !== null && value !== ""
                    ? parseFloat(String(value)) || 0
                    : 0;

                  if (import.meta.env.DEV && numValue === 0 && jsonAttribute !== fieldBindValue) {
                    console.warn(`[Save Action] Cross-subform reference resolved to 0:`, {
                      match,
                      subformName,
                      fieldBindValue,
                      jsonAttribute,
                      formDataKeys: Object.keys(formData).filter(k =>
                        k.toLowerCase().includes(subformName.toLowerCase()) ||
                        k.toLowerCase().includes(fieldBindValue.toLowerCase()) ||
                        k.toLowerCase().includes(jsonAttribute.toLowerCase())
                      ),
                    });
                  }

                  result = result.replace(match, String(numValue));
                }
              }

              // Handle local field references like [txt_discount] or time fields [start_time], [end_time]
              const localFieldMatch = result.match(/\[([^\]]+)\]/g);
              if (localFieldMatch) {
                for (const match of localFieldMatch) {
                  const fieldName = match.slice(1, -1);
                  // Don't re-process subform references (they contain parentheses)
                  if (!fieldName.includes("(")) {
                    const value = formData[fieldName];
                    const timeHours = timeStringToDecimalHours(value);
                    const numValue =
                      timeHours !== null
                        ? timeHours
                        : value !== undefined && value !== null && value !== ""
                          ? parseFloat(String(value)) || 0
                          : 0;
                    result = result.replace(match, String(numValue));

                    if (import.meta.env.DEV) {
                      console.log(`[Save Action] Resolved local field reference:`, {
                        match,
                        fieldName,
                        value,
                        numValue,
                      });
                    }
                  }
                }
              }

              // Evaluate the expression (e.g., "50 + 0 + 0" = "50")
              try {
                // Replace any remaining field references with 0 (safeguard)
                const cleanedExpr = result.replace(/\[[^\]]+\]/g, "0");
                const evaluated = Function(`"use strict"; return (${cleanedExpr})`)();
                return String(evaluated || 0);
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.error(`[ExpressionV2] Error evaluating: ${expression}`, error);
                }
                return "0";
              }
            }

            // Handle simple field references like [drp_supplier.address]
            if (expression.startsWith("[") && expression.endsWith("]")) {
              // Remove brackets
              const expr = expression.slice(1, -1);

              // Handle field references like [drp_supplier.address]
              if (expr.includes(".")) {
                const [fieldName, attribute] = expr.split(".");
                const fieldValue = formData[fieldName];

                // CRITICAL: Handle 0 value (e.g. 0% VAT)
                if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
                  return "";
                }

                // Get lookup options for the field
                const fieldConfig = getFieldConfig(fieldName);
                const lookupName =
                  fieldConfig?.lookupName || fieldConfig?.LookupName;
                const parentField =
                  fieldConfig?.parentField || fieldConfig?.Parent_Field;

                if (lookupName) {
                  // Determine lookup key (handle dependent lookups)
                  let lookupKey = lookupName;
                  if (parentField) {
                    const parentValue = formData[parentField];
                    if (
                      parentValue !== undefined &&
                      parentValue !== null &&
                      parentValue !== ""
                    ) {
                      lookupKey = `${lookupName}_${parentValue}`;
                    }
                  }

                  const lookupOptions = lookupsState.lookups[lookupKey] || [];
                  const rawData = lookupsState.rawData[lookupKey] || [];

                  // First try to find in rawData (has all properties like purchase_rate, sale_rate, etc.)
                  let rawItem = rawData.find(
                    (item: any) =>
                      item.id === fieldValue ||
                      String(item.id) === String(fieldValue) ||
                      item.value === fieldValue ||
                      String(item.value) === String(fieldValue) ||
                      item.label === fieldValue ||
                      String(item.label) === String(fieldValue) ||
                      item.name === fieldValue ||
                      String(item.name) === String(fieldValue)
                  );

                  // If not found in rawData, try lookupOptions
                  let selectedOption = null;
                  if (!rawItem) {
                    selectedOption = lookupOptions.find(
                      (opt: any) =>
                        opt.value === fieldValue ||
                        String(opt.value) === String(fieldValue) ||
                        opt.id === fieldValue ||
                        String(opt.id) === String(fieldValue) ||
                        opt.label === fieldValue ||
                        String(opt.label) === String(fieldValue)
                    );
                  }

                  // Robustly extract property value
                  if (rawItem || selectedOption) {
                    let propValue = extractValue(rawItem, attribute);
                    if (propValue === null && selectedOption) {
                      propValue = extractValue(selectedOption, attribute);
                    }

                    if (propValue !== null && propValue !== undefined) {
                      return String(propValue);
                    }
                  }
                }
              }
            }

            return "";
          };

          // Process ExpressionV2 fields
          const processComponent = (comp: any) => {
            if (comp.ExpressionV2 && comp.Json_Attribute) {
              const ja = comp.Json_Attribute;
              if (comp.persistParentPayload !== false) {
                const value = evaluateExpressionV2(
                  comp.ExpressionV2,
                  formData,
                  lookupsState
                );
                if (value !== "" && value !== null && value !== undefined) {
                  payload.data[ja] = value;
                }
              }
            }

            // Process nested components
            if (comp.Fields && Array.isArray(comp.Fields)) {
              comp.Fields.forEach(processComponent);
            }
            if (comp.childComponents && Array.isArray(comp.childComponents)) {
              comp.childComponents.forEach(processComponent);
            }
          };

          formFields.forEach(processComponent);

          // Calculate SUM expressions from FooterControls
          const calculateSum = (
            fieldBindValue: string,
            subformConfig: any,
            subformItem: any
          ): number => {
            let sum = 0;

            // Find the Json_Attribute for the field name (bindValue)
            const subformFields =
              subformConfig.childControls || subformConfig.Fields || [];
            let jsonAttribute = fieldBindValue; // Default to bindValue

            for (const field of subformFields) {
              const bindValue = field.name || field.BindValue;
              if (bindValue === fieldBindValue && field.Json_Attribute) {
                jsonAttribute = field.Json_Attribute;
                break;
              }
            }

            // Sum up values from the specific subform item's data
            if (
              subformItem &&
              subformItem.data &&
              Array.isArray(subformItem.data)
            ) {
              for (const item of subformItem.data) {
                // Try both Json_Attribute and bindValue (mapped data uses Json_Attribute, but check both)
                const value =
                  item[jsonAttribute] !== undefined
                    ? item[jsonAttribute]
                    : item[fieldBindValue];

                if (value !== undefined && value !== null && value !== "") {
                  const numValue = parseFloat(String(value));
                  if (!isNaN(numValue)) {
                    sum += numValue;
                  }
                }
              }
            }

            return sum;
          };

          const formatFooterPayloadValue = (storedFooterValue: unknown): string => {
            if (typeof storedFooterValue === "number") {
              return formatNumericDecimal(storedFooterValue);
            }
            if (typeof storedFooterValue === "string") {
              const numValue = parseFloat(storedFooterValue);
              return isNaN(numValue)
                ? storedFooterValue
                : formatNumericDecimal(numValue);
            }
            return String(storedFooterValue);
          };

          const resolveFooterStoredValue = (
            jsonAttribute: string,
            bindValue?: string
          ): unknown => {
            if (
              formData[jsonAttribute] !== undefined &&
              formData[jsonAttribute] !== null &&
              formData[jsonAttribute] !== ""
            ) {
              return formData[jsonAttribute];
            }
            if (
              bindValue &&
              formData[bindValue] !== undefined &&
              formData[bindValue] !== null &&
              formData[bindValue] !== ""
            ) {
              return formData[bindValue];
            }
            if (formData[jsonAttribute] === 0 || formData[jsonAttribute] === "0") {
              return formData[jsonAttribute];
            }
            if (bindValue && (formData[bindValue] === 0 || formData[bindValue] === "0")) {
              return formData[bindValue];
            }
            return undefined;
          };

          // Process FooterControls for SubModules
          for (const subformConfig of subformComponents) {
            if (
              !isSubformIncludedInSave(
                subformConfig,
                formVisibilityRules,
                formData as Record<string, unknown>,
                lookupsState.lookups
              )
            ) {
              continue;
            }
            const subformName = subformConfig.Name || subformConfig.name || "";
            // Find matching subform item by name
            const subformItem = subformItems.find(
              (item) => item.name === subformName
            );

            if (
              subformConfig.FooterControls &&
              Array.isArray(subformConfig.FooterControls)
            ) {
              for (const footerControl of subformConfig.FooterControls) {
                const jsonAttribute =
                  footerControl.Json_Attribute ?? footerControl.json_Attribute;
                if (!jsonAttribute) {
                  continue;
                }
                if (footerControl.persistParentPayload === false) {
                  continue;
                }

                const bindValue =
                  footerControl.BindValue ?? footerControl.bindValue ?? footerControl.name;
                const expr = footerControl.ExpressionV2;

                if (expr) {
                  // Calculated footer fields (SUM expressions, net total, etc.)
                  const storedFooterValue = resolveFooterStoredValue(
                    jsonAttribute,
                    bindValue
                  );

                  const hasStoredValue =
                    storedFooterValue !== undefined &&
                    storedFooterValue !== null &&
                    storedFooterValue !== "";

                  if (hasStoredValue) {
                    const formattedValue = formatFooterPayloadValue(storedFooterValue);
                    payload.data[jsonAttribute] = formattedValue;

                    if (import.meta.env.DEV) {
                      console.log(
                        `[Form Submit Footer] Using stored value for ${jsonAttribute}: ${formattedValue}`,
                        {
                          expression: expr,
                          storedValue: storedFooterValue,
                          storedValueType: typeof storedFooterValue,
                          source: "formData",
                        }
                      );
                    }
                  } else {
                    // Fallback: Recalculate from subform data if stored value not available
                    const sumMatch = expr.match(/\[SUM:(\w+)\]/);
                    if (sumMatch) {
                      const fieldBindValue = sumMatch[1];
                      const sumValue = calculateSum(
                        fieldBindValue,
                        subformConfig,
                        subformItem
                      );
                      const formattedValue = formatNumericDecimal(sumValue);
                      payload.data[jsonAttribute] = formattedValue;

                      if (import.meta.env.DEV) {
                        console.log(
                          `[Form Submit Footer] Recalculated ${jsonAttribute}: ${formattedValue}`,
                          {
                            expression: expr,
                            fieldBindValue,
                            subformDataLength: subformItem?.data?.length || 0,
                            source: "recalculation",
                          }
                        );
                      }
                    }
                  }
                } else if (footerControl.IsReadOnly === false) {
                  // Editable footer fields without ExpressionV2 (e.g. round_off_amt)
                  const storedFooterValue = resolveFooterStoredValue(
                    jsonAttribute,
                    bindValue
                  );

                  const hasStoredValue =
                    storedFooterValue !== undefined &&
                    storedFooterValue !== null &&
                    storedFooterValue !== "";

                  if (hasStoredValue) {
                    const formattedValue = formatFooterPayloadValue(storedFooterValue);
                    payload.data[jsonAttribute] = formattedValue;

                    if (import.meta.env.DEV) {
                      console.log(
                        `[Form Submit Footer] Using editable footer value for ${jsonAttribute}: ${formattedValue}`,
                        { bindValue, source: "formData" }
                      );
                    }
                  }
                }
              }
            }
          }

          for (const subformConfig of subformComponents) {
            const footers = (subformConfig as any).FooterControls;
            if (!Array.isArray(footers)) continue;
            for (const fc of footers) {
              const ja = fc.Json_Attribute ?? fc.json_Attribute;
              if (fc.persistParentPayload === false && ja) {
                delete payload.data[ja];
              }
            }
          }

          // Set timestamp and user fields based on operation type
          // CREATE: set created_at and created_by
          // UPDATE: set modified_at and modified_by
          const userId = decryptedData.userid || decryptedData.empId || decryptedData.userId || "1";

          if (isEditMode) {
            // Update operation: set modified_at and modified_by
            // DO NOT include created_at, created_by, or transaction_branch_id in update payloads
            // (lookup masters below are the exception — child lookup_items inserts need branch)
            payload.data.modified_at = formattedDate;
            payload.data.modified_by = userId;

            if (import.meta.env.DEV) {
              console.log("[Save Action] Edit mode - Set modified_at/modified_by:", {
                modified_at: formattedDate,
                modified_by: userId,
              });
            }
          } else {
            // Create operation: set created_at and created_by
            // CRITICAL: These are required for insert operations
            payload.data.created_at = formattedDate;
            payload.data.created_by = userId;

            if (import.meta.env.DEV) {
              console.log("[Save Action] Create mode - Set created_at/created_by:", {
                created_at: formattedDate,
                created_by: userId,
              });
            }

            // Get branch ID - prioritize ID fields over name
            const branchId =
              saveTransactionBranchId ||
              decryptedData.branch_id ||
              decryptedData.branchId ||
              decryptedData.transaction_branch_id ||
              decryptedData.transactionBranchId ||
              decryptedData.transaction_branchid ||
              (decryptedData.loginUser &&
                typeof decryptedData.loginUser === "object" &&
                (decryptedData.loginUser as any).branch_id) ||
              auth.user?.branch_id ||
              (typeof layout.currentBranch === "object" && layout.currentBranch
                ? (layout.currentBranch as any).id
                : layout.currentBranch) ||
              "";

            // If branch ID is not found, log warning with available branch-related fields
            if (!branchId && import.meta.env.DEV) {
              const branchFields = Object.keys(decryptedData).filter((key) =>
                key.toLowerCase().includes("branch")
              );
              console.warn(
                "[Save Action] Branch ID not found in decryptedData.",
                {
                  availableBranchFields: branchFields,
                  branchFieldValues: branchFields.reduce((acc, key) => {
                    acc[key] = decryptedData[key];
                    return acc;
                  }, {} as Record<string, any>),
                  layoutCurrentBranchId: layout.currentBranch?.id,
                  authUserBranchId: auth.user?.branch_id,
                }
              );
            }

            payload.data.transaction_branch_id = branchId;
          }

          // Vehicle make/type: keep parent branch and copy it onto new lookup_items rows.
          // Backend InsertSubForm/UpdateSubForm reads parent data.transaction_branch_id.
          if (isLookupMasterScreen) {
            // lookup_name category row must not parent itself (was sending parent_id=id).
            delete payload.data.parentid;
            delete payload.data.parent_id;
            delete payload.data.parent_id_desc;

            const branchForLookupItems = String(
              payload.data.transaction_branch_id || saveTransactionBranchId || ""
            ).trim();
            if (branchForLookupItems) {
              payload.data.transaction_branch_id = branchForLookupItems;
              const subforms = Array.isArray(payload.data.subform)
                ? payload.data.subform
                : [];
              for (const sub of subforms) {
                const isLookupItemsSub =
                  String(sub?.tablename || "").toLowerCase() ===
                    "lookup_items" ||
                  String(sub?.name || "").toLowerCase() ===
                    "vehiclemake_items" ||
                  String(sub?.name || "").toLowerCase() === "vehicletype_items";
                if (!isLookupItemsSub || !Array.isArray(sub?.data)) continue;
                for (const row of sub.data) {
                  if (!row || typeof row !== "object") continue;
                  if (String(row.act || "").toLowerCase() !== "add") continue;
                  if (
                    !String(row.transaction_branch_id || "").trim()
                  ) {
                    row.transaction_branch_id = branchForLookupItems;
                  }
                }
              }
            }
          }

          payload.data.id = recordId;

          if (tableName === "service_job_approval") {
            delete payload.data.issue_status;
            delete payload.data.ISSUE_STATUS;
            delete payload.data.issueStatus;
            delete payload.data.chk_force_close_without_parts_issue;
            const rawForceClose =
              payload.data.force_close_without_parts_issue ??
              formData.chk_force_close_without_parts_issue ??
              formData.force_close_without_parts_issue;
            const forceCloseOn =
              rawForceClose === true ||
              rawForceClose === 1 ||
              rawForceClose === "1" ||
              String(rawForceClose ?? "").toLowerCase() === "true";
            payload.data.force_close_without_parts_issue = forceCloseOn ? 1 : 0;
          }

          // Comprehensive logging for submitted data
          console.log("=== FORM SUBMISSION LOG ===");
          console.log("[Save Action] Screen ID:", activeScreenId);
          console.log("[Save Action] Table Name:", tableName);
          console.log("[Save Action] Module Endpoint:", moduleEndpoint);
          console.log("[Save Action] Record ID:", recordId);
          console.log("[Save Action] Form Data (Raw):", formData);
          console.log("[Save Action] Form Data next_action/next_state:", {
            next_action: formData.next_action || formData.Next_Action || formData.nextAction,
            next_state: formData.next_state || formData.Next_State || formData.nextState,
          });
          const storedFormDataCheck = getState().forms.formDataByScreen[activeScreenId] || {};
          console.log("[Save Action] Stored Form Data next_action/next_state:", {
            next_action: storedFormDataCheck.next_action || storedFormDataCheck.Next_Action || storedFormDataCheck.nextAction,
            next_state: storedFormDataCheck.next_state || storedFormDataCheck.Next_State || storedFormDataCheck.nextState,
          });
          console.log("[Save Action] Payload Data:", payload.data);
          console.log("[Save Action] Subform Items:", subformItems);
          console.log("[Save Action] Field Mappings:", fieldMappings);
          console.log("[Save Action] Created At:", formattedDate);
          console.log("[Save Action] Created By:", payload.data.created_by);
          console.log(
            "[Save Action] Branch ID (transaction_branch_id):",
            payload.data.transaction_branch_id
          );
          console.log("[Save Action] Branch ID Source:", {
            branch_id: decryptedData.branch_id,
            branchId: decryptedData.branchId,
            transaction_branch_id: decryptedData.transaction_branch_id,
            transactionBranchId: decryptedData.transactionBranchId,
            loginUser_branch_id:
              decryptedData.loginUser &&
                typeof decryptedData.loginUser === "object"
                ? (decryptedData.loginUser as any).branch_id
                : undefined,
            layoutCurrentBranchId: layout.currentBranch?.id,
            authUserBranchId: auth.user?.branch_id,
          });
          console.log("=== END SUBMISSION LOG ===");

          // CRITICAL: Ensure payload structure matches expected format
          // Expected format: { data: { ... }, next_action: "", next_state: "" }
          // Verify payload has the correct structure
          if (!payload.data) {
            // If payload.data doesn't exist, restructure the payload
            const restructuredPayload = {
              data: {
                ...payload,
                subform: payload.subform || [],
              },
              next_action: payload.next_action || "",
              next_state: payload.next_state || "",
            };
            // Remove subform from root level if it exists
            if (restructuredPayload.data.subform) {
              // subform is already in data, keep it there
            }
            // Remove next_action and next_state from data if they exist
            delete restructuredPayload.data.next_action;
            delete restructuredPayload.data.next_state;
            Object.assign(payload, restructuredPayload);
          }

          // Check for workflow state fields (like approve_status) and use their value as next_state
          // Find workflow state fields in the form configuration
          let workflowStateValue: string | undefined;
          walkComponentTree(screenConfig, (component) => {
            if (component.componentType === ComponentType.SELECT_DROPDOWN) {
              const compAny = component as any;
              const lookupName = compAny.lookupName || compAny.LookupName;

              // Check if form has Workflow property
              const findFormComponent = (comps: ComponentConfig[]): any => {
                for (const comp of comps) {
                  if (comp.componentType === LayoutTypes.FORM) {
                    return comp;
                  }
                  const compAny = comp as any;
                  if (compAny.Fields) {
                    const found = findFormComponent(compAny.Fields);
                    if (found) return found;
                  }
                  if (comp.childComponents) {
                    const found = findFormComponent(comp.childComponents);
                    if (found) return found;
                  }
                }
                return null;
              };
              const formComp = findFormComponent(screenConfig);
              const workflowName = formComp?.Workflow || formComp?.workflow;

              // If this is a workflow state field (has Workflow and LookupName)
              if (workflowName && lookupName) {
                const fieldName = compAny.name || compAny.BindValue;
                const jsonAttr = compAny.Json_Attribute;
                // Get the value from formData
                const fieldValue = formData[fieldName] || formData[jsonAttr];
                if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
                  workflowStateValue = String(fieldValue);
                  return true; // Stop searching
                }
              }
            }
            return false;
          });

          // Ensure next_action and next_state are at root level
          // CRITICAL: Always get from formData (set during workflow navigation), even if payload already has empty strings
          // Get from formData first, then check if we need to get from Redux store as fallback
          const formDataNextAction =
            formData.next_action ||
            formData.Next_Action ||
            formData.nextAction;
          let formDataNextState =
            formData.next_state ||
            formData.Next_State ||
            formData.nextState;

          // CRITICAL: If workflow state field has a value, use it as next_state
          // This ensures that when user selects "Rejected" or "Put on hold", next_state matches the selection
          if (workflowStateValue) {
            formDataNextState = workflowStateValue;
            if (import.meta.env.DEV) {
              console.log("[Save Action] Using workflow state field value as next_state:", {
                workflowStateValue,
                fieldName: "approve_status",
                previousNextState: formData.next_state,
              });
            }
          }

          // If not in formData, try to get from Redux store
          if (!formDataNextAction || !formDataNextState) {
            const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
            payload.next_action = formDataNextAction ||
              storedFormData.next_action ||
              storedFormData.Next_Action ||
              storedFormData.nextAction ||
              "";
            payload.next_state = formDataNextState ||
              storedFormData.next_state ||
              storedFormData.Next_State ||
              storedFormData.nextState ||
              "";
          } else {
            payload.next_action = formDataNextAction;
            payload.next_state = formDataNextState;
          }

          if (import.meta.env.DEV) {
            console.log("[Save Action] Setting next_action and next_state:", {
              next_action: payload.next_action,
              next_state: payload.next_state,
              source: {
                fromFormData: !!(formData.next_action || formData.next_state),
                fromStore: !!(getState().forms.formDataByScreen[activeScreenId]?.next_action),
              },
            });
          }

          // Remove empty string values from payload.data
          // This ensures fields like "codeid": "", "codeid_desc": "", "item_name": "" are not included
          // CRITICAL: Always preserve parentid and parent_id fields even if empty, as backend needs them
          const cleanedPayloadData: Record<string, any> = {};
          const removedFields: string[] = [];

          // Fields that should always be preserved even if empty (backend requires them)
          // Include description fields for parentid as backend expects parent_id_desc
          const criticalFields = ['parentid', 'parent_id', 'parent_id_desc', 'id'];

          Object.entries(payload.data).forEach(([key, value]) => {
            // Always preserve critical fields even if empty
            if (criticalFields.includes(key.toLowerCase())) {
              cleanedPayloadData[key] = value !== undefined && value !== null ? value : "";
              return;
            }

            // Exclude only empty strings for other fields
            // Keep everything else: numbers (including 0), booleans, null, undefined, objects, arrays, non-empty strings
            if (value === "") {
              // Skip empty strings
              removedFields.push(key);
            } else {
              // Keep all other values
              cleanedPayloadData[key] = value;
            }
          });

          // CRITICAL: Ensure parentid is included from formData if it exists
          // Backend needs parentid to update service_details table
          if (!cleanedPayloadData.parentid && !cleanedPayloadData.parent_id) {
            // Use getParentIdFromFormData utility to robustly find parentid
            let parentId = getParentIdFromFormData(formData, "parentid");

            // If not found, also check Redux store for this screen
            if (!parentId) {
              const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
              parentId = getParentIdFromFormData(storedFormData, "parentid");
            }

            if (parentId) {
              cleanedPayloadData.parentid = parentId;
              cleanedPayloadData.parent_id = parentId;
              // CRITICAL: Also ensure parent_id_desc is included (backend expects it)
              if (!cleanedPayloadData.parent_id_desc) {
                // Try to get description from selectedData
                const selectedData =
                  formData.parentid1_selectedData ||
                  formData.parentid_selectedData ||
                  formData.parent_id_selectedData;
                if (selectedData && typeof selectedData === 'object') {
                  cleanedPayloadData.parent_id_desc = (selectedData as any).name || (selectedData as any).label || (selectedData as any).estimation_no || "";
                } else {
                  cleanedPayloadData.parent_id_desc = "";
                }
              }
              if (import.meta.env.DEV) {
                console.log("[Save Action] Added parentid from formData (after cleaning):", {
                  parentid: parentId,
                  parent_id: parentId,
                  parent_id_desc: cleanedPayloadData.parent_id_desc,
                });
              }
            } else if (import.meta.env.DEV) {
              console.warn("[Save Action] parentid still missing after cleaning, backend may fail:", {
                activeScreenId,
                formDataSample: Object.keys(formData).slice(0, 10),
              });
            }
          }

          // CRITICAL: Ensure parent_id_desc is always present if parent_id exists
          // Backend expects parent_id_desc even if empty
          if ((cleanedPayloadData.parentid || cleanedPayloadData.parent_id) && !cleanedPayloadData.parent_id_desc) {
            cleanedPayloadData.parent_id_desc = "";
            if (import.meta.env.DEV) {
              console.log("[Save Action] Added empty parent_id_desc to match parent_id");
            }
          }

          payload.data = cleanedPayloadData;

          if (import.meta.env.DEV && removedFields.length > 0) {
            console.log("[Save Action] Removed empty string fields:", removedFields);
          }

          if (import.meta.env.DEV) {
            console.log("[Save Action] Critical fields in payload:", {
              parentid: cleanedPayloadData.parentid,
              parent_id: cleanedPayloadData.parent_id,
              parent_id_desc: cleanedPayloadData.parent_id_desc,
              id: cleanedPayloadData.id,
            });
          }

          if (
            moduleEndpoint === "time_shift_add" ||
            tableName === "time_shift"
          ) {
            const timeShiftErrors = validateTimeShiftTimes({
              startTime:
                cleanedPayloadData.start_time ?? formData.start_time,
              endTime: cleanedPayloadData.end_time ?? formData.end_time,
              breakStartTime:
                cleanedPayloadData.break_start_time ??
                formData.break_start_time,
              breakEndTime:
                cleanedPayloadData.break_end_time ?? formData.break_end_time,
            });
            if (Object.keys(timeShiftErrors).length > 0) {
              const summary =
                timeShiftValidationSummary({
                  startTime:
                    cleanedPayloadData.start_time ?? formData.start_time,
                  endTime: cleanedPayloadData.end_time ?? formData.end_time,
                  breakStartTime:
                    cleanedPayloadData.break_start_time ??
                    formData.break_start_time,
                  breakEndTime:
                    cleanedPayloadData.break_end_time ??
                    formData.break_end_time,
                }) || "Invalid shift or break times.";
              ensureSlidingTimeout(
                dispatch,
                { message: summary, type: "error" },
                5000
              );
              dispatch(
                setScreenSaving({ screenId: activeScreenId, isSaving: false })
              );
              return;
            }
          }

          if (
            isPurchaseReturnContext(activeScreenId, moduleEndpoint) &&
            !(
              payload.data.is_draft === 1 ||
              payload.data.is_draft === true ||
              payload.data.chk_add_to_unadjusted === 1 ||
              payload.data.chk_add_to_unadjusted === true
            ) &&
            !isFromStockChecked(payload.data, formData)
          ) {
            const purchaseIdForValidation = resolvePurchaseIdForValidation(
              payload.data,
              formData
            );
            if (purchaseIdForValidation) {
              try {
                const partsIssueValidation =
                  await validatePurchaseReturnPartsIssue(
                    purchaseIdForValidation
                  );
                if (!partsIssueValidation.allowed) {
                  await showPurchaseReturnBlockedAlert(
                    dispatch,
                    partsIssueValidation.message
                  );
                  dispatch(
                    setScreenSaving({
                      screenId: activeScreenId,
                      isSaving: false,
                    })
                  );
                  return;
                }
              } catch (validationError) {
                await showPurchaseReturnBlockedAlert(
                  dispatch,
                  validationError instanceof Error
                    ? validationError.message
                    : "Unable to verify parts issue return status. Please try again."
                );
                dispatch(
                  setScreenSaving({
                    screenId: activeScreenId,
                    isSaving: false,
                  })
                );
                return;
              }
            }
          }

          if (isCustomerBillAllocationContext(activeScreenId, moduleEndpoint)) {
            const customerId = resolveBillAllocationPartyId(
              "customer",
              payload.data,
              formData
            );
            if (customerId) {
              try {
                const blockingValidation =
                  await validateBillAllocationBlockingEntries(
                    "customer",
                    customerId
                  );
                if (!blockingValidation.allowed) {
                  await showBillAllocationBlockedAlert(
                    dispatch,
                    blockingValidation.message
                  );
                  dispatch(
                    setScreenSaving({
                      screenId: activeScreenId,
                      isSaving: false,
                    })
                  );
                  return;
                }
              } catch (validationError) {
                await showBillAllocationBlockedAlert(
                  dispatch,
                  validationError instanceof Error
                    ? validationError.message
                    : "Unable to verify unadjusted entries for this customer. Please try again."
                );
                dispatch(
                  setScreenSaving({
                    screenId: activeScreenId,
                    isSaving: false,
                  })
                );
                return;
              }
            }
          }

          if (isVendorBillAllocationContext(activeScreenId, moduleEndpoint)) {
            const vendorId = resolveBillAllocationPartyId(
              "vendor",
              payload.data,
              formData
            );
            if (vendorId) {
              try {
                const blockingValidation =
                  await validateBillAllocationBlockingEntries(
                    "vendor",
                    vendorId
                  );
                if (!blockingValidation.allowed) {
                  await showBillAllocationBlockedAlert(
                    dispatch,
                    blockingValidation.message
                  );
                  dispatch(
                    setScreenSaving({
                      screenId: activeScreenId,
                      isSaving: false,
                    })
                  );
                  return;
                }
              } catch (validationError) {
                await showBillAllocationBlockedAlert(
                  dispatch,
                  validationError instanceof Error
                    ? validationError.message
                    : "Unable to verify unadjusted entries for this vendor. Please try again."
                );
                dispatch(
                  setScreenSaving({
                    screenId: activeScreenId,
                    isSaving: false,
                  })
                );
                return;
              }
            }
          }

          // Dynamic endpoint and method selection based on edit mode
          // Both edit and create mode use the same endpoint: /pabws/module/{moduleEndpoint}
          // The backend determines create vs update based on the presence of ID in payload
          const endpoint = `/pabws/module/${moduleEndpoint}`;

          // CRITICAL: For 107_add_inspection, user requires a FLAT payload structure
          // Unwrap payload.data and send it directly, discarding the wrapper and next_action/next_state
          // This fixes the issue where { data: {...}, next_action: ... } was being sent instead of {...}
          let finalPayload = payload;
          if (activeScreenId === "107_add_inspection") {
            finalPayload = payload.data;
            if (import.meta.env.DEV) {
              console.log("[Save Action] Unwrapped payload for 107_add_inspection (sending flat data)");
            }
          } else if (activeScreenId === "120_add_parts_issue" || moduleEndpoint === "parts_issue") {
            // CRITICAL: For Parts Issue, use dedicated payload builder to match Blazor format exactly
            // Blazor sends: { data: {...}, next_action: "...", next_state: "..." }
            // - All fields wrapped in data object
            // - next_action and next_state at root level
            // - issued_qty = "1.0", bal_qty = "0.0" for all items
            // - All numeric values as decimal strings

            // Build form state from payload.data and formData
            const formStateForBuilder = {
              ...payload.data,
              service_spare_parts_issue: payload.data.subform?.[0]?.data || formData.service_spare_parts_issue || [],
            };

            // Use dedicated payload builder
            finalPayload = buildPartsIssuePayload(
              formStateForBuilder,
              payload.next_action || "",
              payload.next_state || ""
            );

            // Runtime validation - fail fast before API call
            const validation = validatePartsIssuePayload(finalPayload);
            if (!validation.valid) {
              const errorMessage = `Parts Issue payload validation failed:\n${validation.errors.join("\n")}`;
              console.error("[Save Action] Parts Issue Payload Validation Failed:", validation.errors);
              ensureSlidingTimeout(
                dispatch,
                {
                  message: errorMessage,
                  type: "error",
                },
                10000
              );
              dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
              return;
            }

            if (import.meta.env.DEV) {
              console.log("[Save Action] Built Parts Issue payload using dedicated builder:", {
                hasData: !!finalPayload.data,
                hasSubform: !!finalPayload.data?.subform,
                subformLength: finalPayload.data?.subform?.length || 0,
                next_action: finalPayload.next_action,
                next_state: finalPayload.next_state,
              });
            }
            } else if (activeScreenId === "160_add_parts_request" || moduleEndpoint === "parts_request") {
              // CRITICAL: For Parts Request, use dedicated payload builder to match Blazor format exactly
              // Blazor sends: { data: {...}, next_action: "...", next_state: "..." }
              // - All fields wrapped in data object
              // - next_action and next_state at root level
              // - All numeric values as decimal strings (e.g., "1.0", "1000.0", "50.0")
              // - issue_date with full datetime (not midnight)

              // Build form state from payload.data and formData
              const formStateForBuilder = {
                ...payload.data,
                subform: payload.data.subform || formData.subform || [],
              };

            // Use dedicated payload builder
            finalPayload = buildPartsRequestPayload(
              formStateForBuilder,
              payload.next_action || "",
              payload.next_state || ""
            );

            if (import.meta.env.DEV) {
              console.log("[Save Action] Built Parts Request payload using dedicated builder:", {
                hasData: !!finalPayload.data,
                hasSubform: !!finalPayload.data?.subform,
                subformLength: finalPayload.data?.subform?.length || 0,
                next_action: finalPayload.next_action,
                next_state: finalPayload.next_state,
                issue_date: finalPayload.data?.issue_date,
              });
            }
          }

          if (isPartsRequestOrIssueContext(activeScreenId, moduleEndpoint)) {
            const isRequest = isPartsRequestContext(activeScreenId, moduleEndpoint);
            const overlapContext = isRequest ? "parts_request" : "parts_issue";
            const parentType = isRequest ? "jobcard" : "service_details";
            const parentId = String(
              finalPayload?.data?.parent_id ??
                finalPayload?.data?.parentid ??
                formData?.parentid ??
                formData?.parent_id ??
                ""
            ).trim();
            const codeids = extractCodeidsFromPartsPayload(finalPayload, overlapContext);

            if (parentId && codeids.length > 0) {
              try {
                const overlapResult = await validatePurchasedOverlapForSave({
                  parentId,
                  parentType,
                  context: overlapContext,
                  codeids,
                });
                if (overlapResult.has_overlap && overlapResult.items.length > 0) {
                  const confirmed = await confirmPurchasedOverlapProceed(
                    dispatch,
                    overlapResult.items,
                    overlapContext
                  );
                  if (!confirmed) {
                    dispatch(
                      setScreenSaving({ screenId: activeScreenId, isSaving: false })
                    );
                    return;
                  }
                  if (finalPayload?.data) {
                    finalPayload.data.purchase_overlap_acknowledged = 1;
                  }
                }
              } catch (overlapError) {
                ensureSlidingTimeout(
                  dispatch,
                  {
                    message:
                      overlapError instanceof Error
                        ? overlapError.message
                        : "Unable to verify purchased items for this job card.",
                    type: "error",
                  },
                  6000
                );
                dispatch(
                  setScreenSaving({ screenId: activeScreenId, isSaving: false })
                );
                return;
              }
            }
          }

          if (isLedgerNameSimilarityContext(activeScreenId, moduleEndpoint)) {
            const ledgerName = resolveLedgerNameForSimilarityCheck(
              finalPayload?.data || formData,
              activeScreenId,
              moduleEndpoint
            );
            const excludeLedgerId = String(
              finalPayload?.data?.id ?? recordId ?? formData?.id ?? ""
            ).trim();

            if (ledgerName) {
              try {
                const similarResult = await validateSimilarLedgerNameForSave({
                  name: ledgerName,
                  excludeId: isEditMode ? excludeLedgerId : undefined,
                });
                if (similarResult.has_similar && similarResult.matches.length > 0) {
                  const confirmed = await confirmSimilarLedgerNameProceed(
                    dispatch,
                    similarResult.matches,
                    similarResult.message
                  );
                  if (!confirmed) {
                    dispatch(
                      setScreenSaving({ screenId: activeScreenId, isSaving: false })
                    );
                    return;
                  }
                  if (finalPayload?.data) {
                    finalPayload.data.ledger_name_similarity_acknowledged = 1;
                  }
                }
              } catch (similarError) {
                ensureSlidingTimeout(
                  dispatch,
                  {
                    message:
                      similarError instanceof Error
                        ? similarError.message
                        : "Unable to check for similar ledger names.",
                    type: "error",
                  },
                  6000
                );
                dispatch(
                  setScreenSaving({ screenId: activeScreenId, isSaving: false })
                );
                return;
              }
            }
          }

          const operationType = isEditMode ? "UPDATE" : "CREATE";

          try {
            console.log(`[Save Action] Operation Type: ${operationType}`);
            console.log(`[Save Action] Edit Mode: ${isEditMode}`);
            console.log(`[Save Action] Record ID: ${recordId}`);
            console.log(`[Save Action] API Endpoint: ${endpoint}`);
            console.log(
              "[Save Action] Full Payload:",
              JSON.stringify(finalPayload, null, 2)
            );

            // GENERIC: Use PUT for create operations, POST for update operations
            // This works for all screens: estimation_add, jobcard_add, invoice_add, etc.
            // EXCEPTION: parts_request always uses the base endpoint with PUT (idempotent request)
            const isPartsRequest = moduleEndpoint === "parts_request";
            const response = isEditMode && !isPartsRequest
              ? await apiService.post(endpoint + "/update", finalPayload, {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
                onSuccess: (data) => {
                  console.log(
                    `[Save Action] ${operationType} API Success Response:`,
                    data
                  );
                },
                onError: (error) => {
                  console.error(
                    `[Save Action] ${operationType} API Error Response:`,
                    error
                  );
                  return null;
                },
              })
              : await apiService.put(endpoint, finalPayload, {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
                onSuccess: (data) => {
                  console.log(
                    `[Save Action] ${operationType} API Success Response:`,
                    data
                  );
                },
                onError: (error) => {
                  console.error(
                    `[Save Action] ${operationType} API Error Response:`,
                    error
                  );
                  return null;
                },
              });

            console.log(
              `[Save Action] ${operationType} Full Response:`,
              response
            );

            if (response.success) {
              console.log(
                `[Save Action] ${operationType} successful! Response:`,
                {
                  operationType,
                  success: response.success,
                  message: response.message,
                  data: response.data || response,
                }
              );

              // Clear all lookup caches to ensure fresh data on next load
              // This is important because after insert/update, lookup data might have changed
              const lookupNames = new Set<string>();

              // Extract all lookup names from the screen configuration
              walkComponentTree(screenConfig, (component) => {
                const comp = component as any;

                // Check for SEARCHABLE_DROPDOWN and SELECT_DROPDOWN
                if (
                  comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
                  comp.componentType === ComponentType.SELECT_DROPDOWN ||
                  comp.componentType === "searchableDropdown" ||
                  comp.componentType === "selectDropdown"
                ) {
                  const lookupName = comp.lookupName || comp.LookupName;
                  if (lookupName) {
                    lookupNames.add(lookupName);

                    // For independent lookups, clear by lookupName
                    dispatch(clearLookup(lookupName));

                    // For dependent lookups, we need to clear all possible parent value combinations
                    // Since we don't know all parent values, we'll clear the base lookup
                    // The dependent lookups will be cleared when they're refetched
                    const parentField = comp.parentField || comp.Parent_Field;
                    if (parentField) {
                      // Clear any cached dependent lookups by checking formData
                      const parentValue = formData[parentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        const dependentLookupKey = `${lookupName}_${parentValue}`;
                        dispatch(clearLookup(dependentLookupKey));
                      }
                    }
                  }
                }

                // Also check SUB_MODULE childControls for lookups
                if (
                  comp.componentType === ComponentType.SUB_MODULE &&
                  comp.childControls
                ) {
                  // CRITICAL: specific fix for SUB_MODULEs that use lookups (like customer_invoice_details)
                  const subModuleLookupName = comp.lookupName || comp.LookupName;
                  if (subModuleLookupName) {
                    lookupNames.add(subModuleLookupName);
                    // Clear independent lookup
                    dispatch(clearLookup(subModuleLookupName));

                    // Clear dependent lookup if parent field exists
                    const parentField = comp.parentField || comp.Parent_Field;
                    if (parentField) {
                      const parentValue = formData[parentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        const dependentLookupKey = `${subModuleLookupName}_${parentValue}`;
                        dispatch(clearLookup(dependentLookupKey));
                      }
                    }
                  }

                  comp.childControls.forEach((childControl: any) => {
                    if (
                      childControl.componentType ===
                      ComponentType.SEARCHABLE_DROPDOWN ||
                      childControl.componentType ===
                      ComponentType.SELECT_DROPDOWN ||
                      childControl.componentType === "searchableDropdown" ||
                      childControl.componentType === "selectDropdown"
                    ) {
                      const childLookupName =
                        childControl.lookupName || childControl.LookupName;
                      if (childLookupName) {
                        lookupNames.add(childLookupName);

                        // Clear independent lookup
                        dispatch(clearLookup(childLookupName));

                        // Clear dependent lookup if parent field exists
                        const childParentField =
                          childControl.parentField ||
                          childControl.Parent_Field ||
                          comp.parentField;
                        if (childParentField) {
                          const childParentValue = formData[childParentField];
                          if (
                            childParentValue !== undefined &&
                            childParentValue !== null &&
                            childParentValue !== ""
                          ) {
                            const childDependentLookupKey = `${childLookupName}_${childParentValue}`;
                            dispatch(clearLookup(childDependentLookupKey));
                          }
                        }
                      }
                    }
                  });
                }

                return false;
              });

              console.log(
                "[Save Action] Cleared lookup caches:",
                Array.from(lookupNames)
              );

              // CRITICAL: Update formData with response data (e.g., est_no) before navigating
              // This ensures fields like Estimate No. are displayed after save
              const responseData = response.data || response;
              if (responseData && typeof responseData === "object") {
                // Build formData from response to map API fields (e.g., est_no) to form fields (e.g., txt_estimate_number)
                const updatedFormData = buildFormDataFromRecord(responseData, {
                  screenId: activeScreenId,
                  fallback: formData, // Preserve existing formData as fallback
                });

                // Merge with existing formData to preserve any user-entered data
                const mergedFormData = {
                  ...formData,
                  ...updatedFormData,
                };

                // Update formData with response data so Estimate No. and other auto-generated fields are displayed
                dispatch(
                  setScreenFormData({
                    screenId: activeScreenId,
                    data: mergedFormData,
                  })
                );

                if (import.meta.env.DEV) {
                  console.log("[Save Action] Updated formData with response data:", {
                    responseData: Object.keys(responseData),
                    updatedFormData: Object.keys(updatedFormData),
                    est_no: responseData.est_no || updatedFormData.est_no || updatedFormData.txt_estimate_number,
                  });
                }
              }

              // Customize success message based on screen
              let successMessage = response.message;
              if (!successMessage) {
                if (activeScreenId === "151") {
                  successMessage = isEditMode
                    ? "Vehicle make updated successfully!"
                    : "Vehicle make added successfully!";
                } else if (activeScreenId === "152") {
                  successMessage = isEditMode
                    ? "Vehicle type updated successfully!"
                    : "Vehicle type added successfully!";
                } else {
                  successMessage = isEditMode
                    ? "Record updated successfully!"
                    : "Record created successfully!";
                }
              }

              // If form has file components and files were uploaded, append confirmation
              // Only show this if files were actually uploaded (not just if form has file components)
              if (hasFileComponents && Object.keys(pendingFiles).length > 0 && Object.keys(uploadedImageIdsByField).length > 0) {
                const fileCount = Object.keys(uploadedImageIdsByField).length;
                successMessage += ` ${fileCount} file(s) uploaded successfully.`;
              }

              autoHideNotification(dispatch, {
                message: successMessage,
                type: "success",
              });

              // Don't reset formData - keep it so Estimate No. is displayed
              // For vehicle type (152) and vehicle make (151), always stay on the same screen
              let targetView: string;
              let isBackNavigation = false;
              const isLookupMasterScreen =
                activeScreenId === "151" || activeScreenId === "152";

              if (isLookupMasterScreen) {
                // Keep edit mode + record id (e.g. 151/6) so deletes submit via
                // POST .../update instead of PUT create/insert.
                const stayRecordId = String(
                  recordId ||
                    editModeInfo?.recordId ||
                    responseData?.id ||
                    formData.id ||
                    ""
                ).trim();
                targetView = stayRecordId
                  ? `${activeScreenId}/${stayRecordId}`
                  : activeScreenId;
                isBackNavigation = false;

                if (stayRecordId) {
                  dispatch(
                    setScreenEditMode({
                      screenId: activeScreenId,
                      isEditMode: true,
                      recordId: stayRecordId,
                    })
                  );
                }

                // Remove soft-deleted submodule rows from form state after a
                // successful update so the UI matches the DB.
                const currentForm =
                  getState().forms.formDataByScreen[activeScreenId] || {};
                const cleanedForm: Record<string, any> = {
                  ...currentForm,
                  ...(stayRecordId ? { id: stayRecordId } : {}),
                };
                for (const key of Object.keys(cleanedForm)) {
                  const value = cleanedForm[key];
                  if (!Array.isArray(value)) continue;
                  cleanedForm[key] = value.filter(
                    (row: any) =>
                      !(
                        row &&
                        typeof row === "object" &&
                        (row.soft_delete === 1 ||
                          row.soft_delete === "1" ||
                          row.soft_delete === true)
                      )
                  );
                }
                dispatch(
                  setScreenFormData({
                    screenId: activeScreenId,
                    data: cleanedForm,
                  })
                );
              } else if (data?.buttonConfig?.Navigate) {
                // Allow overriding navigation target via button config
                targetView = data.buttonConfig.Navigate;
                isBackNavigation = false;
              } else {
                // For other screens, use previous view or default
                targetView = previousView || "101";
                isBackNavigation = !!previousView;
              }

              console.log("[Save Action] Navigating to:", targetView);
              dispatch(navigateToView({ view: targetView, isBack: isBackNavigation }));

              // Lookup masters (vehicle make/type) must keep form + edit mode.
              // Other screens: reset after a short delay so Estimate No. can be seen.
              if (!isLookupMasterScreen) {
                setTimeout(() => {
                  dispatch(resetScreenFormData(activeScreenId));
                }, 2000);
              }
            } else {
              console.error(`[Save Action] ${operationType} failed! Response:`, {
                operationType,
                success: response.success,
                message: response.message,
                data: response.data || response,
              });

              ensureSlidingTimeout(
                dispatch,
                {
                  message:
                    response.message ||
                    (isEditMode
                      ? "Failed to update record. Please try again."
                      : "Failed to create record. Please try again."),
                  type: "error",
                },
                5000
              );
            }
          } catch (error: any) {
            console.error(`[Save Action] Exception during ${operationType}:`, {
              operationType,
              error,
              message: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined,
              endpoint,
              payload: payload.data,
            });

            ensureSlidingTimeout(
              dispatch,
              {
                message:
                  error instanceof Error
                    ? error.message
                    : isEditMode
                      ? "An error occurred while updating. Please try again."
                      : "An error occurred while creating. Please try again.",
                type: "error",
              },
              5000
            );
          } finally {
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
          }

          return;
        }
        case "cancel":
        case "navigate_back": {
          const targetView = previousView || "105"; // Default to products list
          dispatch(resetScreenFormData(currentView));
          dispatch(navigateToView({ view: targetView, isBack: true }));
          return;
        }
        default:
          break;
      }
    };
